import { useState, useEffect, useRef } from "react";
import VAULT_GUIDED_ITEMS from "./vaultGuidedItems";
import { supabase, signUp, signIn, signOut, updateEmail, updatePassword, sendPasswordReset, getProfile, updateProfile, markOnboardingComplete, saveEstate, createInvitation, findInvitationByCode, acceptInvitation, getInvitationsForEstate, resendInvitation, reportDeath, cancelDeathReport, confirmDeath, getActiveDeathReport, getDeathReportById, logNotification, getNotifications, markNotificationRead, markAllNotificationsRead, getEstateId, loadEstateById, sendEmail, removeCollaboratorById, removeInvitationByEmail, leaveEstate, updateEstateExecutor, updateInvitationPermissions, createAccessRequest, getActiveAccessRequest, getAccessRequestById, respondToAccessRequest, updateOwnerAccessSettings, getInvitationsForUser, createAddRoleInvitations, acknowledgeRole, declineRole, getPendingAcknowledgments, markSpouseSetupDone, findCoupleInvitationByCode, setupCoupleLink, createCoupleSignupRecord, claimPreCreatedEstate, linkCoupleEstatesAndRoles, ROLE_PERMISSIONS } from "./supabase";

// ─── STORAGE SHIM ─────────────────────────────────────────────────────────────
// In Claude artifacts we use window.storage; in production we use localStorage.
// This shim makes both work identically.
if (typeof window !== 'undefined' && !window.storage) {
  window.storage = {
    get: async (key) => {
      try {
        const value = localStorage.getItem(key);
        if (value === null) throw new Error('not found');
        return { key, value };
      } catch(e) { throw e; }
    },
    set: async (key, value) => {
      try {
        localStorage.setItem(key, value);
        return { key, value };
      } catch(e) { return null; }
    },
    delete: async (key) => {
      localStorage.removeItem(key);
      return { key, deleted: true };
    },
    list: async (prefix) => {
      const keys = Object.keys(localStorage).filter(k => !prefix || k.startsWith(prefix));
      return { keys };
    }
  };
}



// ─── MFA HELPERS ──────────────────────────────────────────────────────────────
// Returns true if the current user has at least one verified TOTP factor enrolled.
const hasMfaEnabled = async () => {
  try {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    return (data?.totp || []).some(f => f.status === 'verified');
  } catch (e) {
    console.error('[HeirOS] hasMfaEnabled check failed:', e);
    return false;
  }
};

// ─── GOOGLE FONTS ─────────────────────────────────────────────────────────────
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; overflow-anchor: none; }
    body { background: #f8f5f0; overflow-anchor: none; }
    @keyframes fadeUp { from { opacity:0; transform:translateY(24px);} to { opacity:1; transform:translateY(0);} }
    @keyframes fadeIn { from { opacity:0;} to { opacity:1;} }
    @keyframes pulse { 0%,100%{opacity:.4} 50%{opacity:1} }
    @keyframes shimmer { from{background-position:200% center} to{background-position:-200% center} }
    @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    .fade-up { animation: fadeUp 0.6s ease forwards; }
    .fade-up-2 { animation: fadeUp 0.6s 0.15s ease both; }
    .fade-up-3 { animation: fadeUp 0.6s 0.3s ease both; }
    .fade-up-4 { animation: fadeUp 0.6s 0.45s ease both; }
    input:focus, textarea:focus, select:focus { outline: 2px solid #2c4a3e; outline-offset: 1px; }
    ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: #f0ebe3; } ::-webkit-scrollbar-thumb { background: #c4b89a; border-radius: 3px; }
  `}</style>
);

// ─── THEME ────────────────────────────────────────────────────────────────────
const T = {
  cream: "#f8f5f0", parchment: "#f0ebe3", tan: "#e8ddc8",
  forest: "#2c4a3e", forestLight: "#3d6b5a", forestDark: "#1a2e26",
  gold: "#b8935a", goldLight: "#d4ac74", goldDark: "#8a6a3a",
  charcoal: "#2a2a2a", slate: "#4a5568", muted: "#7a8694",
  white: "#ffffff", border: "#ddd5c4", borderLight: "#ece7de",
  success: "#2c7a5e", danger: "#c0392b",
};

// ─── SHARED STYLES ────────────────────────────────────────────────────────────
const btnReset = { background:"none", border:"none", cursor:"pointer", fontFamily:"'DM Sans', sans-serif", padding:0 };
const filledBtn = { background:`linear-gradient(135deg, #2c4a3e, #3d6b5a)`, color:"white", border:"none", borderRadius:"9px", padding:"0.625rem 1.25rem", fontSize:"0.875rem", fontWeight:"500", cursor:"pointer", fontFamily:"'DM Sans', sans-serif", letterSpacing:"-0.01em", transition:"opacity 0.15s, transform 0.15s" };
const outlineBtn = { background:"transparent", border:"1px solid #ddd5c4", borderRadius:"9px", padding:"0.625rem 1.25rem", fontSize:"0.875rem", fontWeight:"500", cursor:"pointer", fontFamily:"'DM Sans', sans-serif", color:"#4a5568", transition:"all 0.15s" };
const labelStyle = { display:"block", fontSize:"0.72rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", color:"#7a8694", marginBottom:"0.375rem" };
const formatPhone = (v) => { const d = v.replace(/\D/g,"").slice(0,10); if (d.length < 4) return d; if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`; return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`; };
const inputStyle = { width:"100%", padding:"0.75rem 1rem", border:"1px solid #ddd5c4", borderRadius:"8px", fontSize:"0.9rem", fontFamily:"'DM Sans', sans-serif", color:"#2a2a2a", background:"#f8f5f0", boxSizing:"border-box" };

// ─── DATA ─────────────────────────────────────────────────────────────────────
const BLOG_POSTS = [
  { id:1, title:"The 5 Things Every Executor Must Do in the First 72 Hours", date:"May 14, 2026", category:"Executor Guide", readTime:"6 min", excerpt:"When a loved one passes, the first three days are both emotionally devastating and administratively critical. Here's exactly what needs to happen — and in what order.", author:"HeirOS Editorial" },
  { id:2, title:"Probate vs. No Probate: What Actually Determines Which Path You're On", date:"May 8, 2026", category:"Legal Basics", readTime:"8 min", excerpt:"Most people assume they'll need probate. Many won't. Understanding the difference can save your family months and thousands of dollars.", author:"HeirOS Editorial" },
  { id:3, title:"How to Talk to Grieving Siblings About Estate Logistics Without It Becoming a Fight", date:"Apr 29, 2026", category:"Family Dynamics", readTime:"5 min", excerpt:"Money and grief are a volatile combination. These communication strategies help families stay aligned during the most stressful process they'll ever navigate together.", author:"HeirOS Editorial" },
  { id:4, title:"Digital Assets After Death: Crypto, Passwords, and Social Media Accounts", date:"Apr 21, 2026", category:"Digital Estate", readTime:"7 min", excerpt:"The average person now has over 90 online accounts. Most estates completely overlook them. Here's a complete guide to handling the digital side of a loved one's passing.", author:"HeirOS Editorial" },
  { id:5, title:"How to Work with an Estate Attorney: What to Prepare, What to Ask, and How to Get the Most from Every Meeting", date:"Apr 12, 2026", category:"Working with Attorneys", readTime:"5 min", excerpt:"Walking into an attorney's office organized — with documents gathered, timelines clear, and basic questions already answered — helps your attorney focus on the legal strategy that actually matters.", author:"HeirOS Editorial" },
  { id:6, title:"State-by-State Probate Thresholds: Does Your Estate Need to Go Through Court?", date:"Apr 3, 2026", category:"Legal Basics", readTime:"10 min", excerpt:"Every state has a different threshold for when probate is required. We've compiled the complete guide so you know exactly what applies to your situation.", author:"HeirOS Editorial" },
];

const BLOG_BODIES = {
  1: [
    "When a loved one passes, the emotional weight of grief can make even the simplest tasks feel impossible. But the administrative and legal clock starts immediately — and knowing exactly what needs to happen in the first 72 hours can save your family significant time, money, and stress in the months ahead.",
    "**Step 1: Obtain the death certificate (within hours)**\n\nThe funeral home will typically assist with the initial filing, but you need to order certified copies — at least 10 to 12. Every institution you'll deal with (banks, the IRS, Social Security, the DMV) will want an original. Digital copies are rarely accepted.",
    "**Step 2: Secure the property (within 24 hours)**\n\nIf the deceased lived alone, someone needs to secure their home immediately. Collect the mail, care for any pets, lock windows and doors, and take an inventory of any valuables. This isn't just practical — it protects the estate from liability.",
    "**Step 3: Notify the immediate family and employer**\n\nThis sounds obvious, but there's an administrative component: the employer needs to be notified to stop payroll and begin processing any owed wages, unused vacation, or life insurance claims. The sooner this happens, the faster those funds reach the estate.",
    "**Step 4: Locate the will**\n\nThe will governs everything. Check the deceased's files, safe deposit box, and contact any attorneys they worked with. If no will is found, the estate becomes intestate — meaning state law will determine how assets are distributed, which may not reflect what your loved one wanted.",
    "**Step 5: Do NOT pay any debts yet**\n\nThis surprises many executors: you should not start paying bills or debts from the estate in the first 72 hours. Creditors must be notified through a formal process, and paying some creditors before others can create legal liability. Hold off until you understand the full picture of what's owed.",
    "The first 72 hours set the tone for everything that follows. Getting these five things right doesn't just help with the legal process — it gives your family a sense of forward momentum during an otherwise overwhelming time.",
  ],
  2: [
    "\"Do I need probate?\" is one of the first questions almost every executor asks — and the honest answer is: it depends on how the deceased's assets were titled, not just on whether they had a will.",
    "**Assets that typically avoid probate**\n\nAnything with a named beneficiary — life insurance, retirement accounts, payable-on-death bank accounts — passes directly to that person, no court required. The same goes for assets held in a living trust, and property owned jointly with rights of survivorship, which passes automatically to the surviving owner.",
    "**Assets that typically require probate**\n\nProperty titled solely in the deceased's name, with no beneficiary designation and no trust, usually has to go through probate before it can be distributed or sold. This is most often real estate, vehicles, and individual bank or brokerage accounts.",
    "**Small estate exceptions**\n\nMost states have a simplified process for small estates — often under $50,000–$150,000 in probate-eligible assets, depending on the state. If the estate qualifies, you may be able to skip formal probate entirely using an affidavit process.",
    "**Why this matters**\n\nProbate isn't inherently bad, but it does add time (often 9–18 months) and cost (court fees plus, frequently, attorney fees). Knowing early which of the estate's assets actually require probate lets you set realistic expectations with the family and avoid unnecessary delays.",
    "The fastest way to find out where an estate stands is to inventory every asset and check how each one is titled. That single exercise usually answers the probate question before you ever need to speak with a court.",
  ],
  3: [
    "Grief changes how people communicate — and estate logistics have a way of surfacing old family dynamics at the worst possible time. The good news is that most sibling conflict during estate settlement isn't about money. It's about feeling left out of the process.",
    "**Share information before you're asked**\n\nSiblings who feel informed rarely feel the need to interrogate. Send a short weekly update — even \"nothing new to report\" counts — so no one is left wondering what's happening behind the scenes.",
    "**Separate decisions from updates**\n\nNot every piece of information requires a group decision. Distinguish clearly between \"here's what's happening\" and \"here's something I need your input on.\" This prevents every small update from turning into a debate.",
    "**Put things in writing**\n\nVerbal agreements about who gets what, or who's handling which task, tend to get remembered differently by different people. A simple shared document — even a basic spreadsheet — removes ambiguity and gives everyone the same source of truth.",
    "**Bring in a neutral third party when needed**\n\nIf a disagreement can't be resolved after a couple of honest conversations, it's usually faster and cheaper to loop in the estate attorney or a mediator than to let it fester. Executors aren't expected to referee family conflict alone.",
    "None of this eliminates grief or tension entirely — but consistent, structured communication is the single biggest factor in whether a family gets through estate settlement together or apart.",
  ],
  4: [
    "The average person now has upwards of 90 online accounts — banking apps, crypto wallets, email, social media, subscriptions, cloud storage. Most estate plans don't mention a single one of them, which leaves executors guessing at what exists and how to access it.",
    "**Start with an inventory, not a login list**\n\nBefore worrying about passwords, make a list of every category of digital asset: financial accounts, cryptocurrency, email, social media, photo/cloud storage, and any subscriptions that renew automatically. You can't manage what you don't know exists.",
    "**Cryptocurrency needs special attention**\n\nUnlike a bank, there's no customer service line to call for a forgotten crypto wallet — if the private keys or seed phrase are lost, the funds are gone permanently. If the deceased held crypto, locating the wallet's recovery phrase is often the single most time-sensitive task in the entire estate.",
    "**Passwords belong in a password manager, not a will**\n\nA will becomes a public record during probate in many states, so listing passwords directly in it is a security risk. The better approach is a password manager (like Bitwarden, 1Password, or Proton Pass) with an emergency access feature, so a trusted person can be granted access without the credentials ever being exposed publicly.",
    "**Social media accounts can usually be memorialized or deleted**\n\nMost major platforms (Facebook, Instagram, Google) have a legacy contact or memorialization request process. It typically requires a death certificate and proof of your relationship to the deceased.",
    "Digital assets are easy to overlook because they're intangible — but between financial accounts, crypto, and sentimental data like photos, they're often some of the most valuable and time-sensitive parts of an estate.",
  ],
  5: [
    "Estate attorneys typically charge $250–$500 an hour, and a moderately complex estate can easily rack up $5,000–$15,000 in legal fees. That's a real cost — but it's not automatically the wrong choice. The key is knowing which situations actually need one.",
    "**When you probably don't need an attorney**\n\nSmall, simple estates — a single beneficiary, no real property, no business interests, no disputes among heirs, and clear beneficiary designations on most accounts — can often be settled with self-help resources, county probate court staff, and a platform like HeirOS to keep the process organized.",
    "**When you almost certainly do**\n\nIf the estate includes a business, real estate in multiple states, a contested will, unclear or missing estate documents, or any heir who is likely to dispute the outcome, professional legal guidance isn't optional — the cost of getting it wrong (or of a drawn-out dispute) far exceeds the hourly rate.",
    "**The middle ground: pay for advice, not full representation**\n\nMany attorneys offer a flat-fee consultation to review your specific situation and flag any red flags, without you having to retain them for the entire process. This can cost a few hundred dollars and save you from an expensive mistake, while still letting you handle the bulk of the process yourself.",
    "**Ask about flat fees for routine filings**\n\nFor estates that do need some legal help, ask whether specific documents (like a probate petition) can be handled at a flat rate rather than hourly billing. Many attorneys will do this for routine, uncontested filings.",
    "The honest rule of thumb: the more disagreement, ambiguity, or complexity an estate involves, the more an attorney's hourly rate is worth it. The more straightforward and uncontested it is, the more of the process you can reasonably handle yourself.",
  ],
  6: [
    "Every state sets its own dollar threshold for when an estate must go through formal probate versus qualifying for a simplified small-estate process — and the difference between states is larger than most people expect.",
    "**Why thresholds exist**\n\nSmall-estate procedures exist so that modest estates aren't forced through months of court proceedings and legal fees that could exceed the value of the estate itself. If the total probate-eligible assets fall under the state's threshold, heirs can often use a simplified affidavit process instead.",
    "**What counts toward the threshold**\n\nOnly probate-eligible assets count — property titled solely in the deceased's name with no beneficiary designation. Assets with named beneficiaries, jointly owned property, and anything in a trust are typically excluded from the calculation entirely, which means an estate can look large on paper but still qualify for the simplified process.",
    "**Thresholds vary dramatically**\n\nSome states set the bar as low as $20,000–$50,000, while others allow small-estate procedures for estates worth $150,000 or more. Real property (like a house) is treated differently from personal property in several states, so a modest home can sometimes disqualify an otherwise small estate.",
    "**How to find your state's threshold**\n\nYour state's probate court or Secretary of State website will list the current small-estate limit and the required affidavit or petition. HeirOS's guided checklist automatically factors in your state's threshold once you enter your estate details, so you don't have to track down the figure yourself.",
    "Knowing your state's threshold early can save months of unnecessary court proceedings — it's one of the first things worth checking once you have a rough inventory of the estate's assets.",
  ],
};

const FAQ_ITEMS = [
  { q:"What exactly is HeirOS?", a:"HeirOS is a guided estate settlement platform that helps families navigate the administrative and legal process of settling a loved one's estate after they pass away. We provide step-by-step checklists, auto-generated legal letters, Artificial Intelligence (AI) guidance, and a shared family dashboard — all for a flat fee of $299." },
  { q:"Is my information safe on HeirOS?", a:"Yes. Every account is protected by two-factor authentication (required for Vault access), and your data is encrypted both in transit and at rest. Database-level access controls mean only you — and anyone you've explicitly invited — can ever see your estate information. Payments are processed securely through Stripe; we never see or store your card details." },
  { q:"Do I need a lawyer to use HeirOS?", a:"HeirOS is not a law firm and does not provide legal advice. HeirOS organizes the documents, timelines, and correspondence that every estate requires — whether or not you work with an attorney. For complex estates — large taxable estates, contested wills, business interests, or multi-state property — using HeirOS means you arrive at your attorney's office prepared, which saves time and helps them help you faster. We can also connect you with vetted estate attorneys in our network." },
  { q:"How much does HeirOS cost?", a:"HeirOS is a flat fee of $299 per estate. That covers your personalized checklist, all auto-generated documents and letters, AI-powered guidance, and family collaboration tools — everything you need to administer the estate from start to finish, with no percentage of the estate value and no surprise charges." },
  { q:"What states does HeirOS support?", a:"HeirOS currently provides guidance for all 50 U.S. states. Probate laws, filing deadlines, and required forms vary significantly by state, and our platform accounts for your specific state's requirements throughout the process." },
  { q:"What if the estate has no will (intestate)?", a:"Dying without a will is more common than you might think. HeirOS fully supports intestate estates and will guide you through how your state's intestacy laws determine asset distribution, what additional court filings may be required, and how to navigate the process without a will as your roadmap." },
  { q:"How long does it take to settle an estate with HeirOS?", a:"The national average for settling an estate is 16 months. HeirOS users typically complete the process in 4–8 weeks for simple estates, primarily because our platform eliminates the research, confusion, and back-and-forth that causes most delays. Complex estates with real property, business interests, or disputes naturally take longer." },
  { q:"Can multiple family members access the same estate?", a:"Yes. HeirOS includes a collaboration feature that lets you invite siblings, co-executors, attorneys, or other family members to view progress, claim tasks, and stay aligned — without needing to coordinate over group texts or shared spreadsheets." },
  { q:"Is my information secure?", a:"Yes. All estate data is encrypted in transit and at rest. We never sell your personal information or use it for advertising. Your estate data is yours — you can export or delete it at any time." },
  { q:"What documents does HeirOS generate?", a:"HeirOS generates pre-filled notification letters for Social Security, banks and financial institutions, and employers — along with Internal Revenue Service (IRS) final return notices, probate court filings, creditor notifications, and deed transfer documents." },
  { q:"What if I get stuck or have a question not covered in the app?", a:"Every HeirOS account comes with our AI guide, ready to answer estate questions in plain English any time, day or night. For more complex questions, our support team typically gets back to you within 4 business hours. And if you run into something that's really a legal matter, we'll point you to a vetted estate attorney." },
];

const HELPFUL_LINKS = [
  { category:"Government & Official", links:[
    { title:"Social Security Administration — Death Benefits", url:"https://www.ssa.gov/survivors/", desc:"Apply for survivor benefits and report a death to the SSA." },
    { title:"Internal Revenue Service (IRS) — Filing for Deceased Taxpayers", url:"https://www.irs.gov/taxtopics/tc356", desc:"Official IRS guidance on filing the final return and estate taxes." },
    { title:"USA.gov — Death of a Loved One", url:"https://www.usa.gov/death-loved-one", desc:"Comprehensive government resource for post-death administrative steps." },
    { title:"Veterans Affairs — Burial Benefits", url:"https://www.va.gov/burials-memorials/", desc:"Burial allowances and memorial benefits for veterans." },
  ]},
  { category:"Probate & Legal Resources", links:[
    { title:"NOLO — Probate Overview", url:"https://www.nolo.com/legal-encyclopedia/probate", desc:"Plain-English guide to the probate process state by state." },
    { title:"Find Law — Estate Administration", url:"https://www.findlaw.com/estate/estate-administration.html", desc:"Legal overview of executor duties and estate administration." },
    { title:"American Bar Association — Estate Planning", url:"https://www.americanbar.org/groups/real_property_trust_estate/", desc:"Find a qualified estate attorney in your area." },
    { title:"State Probate Court Locator", url:"https://www.probatecourts.org/", desc:"Find your local probate court for filing requirements." },
  ]},
  { category:"Financial & Tax", links:[
    { title:"FINRA — Deceased Account Holder Guide", url:"https://www.finra.org/investors/insights/when-brokerage-account-holder-dies", desc:"How to handle brokerage accounts and investments after death." },
    { title:"Federal Trade Commission — Debt Collection & Estates", url:"https://consumer.ftc.gov/articles/deceased-consumers", desc:"Your rights regarding debt collectors and a deceased person's estate." },
    { title:"IRS Form 1310 — Refund for Deceased", url:"https://www.irs.gov/forms-pubs/about-form-1310", desc:"Claim a tax refund on behalf of a deceased taxpayer." },
  ]},
  { category:"Grief & Support", links:[
    { title:"What's Your Grief", url:"https://whatsyourgrief.com/", desc:"Practical and emotional resources for navigating loss." },
    { title:"The Dougy Center", url:"https://www.dougy.org/", desc:"Grief support for children, teens, and families." },
    { title:"National Alliance for Grieving Children", url:"https://childrengrieve.org/", desc:"Resources for supporting grieving children through loss." },
  ]},
];

const CHECKLIST_STEPS = [
  { id:"death_cert", category:"Immediate", title:"Obtain Death Certificates", description:"Order at least 10–12 certified copies from the funeral home or county vital records office. You will need them for banks, the Social Security Administration (SSA), the Internal Revenue Service (IRS), the Department of Motor Vehicles (DMV), insurance companies, and more.", urgency:"Within 1–2 days", required:true },
  { id:"notify_family", category:"Immediate", title:"Notify Close Family & Friends", description:"Reach out to immediate family, close friends, and the deceased's employer.", urgency:"Within 1–2 days", required:true },
  { id:"secure_property", category:"Immediate", title:"Secure Property & Valuables", description:"Lock the home, collect mail, care for pets, and secure any vehicles or valuables.", urgency:"Within 3 days", required:true },
  { id:"locate_will", category:"Legal", title:"Locate the Will", description:"Search for the original will in files, safe deposit boxes, or with an attorney. If no will exists, the estate is 'intestate' and state law governs distribution.", urgency:"Within 1 week", required:true },
  { id:"notify_ssa", category:"Government", title:"Notify Social Security Administration", description:"Call 1-800-772-1213. Any benefits received for the month of death must be returned. Surviving spouses and children may be eligible for survivor benefits.", urgency:"Within 1 week", required:true },
  { id:"notify_employer", category:"Financial", title:"Contact Employer & Benefits", description:"Notify the employer to stop payroll, collect any owed wages, and handle life insurance, pension, or 401(k) beneficiary claims.", urgency:"Within 2 weeks", required:true },
  { id:"open_estate_account", category:"Financial", title:"Open an Estate Bank Account", description:"Open a dedicated checking account for the estate to receive income and pay debts. Do not mix with personal funds.", urgency:"Within 2 weeks", required:true },
  { id:"notify_banks", category:"Financial", title:"Notify Banks & Financial Institutions", description:"Contact all banks, credit unions, and investment accounts. Request account balances as of date of death for tax purposes.", urgency:"Within 2 weeks", required:true },
  { id:"file_probate", category:"Legal", title:"File for Probate (if required)", description:"Probate is required when assets are in the deceased's name alone without a beneficiary. File with your county probate court.", urgency:"Within 30 days", required:false },
  { id:"notify_creditors", category:"Legal", title:"Notify Creditors & Pay Debts", description:"Publish a notice to creditors in a local newspaper (required in most states). Compile all debts and pay valid claims from estate funds.", urgency:"Within 30–60 days", required:true },
  { id:"cancel_subscriptions", category:"Digital", title:"Cancel Subscriptions & Digital Accounts", description:"Cancel streaming services, memberships, credit cards, utilities, and insurance. Handle social media memorialization or deletion.", urgency:"Within 30 days", required:false },
  { id:"file_taxes", category:"Tax", title:"File Final Tax Returns", description:"File the deceased's final federal and state income tax returns. An estate tax return (Form 706) may also be required for larger estates.", urgency:"By April 15 of following year", required:true },
  { id:"transfer_assets", category:"Financial", title:"Transfer & Distribute Assets", description:"Transfer titled assets through the probate court or via beneficiary designation. Distribute remaining assets to heirs per the will or state law.", urgency:"After debts settled", required:true },
  { id:"close_estate", category:"Legal", title:"Close the Estate", description:"File a final accounting with the probate court, get court approval, and formally close the estate. Keep records for at least 7 years.", urgency:"After all tasks complete", required:true },
];

const PLANNING_STEPS = [
  // ── PHASE 1: GET YOUR HOUSE IN ORDER ───────────────────────────────────────
  { id:"create_will", phase:"Phase 1", category:"Legal", title:"Create or upload your will", description:"A will is the single most important document in your estate. If you already have one, upload it to your vault. If not, meet with an estate attorney to create or review your will.", urgency:"Do this first", required:true },
  { id:"name_executor", phase:"Phase 1", category:"Legal", title:"Name your executor and two backups", description:"Your executor carries out your wishes. Name a primary and two backups in case the first can't serve.", action:{ label:"Set Up Executors →", page:"app-collab" }, urgency:"Do this first", required:true },
  { id:"setup_vault", phase:"Phase 1", category:"Vault", title:"Set up your HeirOS vault", description:"Your vault stores everything your family needs — accounts, passwords, wishes, and more. Start adding items now.", action:{ label:"Open My Vault →", page:"app-vault" }, urgency:"Do this first", required:true },
  { id:"store_financial", phase:"Phase 1", category:"Vault", title:"Add your financial accounts to the vault", description:"Bank accounts, investment accounts, retirement accounts, and crypto. Your executor needs to find these.", action:{ label:"Add Financial Accounts →", page:"app-vault", vaultCategory:"financial" }, urgency:"High priority", required:true },
  { id:"store_insurance", phase:"Phase 1", category:"Vault", title:"Add your insurance policies", description:"Life insurance, health, home, auto, and long-term care policies. Include policy numbers and agent contacts.", action:{ label:"Add Insurance →", page:"app-vault", vaultCategory:"insurance" }, urgency:"High priority", required:true },
  { id:"store_digital", phase:"Phase 1", category:"Vault", title:"Document your digital assets and passwords", description:"Email accounts, social media, streaming services, and online accounts. Consider setting up Bitwarden emergency access.", action:{ label:"Add Digital Assets →", page:"app-vault", vaultCategory:"digital" }, urgency:"High priority", required:true },
  { id:"bitwarden", phase:"Phase 1", category:"Vault", title:"Set up Bitwarden emergency access for your executor", description:"Bitwarden's Emergency Access feature lets your executor access your passwords after death — without seeing them while you're alive.", action:{ label:"Set Up Password Access →", page:"app-vault" }, urgency:"Recommended", required:false },
  { id:"personal_messages", phase:"Phase 1", category:"Vault", title:"Write personal messages to your loved ones", description:"Leave letters, life advice, or final wishes for the people you love. These are stored privately in your vault.", action:{ label:"Write Messages →", page:"app-vault", vaultCategory:"personal" }, urgency:"Meaningful", required:false },
  { id:"funeral_wishes", phase:"Phase 1", category:"Vault", title:"Record your funeral and burial wishes", description:"Burial or cremation preference, service type, music, readings, and any other wishes. Removes guesswork for your family.", action:{ label:"Record Wishes →", page:"app-vault", vaultCategory:"other" }, urgency:"Meaningful", required:false },
  // ── PHASE 2: PROTECT YOUR FAMILY ───────────────────────────────────────────
  { id:"beneficiaries", phase:"Phase 2", category:"Financial", title:"Review beneficiary designations on all accounts", description:"401k, IRA, life insurance, and bank accounts all have beneficiary designations that override your will. Make sure they are up to date.", urgency:"Important", required:true },
  { id:"invite_executor", phase:"Phase 2", category:"Family", title:"Invite your executor to HeirOS", description:"Your executor can start learning the platform now so they are not overwhelmed when the time comes.", action:{ label:"Invite Executor →", page:"app-collab" }, urgency:"Recommended", required:false },
  { id:"invite_family", phase:"Phase 2", category:"Family", title:"Invite trusted family members", description:"Share your planning progress with family members you trust. They can see what is organized and what still needs attention.", action:{ label:"Invite Family →", page:"app-collab" }, urgency:"Recommended", required:false },
  { id:"power_of_attorney", phase:"Phase 2", category:"Legal", title:"Create a power of attorney", description:"Names someone to make financial and legal decisions if you become incapacitated. Different from your executor.", urgency:"Important", required:false },
  { id:"healthcare_directive", phase:"Phase 2", category:"Legal", title:"Create a healthcare directive / living will", description:"Documents your medical wishes if you cannot speak for yourself. Saves your family from impossible decisions.", urgency:"Important", required:false },
  { id:"life_insurance", phase:"Phase 2", category:"Financial", title:"Verify your life insurance coverage is adequate", description:"Rule of thumb: 10–12x your annual income. Review your policy and make sure your family is protected.", urgency:"Important", required:false },
  { id:"attorney_review", phase:"Phase 2", category:"Legal", title:"Consult an estate attorney if your estate exceeds $1M", description:"Complex estates benefit from professional guidance on trusts, tax minimization, and asset protection.", action:{ label:"Find an Attorney →", page:"attorneys" }, urgency:"If applicable", required:false },
  // ── PHASE 3: ANNUAL REVIEW ──────────────────────────────────────────────────
  { id:"annual_will", phase:"Phase 3", category:"Annual Review", title:"Annual will review", description:"Is your will still accurate? Major life changes — marriage, divorce, new children, new assets — may require updates.", action:{ label:"Review My Will →", page:"app-vault", category:"will" }, urgency:"Once a year", required:false, annual:true },
  { id:"annual_vault", phase:"Phase 3", category:"Annual Review", title:"Update vault if accounts or assets have changed", description:"New accounts, closed accounts, new property, or changed passwords should be updated in your vault.", action:{ label:"Review Vault →", page:"app-vault" }, urgency:"Once a year", required:false, annual:true },
  { id:"annual_executor", phase:"Phase 3", category:"Annual Review", title:"Review your executor lineup", description:"Are your executors still the right people? Have relationships changed? Update if needed.", action:{ label:"Review Executors →", page:"app-collab" }, urgency:"Once a year", required:false, annual:true },
  { id:"annual_beneficiaries", phase:"Phase 3", category:"Annual Review", title:"Check beneficiary designations after any life changes", description:"Marriage, divorce, birth, or death in the family means beneficiary designations need review.", urgency:"Once a year", required:false, annual:true },
  { id:"annual_messages", phase:"Phase 3", category:"Annual Review", title:"Update personal messages to loved ones", description:"Your life changes. Your messages should too. Take a few minutes to update what you have written.", action:{ label:"Update Messages →", page:"app-vault", vaultCategory:"personal" }, urgency:"Once a year", required:false, annual:true },
];

const CAT_COLORS = { Immediate:"#c0392b", Government:"#2980b9", Legal:"#8e44ad", Financial:"#27ae60", Digital:"#d4850a", Tax:"#2c4a3e" };

const DOC_TEMPLATES = {
  ssa: { title:"Social Security Notification", icon:"🏛", generate:(e)=>`Date: ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}

Social Security Administration
Office of Earnings & International Operations

RE: Notice of Death
Deceased: ${e.deceasedName||'[DECEASED NAME]'}
Date of Death: ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}) : '[DATE OF DEATH]'}

To Whom It May Concern:

I am writing to inform the Social Security Administration of the death of ${e.deceasedName||'[DECEASED NAME]'}, who passed away on ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}) : '[DATE OF DEATH]'}.

I am the ${e.executorRelation||'authorized representative'} of the deceased and am acting as executor/administrator of the estate.

Please take the following actions:
1. Cease all Social Security benefit payments effective immediately
2. Recover any payments made for the month of death or thereafter
3. Provide information regarding survivor benefits for eligible family members

Enclosed: Certified copy of the death certificate.

Sincerely,
${e.executorName||'[EXECUTOR NAME]'}
Executor, Estate of ${e.deceasedName||'[DECEASED NAME]'}
${e.executorPhone||'[PHONE]'} | ${e.executorEmail||'[EMAIL]'}
${e.executorAddress||'[ADDRESS]'}` },

  bank: { title:"Bank / Financial Institution Notice", icon:"🏦", generate:(e)=>`Date: ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}

[BANK NAME & ADDRESS]

RE: Death Notification — Account Holder: ${e.deceasedName||'[DECEASED NAME]'}
Date of Death: ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}) : '[DATE OF DEATH]'}

To the Estate Services Department:

I am writing to notify you of the death of ${e.deceasedName||'[DECEASED NAME]'}, an account holder at your institution.

As executor/administrator of the estate, I am requesting:
1. Freeze all accounts to prevent unauthorized activity
2. Account balance(s) as of the date of death for estate tax purposes
3. Information on transferring or closing accounts
4. Details on any POD/TOD beneficiary designations

Documentation available upon request:
• Certified death certificate
• Letters Testamentary / Letters of Administration
• Government-issued photo ID

Sincerely,
${e.executorName||'[EXECUTOR NAME]'}
Executor, Estate of ${e.deceasedName||'[DECEASED NAME]'}
${e.executorPhone||'[PHONE]'} | ${e.executorEmail||'[EMAIL]'}` },

  employer: { title:"Employer Death Notification", icon:"💼", generate:(e)=>`Date: ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}

Human Resources Department
[EMPLOYER NAME & ADDRESS]

RE: Death Notification — ${e.deceasedName||'[DECEASED NAME]'}

Dear Human Resources:

I am writing to inform you of the passing of ${e.deceasedName||'[DECEASED NAME]'} on ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}) : '[DATE OF DEATH]'}.

As executor of the estate, I am requesting:
1. Final paycheck including accrued vacation/PTO
2. Life insurance claim forms
3. 401(k)/pension/retirement account information
4. COBRA or survivor health coverage options
5. Instructions for returning company property

Please send correspondence to:
${e.executorName||'[EXECUTOR NAME]'}
Estate of ${e.deceasedName||'[DECEASED NAME]'}
${e.executorAddress||'[ADDRESS]'}

Sincerely,
${e.executorName||'[EXECUTOR NAME]'}
${e.executorPhone||'[PHONE]'} | ${e.executorEmail||'[EMAIL]'}` },

  probate: { title:"Probate Court Petition", icon:"⚖️", generate:(e)=>`Date: ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}

IN THE PROBATE COURT OF [COUNTY NAME] COUNTY
STATE OF ${(e.state||'[STATE]').toUpperCase()}

─────────────────────────────────────────
IN RE: THE ESTATE OF                    )
${e.deceasedName||'[DECEASED NAME]'},   )   Case No.: _______________
         Deceased.                      )
─────────────────────────────────────────

PETITION FOR PROBATE OF WILL AND
APPOINTMENT OF PERSONAL REPRESENTATIVE

Petitioner, ${e.executorName||'[EXECUTOR NAME]'}, respectfully states as follows:

1. DECEDENT INFORMATION
   Full Legal Name: ${e.deceasedName||'[DECEASED NAME]'}
   Date of Death: ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}) : '[DATE OF DEATH]'}
   County of Residence at Death: [COUNTY NAME]
   State of Residence at Death: ${e.state||'[STATE]'}

2. PETITIONER INFORMATION
   Name: ${e.executorName||'[EXECUTOR NAME]'}
   Relationship to Decedent: ${e.executorRelation||'[RELATIONSHIP]'}
   Address: ${e.executorAddress||'[ADDRESS]'}
   Phone: ${e.executorPhone||'[PHONE]'}
   Email: ${e.executorEmail||'[EMAIL]'}

3. WILL
   ${e.hasWill==='Yes'
     ? 'The Decedent left a valid Last Will and Testament dated [WILL DATE], a copy of which is attached hereto as Exhibit A. Petitioner requests that said Will be admitted to probate.'
     : e.hasWill==='No'
     ? 'The Decedent died intestate (without a Will). Petitioner requests appointment as Administrator of the estate pursuant to the intestacy laws of '+( e.state||'[STATE]')+'.'
     : 'Petitioner is currently searching for a Last Will and Testament. If located, it will be submitted to this Court promptly.'}

4. ESTIMATED ESTATE VALUE
   Approximate gross estate value: ${e.estateValue||'[ESTATE VALUE]'}
   ${e.hasProperty==='Yes' ? 'The estate includes real property located in [COUNTY], '+( e.state||'[STATE]')+'. A legal description is attached as Exhibit B.' : 'The estate does not appear to include real property.'}

5. HEIRS AND BENEFICIARIES
   The following individuals are known heirs or beneficiaries of the estate:
   Name: ___________________________  Relationship: _______________  Address: ___________________________
   Name: ___________________________  Relationship: _______________  Address: ___________________________
   Name: ___________________________  Relationship: _______________  Address: ___________________________
   (Attach additional sheet if needed)

6. REQUEST FOR APPOINTMENT
   Petitioner requests to be appointed as Personal Representative / Administrator of the estate of ${e.deceasedName||'[DECEASED NAME]'}, to serve ${e.hasWill==='Yes'?'as named in the Will':'pursuant to applicable state law'}, with full authority to administer the estate.

7. BOND
   Petitioner [ ] waives bond as provided in the Will   [ ] requests bond be waived   [ ] consents to bond in the amount of $_______________

WHEREFORE, Petitioner respectfully requests that this Court:
   a) Admit the ${e.hasWill==='Yes'?'Will':'intestate estate'} to probate;
   b) Appoint ${e.executorName||'[EXECUTOR NAME]'} as Personal Representative/Administrator;
   c) Issue Letters Testamentary/Letters of Administration; and
   d) Grant such other relief as the Court deems proper.

I declare under penalty of perjury that the foregoing is true and correct to the best of my knowledge.

Dated: ___________________________

___________________________________
${e.executorName||'[EXECUTOR NAME]'}, Petitioner
${e.executorAddress||'[ADDRESS]'}

⚠ IMPORTANT: Probate petition requirements vary significantly by state and county. This template is a starting point only. You MUST file on the court's official forms for your jurisdiction. Visit your county probate court's website or consult an attorney before filing.` },

  creditor: { title:"Creditor Notice Letter", icon:"📰", generate:(e)=>`Date: ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}

NOTICE TO CREDITORS

ESTATE OF ${(e.deceasedName||'[DECEASED NAME]').toUpperCase()}

Notice is hereby given that ${e.executorName||'[EXECUTOR NAME]'} has been appointed Personal Representative/Administrator of the estate of ${e.deceasedName||'[DECEASED NAME]'}, who died on ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}) : '[DATE OF DEATH]'}, in ${e.state||'[STATE]'}.

All persons having claims against the estate are required to present their claims, with proof, to the Personal Representative at the address below within [STATE-SPECIFIC TIMEFRAME — typically 3–6 months from first publication] from the date of first publication of this notice, or the claims will be forever barred.

Claims must be presented to:

${e.executorName||'[EXECUTOR NAME]'}
Personal Representative, Estate of ${e.deceasedName||'[DECEASED NAME]'}
${e.executorAddress||'[ADDRESS]'}
${e.executorPhone||'[PHONE]'}
${e.executorEmail||'[EMAIL]'}

Date of First Publication: ___________________________
Name of Publication: ___________________________

Respectfully submitted,

___________________________________
${e.executorName||'[EXECUTOR NAME]'}
Personal Representative
Estate of ${e.deceasedName||'[DECEASED NAME]'}

⚠ IMPORTANT: Most states require this notice to be published in a local newspaper of general circulation once per week for 2–3 consecutive weeks. The exact deadline for creditor claims varies by state (commonly 3–6 months from first publication). Check your state's specific probate code or consult an attorney for the correct timeframe and publication requirements.` },

  irs: { title:"IRS Final Return Notice", icon:"📋", generate:(e)=>`Date: ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}

Internal Revenue Service
[LOCAL IRS SERVICE CENTER]

RE: Final Income Tax Return — Deceased Taxpayer
Taxpayer Name: ${e.deceasedName||'[DECEASED NAME]'}
Date of Death: ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}) : '[DATE OF DEATH]'}
Tax Year: ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').getFullYear() : '[TAX YEAR]'}

To Whom It May Concern:

I am writing as the executor/personal representative of the estate of ${e.deceasedName||'[DECEASED NAME]'} to notify the IRS of the taxpayer's death and to fulfill required filing obligations.

FILING INFORMATION:
• The final Form 1040 for the period January 1 – ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}) : '[DATE OF DEATH]'} is enclosed/will be filed by the April 15, ${e.dateOfDeath ? new Date(e.dateOfDeath+'T12:00:00').getFullYear()+1 : '[YEAR]'} deadline.
• The word "DECEASED" has been written across the top of the return, along with the date of death.
• Form 1310 (Statement of Person Claiming Refund Due a Deceased Taxpayer) is attached if a refund is due.

REPRESENTATIVE INFORMATION:
Name: ${e.executorName||'[EXECUTOR NAME]'}
Relationship: ${e.executorRelation||'[RELATIONSHIP]'} / Personal Representative
Address: ${e.executorAddress||'[ADDRESS]'}
Phone: ${e.executorPhone||'[PHONE]'}
Email: ${e.executorEmail||'[EMAIL]'}

Form 56 (Notice Concerning Fiduciary Relationship) has been [ ] filed  [ ] enclosed  [ ] will be filed separately to establish my authority as fiduciary.

Please direct all future correspondence regarding this taxpayer's account to the address above.

Sincerely,

___________________________________
${e.executorName||'[EXECUTOR NAME]'}
Personal Representative, Estate of ${e.deceasedName||'[DECEASED NAME]'}

Enclosures:
  [ ] Final Form 1040
  [ ] Form 1310 (if refund due)
  [ ] Form 56
  [ ] Certified copy of Death Certificate
  [ ] Letters Testamentary / Letters of Administration

⚠ NOTE: If the estate's gross value exceeds $13.61 million (2026 threshold), a separate estate tax return (Form 706) may be required within 9 months of the date of death. Consult a tax professional for estates near or above this threshold.` },
};

// ─── INVITATION MERGE HELPER ──────────────────────────────────────────────────
// A user can hold multiple accepted invitations on the same estate (e.g. both
// "Named as executor" and "Medical POA"). We must OR-merge permissions across
// all matching rows so that adding a second role never reduces effective access.
const INVITATION_PERM_KEYS = [
  'view_checklist','edit_checklist','view_documents','download_documents',
  'view_vault','view_family','full_access','view_medical_documents','download_medical_documents',
];
function mergeInvitationRows(invs) {
  if (!invs || !invs.length) return { primaryRole: '', roles: [], mergedPerms: {}, inviterName: null, primaryId: null };
  const roles = [...new Set(invs.map(inv => inv.role).filter(Boolean))];
  // OR-merge: a permission is granted if ANY row grants it
  const mergedPerms = {};
  for (const key of INVITATION_PERM_KEYS) {
    mergedPerms[key] = invs.some(inv => inv.permissions?.[key] === true);
  }
  // Primary role: executor > Medical POA > Power of Attorney > first found (determines vault tab + playbook gate).
  // Accept both 'Named as executor' (canonical) and 'Executor' (legacy DB rows written before the rename)
  // so that multi-role merges always elevate the executor role regardless of which code path created it.
  const hasExecutor = roles.includes('Named as executor') || roles.includes('Executor');
  const primaryRole = hasExecutor
    ? 'Named as executor'  // always return canonical string, even if DB row contains legacy 'Executor'
    : roles.includes('Medical POA') ? 'Medical POA'
    : roles.includes('Power of Attorney') ? 'Power of Attorney'
    : roles[0] || '';
  const inviterName = invs.find(inv => inv.inviter_name)?.inviter_name || null;
  // Primary ID: the row matching the primary role (used for per-row permission updates)
  const primaryId = invs.find(inv => inv.role === primaryRole)?.id ?? invs[0]?.id ?? null;
  return { primaryRole, roles, mergedPerms, inviterName, primaryId };
}

const KNOWN_PAGE_IDS = new Set([
  "home",
  "login",
  "set-new-password",
  "blog",
  "demo",
  "faq",
  "privacy",
  "terms",
  "documents",
  "links",
  "attorneys",
  "pricing",
  "contact",
  "signup",
  "account",
  "cancel-death-report",
  "app-dashboard",
  "app-docs",
  "app-collab",
  "app-ai",
  "app-vault",
  "app-playbook",
  "app-my-vault",
  "app-add-spouse",
  "app-pending-roles",
]);

const isKnownPageId = (pageId) =>
  typeof pageId === "string" &&
  (
    KNOWN_PAGE_IDS.has(pageId) ||
    /^blog-post-\d+$/.test(pageId) ||
    pageId.startsWith("app-docs-")
  );

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState(() => {
    const requestedPage = new URLSearchParams(window.location.search).get("page");
    if (isKnownPageId(requestedPage)) return requestedPage;

    try {
      const savedPage = localStorage.getItem("heiros_page");
      if (isKnownPageId(savedPage)) return savedPage;
    } catch {
      // localStorage can be unavailable under restrictive browser settings.
    }

    return "home";
  });

  const persistPage = (nextPage) => {
    setPage(nextPage);
    if (!isKnownPageId(nextPage)) return;

    try {
      localStorage.setItem("heiros_page", nextPage);
    } catch {
      // Navigation still works when localStorage is unavailable.
    }
  };
  const [estate, setEstate] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [vault, setVault] = useState({ owner:null, items:[], trusted:[], mode:"owner" });
  const [completingOnboarding, setCompletingOnboarding] = useState(false);
  const vaultConfirmedEstateRef = useRef(null);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Mandatory TOTP MFA — checked after every login/session load
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaCurrentLevel, setMfaCurrentLevel] = useState(null);
  const [mfaNextLevel, setMfaNextLevel] = useState(null);
  const [mfaFactorId, setMfaFactorId] = useState(null);

  // Invitation & death verification system state
  const [cancelReportId, setCancelReportId] = useState(null);
  const [deathReport, setDeathReport] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [estateSupabaseId, setEstateSupabaseId] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [inviteParams, setInviteParams] = useState(null); // { code, email } from email deep link

  // Permission & access request state
  const [invitedPermissions, setInvitedPermissions] = useState(null);
  const [invitedRole, setInvitedRole] = useState(null);   // primary role string (backward compat)
  const [invitedRoles, setInvitedRoles] = useState([]);   // all roles this user holds on the estate
  const [invitedInvitationId, setInvitedInvitationId] = useState(null);
  const [activeAccessRequest, setActiveAccessRequest] = useState(null);
  const [accessAction, setAccessAction] = useState(null); // { type: 'approve'|'deny', status: 'loading'|'success'|'error' }
  const [pendingAccessAction, setPendingAccessAction] = useState(null); // { type: 'approve_access'|'deny_access', requestId, actionType: 'approve'|'deny' }
  const [ownerSettings, setOwnerSettings] = useState({ responseWindow: 72, grantImmediateAccess: false });

  // Pending role acknowledgments badge count
  const [pendingAckCount, setPendingAckCount] = useState(0);
  // Auto-popup modal for pending role acknowledgments — shown once per session on sign-in
  const [showPendingRolesModal, setShowPendingRolesModal] = useState(false);
  const pendingModalShownRef = useRef(false);

  // "Skip for now" on the mandatory MFA enrollment screen (signup gate only) — email
  // verification stays mandatory, but MFA enrollment itself becomes optional here.
  const [mfaSkipped, setMfaSkipped] = useState(false);

  // Generic MFA gate used to block sensitive in-app actions (Vault access, executor
  // access-request actions) that require a verified TOTP factor beyond the login gate.
  const [mfaRequiredReason, setMfaRequiredReason] = useState(null); // 'vault' | 'executor' | null
  const [mfaRequiredCallback, setMfaRequiredCallback] = useState(null);
  const requireMfa = async (reason, callback) => {
    const ok = await hasMfaEnabled();
    if (ok) { callback(); return; }
    setMfaRequiredReason(reason);
    setMfaRequiredCallback(() => callback);
  };

  // "Start Your Own Estate" — invited users who upgrade to their own estate
  const [ownEstateId, setOwnEstateId] = useState(null);
  const [activeEstateId, setActiveEstateId] = useState(null); // UUID of currently active estate
  const [accessibleEstates, setAccessibleEstates] = useState([]); // [{estateId,estateName,role,permissions,isOwn}]
  const [ownEstate, setOwnEstate] = useState(null);
  const [coupleCodeLoading, setCoupleCodeLoading] = useState(false);
  const [bundleInvitation, setBundleInvitation] = useState(null);
  const [ownTasks, setOwnTasks] = useState([]);
  const [ownInvitations, setOwnInvitations] = useState([]);
  // Derived: preserves all existing activeEstateMode checks without mass-editing downstream code
  const activeEstateMode = (ownEstateId && activeEstateId === ownEstateId) ? 'own' : 'invited';
  const setActiveEstateMode = (mode) => {
    if (mode === 'own') { setActiveEstateId(ownEstateId); }
    else { const first = accessibleEstates.find(e => !e.isOwn); setActiveEstateId(first ? first.estateId : null); }
  };

  // On mount: detect URL params (?paid, ?code+email invite link, ?cancel_death_report, ?deny_access), clean URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "true") {
      console.log("[HeirOS] ?paid=true detected in URL — storing in localStorage");
      localStorage.setItem("heiros_paid_pending", "true");
      window.history.replaceState({}, "", "/");
    }
    const code = params.get("code");
    const email = params.get("email");
    if (code && email) {
      console.log("[HeirOS] ?code+email detected — routing to invited signup:", { code, email });
      window.history.replaceState({}, "", "/");
      setInviteParams({ code, email });
      setPage("signup");
    }
    const cancelId = params.get("cancel_death_report");
    if (cancelId) {
      window.history.replaceState({}, "", "/");
      setCancelReportId(cancelId);
      setPage("cancel-death-report");
    }
    // deny/approve access-request links require a verified MFA factor before they're
    // executed — stash them and let the MFA-gate effect below process them once auth
    // has resolved and (if needed) the owner has completed enrollment.
    const denyId = params.get("deny_access");
    if (denyId) {
      window.history.replaceState({}, "", "/");
      setPendingAccessAction({ type: 'deny_access', requestId: denyId, actionType: 'deny' });
    }
    const approveId = params.get("approve_access");
    if (approveId) {
      window.history.replaceState({}, "", "/");
      setPendingAccessAction({ type: 'approve_access', requestId: approveId, actionType: 'approve' });
    }
    // Detect password recovery link — Supabase appends type=recovery as a query param
    // (PKCE flow) or hash fragment (implicit flow). Handle both.
    const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
    if (params.get("type") === "recovery" || hashParams.get("type") === "recovery") {
      window.history.replaceState({}, "", "/");
      setPage("set-new-password");
    }
    // Detect couple invite link: ?couple_code=HEIR-XXXXXX
    // Store inviter data in localStorage so it survives the auth/signup flow.
    // IMPORTANT: setPage("signup") is intentionally moved INSIDE the .then() so all
    // localStorage keys (especially heiros_couple_bundle_paid) are written before the
    // signup page renders and reads them. A loading state is shown in the interim.
    const coupleCode = params.get("couple_code");
    if (coupleCode) {
      console.log("[HeirOS] ?couple_code detected — looking up couple invite:", coupleCode);
      window.history.replaceState({}, "", "/");
      console.log("[HeirOS] couple_code: calling setCoupleCodeLoading(true)");
      setCoupleCodeLoading(true);
      console.log("[HeirOS] couple_code: setCoupleCodeLoading(true) called — firing findCoupleInvitationByCode");

      // Race the DB lookup against a 10-second timeout so a hanging Supabase query
      // (e.g. RLS blocking anonymous reads) never leaves the user on the spinner forever.
      const lookupPromise = findCoupleInvitationByCode(coupleCode);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('couple_code lookup timed out after 10 s')), 10000)
      );

      Promise.race([lookupPromise, timeoutPromise])
        .then(({ data, error }) => {
          console.log("[HeirOS] couple_code .then() fired — raw data:", data, "| raw error:", error);
          if (error) {
            console.warn("[HeirOS] couple_code lookup returned a Supabase error:", error);
          }
          if (data) {
            console.log("[HeirOS] couple_code: match found — writing localStorage keys");
            localStorage.setItem('heiros_couple_code',               coupleCode);
            localStorage.setItem('heiros_couple_inviter_estate_id',  data.estate_id    || '');
            localStorage.setItem('heiros_couple_inviter_user_id',    data.invited_by   || '');
            localStorage.setItem('heiros_couple_inviter_name',       data.inviter_name || '');
            // Couple Signup path ($399): store extra data needed at onboarding completion
            if (data.is_couple_signup) {
              console.log("[HeirOS] couple_code: is_couple_signup=true — storing signup keys");
              localStorage.setItem('heiros_couple_signup',             'true');
              localStorage.setItem('heiros_couple_roles',              JSON.stringify(data.couple_roles || []));
              localStorage.setItem('heiros_couple_first_spouse_email', data.first_spouse_email || '');
            }
            // Bundle paid — bypass payment wall for spouse 2
            if ((data.is_couple_bundle && data.is_bundle_paid) || (data.is_couple_signup && data.is_bundle_paid)) {
              console.log("[HeirOS] couple_code: is_bundle_paid=true — setting heiros_couple_bundle_paid");
              localStorage.setItem('heiros_couple_bundle_paid', 'true');
            } else {
              console.log("[HeirOS] couple_code: is_bundle_paid is falsy — payment wall will NOT be bypassed. is_couple_bundle:", data.is_couple_bundle, "is_couple_signup:", data.is_couple_signup, "is_bundle_paid:", data.is_bundle_paid);
            }
          } else {
            console.warn("[HeirOS] couple_code: no matching row returned (data is null/undefined). Check RLS policies on the invitations table — anonymous reads may be blocked.");
          }
          // Navigate to signup only after all localStorage keys are committed —
          // regardless of whether the code was valid, so the user lands somewhere.
          console.log("[HeirOS] couple_code: calling setCoupleCodeLoading(false) then setPage('signup')");
          setCoupleCodeLoading(false);
          setPage("signup");
        })
        .catch((err) => {
          console.error("[HeirOS] couple_code lookup failed or timed out:", err?.message || err);
          setCoupleCodeLoading(false);
          setPage("signup");
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Run any pending approve/deny access-request action once auth has resolved,
  // gated behind a verified MFA factor (owners must have 2FA enabled to approve or
  // deny executor emergency access requests).
  useEffect(() => {
    if (!pendingAccessAction || userLoading) return;
    const action = pendingAccessAction;
    setPendingAccessAction(null);
    requireMfa('executor', async () => {
      setAccessAction({ type: action.actionType, status: 'loading' });
      try {
        const { error } = await supabase.functions.invoke('send-email', {
          body: { type: action.type, requestId: action.requestId },
        });
        setAccessAction({ type: action.actionType, status: error ? 'error' : 'success' });
      } catch (e) {
        setAccessAction({ type: action.actionType, status: 'error' });
      }
    });
  }, [pendingAccessAction, userLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load localStorage data + establish auth session listener
  useEffect(() => {
    const tryGet = async (key) => {
      try { return await window.storage.get(key); } catch(e) { return null; }
    };
    const load = async () => {
      const r = await tryGet("heiros_estate");
      let loadedEstate = null;
      if (r) { try { loadedEstate = JSON.parse(r.value); setEstate(loadedEstate); } catch(e) {} }

      // Only load tasks from localStorage as temporary fallback
      // Supabase will load the real tasks after auth — skip localStorage for owners
      const t = await tryGet("heiros_tasks");
      if (t) {
        try {
          let loadedTasks = JSON.parse(t.value);
          if (loadedEstate?.signupMode === "owner" && loadedTasks.length && !loadedTasks[0].phase) {
            console.log("[HeirOS] Migrating owner tasks from CHECKLIST_STEPS → PLANNING_STEPS");
            loadedTasks = PLANNING_STEPS.map(s => ({ ...s, done: false }));
            window.storage.set("heiros_tasks", JSON.stringify(loadedTasks));
          }
          // Only use localStorage tasks if they have done:true data (avoid overwriting Supabase)
          const hasProgress = loadedTasks.some(t => t.done);
          if (!hasProgress) {
            console.log("[HeirOS] localStorage tasks have no progress — skipping, will load from Supabase");
          } else {
            console.log("[HeirOS] tasks loaded from localStorage — count:", loadedTasks.length);
            setTasks(loadedTasks);
          }
        } catch(e) {}
      }

      const c = await tryGet("heiros_collabs");
      if (c) { try { setCollaborators(JSON.parse(c.value)); } catch(e) {} }
      const v = await tryGet("heiros_vault");
      if (v) { try { setVault(JSON.parse(v.value)); } catch(e) {} }
    };
    load();

    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("[HeirOS] getSession →", session?.user ? "user found" : "no user");
      setUser(session?.user ?? null);
      setUserLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("[HeirOS] onAuthStateChange →", _event, session?.user ? "user found" : "no user");
      setUser(prev => {
        // Preserve object identity when it's the same user. TOKEN_REFRESHED fires on
        // every tab refocus; returning a new object reference reruns every [user]-keyed
        // effect, flips mfaLoading/profileLoading true, and unmounts the entire app
        // tree behind the loading gate at line 2380 — wiping unsaved form state,
        // closing modals, and resetting scroll.
        const next = session?.user ?? null;
        if (prev?.id === next?.id && prev?.email_confirmed_at === next?.email_confirmed_at) return prev;
        return next;
      });
      setUserLoading(false);
      if (_event === 'PASSWORD_RECOVERY') {
        setPage('set-new-password');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Mandatory TOTP MFA — re-checked whenever the user/session changes.
  const refreshMfaStatus = async () => {
    setMfaLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) throw error;
      console.log('[HeirOS] MFA assurance level:', data);
      setMfaCurrentLevel(data?.currentLevel ?? null);
      setMfaNextLevel(data?.nextLevel ?? null);
      if (data?.currentLevel === 'aal1' && data?.nextLevel === 'aal2') {
        const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
        if (factorsError) throw factorsError;
        const totp = (factorsData?.totp || []).find(f => f.status === 'verified');
        setMfaFactorId(totp?.id ?? null);
      } else {
        setMfaFactorId(null);
      }
    } catch (e) {
      console.error('[HeirOS] MFA status check failed:', e);
      setMfaCurrentLevel(null);
      setMfaNextLevel(null);
      setMfaFactorId(null);
    } finally {
      setMfaLoading(false);
    }
  };

  useEffect(() => {
    if (userLoading) return;
    if (!user) {
      setMfaLoading(false);
      setMfaCurrentLevel(null);
      setMfaNextLevel(null);
      setMfaFactorId(null);
      return;
    }
    refreshMfaStatus();
  }, [user, userLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load profile whenever user changes
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    let cancelled = false;
    setProfileLoading(true);
    console.log("[HeirOS] loading profile for user", user.id);

    Promise.race([
      getProfile(user.id),
      new Promise(resolve => setTimeout(() => resolve({ data: null, timedOut: true }), 4000)),
    ])
      .then(async ({ data, timedOut } = {}) => {
        if (cancelled) return;
        console.log("[HeirOS] profile fetch result:", { data, timedOut });
        let loadedProfile = data || null;

        // Cache a good profile so we can recover if the DB is temporarily unreachable
        if (loadedProfile?.id) {
          try { localStorage.setItem('heiros_profile_cache', JSON.stringify(loadedProfile)); } catch(e) {}
          // Clear stale invited-session flags so owner accounts are never contaminated
          // by localStorage left over from testing or a previous invited session
          if (loadedProfile.signup_mode !== 'invited') {
            try {
              await window.storage.delete('heiros_invited_estate_id');
              await window.storage.delete('heiros_is_invited');
              await window.storage.delete('heiros_profile_cache');
            } catch(e) {}
          }
        }

        // If the fetch failed, fall back to the locally cached profile
        if (!loadedProfile) {
          try {
            const cached = localStorage.getItem('heiros_profile_cache');
            if (cached) {
              loadedProfile = JSON.parse(cached);
              console.log("[HeirOS] DB unreachable — using cached profile:", loadedProfile?.signup_mode);
            }
          } catch(e) {}
          loadedProfile = loadedProfile || {};
        }

        // If localStorage says this user is invited, patch profile immediately so
        // there is never a render cycle where the payment wall appears.
        if (localStorage.getItem('heiros_is_invited') === 'true' && loadedProfile.signup_mode !== 'invited') {
          console.log("[HeirOS] patching loaded profile with invited flags from localStorage");
          loadedProfile = { ...loadedProfile, signup_mode: 'invited', has_paid: true };
        }

        // Self-healing invited user check.
        // Runs whenever signup_mode is missing — checks Supabase for a matching invitation
        // and fixes the profile immediately, regardless of how the profile was loaded.
        // GUARD: skip entirely for couple-signup invitees. They are owners claiming a
        // pre-created estate, not collaborators joining someone else's. An old generic
        // invitation row (e.g. sent before the couple-signup system) would otherwise win
        // this race and permanently set signup_mode:'invited', blocking Onboarding.
        const isCoupleSignupLoad = localStorage.getItem('heiros_couple_signup') === 'true';
        if (!isCoupleSignupLoad && (!loadedProfile.signup_mode || loadedProfile.signup_mode === '')) {
          try {
            const { data: invite } = await supabase
              .from('invitations')
              .select('*')
              .eq('invited_email', user.email)
              .in('status', ['pending', 'accepted'])
              // Exclude couple-signup detection/linking records — these are never real
              // collaborator invitations and must never trigger the self-healing path,
              // even if the heiros_couple_signup localStorage flag is absent (e.g. after
              // a page reload between payment and account creation).
              .neq('role', 'Couple')
              .neq('is_couple_invite', true)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            if (invite) {
              await supabase.from('profiles').update({
                signup_mode: 'invited',
                has_paid: true,
                onboarding_complete: true,
                invited_estate_id: invite.estate_id,
                invited_role: invite.role,
              }).eq('id', user.id);

              await supabase.from('invitations').update({
                status: 'accepted',
                accepted_by: user.id,
                accepted_at: new Date().toISOString(),
              }).eq('id', invite.id).eq('status', 'pending');

              loadedProfile = {
                ...loadedProfile,
                signup_mode: 'invited',
                has_paid: true,
                onboarding_complete: true,
                invited_estate_id: invite.estate_id,
                invited_role: invite.role,
              };

              localStorage.removeItem('heiros_profile_cache');
              localStorage.setItem('heiros_is_invited', 'true');
              localStorage.setItem('heiros_invited_estate_id', invite.estate_id);

              console.log('[HeirOS] self-healing invited profile applied for', user.email);
            }
          } catch (e) {
            console.warn('[HeirOS] self-healing check failed:', e);
          }
        }

        setProfile(loadedProfile);
        if (loadedProfile?.own_estate_id) setOwnEstateId(loadedProfile.own_estate_id);
        // Auto-navigate invited users with completed onboarding to the app
        const isInvitedProfile = loadedProfile.signup_mode === 'invited' || localStorage.getItem('heiros_is_invited') === 'true';
        if (isInvitedProfile && loadedProfile.onboarding_complete) {
          setPage(prev => prev.startsWith('app-') ? prev : 'app-dashboard');
        }
      })
      .catch(() => { if (!cancelled) setProfile({}); })
      .finally(() => { if (!cancelled) setProfileLoading(false); });

    return () => { cancelled = true; };
  }, [user?.id]);

  // After user is confirmed: apply any pending paid flag from Stripe redirect.
  // Runs whenever user changes — if user is null, waits. Uses localStorage so it
  // survives multiple auth-state-change events without losing the flag.
  // Payment is now verified server-side via Stripe webhook; we just reload the
  // profile to pick up the has_paid value the webhook already wrote.
  useEffect(() => {
    console.log("[HeirOS] paid check — user:", user?.id ?? "null", "| userLoading:", userLoading);
    if (userLoading) return;
    if (!user) return;

    const pendingPaid = localStorage.getItem("heiros_paid_pending");
    if (!pendingPaid) return;

    // Invited-upgrade payments are handled by their own useEffect below
    if (localStorage.getItem('heiros_invited_upgrade') === 'true') return;

    console.log("[HeirOS] pendingPaid=true — reloading profile (webhook already set has_paid)");
    localStorage.removeItem("heiros_paid_pending");
    localStorage.removeItem("heiros_pending_mode");

    supabase.from('profiles').select('*').eq('id', user.id).maybeSingle()
      .then(({ data }) => {
        if (data) {
          console.log("[HeirOS] profile reloaded after payment:", data);
          setProfile(data);
          setPage(prev => prev.startsWith('app-') ? prev : 'app-dashboard');
        }
      })
      .catch(e => console.log("[HeirOS] profile reload error:", e));
  }, [user, userLoading]);


  // localStorage backup: if profile loaded but signup_mode not yet set,
  // check the localStorage flag set during the session-available path.
  useEffect(() => {
    if (userLoading || !user || !profile || profileLoading) return;
    if (profile.signup_mode === 'invited') return;
    if (localStorage.getItem('heiros_is_invited') !== 'true') return;
    console.log('[HeirOS] heiros_is_invited flag found — patching profile to bypass payment wall');
    setProfile(prev => ({ ...(prev || {}), signup_mode: 'invited', has_paid: true }));
  }, [user, userLoading, profile, profileLoading]);

  // Handle Stripe return for invited users upgrading to their own estate.
  // Webhook has already created the estate row and written own_estate_id to the
  // profile — we just reload the profile and switch to 'own' mode.
  useEffect(() => {
    if (userLoading || profileLoading || !user || !profile) return;
    if (localStorage.getItem('heiros_invited_upgrade') !== 'true') return;
    if (localStorage.getItem('heiros_paid_pending') !== 'true') return;
    if (profile.signup_mode !== 'invited') {
      localStorage.removeItem('heiros_invited_upgrade');
      return;
    }
    localStorage.removeItem('heiros_invited_upgrade');
    localStorage.removeItem('heiros_paid_pending');
    console.log('[HeirOS] invited upgrade return — reloading profile (webhook already set own_estate_id)');
    supabase.from('profiles').select('*').eq('id', user.id).maybeSingle()
      .then(({ data }) => {
        if (data) {
          setProfile(data);
          if (data.own_estate_id) {
            console.log('[HeirOS] invited upgrade — own_estate_id from webhook:', data.own_estate_id);
            setOwnEstateId(data.own_estate_id);
            setActiveEstateId(data.own_estate_id);
          }
        }
      })
      .catch(e => console.error('[HeirOS] invited upgrade profile reload failed:', e));
  }, [user, userLoading, profile, profileLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load own estate data whenever ownEstateId is set
  useEffect(() => {
    if (!ownEstateId || !user) return;
    (async () => {
      const { data } = await supabase.from('estates').select('*').eq('id', ownEstateId).maybeSingle();
      if (data) {
        setOwnEstate({
          signupMode: data.signup_mode || 'owner',
          ownerName: data.owner_name || '',
          ownerEmail: data.owner_email || '',
          ownerPhone: data.owner_phone || '',
          ownerState: data.owner_state || '',
          deceasedState: data.deceased_state || '',
          deceasedName: data.deceased_name || '',
          dateOfDeath: data.date_of_death || '',
          hasWill: data.has_will || '',
          hasProperty: data.has_property || '',
          estateValue: data.estate_value || '',
          collaborators: [],
          status: data.status || '',
          createdAt: data.created_at || '',
          linked_spouse_estate_id: data.linked_spouse_estate_id || null,
        });
      }
      const { data: taskRows } = await supabase.from('tasks').select('*').eq('estate_id', ownEstateId);
      const nonPlaybook = (taskRows || []).filter(t => t.source !== 'playbook');
      if (nonPlaybook.length) {
        setOwnTasks(nonPlaybook.map(t => ({
          id: t.task_id, title: t.title, category: t.category,
          phase: t.phase, done: t.done, urgency: t.urgency,
          description: t.description, required: t.required,
          annual: t.annual, wasReset: t.was_reset,
          completedAt: t.completed_at, isCustom: t.is_custom,
          isOverride: t.is_override, notes: t.notes, subtasks: t.subtasks || [],
        })));
      } else {
        setOwnTasks(PLANNING_STEPS.map(s => ({ ...s, done: false })));
      }
      // Load invitations for own estate so AppCollaborate shows the right list when in 'own' mode
      const { data: ownInvData } = await supabase.from('invitations').select('*').eq('estate_id', ownEstateId);
      setOwnInvitations(ownInvData || []);
    })();
  }, [ownEstateId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the user returns to this tab, re-fetch the estate row if linked_spouse_estate_id
  // is not yet reflected in local state.  This covers the case where setupCoupleLink ran
  // in a different browser session (sp2's onboarding) and wrote the field to the DB while
  // sp1's tab was in the background — without this, needsSpouseSetup stays true on sp1's
  // side until a hard reload.
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!ownEstateId || ownEstate?.linked_spouse_estate_id) return;
      const { data } = await supabase
        .from('estates').select('*').eq('id', ownEstateId).maybeSingle();
      if (data) {
        setOwnEstate({
          signupMode:              data.signup_mode              || 'owner',
          ownerName:               data.owner_name               || '',
          ownerEmail:              data.owner_email              || '',
          ownerPhone:              data.owner_phone              || '',
          ownerState:              data.owner_state              || '',
          deceasedState:           data.deceased_state           || '',
          deceasedName:            data.deceased_name            || '',
          dateOfDeath:             data.date_of_death            || '',
          hasWill:                 data.has_will                 || '',
          hasProperty:             data.has_property             || '',
          estateValue:             data.estate_value             || '',
          collaborators:           [],
          status:                  data.status                   || '',
          createdAt:               data.created_at               || '',
          linked_spouse_estate_id: data.linked_spouse_estate_id  || null,
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [ownEstateId, ownEstate?.linked_spouse_estate_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load invitation permissions for invited users.
  // Runs once profile is settled. Sets invitedPermissions so every page can
  // gate content correctly. Without this, invitedPermissions stays null and
  // all pages treat the invited user as an owner (can see everything).
  useEffect(() => {
    if (!user || !profile) return;
    const isInvitedUser = profile.signup_mode === 'invited';
    if (!isInvitedUser) {
      setInvitedPermissions(null); // clear when owner account
      return;
    }
    const estateId = profile.invited_estate_id;
    if (!estateId) return;
    getInvitationsForUser(user.email, estateId).then(({ data: invs }) => {
      const { primaryRole, roles, mergedPerms, inviterName } = mergeInvitationRows(invs);
      if (primaryRole) {
        setInvitedRole(primaryRole);
        setInvitedRoles(roles);
        setProfile(prev => ({ ...(prev || {}), invited_role: primaryRole }));
      }
      // Patch ownerName on the estate if it's blank — inviter_name is readable by the invitee
      if (inviterName) {
        setEstate(prev => prev && !prev.ownerName ? { ...prev, ownerName: inviterName } : prev);
      }
      const loadedPerms = invs.length ? mergedPerms : {
        view_checklist: true, edit_checklist: false,
        view_documents: true, download_documents: false,
        view_vault: false, view_family: true, full_access: false,
      };
      setInvitedPermissions(loadedPerms);
      console.log('INVITATION PERMISSIONS LOADED:', {
        roles, permissions: loadedPerms,
        view_vault: loadedPerms?.view_vault,
        view_documents: loadedPerms?.view_documents,
        view_family: loadedPerms?.view_family,
        view_checklist: loadedPerms?.view_checklist,
      });
    });
  }, [user?.id, profile?.signup_mode, profile?.invited_estate_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build the full list of estates this user can access: their own + all accepted invitations.
  // This powers the multi-estate switcher and replaces the old binary isInvited&&ownEstateId gate.
  useEffect(() => {
    if (!user || !profile) return;
    const buildAccessibleEstates = async () => {
      const list = [];
      let myEstateId = ownEstateId || profile.own_estate_id;
      // Fallback: true original owners (never went through invited-upgrade flow) have no
      // own_estate_id in their profile — look them up directly by user_id in the estates table.
      if (!myEstateId && profile.signup_mode !== 'invited') {
        try {
          // Use order + limit(1) before maybeSingle() so that users with duplicate
          // user_id rows (can happen after re-onboarding) don't cause maybeSingle()
          // to error with PGRST116 and silently return null — we always deterministically
          // pick the most recently created estate as "the" owned one.
          const { data: directEstate } = await supabase
            .from('estates')
            .select('id')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (directEstate?.id) {
            myEstateId = directEstate.id;
            setOwnEstateId(directEstate.id); // hydrate state so estate-loading effect fires
          }
        } catch (_) {}
      }
      if (myEstateId) {
        list.push({ estateId: myEstateId, estateName: 'My Estate', role: 'owner', isOwn: true });
      }
      try {
        const { data: invites } = await supabase
          .from('invitations')
          .select('*, estates(owner_name)')
          .eq('accepted_by', user.id)
          .eq('status', 'accepted');
        if (invites?.length) {
          for (const inv of invites) {
            if (!list.find(e => e.estateId === inv.estate_id)) {
              list.push({
                estateId: inv.estate_id,
                estateName: inv.estates?.owner_name || inv.inviter_name || 'Estate',
                role: inv.role,
                permissions: inv.permissions,
                isOwn: false,
                invitationId: inv.id,
              });
            }
          }
        }
      } catch (e) {
        console.warn('[HeirOS] accessibleEstates load failed:', e);
      }
      // Fallback: include profile.invited_estate_id even if accepted_by isn't set (pre-new-flow users)
      if (profile.signup_mode === 'invited' && profile.invited_estate_id) {
        if (!list.find(e => e.estateId === profile.invited_estate_id)) {
          // Use inviter_name from the invitation row rather than estate?.ownerName (viewer's
          // own React state) — the latter was the source of wrong-name display in the switcher.
          let fallbackEstateName = 'Estate';
          try {
            const { data: fallbackInv, error: fallbackInvErr } = await supabase
              .from('invitations')
              .select('inviter_name')
              .eq('estate_id', profile.invited_estate_id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            if (fallbackInvErr) console.warn('[HeirOS] fallback inviter_name lookup failed:', fallbackInvErr);
            if (fallbackInv?.inviter_name) fallbackEstateName = fallbackInv.inviter_name;
          } catch (err) { console.warn('[HeirOS] fallback inviter_name lookup threw:', err); }
          list.push({
            estateId: profile.invited_estate_id,
            estateName: fallbackEstateName,
            role: profile.invited_role || invitedRole || 'collaborator',
            isOwn: false,
          });
        }
      }
      setAccessibleEstates(list);
      // Default to own estate if present, otherwise first invited estate
      setActiveEstateId(prev => prev || (list[0]?.estateId ?? null));
    };
    buildAccessibleEstates();
  }, [user?.id, ownEstateId, profile?.invited_estate_id, profile?.own_estate_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Estate-switch effect: when activeEstateId changes to a different invited estate,
  // fully clear the previous estate's state + window.storage cache, then re-fetch.
  // Uses a ref to detect actual changes and skip the initial mount.
  const prevActiveEstateIdRef = useRef(undefined);
  useEffect(() => {
    console.log('[HeirOS] estate-switch effect fired', {
      activeEstateId, ownEstateId, prevRef: prevActiveEstateIdRef.current, estateSupabaseId,
    });
    if (!activeEstateId || !user || !profile) return;
    if (activeEstateId === ownEstateId) {
      // Returning to our own estate. Every own-estate loader -- the estate object,
      // the tasks, AND the vault, all in the effect keyed on
      // [user?.id, estateSupabaseId, ...] -- reads estateSupabaseId, which at this
      // point still points at the estate we just left. The previous version bailed
      // out here without resyncing it, so none of those loaders re-ran and the vault
      // kept showing the other estate's items even though the switcher said "My
      // Estate". Switching away had already hard-cleared vault/tasks/estate, so the
      // result was an apparently empty or wrong vault.
      // Resyncing estateSupabaseId re-runs that single effect, which reloads all
      // three correctly. This effect's dependency array is [activeEstateId] only,
      // so setting estateSupabaseId here cannot re-trigger it -- no loop.
      const returningFromAnotherEstate =
        prevActiveEstateIdRef.current !== undefined &&
        prevActiveEstateIdRef.current !== ownEstateId;
      prevActiveEstateIdRef.current = activeEstateId;
      if (returningFromAnotherEstate) {
        console.log('[HeirOS] estate-switch: returning to own estate, clearing and resyncing', ownEstateId);
        // Resyncing estateSupabaseId alone is NOT enough. The owner-estate loader
        // (deps: [user?.id, estateSupabaseId, profile?.signup_mode, estate?.signupMode])
        // is guarded by `if (estate?.signupMode) return;`. On the way back, `estate`
        // still holds the estate we just left, whose signupMode is truthy -- so that
        // guard fires and the reload is discarded, leaving the previous estate's vault
        // on screen. Clearing this state mirrors the hard-clear the switch-away path
        // performs further down in this same effect, and changing `estate` also
        // changes one of the loader's dependencies, forcing a real re-fetch.
        setEstate(null);
        setTasks([]);
        setVault({ owner: null, items: [] });
        setInvitedPermissions(null);
        setInvitedRole(null);
        setInvitedRoles([]);
        setInvitations([]);
        try { window.storage.set('heiros_vault', 'null'); } catch(e) {}
        try { window.storage.set('heiros_tasks', 'null'); } catch(e) {}
        setEstateSupabaseId(ownEstateId);
      } else {
        console.log('[HeirOS] estate-switch: skip (own estate, no prior switch)');
      }
      return;
    }
    // Only skip if the data already loaded in state genuinely matches the target estate.
    // The old undefined-ref proxy assumed the profile effect had already fetched the estate,
    // but that's not guaranteed. Now we check directly: if estateSupabaseId already equals
    // activeEstateId, the estate is loaded and we can skip; otherwise always fetch.
    if (prevActiveEstateIdRef.current === activeEstateId && estateSupabaseId === activeEstateId) {
      console.log('[HeirOS] estate-switch → skip (already loaded, no change)');
      return;
    }
    prevActiveEstateIdRef.current = activeEstateId;

    // ── Hard-clear all previous estate data before loading the new one ──────
    setEstate(null);
    setTasks([]);
    setVault({ owner: null, items: [] });
    setInvitedPermissions(null);
    setInvitedRole(null);
    setInvitedRoles([]);
    setInvitations([]);
    setEstateSupabaseId(null);
    // Clear window.storage cache so stale data can't bleed through on slow loads
    try { window.storage.set('heiros_vault', 'null'); } catch(e) {}
    try { window.storage.set('heiros_tasks', 'null'); } catch(e) {}

    // ── Fetch the new estate ─────────────────────────────────────────────────
    (async () => {
      console.log('[HeirOS] estate-switch → calling loadEstateById', activeEstateId);
      const { data, error: estateError } = await loadEstateById(activeEstateId);
      console.log('[HeirOS] estate-switch → loadEstateById result', { id: data?.id ?? null, owner_name: data?.owner_name ?? null, error: estateError ?? null });
      if (!data) return;
      const resolvedOwnerName = data.owner_name || '';
      setEstate({
        ownerName: resolvedOwnerName, ownerEmail: data.owner_email || '',
        ownerPhone: data.owner_phone || '', ownerState: data.owner_state || '',
        deceasedState: data.deceased_state || '',
        deceasedName: data.deceased_name || '', dateOfDeath: data.date_of_death || '',
        hasWill: data.has_will, hasProperty: data.has_property,
        estateValue: data.estate_value || '', executorName: data.executor_name || '',
        executorEmail: data.executor_email || '', executorPhone: data.executor_phone || '',
        executorRelation: data.executor_relation || '', executorAddress: data.executor_address || '',
        inviteExecutor: data.invite_executor || '', inviteExecutorEmail: data.invite_executor_email || '',
        inviteBackup1Name: data.backup1_name || '', inviteBackup1Email: data.backup1_email || '',
        inviteBackup2Name: data.backup2_name || '', inviteBackup2Email: data.backup2_email || '',
        signupMode: data.signup_mode || 'owner', status: data.status, createdAt: data.created_at,
      });
      console.log('[HeirOS] estate-switch → setEstate called', { resolvedOwnerName, signupMode: data.signup_mode });
      setEstateSupabaseId(data.id);
      // Defensive: no invited role can reach saveVault today (AppVault gates every
      // write control behind isOwner), but arm the ref anyway so a future
      // vault-write permission for collaborators doesn't silently reactivate this gap.
      vaultConfirmedEstateRef.current = data.id;
      // Keep switcher label in sync with the authoritative owner_name from DB.
      // This matters when the JOIN in buildAccessibleEstates returned null (RLS) or
      // when the DB was corrected after the list was initially built.
      if (resolvedOwnerName) {
        setAccessibleEstates(prev => prev.map(e =>
          e.estateId === activeEstateId ? { ...e, estateName: resolvedOwnerName } : e
        ));
      }
      // Load invitations (for succession chain)
      const { data: invData } = await supabase.from('invitations').select('*').eq('estate_id', data.id);
      if (invData) setInvitations(invData);
      // Restore invitedPermissions + invitedRole for this user from their invitation record(s).
      // The hard-clear above nulled these out; we need them back so sidebar nav labels,
      // permission gates, and isInvitedUser checks all work correctly in the switched view.
      // We OR-merge across ALL matching rows so that holding multiple roles never collapses perms.
      const userEmail = user.email?.toLowerCase();
      const myInvs = (invData || []).filter(inv =>
        (inv.invitee_email || inv.invited_email)?.toLowerCase() === userEmail ||
        inv.invitee_id === user.id
      );
      if (myInvs.length) {
        const { primaryRole, roles, mergedPerms, primaryId } = mergeInvitationRows(myInvs);
        setInvitedPermissions(mergedPerms);
        setInvitedRole(primaryRole);
        setInvitedRoles(roles);
        if (primaryId) setInvitedInvitationId(primaryId);
      }
      // Load tasks — filter playbook tasks (same as own-estate loader) and merge
      // against PLANNING_STEPS/CHECKLIST_STEPS template to guarantee the `required`
      // field is always correct regardless of what the DB column contains.
      const { data: tasksData, error: tasksError } = await supabase.from('tasks').select('*').eq('estate_id', data.id).order('created_at', { ascending: true });
      console.log('[HeirOS] estate-switch → tasks fetch', { count: tasksData?.length ?? null, error: tasksError ?? null });
      const nonPlaybookSwitch = (tasksData || []).filter(t => t.source !== 'playbook');
      if (nonPlaybookSwitch.length) {
        const switchTemplate = data.signup_mode === 'owner' ? PLANNING_STEPS : CHECKLIST_STEPS;
        const switchTmplMap = new Map(switchTemplate.map(s => [s.id, s]));
        setTasks(nonPlaybookSwitch.map(t => {
          const tmpl = switchTmplMap.get(t.task_id || t.id);
          return {
            id: t.task_id || t.id, _dbId: t.id, title: t.title, category: t.category,
            phase: t.phase, done: t.done, urgency: t.urgency, description: t.description,
            required: tmpl ? tmpl.required : (t.required || false),
            annual: tmpl ? tmpl.annual : (t.annual || false),
            wasReset: t.was_reset,
            isCustom: t.is_custom || false, isOverride: t.is_override || false,
            notes: t.notes || '', subtasks: t.subtasks || [],
          };
        }));
      } else {
        setTasks((data.signup_mode === 'owner' ? PLANNING_STEPS : CHECKLIST_STEPS).map(s => ({ ...s, done: false })));
      }
      // Load vault items
      const { data: vaultData } = await supabase.from('vault_items').select('*').eq('estate_id', data.id);
      setVault({
        owner: { name: data.vault_owner_name || '', dob: data.vault_owner_dob || '', email: data.vault_owner_email || '' },
        mode: 'owner', trusted: [],
        items: (vaultData || []).map(v => ({
          id: v.id, title: v.title, category: v.category, subcategory: v.subcategory,
          notes: v.notes, accessLevel: v.access_level, details: v.details || {},
          file_url: v.file_url || null, file_name: v.file_name || null,
          shared_with_medical_poa: v.shared_with_medical_poa === true,
          excluded_invitation_ids: v.excluded_invitation_ids || [],
          ai_analysis: v.ai_analysis || null,
          createdAt: v.created_at,
        })),
      });
      // Load and merge invitation permissions for this estate across all matching rows
      getInvitationsForUser(user.email, activeEstateId).then(({ data: invs }) => {
        const { primaryRole, roles, mergedPerms, inviterName, primaryId } = mergeInvitationRows(invs);
        if (primaryRole) { setInvitedRole(primaryRole); setInvitedRoles(roles); }
        if (inviterName) { setEstate(prev => prev && !prev.ownerName ? { ...prev, ownerName: inviterName } : prev); }
        setInvitedPermissions(invs.length ? mergedPerms : {
          view_checklist: true, edit_checklist: false,
          view_documents: true, download_documents: false,
          view_vault: false, view_family: true, full_access: false,
        });
        if (primaryId) setInvitedInvitationId(primaryId);
      });
    })();
  }, [activeEstateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (key, val) => { try { await window.storage.set(key, JSON.stringify(val)); } catch(e) {} };

  const saveVault = (updated) => {
    setVault(updated);
    save("heiros_vault", updated);
    if (estateSupabaseId && user && vaultConfirmedEstateRef.current === estateSupabaseId) {
      if (updated.owner) {
        supabase.from('estates').update({
          vault_owner_name: updated.owner.name || null,
          vault_owner_dob: updated.owner.dob || null,
          vault_owner_email: updated.owner.email || null,
        }).eq('id', estateSupabaseId).then(({ error }) => {
          if (error) console.log('[HeirOS] vault owner save error:', error);
        });
      }
      // Filter to items this estate owns. Joint items loaded from the linked
      // spouse's estate carry a different estate_id and must never be written
      // here -- stamping estateSupabaseId on them creates duplicate rows that
      // trigger Postgres error 21000 ("ON CONFLICT DO UPDATE command cannot
      // affect row a second time") and breaks the entire upsert batch.
      // Newly created items have no estate_id yet and are always included.
      const ownItems = (updated.items || []).filter(
        item => !item.estate_id || item.estate_id === estateSupabaseId
      );
      // Dedupe by title+category (the conflict key), keeping the last
      // occurrence, so the batch is safe even if a duplicate reaches state.
      const dedupeMap = new Map();
      ownItems.forEach(item => {
        dedupeMap.set(`${item.title || ''}||${item.category || ''}`, item);
      });
      const itemsToUpsert = [...dedupeMap.values()];
      if (itemsToUpsert.length) {
        supabase.from('vault_items').upsert(
          itemsToUpsert.map(item => ({
            estate_id: estateSupabaseId,
            user_id: user.id,
            title: item.title || '',
            category: item.category || '',
            notes: item.notes || '',
            access_level: item.accessLevel || item.access_level || '',
            details: item.details || {},
            file_url: item.file_url || null,
            file_name: item.file_name || null,
            ai_analysis: item.ai_analysis || null,
            subcategory: item.subcategory || null,
            shared_with_medical_poa: item.shared_with_medical_poa === true,
            excluded_invitation_ids: item.excluded_invitation_ids || [],
            joint_estate_id: item.joint_estate_id || null,
          })),
          { onConflict: 'estate_id,title,category' }
        ).select().then(({ data: upserted, error }) => {
          if (error) { console.log('[HeirOS] vault upsert error:', error); return; }
          if (!upserted?.length) return;
          // B9: upsert previously discarded the real UUIDs Postgres assigned to new
          // rows. React state kept the Date.now() temp ids, so any later
          // .eq('id', item.id) call (edit, delete, make-joint) sent a millisecond
          // number to a uuid column and was rejected.
          // Reconcile: match each returned row to its in-state item on
          // title + category (the conflict key columns that exist in state; estate_id
          // is added at upsert time from estateSupabaseId so it is not in the item).
          // Only replace ids that are not already real UUIDs -- joint items loaded
          // from a linked spouse estate already carry correct UUIDs and must not be
          // repointed.
          const isUuid = (v) => typeof v === 'string' &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
          const byKey = {};
          upserted.forEach(row => { byKey[`${row.title}||${row.category}`] = row.id; });
          setVault(prev => {
            const corrected = (prev.items || []).map(item => {
              if (isUuid(item.id)) return item;
              const realId = byKey[`${item.title || ''}||${item.category || ''}`];
              return realId ? { ...item, id: realId } : item;
            });
            const next = { ...prev, items: corrected };
            save("heiros_vault", next);
            return next;
          });
        });
      }
    }
  };

  const handleSignOut = async () => {
    // B4: sign-out previously cleared 9 pieces of React state and NOTHING in
    // localStorage, so estate/vault/tasks data survived into the next account
    // created on the same device. It also left ~17 other state values live in
    // memory, which is why a manual localStorage clear alone did not stop the
    // old account's name reappearing in the vault setup modal.
    //
    // Rather than enumerate every setter (which silently rots as state is added),
    // tear the session down completely: end the Supabase session, sweep every
    // heiros_* key, then hard-navigate so no component state survives at all.
    try {
      await signOut();
    } catch (e) {
      console.warn('[HeirOS] signOut failed, clearing local session anyway:', e);
    }

    // Sweep by prefix, not by an explicit list -- some keys are dynamic
    // (heiros_spouse_setup_done_<id>, heiros_vault_edit_draft_<id>) and an
    // explicit list would miss them and every key added later.
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('heiros_'))
        .forEach(k => localStorage.removeItem(k));
    } catch (e) {
      console.warn('[HeirOS] Could not clear heiros_ localStorage on sign out:', e);
    }

    // Also clear any heiros_ sessionStorage, in case anything is written there.
    try {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('heiros_'))
        .forEach(k => sessionStorage.removeItem(k));
    } catch (e) { /* non-fatal */ }

    // Hard navigation guarantees every in-memory value is gone. replace() rather
    // than href so the authenticated view is not reachable via the back button.
    window.location.replace('/');
  };

  const startEstate = (form, navigate = true) => {
    const newEstate = { ...form, createdAt: new Date().toISOString() };
    const steps = form.signupMode === "owner" ? PLANNING_STEPS : CHECKLIST_STEPS;
    const newTasks = steps.map(s => ({ ...s, done:false }));
    setEstate(newEstate);
    setTasks(newTasks);
    save("heiros_estate", newEstate);
    save("heiros_tasks", newTasks);
    if (navigate) {
      if (form.signupMode === "owner") {
        persistPage("app-vault");
      } else {
        persistPage("app-dashboard");
      }
    }
  };

  const handleOnboardingComplete = async (form) => {
    if (user) {
      setCompletingOnboarding(true);
      try {
      // ── Always attempt to claim a pre-created estate BEFORE saveEstate runs ────
      // Called unconditionally so it works even when the heiros_couple_signup
      // localStorage flag was lost (e.g. email confirmation opened in a different
      // browser/tab/device than the one that landed on the ?couple_code= URL).
      // claimPreCreatedEstate queries estates WHERE user_id IS NULL AND
      // owner_email = user.email — it returns {data:null} immediately if no
      // unclaimed placeholder exists, making it a true no-op for normal owners.
      // When it does find a placeholder it sets user_id, has_paid, own_estate_id,
      // and spouse_setup_done on both the estate and the profile, so that
      // saveEstate below finds the row by user_id and UPDATEs it (with onboarding
      // form data) instead of INSERTing an orphaned duplicate estate.
      const coupleSignup = localStorage.getItem('heiros_couple_signup') === 'true';
      // spouseEstateId = sp1's estate (stored by the ?couple_code= detection from the invite
      // record's estate_id, which the webhook sets at step 6). The placeholder for sp2 is the
      // estate that has linked_spouse_estate_id = sp1's estate AND user_id IS NULL.
      // Passing this as a fallback makes claimPreCreatedEstate resilient to owner_email
      // mismatches on the placeholder (observed when the webhook wrote sp1's email instead
      // of sp2's email into owner_email on the placeholder estate).
      const spouseEstateId = localStorage.getItem('heiros_couple_inviter_estate_id') || bundleInvitation?.estate_id || undefined;
      try {
        await claimPreCreatedEstate(user.id, user.email || form.ownerEmail || '', spouseEstateId);
      } catch (e) {
        console.warn('[HeirOS] claimPreCreatedEstate (pre-saveEstate) threw:', e);
      }

      const { data } = await markOnboardingComplete(user.id);
      if (data) setProfile(data);
      // Save estate to Supabase so we have an ID for invitations & death reports.
      // Explicitly prefer profile.onboarding_data.fullName so that if a webhook
      // already created the row with owner_name = email (before onboarding completed),
      // this call overwrites it with the real name.
      const { data: estateRecord } = await saveEstate(user.id, {
        ...form,
        ownerName: form.ownerName || profile?.onboarding_data?.fullName || '',
        ownerEmail: user.email,
      });
      if (estateRecord?.id) {
        setEstateSupabaseId(estateRecord.id);
        // Arm the vault-write guard immediately: this estate row was just
        // created/confirmed for this user by saveEstate above, so saveVault's
        // `vaultConfirmedEstateRef.current === estateSupabaseId` check must not
        // have to wait on the loadEstateById effect (2157) to win a timing race
        // against startEstate()'s setEstate() call below -- without this, a
        // brand-new signup's vault writes silently no-op for the whole session.
        vaultConfirmedEstateRef.current = estateRecord.id;
        // Explicit patch by primary key — belt-and-suspenders for fields that saveEstate
        // may have silently failed to write if its .eq('user_id', ...) UPDATE was blocked
        // by an RLS policy that checks owner_user_id (null on webhook-created rows).
        // Using .eq('id', ...) bypasses that ambiguity.
        // has_will / has_property / estate_value are the fields most likely to be null
        // because they come exclusively from the onboarding form and are never set by the
        // webhook — if saveEstate's UPDATE silently returned 0 rows they stay null forever.
        const { error: patchErr } = await supabase.from('estates').update({
          owner_user_id:  user.id,
          has_will:       form.hasWill       || null,
          has_property:   form.hasProperty   || null,
          estate_value:   form.estateValue   || null,
          owner_name:     form.ownerName     || form.fullName || null,
          owner_state:    form.state         || form.ownerState || null,
          deceased_state: form.deceasedState || null,
          owner_email:    user.email         || null,
        }).eq('id', estateRecord.id);
        if (patchErr) console.error('[HeirOS] estate patch after saveEstate failed:', patchErr.message);
        // Re-fetch the full estate row from the database so that linked_spouse_estate_id
        // (written moments earlier by claimPreCreatedEstate or the webhook) is immediately
        // present in ownEstate state.  Building ownEstate from the onboarding form object
        // omits this field entirely, which causes needsSpouseSetup to evaluate true even
        // when the database row is correct — the SpouseSetupScreen flashes on every couple
        // signup completion.  A local prev-spread has the same problem when prev is null
        // (first-time owner) because the callback returns null unchanged.
        const { data: freshEstate } = await supabase
          .from('estates').select('*').eq('id', estateRecord.id).maybeSingle();
        if (freshEstate) {
          setOwnEstateId(freshEstate.id);
          setOwnEstate(freshEstate);
        }

        if (form.signupMode !== 'invited') {
          const { data: sync, error: syncErr } = await supabase.rpc('sync_own_estate_after_claim');
          if (syncErr || !sync?.ok) {
            console.error('[HeirOS] sync_own_estate_after_claim FAILED after owner onboarding —',
              'error:', JSON.stringify(syncErr), '| reason:', sync?.reason ?? 'none returned');
          }
        }

        // -- Bundle paid: claim entitlement server-side -------------------------
        // Was markPaid(user.id, 'owner'), which wrote profiles.has_paid directly
        // from the browser based only on a localStorage flag -- a trivial paywall
        // bypass. claim_couple_entitlement() takes no arguments: it derives the
        // caller from their JWT and requires an invitation naming that email with
        // is_bundle_paid = true, which only the stripe-webhook can set. The RPC
        // returns a status object rather than the profile row, so the profile is
        // re-fetched afterwards.
        if (localStorage.getItem('heiros_couple_bundle_paid') || bundleInvitation) {
          const { data: claim, error: claimErr } = await supabase.rpc('claim_couple_entitlement');
          if (claimErr || !claim?.ok) {
            console.error('[HeirOS] claim_couple_entitlement failed:', claimErr || claim);
          } else {
            console.log('[HeirOS] couple entitlement claimed, invitation:', claim.invitation_id);
            const { data: paidProfile } = await getProfile(user.id);
            if (paidProfile) setProfile(paidProfile);
          }
        }

        // ── Couple Signup path ($399): link roles after estate claim ─────────────
        // linkCoupleEstatesAndRoles is the ONLY place role invitation rows are
        // created for the couple-signup flow. Both user IDs are real at this
        // point (sp2 just claimed their estate), so every row gets correct
        // accepted_by values in both directions. ROLE_PERMISSIONS comes from
        // the same shared map used by the Edge Functions.
        if (coupleSignup) {
          if (!estateRecord?.id) {
            console.error('[HeirOS] couple link FAILED: estateRecord.id missing — estate claim may have failed before this point');
          } else {
            let coupleLinkErr = null;
            try {
              const { error: clErr } = await setupCoupleLink({
                inviterEstateId: localStorage.getItem('heiros_couple_inviter_estate_id') || bundleInvitation?.estate_id    || '',
                inviterUserId:   localStorage.getItem('heiros_couple_inviter_user_id')   || bundleInvitation?.invited_by   || '',
                inviterName:     localStorage.getItem('heiros_couple_inviter_name')      || bundleInvitation?.inviter_name || '',
                inviterEmail:    localStorage.getItem('heiros_couple_first_spouse_email') || '',
                inviteeEstateId: estateRecord.id,
                inviteeUserId:   user.id,
                inviteeName:     form.ownerName || '',
                inviteeEmail:    (user.email || '').toLowerCase(),
                coupleRoles:     JSON.parse(localStorage.getItem('heiros_couple_roles') || '[]'),
              });
              if (clErr) coupleLinkErr = clErr;
            } catch (e) {
              coupleLinkErr = e;
            }
            if (coupleLinkErr) {
              console.error('[HeirOS] couple link FAILED:', coupleLinkErr);
              window.alert(
                "Your account was created successfully, but we couldn't finish linking it to " +
                "your spouse's estate. Your payment is safe and nothing was lost. Please " +
                "contact support@heiros.estate and we'll finish the setup for you."
              );
            }
          }
          ['heiros_couple_signup', 'heiros_couple_roles', 'heiros_couple_first_spouse_email',
           'heiros_couple_code', 'heiros_couple_inviter_estate_id', 'heiros_couple_inviter_user_id',
           'heiros_couple_inviter_name', 'heiros_couple_bundle_paid'].forEach(k => localStorage.removeItem(k));

        // ── SpouseSetupScreen path: setupCoupleLink (all 3 roles, both estates) ──
        } else {
          const coupleInviterEstateId = localStorage.getItem('heiros_couple_inviter_estate_id') || bundleInvitation?.estate_id    || null;
          const coupleInviterUserId   = localStorage.getItem('heiros_couple_inviter_user_id')   || bundleInvitation?.invited_by   || null;
          const coupleInviterName     = localStorage.getItem('heiros_couple_inviter_name')      || bundleInvitation?.inviter_name || '';
          if (coupleInviterEstateId && coupleInviterUserId) {
            const ownerName  = form.ownerName || profile?.onboarding_data?.fullName || '';
            const ownerEmail = form.ownerEmail || user.email || '';
            try {
              // Fetch couple_roles directly here rather than relying on the bundleInvitation
              // state value, which may not have resolved yet in the same-browser case (the
              // payment wall is bypassed via localStorage so onboarding starts before the
              // async bundleInvitation query settles — a race that leaves bundleInvitation
              // null and triggers the all-three fallback regardless of what sp1 selected).
              const { data: freshInviteForRoles } = await supabase
                .from('invitations')
                .select('couple_roles')
                .ilike('invited_email', user.email)
                .eq('is_couple_bundle', true)
                .eq('is_bundle_paid', true)
                .neq('status', 'accepted')
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();
              // TEMP DIAGNOSTIC — remove after confirming couple_roles lands correctly
              console.log('[DIAG] freshInviteForRoles query — user.email:', user.email);
              console.log('[DIAG] freshInviteForRoles raw result:', freshInviteForRoles);
              const coupleRolesToUse = freshInviteForRoles?.couple_roles ?? ['Named as executor', 'Medical POA', 'Power of Attorney'];
              console.log('[DIAG] coupleRolesToUse branch:', freshInviteForRoles?.couple_roles ? 'found row — using DB value' : 'no row found — defaulted to all three', coupleRolesToUse);
              await setupCoupleLink({
                inviterEstateId: coupleInviterEstateId,
                inviterUserId:   coupleInviterUserId,
                inviterName:     coupleInviterName || '',
                inviterEmail:    '', // Edge Function looks up owner_email from estate
                inviteeEstateId: estateRecord.id,
                inviteeUserId:   user.id,
                inviteeName:     ownerName,
                inviteeEmail:    ownerEmail,
                coupleRoles:     coupleRolesToUse,
              });
              console.log('[HeirOS] Couple link established:', coupleInviterEstateId, '↔', estateRecord.id);
              // Re-fetch estate row now that setupCoupleLink has written linked_spouse_estate_id
              // to the DB — without this, needsSpouseSetup evaluates true (stale ownEstate state)
              // and SpouseSetupScreen incorrectly appears after the bundle-claim onboarding flow.
              const { data: freshEstateAfterLink } = await supabase
                .from('estates').select('*').eq('id', estateRecord.id).maybeSingle();
              if (freshEstateAfterLink) {
                setOwnEstateId(freshEstateAfterLink.id);
                setOwnEstate(freshEstateAfterLink);
              }
              // Re-fetch ownInvitations so the invited user's Dashboard/Vault/Collaborators
              // immediately reflect the couple-role rows written by setupCoupleLink, without
              // waiting for AppCollaborate's poll or a page navigation.
              const { data: freshOwnInvData } = await supabase
                .from('invitations').select('*').eq('estate_id', estateRecord.id);
              if (freshOwnInvData) setOwnInvitations(freshOwnInvData);
            } catch (e) {
              console.warn('[HeirOS] setupCoupleLink failed (non-fatal):', e);
            }
            ['heiros_couple_code', 'heiros_couple_inviter_estate_id',
             'heiros_couple_inviter_user_id', 'heiros_couple_inviter_name',
             'heiros_couple_bundle_paid'].forEach(k => localStorage.removeItem(k));
            if (bundleInvitation?.id) {
              await supabase.from('invitations')
                .update({ status: 'accepted', accepted_by: user.id, accepted_at: new Date().toISOString() })
                .eq('id', bundleInvitation.id);
              setBundleInvitation(null);
            }
          }
        }
      }
      } finally {
        setCompletingOnboarding(false);
      }
    }
    startEstate(form, true);
  };

  const toggleTask = (id) => {
    const u = tasks.map(t => {
      if (t.id !== id) return t;
      const nowDone = !t.done;
      return { ...t, done: nowDone, completedAt: (nowDone && t.annual) ? new Date().toISOString() : (t.annual ? null : t.completedAt), wasReset: false };
    });
    setTasks(u); save("heiros_tasks", u);
    // Save to Supabase for cross-device sync
    console.log('[HeirOS] toggleTask — estateSupabaseId:', estateSupabaseId, 'user:', user?.id);
    if (estateSupabaseId && user) {
      supabase.from('tasks').upsert(
        u.map(t => ({
          task_id: t.id,
          estate_id: estateSupabaseId,
          user_id: user.id,
          title: t.title,
          category: t.category,
          phase: t.phase || null,
          done: t.done,
          urgency: t.urgency || null,
          description: t.description || null,
          required: t.required || false,
          annual: t.annual || false,
          was_reset: t.wasReset || false,
          completed_at: t.completedAt || null,
          is_custom: t.isCustom || false,
          is_override: t.isOverride || false,
          notes: t.notes || null,
          subtasks: t.subtasks || [],
        })),
        { onConflict: 'estate_id,task_id' }
      ).then(({ data, error }) => {
        console.log('[HeirOS] tasks upsert result:', JSON.stringify({ data, error }));
      });
    }
  };

  // Task toggle that respects the active estate mode (invited vs own)
  const toggleActiveTask = async (id) => {
    if (activeEstateMode === 'own') {
      const u = ownTasks.map(t => {
        if (t.id !== id) return t;
        const nowDone = !t.done;
        return { ...t, done: nowDone, completedAt: nowDone ? new Date().toISOString() : null };
      });
      setOwnTasks(u);
      if (ownEstateId && user) {
        try {
          const { error } = await supabase.from('tasks').upsert(
            u.map(t => ({
              task_id: t.id, estate_id: ownEstateId, user_id: user.id,
              title: t.title, category: t.category, phase: t.phase || null,
              done: t.done, urgency: t.urgency || null, description: t.description || null,
              required: t.required || false, annual: t.annual || false,
              was_reset: t.wasReset || false, completed_at: t.completedAt || null,
              is_custom: t.isCustom || false, is_override: t.isOverride || false,
              notes: t.notes || null, subtasks: t.subtasks || [],
            })),
            { onConflict: 'estate_id,task_id' }
          );
          if (error) console.error('[HeirOS] checklist save FAILED —', JSON.stringify(error));
        } catch (e) {
          console.error('[HeirOS] checklist save THREW —', e?.message || e);
        }
      }
    } else {
      toggleTask(id);
    }
  };

  // On mount: auto-reset annual tasks that were completed more than 365 days ago
  useEffect(() => {
    if (!tasks.length) return;
    const now = Date.now();
    const ONE_YEAR = 365 * 24 * 60 * 60 * 1000;
    let changed = false;
    const updated = tasks.map(t => {
      if (!t.annual || !t.done || !t.completedAt) return t;
      if (now - new Date(t.completedAt).getTime() > ONE_YEAR) {
        changed = true;
        return { ...t, done: false, completedAt: null, wasReset: true };
      }
      return t;
    });
    if (changed) { setTasks(updated); save("heiros_tasks", updated); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load estate Supabase ID when user logs in (needed for invitations & death reports).
  // Also sets vaultConfirmedEstateRef so saveVault can write to the DB for returning
  // users whose estate was loaded from localStorage (Path C exits early in that case,
  // leaving the ref null and silently skipping every vault upsert).
  // Runs the one-time reconciliation: any item in localStorage that has no matching
  // DB row (title+category match) is upserted to recover from the lost-write bug.
  useEffect(() => {
    console.log('[HeirOS] getEstateId effect fired for user', user?.id);
    if (!user) { setEstateSupabaseId(null); return; }
    getEstateId(user.id).then(async ({ data }) => {
      console.log('[HeirOS] getEstateId result:', data);
      if (!data?.id) {
        console.log('[HeirOS] getEstateId — no estate id found, aborting reconciliation');
        return;
      }
      const confirmedId = data.id;
      setEstateSupabaseId(confirmedId);
      // Part 1 — arm the ref so saveVault's guard passes from this load onward
      vaultConfirmedEstateRef.current = confirmedId;
      // Part 2 — reconcile any items currently stuck in localStorage-only state.
      // Include both own items (estate_id match) and partner's joint items
      // (joint_estate_id match) in dbKeys so we don't try to re-upsert a
      // partner's joint item as if it were a missing local-only item.
      const [{ data: ownDbRows }, { data: jointDbRows }] = await Promise.all([
        supabase.from('vault_items').select('title, category').eq('estate_id', confirmedId),
        supabase.from('vault_items').select('title, category').eq('joint_estate_id', confirmedId),
      ]);
      const dbKeys = new Set(
        [...(ownDbRows || []), ...(jointDbRows || [])].map(r => `${r.title}||${r.category}`)
      );
      let localItems = [];
      try {
        const raw = localStorage.getItem('heiros_vault');
        if (raw) { const parsed = JSON.parse(raw); localItems = parsed?.items || []; }
      } catch {}
      const missing = localItems.filter(
        item => item.title && item.category && !dbKeys.has(`${item.title}||${item.category}`)
      );
      if (missing.length) {
        console.log(`[HeirOS] vault reconciliation — found ${missing.length} local-only item(s), upserting to DB`);
        supabase.from('vault_items').upsert(
          missing.map(item => ({
            estate_id: confirmedId,
            user_id: user.id,
            title: item.title || '',
            category: item.category || '',
            notes: item.notes || '',
            access_level: item.accessLevel || item.access_level || '',
            details: item.details || {},
            file_url: item.file_url || null,
            file_name: item.file_name || null,
            ai_analysis: item.ai_analysis || null,
            subcategory: item.subcategory || null,
            shared_with_medical_poa: item.shared_with_medical_poa === true,
            excluded_invitation_ids: item.excluded_invitation_ids || [],
            joint_estate_id: item.joint_estate_id || null,
          })),
          { onConflict: 'estate_id,title,category' }
        ).then(({ error }) => {
          if (error) console.log('[HeirOS] vault reconciliation upsert error:', error);
          else console.log(`[HeirOS] vault reconciliation complete — ${missing.length} item(s) written to DB`);
        });
      }
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Couple-bundle DB-backed invitation lookup.
  // Runs once user+profile are available (and not yet paid).  Queries for a
  // is_couple_bundle invitation addressed to this email that has been marked
  // is_bundle_paid=true by the Stripe webhook.  Result is stored in bundleInvitation
  // state so every localStorage read for the four couple-bundle keys can fall back
  // to it — making the payment-wall bypass and estate-link survive a different
  // browser/tab/device from the one that first loaded the ?couple_code= link.
  // Skipped entirely for the $399 couple-signup path (has its own localStorage set)
  // and for invited collaborators.
  useEffect(() => {
    if (!user || !profile || profileLoading) return;
    if (profile.signup_mode === 'invited') return;
    if (profile.has_paid) return;
    if (localStorage.getItem('heiros_couple_signup') === 'true') return;

    supabase
      .from('invitations')
      .select('id, estate_id, invited_by, inviter_name, is_bundle_paid, couple_roles')
      .ilike('invited_email', user.email)
      .eq('is_couple_bundle', true)
      .eq('is_bundle_paid', true)
      .neq('status', 'accepted')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => { if (data) setBundleInvitation(data); });
  }, [user?.id, profile?.has_paid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Couple-signup retry: if onboarding was marked complete but own_estate_id was
  // never written (e.g. claimPreCreatedEstate failed mid-onboarding due to an RLS
  // error that has since been fixed), find the unclaimed is_couple_signup invitation
  // and re-run claim + role-link now.  This runs on every login until own_estate_id
  // is set, then the profile.own_estate_id guard exits it immediately.
  //
  // Must run BEFORE the generic backfill so it can't race with a plain saveEstate
  // that would create an unrelated estate row without the couple link.
  useEffect(() => {
    if (!user || !profile) return;
    if (!profile.onboarding_complete || profile.own_estate_id) return;
    if (profile.signup_mode === 'invited' || profile.invited_estate_id) return;

    const retryCoupleClaim = async () => {
      // Look for an unclaimed couple-signup invitation for this user's email.
      // estate_id is null on these rows (set to null in createCoupleSignupRecord);
      // we need invited_by / first_spouse_email to rebuild sp1 context.
      const { data: invite } = await supabase
        .from('invitations')
        .select('id, estate_id, couple_roles, invited_by, inviter_name, first_spouse_email')
        .eq('is_couple_signup', true)
        .ilike('invited_email', user.email)
        .maybeSingle();

      if (!invite) return; // Not a couple-signup user — let the generic backfill handle it

      console.log('[HeirOS] couple-signup retry — found invite for', user.email, '| invite id:', invite.id);

      // Resolve sp1's estate ID.  createCoupleSignupRecord stores estate_id = null;
      // the Stripe webhook may have back-filled it later.  If still null, query sp1's profile.
      let sp1EstateId = invite.estate_id || null;
      if (!sp1EstateId && invite.invited_by) {
        const { data: sp1Prof } = await supabase
          .from('profiles')
          .select('own_estate_id')
          .eq('id', invite.invited_by)
          .maybeSingle();
        sp1EstateId = sp1Prof?.own_estate_id || null;
        console.log('[HeirOS] couple-signup retry — resolved sp1EstateId:', sp1EstateId, 'from profile');
      }

      const { data: claimedEstate, error: claimErr } = await claimPreCreatedEstate(
        user.id, user.email, sp1EstateId,
      );

      if (claimErr || !claimedEstate?.id) {
        console.warn('[HeirOS] couple-signup retry — claim failed:', claimErr?.message);
        return;
      }

      console.log('[HeirOS] couple-signup retry — estate claimed:', claimedEstate.id);
      setOwnEstateId(claimedEstate.id);
      setEstateSupabaseId(claimedEstate.id);
      // Arm so saveVault can write to this (the user's own, just-claimed) estate
      // in this same session — same gap class as B13's handleOnboardingComplete fix.
      vaultConfirmedEstateRef.current = claimedEstate.id;

      // Reload profile so own_estate_id is reflected in state and this effect
      // won't fire again on the next render (own_estate_id guard exits early).
      const { data: freshProfile } = await supabase
        .from('profiles').select('*').eq('id', user.id).maybeSingle();
      if (freshProfile) setProfile(freshProfile);

      // Link roles only if rows don't already exist on this estate
      // (guards against running twice if the claim succeeded but roles were already linked).
      if (Array.isArray(invite.couple_roles) && invite.couple_roles.length && sp1EstateId) {
        const { data: existingRoleRow } = await supabase
          .from('invitations')
          .select('id')
          .eq('estate_id', claimedEstate.id)
          .neq('is_couple_signup', true)
          .limit(1)
          .maybeSingle();

        if (!existingRoleRow) {
          let retryLinkErr = null;
          try {
            const { error: rlErr } = await setupCoupleLink({
              inviterEstateId: sp1EstateId,
              inviterUserId:   invite.invited_by        || '',
              inviterName:     invite.inviter_name       || '',
              inviterEmail:    invite.first_spouse_email || '',
              inviteeEstateId: claimedEstate.id,
              inviteeUserId:   user.id,
              inviteeName:     profile.onboarding_data?.fullName || '',
              inviteeEmail:    (user.email || '').toLowerCase(),
              coupleRoles:     invite.couple_roles,
            });
            if (rlErr) retryLinkErr = rlErr;
          } catch (e) {
            retryLinkErr = e;
          }
          if (retryLinkErr) {
            console.error('[HeirOS] couple link FAILED (retry):', retryLinkErr);
          } else {
            console.log('[HeirOS] couple-signup retry — roles linked');
          }
        } else {
          console.log('[HeirOS] couple-signup retry — role rows already exist for estate', claimedEstate.id, '— skipping link');
        }
      }
    };

    retryCoupleClaim();
  }, [user?.id, profile?.onboarding_complete, profile?.own_estate_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backfill: if user has estate data but no Supabase estates row (onboarded before
  // saveEstate was wired up), create the row now so invitations & death reports work.
  useEffect(() => {
    if (!user || !estate || estateSupabaseId) return;
    if (profile?.signup_mode === "invited" || profile?.invited_estate_id || invitedRole) return;
    console.log('[HeirOS] backfill — no estateSupabaseId, calling saveEstate for user', user.id);
    saveEstate(user.id, estate).then(({ data, error }) => {
      console.log('[HeirOS] backfill saveEstate result:', { data, error });
      if (data?.id) {
        setEstateSupabaseId(data.id);
        // Arm so saveVault can write to this (the user's own, just-backfilled)
        // estate in this same session — same gap class as B13's fix.
        vaultConfirmedEstateRef.current = data.id;
      } else if (error) {
        console.error('[HeirOS] backfill saveEstate FAILED:', error);
      }
    });
  }, [user?.id, estate, estateSupabaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backfill owner_name on estate + inviter_name on invitations when owner has no name saved
  useEffect(() => {
    if (!user || !profile || profile.signup_mode === 'invited') return;
    const name = profile?.onboarding_data?.fullName || '';
    if (!name || !estateSupabaseId) return;
    // Guard: only backfill the user's OWN estate. When viewing an invited estate,
    // estateSupabaseId is the invited estate's ID, which must never be overwritten
    // with the viewer's name.
    if (!ownEstateId || estateSupabaseId !== ownEstateId) return;
    // Only run if the estate in state still has no ownerName
    // Allow backfill to run if ownerName is missing OR looks like an email address
    // (emails get written when a webhook creates the estate row before onboarding).
    if (estate?.ownerName && !estate.ownerName.includes('@')) return;
    // Update estate.ownerName in state and DB
    setEstate(prev => prev ? { ...prev, ownerName: name } : prev);
    supabase.from('estates').update({ owner_name: name }).eq('id', estateSupabaseId).then(() => {});
    // Also patch inviter_name on all invitations for this estate so invited users see the name
    supabase.from('invitations').update({ inviter_name: name }).eq('estate_id', estateSupabaseId).then(() => {});
  }, [user?.id, profile?.onboarding_data?.fullName, estateSupabaseId, estate?.ownerName, ownEstateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load invited estate from Supabase when user is a guest collaborator
  // Checks both profile (primary) and localStorage backup (for cases where profile update is still pending)
  useEffect(() => {
    if (!user || !profile) return;
    const estateId = profile.invited_estate_id || localStorage.getItem("heiros_invited_estate_id");
    const looksInvited = profile.signup_mode === "invited";
    if (!looksInvited || !estateId) return;
    loadEstateById(estateId).then(async ({ data }) => {
      if (!data) return;
      // Use owner_name from DB; inviter_name from invitation record is patched in separately
      const resolvedOwnerName = data.owner_name || '';
      const invitedEstate = {
        ownerName: resolvedOwnerName,
        ownerEmail: data.owner_email || '',
        ownerPhone: data.owner_phone || '',
        ownerState: data.owner_state || '',
        deceasedState: data.deceased_state || '',
        deceasedName: data.deceased_name || '',
        dateOfDeath: data.date_of_death || '',
        hasWill: data.has_will,
        hasProperty: data.has_property,
        estateValue: data.estate_value || '',
        executorName: data.executor_name || '',
        executorEmail: data.executor_email || '',
        executorPhone: data.executor_phone || '',
        executorRelation: data.executor_relation || '',
        executorAddress: data.executor_address || '',
        inviteExecutor: data.invite_executor || '',
        inviteExecutorEmail: data.invite_executor_email || '',
        inviteBackup1Name: data.backup1_name || '',
        inviteBackup1Email: data.backup1_email || '',
        inviteBackup2Name: data.backup2_name || '',
        inviteBackup2Email: data.backup2_email || '',
        signupMode: data.signup_mode || 'owner',
        status: data.status,
        createdAt: data.created_at,
      };
      setEstate(invitedEstate);
      setEstateSupabaseId(data.id);
      // Defensive: no invited role can reach saveVault today (AppVault gates every
      // write control behind isOwner), but arm the ref anyway so a future
      // vault-write permission for collaborators doesn't silently reactivate this gap.
      vaultConfirmedEstateRef.current = data.id;
      // Load invitations for succession chain
      const { data: invData } = await supabase.from("invitations").select("*").eq("estate_id", data.id);
      if (invData) setInvitations(invData);
      // Load the owner's actual tasks by estate_id
      const { data: tasksData } = await supabase
        .from('tasks')
        .select('*')
        .eq('estate_id', data.id)
        .order('created_at', { ascending: true });
      console.log('[HeirOS] invited tasks loaded:', tasksData?.length ?? 0);
      if (tasksData?.length) {
        setTasks(tasksData.map(t => ({
          id: t.task_id || t.id,
          _dbId: t.id,
          title: t.title,
          category: t.category,
          phase: t.phase,
          done: t.done,
          urgency: t.urgency,
          description: t.description,
          required: t.required,
          annual: t.annual,
          wasReset: t.was_reset,
          isCustom: t.is_custom || false,
          isOverride: t.is_override || false,
          notes: t.notes || '',
          subtasks: t.subtasks || [],
        })));
      } else {
        // No DB tasks yet — fall back to template for the owner's mode
        const steps = invitedEstate.signupMode === 'owner' ? PLANNING_STEPS : CHECKLIST_STEPS;
        setTasks(steps.map(s => ({ ...s, done: false })));
      }
      // Load vault items for this estate
      const { data: vaultData } = await supabase
        .from('vault_items')
        .select('*')
        .eq('estate_id', data.id);
      setVault({
        owner: {
          name: data.vault_owner_name || '',
          dob: data.vault_owner_dob || '',
          email: data.vault_owner_email || '',
        },
        mode: 'owner',
        trusted: [],
        items: (vaultData || []).map(v => ({
          id: v.id,
          title: v.title,
          category: v.category,
            subcategory: v.subcategory,
          notes: v.notes,
          accessLevel: v.access_level,
          details: v.details || {},
          file_url: v.file_url || null,
          file_name: v.file_name || null,
          shared_with_medical_poa: v.shared_with_medical_poa === true,
          excluded_invitation_ids: v.excluded_invitation_ids || [],
          ai_analysis: v.ai_analysis || null,
          createdAt: v.created_at,
        })),
      });
    });
  }, [user?.id, profile?.invited_estate_id, profile?.signup_mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load owner estate from DB when there is no localStorage data (e.g. fresh session / incognito)
  useEffect(() => {
    if (!user || !profile || profile.signup_mode === 'invited') return;
    if (!estateSupabaseId) return;
    if (estate?.signupMode) return; // already loaded from localStorage
    loadEstateById(estateSupabaseId).then(async ({ data }) => {
      if (!data) return;
      const ownerEstate = {
        ownerName: data.owner_name || '',
        ownerEmail: data.owner_email || '',
        ownerPhone: data.owner_phone || '',
        ownerState: data.owner_state || '',
        deceasedState: data.deceased_state || '',
        deceasedName: data.deceased_name || '',
        dateOfDeath: data.date_of_death || '',
        hasWill: data.has_will,
        hasProperty: data.has_property,
        estateValue: data.estate_value || '',
        executorName: data.executor_name || '',
        executorEmail: data.executor_email || '',
        executorPhone: data.executor_phone || '',
        executorRelation: data.executor_relation || '',
        executorAddress: data.executor_address || '',
        inviteExecutor: data.invite_executor || '',
        inviteExecutorEmail: data.invite_executor_email || '',
        inviteBackup1Name: data.backup1_name || '',
        inviteBackup1Email: data.backup1_email || '',
        inviteBackup2Name: data.backup2_name || '',
        inviteBackup2Email: data.backup2_email || '',
        signupMode: data.signup_mode || 'owner',
        status: data.status,
        createdAt: data.created_at,
      };
      setEstate(ownerEstate);
      setEstateSupabaseId(data.id);
      console.log('[HeirOS] owner estate loaded from DB — signupMode:', ownerEstate.signupMode, 'id:', data.id);
      // Load tasks from DB, merge against PLANNING_STEPS as source of truth
      const { data: tasksData } = await supabase
        .from('tasks')
        .select('*')
        .eq('estate_id', data.id)
        .order('created_at', { ascending: true });
      if (tasksData?.length) {
        // Build a lookup by task_id from DB rows
        const dbMap = new Map(tasksData.map(t => [t.task_id || t.id, t]));
        const planIds = new Set(PLANNING_STEPS.map(s => s.id));
        // Merge PLANNING_STEPS with DB data (DB provides done/notes/subtasks/overrides)
        const baseTasks = PLANNING_STEPS.map(s => {
          const dbT = dbMap.get(s.id);
          if (dbT) {
            const isOverride = dbT.is_override || false;
            return {
              ...s,
              _dbId: dbT.id,
              done: dbT.done || false,
              wasReset: dbT.was_reset || false,
              completedAt: dbT.completed_at || null,
              notes: dbT.notes || '',
              subtasks: dbT.subtasks || [],
              isCustom: false,
              isOverride,
              // If an override, apply edited fields
              ...(isOverride ? {
                title: dbT.title || s.title,
                category: dbT.category || s.category,
                urgency: dbT.urgency || s.urgency,
                description: dbT.description || s.description,
              } : {}),
            };
          }
          return { ...s, done: false, notes: '', subtasks: [], isCustom: false, isOverride: false };
        });
        // Append any custom tasks from DB (not in PLANNING_STEPS)
        const customTasks = tasksData
          .filter(t => (t.is_custom || !planIds.has(t.task_id)) && t.task_id && !planIds.has(t.task_id))
          .map(t => ({
            id: t.task_id || t.id,
            _dbId: t.id,
            title: t.title,
            category: t.category,
            phase: t.phase,
            done: t.done || false,
            urgency: t.urgency,
            description: t.description || '',
            required: t.required || false,
            annual: t.annual || false,
            wasReset: t.was_reset || false,
            isCustom: true,
            isOverride: false,
            notes: t.notes || '',
            subtasks: t.subtasks || [],
          }));
        setTasks([...baseTasks, ...customTasks]);
      } else {
        setTasks(PLANNING_STEPS.map(s => ({ ...s, done: false, notes: '', subtasks: [], isCustom: false, isOverride: false })));
      }
      // Load vault items from Supabase: own items + partner's joint items merged.
      // The joint_estate_id query pulls in items created by the linked spouse that
      // point to this estate — these are the items that should appear in "My Vault"
      // alongside the user's own items.
      const [{ data: ownVaultData }, { data: jointVaultData }] = await Promise.all([
        supabase.from('vault_items').select('*').eq('estate_id', data.id),
        supabase.from('vault_items').select('*').eq('joint_estate_id', data.id),
      ]);
      const seenIds = new Set();
      const allVaultRows = [...(ownVaultData || []), ...(jointVaultData || [])].filter(v => {
        if (seenIds.has(v.id)) return false;
        seenIds.add(v.id);
        return true;
      });
      const loadedOwner = data.vault_owner_name ? {
        name: data.vault_owner_name,
        dob: data.vault_owner_dob || '',
        email: data.vault_owner_email || '',
      } : null;
      if (allVaultRows.length) {
        const loadedVault = {
          owner: loadedOwner,
          mode: 'owner',
          trusted: [],
          items: allVaultRows.map(v => ({
            id: v.id,
            estate_id: v.estate_id,
            title: v.title,
            category: v.category,
            subcategory: v.subcategory,
            notes: v.notes,
            accessLevel: v.access_level,
            details: v.details || {},
            file_url: v.file_url || null,
            file_name: v.file_name || null,
            shared_with_medical_poa: v.shared_with_medical_poa === true,
            excluded_invitation_ids: v.excluded_invitation_ids || [],
            joint_estate_id: v.joint_estate_id || null,
          }))
        };
        vaultConfirmedEstateRef.current = data.id;
        setVault(loadedVault);
        save('heiros_vault', loadedVault);
      } else {
        vaultConfirmedEstateRef.current = data.id;
        if (loadedOwner) {
          setVault(prev => {
            const loadedVault = { ...prev, owner: loadedOwner };
            save('heiros_vault', loadedVault);
            return loadedVault;
          });
        }
      }
    });
  }, [user?.id, estateSupabaseId, profile?.signup_mode, estate?.signupMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load death report + notifications + access requests when estate ID is known
  useEffect(() => {
    if (!user || !estateSupabaseId) return;
    // Check for active death report
    getActiveDeathReport(estateSupabaseId).then(({ data }) => {
      if (!data) return;
      // Auto-confirm if 72h deadline has passed
      if (data.status === 'pending' && new Date(data.confirmation_deadline) < new Date()) {
        confirmDeath(data.id, estateSupabaseId, {
          executorEmail: estate?.executorEmail || estate?.inviteExecutorEmail || null,
          deceasedName: data.deceased_name || estate?.ownerName || '',
        }).then(() => {
          setDeathReport({ ...data, status: 'confirmed' });
        });
      } else {
        setDeathReport(data);
      }
    });
    // Load invitations for this estate
    getInvitationsForEstate(estateSupabaseId).then(({ data }) => {
      if (data) setInvitations(data);
    });
    // Load notifications
    getNotifications(user.id).then(({ data }) => {
      if (data) setNotifications(data);
    });
  }, [user?.id, estateSupabaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load pending role acknowledgment count so the nav badge stays current.
  // Re-runs on estate switch (activeEstateId) so the badge is always fresh
  // for the current context, even though getPendingAcknowledgments is user-scoped.
  // Also triggers the auto-popup modal once per session when count > 0.
  useEffect(() => {
    if (!user?.email) return;
    getPendingAcknowledgments(user.email).then(({ data }) => {
      const count = (data || []).length;
      setPendingAckCount(count);
      if (count > 0 && !pendingModalShownRef.current) {
        pendingModalShownRef.current = true;
        setShowPendingRolesModal(true);
      }
    });
  }, [user?.email, activeEstateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll activeAccessRequest every 15s so status stays fresh across sessions
  useEffect(() => {
    if (!estateSupabaseId) return;

    const checkAccessRequest = async () => {
      const { data: req } = await getActiveAccessRequest(estateSupabaseId);
      if (!req) return;
      setActiveAccessRequest(req);
      // Auto-grant if response deadline has passed
      if (req.status !== 'granted' && req.response_deadline && new Date() > new Date(req.response_deadline)) {
        const { data: granted } = await respondToAccessRequest(req.id, 'granted');
        if (granted) {
          setActiveAccessRequest(granted);
          if (req.requested_by) {
            const inv = invitations.find(i => i.role === req.requester_role || i.invited_email === req.requester_name);
            if (inv?.id) {
              await updateInvitationPermissions(inv.id, {
                view_checklist: true, edit_checklist: true,
                view_documents: true, download_documents: true,
                view_vault: true, edit_vault: false,
                view_family: true, full_access: true,
              });
            }
          }
          if (req.requester_name) {
            sendEmail(req.requester_name, 'access_granted', {
              executorName: req.requester_name,
              ownerName: estate?.ownerName || 'the estate owner',
            });
          }
        }
      }
    };

    checkAccessRequest();
    const interval = setInterval(checkAccessRequest, 15000);
    return () => clearInterval(interval);
  }, [estateSupabaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll invitations every 15s in the parent so Dashboard and Vault reflect collaborator
  // changes without requiring the user to be on the Collaborators page.
  // Two effects — one per state variable — because activeInvitations is a derived const:
  //   activeEstateMode === 'own'     → ownInvitations (scoped to ownEstateId)
  //   activeEstateMode === 'invited' → invitations     (scoped to estateSupabaseId)
  useEffect(() => {
    if (!estateSupabaseId) return;
    const poll = async () => {
      const { data } = await getInvitationsForEstate(estateSupabaseId);
      if (data) setInvitations(data);
    };
    poll();
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [estateSupabaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ownEstateId) return;
    const poll = async () => {
      const { data } = await supabase.from('invitations').select('*').eq('estate_id', ownEstateId);
      if (data) setOwnInvitations(data);
    };
    poll();
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [ownEstateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track invitation ID for the invited user (permissions are already loaded by the
  // earlier effect; this only captures the DB row id for future updates)
  useEffect(() => {
    if (!user?.email || !estateSupabaseId) return;
    const looksInvited = profile?.signup_mode === 'invited';
    if (!looksInvited) return;
    getInvitationsForUser(user.email, estateSupabaseId).then(({ data: invs }) => {
      const { primaryRole, roles, mergedPerms, primaryId } = mergeInvitationRows(invs);
      if (primaryId) setInvitedInvitationId(primaryId);
      // Only update permissions if they haven't been loaded yet (avoid overwriting)
      if (primaryId && !invitedPermissions && invs.length) {
        setInvitedPermissions(mergedPerms);
        if (primaryRole) { setInvitedRole(primaryRole); setInvitedRoles(roles); }
      }
    });
  }, [user?.email, estateSupabaseId, profile?.signup_mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const addCollaborator = (c) => {
    if (c.__replaceIndex !== undefined) {
      const updated = collaborators.map((x, j) => j === c.__replaceIndex ? {...x, ...c.__data} : x);
      setCollaborators(updated);
      save("heiros_collabs", updated);
      return;
    }
    if (c.__removeIndex !== undefined) {
      const updated = collaborators.filter((_, j) => j !== c.__removeIndex);
      setCollaborators(updated);
      save("heiros_collabs", updated);
      return;
    }
    const u = [...collaborators, c];
    setCollaborators(u); save("heiros_collabs", u);
  };

  const navTo = (p) => {
    persistPage(p); setMobileMenuOpen(false); requestAnimationFrame(()=>window.scrollTo({top:0,left:0,behavior:"auto"}));
  };

  const isAppPage = page.startsWith("app-");
  const isAppDocsDeep = page.startsWith("app-docs-");
  const deepDocKey = isAppDocsDeep ? page.replace("app-docs-", "") : null;

  // ── App gate: determine what the logged-in user should see ──────────────────
  const isInvited = profile?.signup_mode === "invited";

  // Active estate resolution — switches between invited estate and user's own estate
  const activeEstate = (activeEstateMode === 'own' && ownEstate) ? ownEstate : estate;
  const activeTasks = (activeEstateMode === 'own') ? ownTasks : tasks;
  const activeEstateSupabaseId = (activeEstateMode === 'own') ? ownEstateId : estateSupabaseId;
  const activeInvitedPermissions = (activeEstateMode === 'own') ? null : invitedPermissions;
  const activeInvitedRole = (activeEstateMode === 'own') ? null : invitedRole;
  const activeInvitedRoles = (activeEstateMode === 'own') ? [] : invitedRoles;
  const activeInvitations = (activeEstateMode === 'own') ? ownInvitations : invitations;
  const activeProgress = activeTasks.length ? Math.round((activeTasks.filter(t=>t.done).length/activeTasks.length)*100) : 0;

  console.log('PAYMENT GATE:', {
    user: user?.email,
    has_paid: profile?.has_paid,
    signup_mode: profile?.signup_mode,
    onboarding_complete: profile?.onboarding_complete,
    isInvited,
    profileLoading,
    userLoading,
    profileIsNull: profile === null,
  });

  // Wait when: auth is loading, OR user is logged in but profile hasn't arrived yet.
  // The extra (user && !profile) clause closes the race-condition window that exists
  // between setUser() firing and the profile-loading effect starting — during that
  // window profileLoading is still false even though no profile is in state yet.
  const waitingForProfile = userLoading || (user && profileLoading) || (user && !profile);
  // Couple's Bundle bypass: if the $100 add-on was paid, the spouse skips the $299 gate.
  const coupleBundlePaid = !!(user && (localStorage.getItem('heiros_couple_bundle_paid') || bundleInvitation));
  const needsPayment = user && profile && !profileLoading && !profile.has_paid && !isInvited && !coupleBundlePaid;
  const needsOnboarding = user && profile && !profileLoading && !isInvited && (profile.has_paid || coupleBundlePaid) && !profile.onboarding_complete;
  // Show the spouse-setup screen once to the owner right after they complete their own onboarding.
  // Skip entirely if the estate already has a linked spouse (e.g. came through the $399 Couple's
  // Bundle signup, which sets linked_spouse_estate_id automatically in the webhook).
  const needsSpouseSetup = ownEstate !== null && user && profile && !profileLoading &&
    profile.signup_mode === 'owner' && profile.onboarding_complete &&
    !profile.spouse_setup_done &&
    !localStorage.getItem(`heiros_spouse_setup_done_${user.id}`) &&
    !ownEstate?.linked_spouse_estate_id;
  // Invited user who upgraded ("Start Your Own Estate"): webhook created their estate with
  // owner_name = email. Gate them into the owner onboarding wizard until they fill in real data.
  // The @ check is the canonical "webhook ran but onboarding never collected real data" signal.
  // ownEstate !== null ensures we wait for the estate row to load before showing the gate.
  const needsOwnEstateOnboarding = user && profile && !profileLoading &&
    isInvited && ownEstateId && ownEstate !== null &&
    (!ownEstate.ownerName || ownEstate.ownerName.includes('@'));

  const needsEmailVerification = user && !userLoading && !user.email_confirmed_at;

  // Mandatory MFA gate — applies to all users regardless of signup mode, enforced on
  // login/session load only (never during signup itself).
  const waitingForMfa = user && !userLoading && !needsEmailVerification && mfaLoading;
  const needsMfaVerify = user && !userLoading && !needsEmailVerification && !mfaLoading && mfaCurrentLevel === 'aal1' && mfaNextLevel === 'aal2';
  const needsMfaEnroll = user && !userLoading && !needsEmailVerification && !mfaLoading && mfaCurrentLevel === 'aal1' && mfaNextLevel === 'aal1' && !mfaSkipped && !profile?.mfa_enrollment_skipped;

  let gateContent = null;
  if (completingOnboarding) {
    gateContent = (
      <div style={{minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:"48px", height:"48px", border:`3px solid ${T.forestLight}`, borderTopColor:T.forest, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 1rem"}} />
          <p style={{color:T.muted, fontSize:"0.9rem"}}>Loading your account…</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  } else if (coupleCodeLoading) {
    gateContent = (
      <div style={{minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:"48px", height:"48px", border:`3px solid ${T.forestLight}`, borderTopColor:T.forest, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 1rem"}} />
          <p style={{color:T.muted, fontSize:"0.9rem"}}>Preparing your invitation…</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  } else if (waitingForProfile || waitingForMfa) {
    gateContent = (
      <div style={{minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:"48px", height:"48px", border:`3px solid ${T.forestLight}`, borderTopColor:T.forest, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 1rem"}} />
          <p style={{color:T.muted, fontSize:"0.9rem"}}>Loading your account…</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  } else if (needsEmailVerification) {
    gateContent = <EmailVerificationWall user={user} onSignOut={handleSignOut} />;
  } else if (needsMfaVerify) {
    gateContent = <MfaVerifyWall factorId={mfaFactorId} onVerified={refreshMfaStatus} onSignOut={handleSignOut} />;
  } else if (needsMfaEnroll) {
    gateContent = <MfaEnrollWall onVerified={refreshMfaStatus} onSignOut={handleSignOut} allowSkip={true} onSkip={async () => { setMfaSkipped(true); try { await updateProfile(user.id, { mfa_enrollment_skipped: true }); } catch(e) { console.warn('[HeirOS] Could not persist MFA skip:', e); } }} />;
  } else if (needsPayment) {
    gateContent = <PaymentWall profile={profile} user={user} onSignOut={handleSignOut} />;
  } else if (needsOnboarding) {
    gateContent = <Onboarding user={user} profile={profile} onComplete={handleOnboardingComplete} setProfile={setProfile} />;
  } else if (needsOwnEstateOnboarding) {
    gateContent = <Onboarding user={user} profile={profile} onComplete={handleOnboardingComplete} setProfile={setProfile} modeOverride="owner" />;
  } else if (needsSpouseSetup) {
    const dismissSpouseSetup = async () => {
      localStorage.setItem(`heiros_spouse_setup_done_${user.id}`, 'true');
      const { data } = await markSpouseSetupDone(user.id);
      if (data) setProfile(prev => prev ? { ...prev, spouse_setup_done: true } : prev);
    };
    gateContent = (
      <SpouseSetupScreen
        user={user}
        profile={profile}
        estateSupabaseId={estateSupabaseId}
        onDismiss={dismissSpouseSetup}
        onComplete={dismissSpouseSetup}
      />
    );
  }

  return (
    <div style={{fontFamily:"'DM Sans', sans-serif", background:T.cream, minHeight:"100vh", color:T.charcoal}}>
      <FontLoader />
      <TopNav page={page} navTo={navTo} estate={activeEstate} invitedEstate={estate} mobileOpen={mobileMenuOpen} setMobileOpen={setMobileMenuOpen} user={user} onSignOut={handleSignOut} notifications={notifications} setNotifications={setNotifications} isInvited={isInvited} profile={profile} invitedPermissions={activeInvitedPermissions} invitedRole={activeInvitedRole} invitedRoles={activeInvitedRoles} ownEstateId={ownEstateId} activeEstateMode={activeEstateMode} setActiveEstateMode={setActiveEstateMode} accessibleEstates={accessibleEstates} activeEstateId={activeEstateId} setActiveEstateId={setActiveEstateId} pendingAckCount={pendingAckCount} setPendingAckCount={setPendingAckCount} />
      {gateContent ? (
        gateContent
      ) : mfaRequiredReason ? (
        <MfaEnrollWall
          onVerified={() => {
            const cb = mfaRequiredCallback;
            setMfaRequiredReason(null);
            setMfaRequiredCallback(null);
            refreshMfaStatus();
            if (cb) cb();
          }}
          onSignOut={handleSignOut}
          allowSkip={false}
          requiredMessage="Two-factor authentication is required for executor access actions."
        />
      ) : completingOnboarding ? (
        <div style={{minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center"}}>
          <div style={{textAlign:"center"}}>
            <div style={{width:"48px", height:"48px", border:`3px solid ${T.forestLight}`, borderTopColor:T.forest, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 1rem"}} />
            <p style={{color:T.muted, fontSize:"0.9rem"}}>Loading your account…</p>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        </div>
      ) : (
        <>
          {/* Guest collaborator banner */}
          {isInvited && activeEstateMode !== 'own' && profile?.invited_estate_id && estate && (() => {
            const bannerName = estate.signupMode === 'owner'
              ? (estate.ownerName || estate.owner_name || null)
              : (estate.deceasedName || estate.deceased_name || estate.ownerName || estate.owner_name || null);
            return (
              <div style={{background:`linear-gradient(135deg, ${T.forest}, #3d6b5a)`, color:"white", padding:"0.625rem 2rem", display:"flex", alignItems:"center", justifyContent:"center", gap:"0.75rem", flexWrap:"wrap"}}>
                <span style={{fontSize:"0.85rem", opacity:0.9}}>You are viewing</span>
                <span style={{fontSize:"0.875rem", fontWeight:"600"}}>{bannerName ? `${bannerName}'s estate` : "the estate"}</span>
                <span style={{fontSize:"0.75rem", background:"rgba(255,255,255,0.15)", borderRadius:"20px", padding:"0.15rem 0.625rem", textTransform:"capitalize"}}>
                  {invitedRoles.length > 1
                    ? invitedRoles.map(r => r === 'Named as executor' ? 'Executor' : r === 'Medical POA' ? 'Medical POA' : r).join(' & ')
                    : (invitedRole || profile.invited_role?.replace(/_/g, " ") || "collaborator")}
                </span>
              </div>
            );
          })()}

          {/* Owner death alert banner — shown on every app page if a death report is pending */}
          {estate?.signupMode === "owner" && deathReport?.status === "pending" && (
            <div style={{background:"#7c2d12", color:"white", padding:"0.875rem 2rem", display:"flex", alignItems:"center", justifyContent:"center", gap:"1rem", flexWrap:"wrap", zIndex:90, position:"sticky", top:"72px"}}>
              <span style={{fontSize:"1.1rem"}}>⚠</span>
              <span style={{fontSize:"0.9rem", fontWeight:"500"}}>
                Someone has reported your passing. If you are alive, please cancel this report immediately.
              </span>
              <button
                onClick={() => persistPage("cancel-death-report")}
                style={{background:"white", color:"#7c2d12", border:"none", borderRadius:"6px", padding:"0.375rem 1rem", fontSize:"0.82rem", fontWeight:"700", cursor:"pointer", flexShrink:0}}
              >
                I'm Alive — Cancel Report →
              </button>
            </div>
          )}

          {/* Death confirmed transition banner for executors */}
          {deathReport?.status === "confirmed" && estate?.signupMode !== "owner" && (
            <div style={{background:`linear-gradient(135deg, ${T.forest}, #1a3329)`, color:"white", padding:"1.25rem 2rem", display:"flex", alignItems:"center", gap:"1.25rem", flexWrap:"wrap"}}>
              <div style={{flex:1}}>
                <p style={{fontFamily:"'Playfair Display', serif", fontSize:"1rem", fontWeight:"700", marginBottom:"0.25rem"}}>We're sorry for your loss.</p>
                <p style={{fontSize:"0.82rem", opacity:0.85}}>Your access to {deathReport.deceased_name}'s estate is now active. Everything they organized is here for you.</p>
              </div>
              <button onClick={()=>navTo("app-dashboard")} style={{background:T.gold, color:"white", border:"none", borderRadius:"8px", padding:"0.625rem 1.25rem", fontSize:"0.85rem", fontWeight:"600", cursor:"pointer", flexShrink:0}}>
                Begin Settlement →
              </button>
            </div>
          )}

          <div>
            {/* Public pages — always accessible */}
            {page==="home"          && <HomePage navTo={navTo} />}
            {page==="login"         && <LoginPage navTo={navTo} estate={estate} onSignIn={setUser} />}
            {page==="set-new-password" && <SetNewPasswordPage navTo={navTo} />}
            {page==="blog"          && <BlogPage navTo={navTo} />}
            {page.startsWith("blog-post") && <BlogPostPage navTo={navTo} postId={parseInt(page.split("-")[2], 10) || 1} />}
            {page==="demo"          && <DemoPage navTo={navTo} />}
            {page==="faq"           && <FAQPage navTo={navTo} />}
            {page==="privacy"       && <PrivacyPage navTo={navTo} />}
            {page==="terms"         && <TermsPage navTo={navTo} />}
            {page==="documents"     && <DocumentsPage navTo={navTo} estate={estate} />}
            {page==="links"         && <LinksPage navTo={navTo} />}
            {page==="attorneys"     && <AttorneysPage navTo={navTo} />}
            {page==="pricing"       && <PricingPage navTo={navTo} />}
            {page==="contact"       && <ContactPage navTo={navTo} />}
            {page==="signup"        && <SignupFlow navTo={navTo} onSignIn={(u, prof, acceptedEstateId) => {
                setUser(u);
                if (prof) {
                  setProfile(prof);
                  if (prof.signup_mode === "invited" && prof.onboarding_complete) {
                    persistPage("app-dashboard");
                  } else if (acceptedEstateId) {
                    setAccessibleEstates(prev => prev.find(e => e.estateId === acceptedEstateId) ? prev : [...prev, { estateId: acceptedEstateId, estateName: 'Estate', role: 'collaborator', isOwn: false }]);
                    setActiveEstateId(acceptedEstateId);
                    persistPage("app-dashboard");
                  }
                }
              }} inviteParams={inviteParams} />}
            {accessAction && (
              <div style={{position:"fixed", inset:0, background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999, padding:"2rem"}}>
                <div style={{background:"white", borderRadius:"20px", padding:"3rem", textAlign:"center", maxWidth:"420px", width:"100%", boxShadow:"0 8px 40px rgba(0,0,0,0.1)"}}>
                  {accessAction.status === 'loading' && (
                    <>
                      <div style={{fontSize:"2rem", marginBottom:"1rem"}}>⏳</div>
                      <p style={{color:T.muted, fontSize:"0.95rem"}}>Processing your response…</p>
                    </>
                  )}
                  {accessAction.status === 'success' && (
                    <>
                      <div style={{fontSize:"3rem", marginBottom:"1rem"}}>{accessAction.type === 'approve' ? '✅' : '❌'}</div>
                      <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", color:T.forest, marginBottom:"0.75rem"}}>
                        Access {accessAction.type === 'approve' ? 'Approved' : 'Denied'}
                      </h2>
                      <p style={{fontSize:"0.875rem", color:T.muted, lineHeight:"1.6", marginBottom:"1.5rem"}}>
                        {accessAction.type === 'approve'
                          ? "The executor has been granted full access to the estate."
                          : "The access request has been denied. The executor will be notified."}
                      </p>
                      <a href="https://heiros.estate" style={{...filledBtn, textDecoration:"none", display:"inline-block"}}>Sign In to HeirOS</a>
                    </>
                  )}
                  {accessAction.status === 'error' && (
                    <>
                      <div style={{fontSize:"3rem", marginBottom:"1rem"}}>⚠️</div>
                      <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", color:T.forest, marginBottom:"0.75rem"}}>Something went wrong</h2>
                      <p style={{fontSize:"0.875rem", color:T.muted, marginBottom:"1.5rem"}}>Please try again or contact support.</p>
                      <a href="https://heiros.estate" style={{...outlineBtn, textDecoration:"none", display:"inline-block"}}>Go to HeirOS</a>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Auth-required pages — secondary payment guard: block app pages if payment
                not verified, even if the primary gateContent gate somehow was bypassed.
                This cannot be reached during the spinner (waitingForProfile) since
                gateContent is set then, but it is a hard fallback for any future race. */}
            {needsPayment ? (
              (page==="account" || page==="cancel-death-report" || isAppPage || isAppDocsDeep) && (
                <PaymentWall profile={profile} user={user} onSignOut={handleSignOut} />
              )
            ) : (
              <>
                {page==="account"       && <AccountPage navTo={navTo} user={user} onSignOut={handleSignOut} profile={profile} setProfile={setProfile} estateSupabaseId={estateSupabaseId} ownerSettings={ownerSettings} setOwnerSettings={setOwnerSettings} ownEstateId={ownEstateId} ownEstate={ownEstate} setActiveEstateMode={setActiveEstateMode} />}
                {page==="cancel-death-report" && <CancelDeathReportPage reportId={cancelReportId || deathReport?.id} navTo={navTo} onCancelled={() => setDeathReport(null)} />}
                {page==="app-dashboard" && <AppDashboard tasks={activeTasks} onToggle={toggleActiveTask} progress={activeProgress} estate={activeEstate} navTo={navTo} deathReport={deathReport} invitedPermissions={activeInvitedPermissions} invitedRole={activeInvitedRole} setTasks={activeEstateMode==='own'?setOwnTasks:setTasks} estateSupabaseId={activeEstateSupabaseId} user={user} />}
                {(page==="app-docs" || isAppDocsDeep) && <AppDocuments estate={activeEstate} navTo={navTo} initialDoc={deepDocKey} invitedPermissions={activeInvitedPermissions} invitedRole={activeInvitedRole} />}
                {page==="app-collab"    && <AppCollaborate estate={activeEstate} setEstate={activeEstateMode==='own'?setOwnEstate:setEstate} tasks={activeTasks} collaborators={collaborators} onAdd={addCollaborator} navTo={navTo} user={user} estateSupabaseId={activeEstateSupabaseId} invitations={activeInvitations} setInvitations={activeEstateMode==='own'?setOwnInvitations:setInvitations} deathReport={deathReport} setDeathReport={setDeathReport} invitedPermissions={activeInvitedPermissions} invitedRole={activeInvitedRole} activeAccessRequest={activeAccessRequest} setActiveAccessRequest={setActiveAccessRequest} ownerSettings={ownerSettings} requireMfa={requireMfa} vault={vault} onSaveVault={saveVault} />}
                {page==="app-ai"        && <AppAI estate={activeEstate} navTo={navTo} invitedPermissions={activeInvitedPermissions} invitedRole={activeInvitedRole} />}
                {page==="app-vault"     && <AppVault vault={vault} onSave={saveVault} estate={activeEstate} navTo={navTo} invitedPermissions={activeInvitedPermissions} invitedRole={activeInvitedRole} estateSupabaseId={activeEstateSupabaseId} user={user} profile={profile} invitations={activeInvitations} invitedInvitationId={invitedInvitationId} isOwnVault={activeEstateMode === 'own'} />}
                {page==="app-playbook"  && <ExecutorPlaybook estate={activeEstate} navTo={navTo} invitedPermissions={activeInvitedPermissions} invitedRole={activeInvitedRole} estateSupabaseId={activeEstateSupabaseId} user={user} />}
                {page==="app-my-vault"  && !ownEstateId && <LockedMyVault navTo={navTo} estate={activeEstate} invitedPermissions={activeInvitedPermissions} invitedRole={activeInvitedRole} user={user} />}
                {page==="app-my-vault"  && ownEstateId  && <AppVault vault={vault} onSave={saveVault} estate={ownEstate||{}} navTo={navTo} invitedPermissions={null} invitedRole={null} estateSupabaseId={ownEstateId} user={user} profile={profile} isOwnVault={true} />}
                {page==="app-add-spouse" && (
                  <AppShell estate={activeEstate} tasks={[]} page="app-add-spouse" navTo={navTo} invitedPermissions={null} invitedRole={null}>
                    <SpouseSetupScreen
                      user={user}
                      profile={profile}
                      estateSupabaseId={ownEstateId}
                      onDismiss={() => navTo('account')}
                      onComplete={async () => {
                        localStorage.setItem(`heiros_spouse_setup_done_${user.id}`, 'true');
                        const { data } = await markSpouseSetupDone(user.id);
                        if (data) setProfile(prev => prev ? { ...prev, spouse_setup_done: true } : prev);
                        navTo('account');
                      }}
                    />
                  </AppShell>
                )}
                {page==="app-pending-roles" && <AppPendingRoles user={user} navTo={navTo} setPendingAckCount={setPendingAckCount} />}
              </>
            )}
          </div>
          {!isAppPage && page !== "account" && <Footer navTo={navTo} />}
        </>
      )}
      {user && <FeedbackButton user={user} currentPage={page} />}
      {user && <HelpPanel user={user} profile={profile} />}
      {showPendingRolesModal && (
        <PendingRolesModal
          user={user}
          setPendingAckCount={setPendingAckCount}
          onClose={() => setShowPendingRolesModal(false)}
        />
      )}
    </div>
  );
}

// ─── TOP NAV ──────────────────────────────────────────────────────────────────
function TopNav({ page, navTo, estate, invitedEstate, mobileOpen, setMobileOpen, user, onSignOut, notifications, setNotifications, isInvited, profile, invitedPermissions, invitedRole, invitedRoles, ownEstateId, activeEstateMode, setActiveEstateMode, accessibleEstates, activeEstateId, setActiveEstateId, pendingAckCount, setPendingAckCount }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const menuRef = useRef(null);
  const notifRef = useRef(null);
  const switcherRef = useRef(null);

  const unreadCount = (notifications || []).filter(n => !n.read).length;

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (switcherRef.current && !switcherRef.current.contains(e.target)) setSwitcherOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const isApp = page.startsWith("app-") || (page === "account" && !!user);
  const iPerms = invitedPermissions || {};
  // Fall back to single-role array when invitedRoles isn't populated yet
  const roles = (invitedRoles && invitedRoles.length > 0) ? invitedRoles : (invitedRole ? [invitedRole] : []);
  const hasExecutorRole = roles.includes('Named as executor') || roles.includes('Executor');
  const isExecutorInSettlement = isInvited && hasExecutorRole && estate?.status === 'active_settlement';
  // Single vault tab: label and permission gate depend on which estate is active.
  // "Medical Documents" only when the user is EXCLUSIVELY Medical POA — if they're also
  // an executor they have full vault access and we show the estate vault instead.
  const isViewingOwnEstate = ownEstateId != null && activeEstateId === ownEstateId;
  const vaultOwnerName = estate?.ownerName || estate?.deceasedName || estate?.owner_name || estate?.deceased_name || 'Estate';
  const isMedicalPoaOnly = !isViewingOwnEstate && roles.includes('Medical POA') && !hasExecutorRole;
  const vaultLabel = isMedicalPoaOnly ? "Medical Documents"
    : isViewingOwnEstate ? "My Vault"
    : `${vaultOwnerName}'s Vault`;
  const vaultPermKey = isViewingOwnEstate ? null
    : isMedicalPoaOnly ? "view_medical_documents"
    : "view_vault";
  const appNavTabs = [
    {id:"app-dashboard", label:"Checklist",  permKey:"view_checklist"},
    {id:"app-vault",     label:vaultLabel,   permKey:vaultPermKey},
    {id:"app-collab",    label:"Collaborators",     permKey:"view_family"},
    {id:"app-docs",      label:"Documents",  permKey:"view_documents"},
    {id:"app-ai",        label:"AI Guide",   permKey:null},
    {id:"app-playbook",  label:"Executor Playbook", permKey:null, executorOnly:true},
  ];
  const links = isApp
    ? appNavTabs.filter(tab => {
        if (tab.executorOnly) return isExecutorInSettlement;
        if (!isInvited) return true;
        if (!tab.permKey) return true;
        if (tab.permKey === 'view_vault' || tab.permKey === 'view_medical_documents') return iPerms[tab.permKey] === true;
        return iPerms[tab.permKey] !== false;
      })
    : [
        { id:"home", label:"Home" },
        { id:"demo", label:"See a Demo" },
        { id:"blog", label:"Blog" },
        { id:"faq", label:"FAQ" },
        { id:"documents", label:"Documents" },
        { id:"links", label:"Resources" },
        { id:"attorneys", label:"Find an Attorney" },
        { id:"pricing", label:"Pricing" },
      ];

  return (
    <nav style={{
      position:"sticky", top:0, zIndex:100,
      background: scrolled ? "rgba(248,245,240,0.97)" : T.cream,
      backdropFilter: scrolled ? "blur(12px)" : "none",
      borderBottom: `1px solid ${scrolled ? T.border : "transparent"}`,
      transition:"all 0.3s ease",
      boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,0.06)" : "none",
    }}>
      <div style={{maxWidth:"1200px", margin:"0 auto", padding:"0 2rem", height:"72px", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
        {/* Logo */}
        <button onClick={()=>navTo(isApp?"app-dashboard":"home")} style={btnReset}>
          <div style={{display:"flex", alignItems:"center", gap:"0.625rem"}}>
            <div style={{width:"36px", height:"36px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"9px", display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:"1.1rem", fontWeight:"700", fontFamily:"'Playfair Display', serif"}}>H</div>
            <span style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em"}}>HeirOS</span>
          </div>
        </button>

        {/* Desktop links */}
        {!isMobile && (
          <div style={{display:"flex", alignItems:"center", gap:"0.25rem"}}>
            {links.map(l => (
              <button key={l.id} onClick={()=>navTo(l.id)} style={{
                ...btnReset, padding:"0.5rem 0.875rem", borderRadius:"8px", fontSize:"0.875rem",
                color: page===l.id ? T.forest : T.slate,
                fontWeight: page===l.id ? "600" : "400",
                background: page===l.id ? T.parchment : "transparent",
                transition:"all 0.15s",
              }}>{l.label}</button>
            ))}
          </div>
        )}

        {/* Hamburger — mobile only */}
        {isMobile && (
          <button
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Toggle menu"
            style={{...btnReset, width:"38px", height:"38px", borderRadius:"8px", border:`1px solid ${T.border}`, background:mobileOpen?T.parchment:T.white, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem"}}
          >
            {mobileOpen ? "✕" : "☰"}
          </button>
        )}

        {/* CTA / Account */}
        {!isMobile && (
        <div style={{display:"flex", alignItems:"center", gap:"0.75rem"}}>
          {isApp && !user && (
            <button onClick={()=>navTo("home")} style={{...btnReset, padding:"0.5rem 1rem", fontSize:"0.875rem", color:T.slate}}>← Back to Site</button>
          )}

          {/* My Estate button — shown on public pages when logged in */}
          {user && !isApp && (
            <button
              onClick={()=>navTo(estate?.signupMode==="executor" ? "app-dashboard" : "app-vault")}
              style={{...filledBtn, padding:"0.5rem 1.25rem", fontSize:"0.875rem"}}
            >
              {(profile?.signup_mode === 'invited' || profile?.invited_estate_id) ? 'View Estate →' : 'My Estate →'}
            </button>
          )}

          {/* Estate switcher — shown whenever the user has access to more than one estate */}
          {user && isApp && (accessibleEstates || []).length > 1 && (
            <div ref={switcherRef} style={{position:"relative"}}>
              <button
                onClick={() => setSwitcherOpen(o => !o)}
                style={{...btnReset, display:"flex", alignItems:"center", gap:"0.5rem", padding:"0.375rem 0.875rem", borderRadius:"8px", border:`1px solid ${T.border}`, background:switcherOpen?T.parchment:T.white, fontSize:"0.78rem", fontWeight:"500", color:T.charcoal, transition:"all 0.15s"}}
              >
                <span>{(() => { const a = (accessibleEstates||[]).find(e=>e.estateId===activeEstateId); if (!a) return 'My Estate'; if (a.isOwn) return 'My Estate'; return `${a.estateName}'s Estate`; })()}</span>
                <span style={{fontSize:"0.55rem", color:T.muted}}>{switcherOpen ? "▲" : "▼"}</span>
              </button>
              {switcherOpen && (
                <div style={{position:"absolute", right:0, top:"calc(100% + 8px)", background:T.white, border:`1px solid ${T.border}`, borderRadius:"12px", boxShadow:"0 8px 32px rgba(0,0,0,0.12)", minWidth:"230px", overflow:"hidden", zIndex:200}}>
                  <div style={{padding:"0.5rem 0.875rem", fontSize:"0.65rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.06em", borderBottom:`1px solid ${T.borderLight}`}}>SWITCH ESTATE</div>
                  {(accessibleEstates||[]).map((entry, idx) => {
                    const isActive = activeEstateId === entry.estateId;
                    const label = entry.isOwn ? 'My Estate' : `${entry.estateName}'s Estate`;
                    const sublabel = entry.isOwn ? null : (entry.role ? entry.role.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) : null);
                    return (
                      <div key={entry.estateId}>
                        {idx > 0 && <div style={{height:"1px", background:T.borderLight, margin:"0 1rem"}} />}
                        <button
                          onClick={() => { setActiveEstateId(entry.estateId); setSwitcherOpen(false); navTo(entry.isOwn ? 'app-vault' : 'app-dashboard'); }}
                          style={{...btnReset, width:"100%", textAlign:"left", padding:"0.75rem 1rem", fontSize:"0.82rem", color:isActive?T.forest:T.charcoal, fontWeight:isActive?"600":"400", display:"flex", alignItems:"center", gap:"0.5rem"}}
                          onMouseEnter={e=>e.currentTarget.style.background=T.parchment}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                        >
                          {isActive && <span style={{color:T.forest, fontSize:"0.75rem", flexShrink:0}}>✓</span>}
                          <span style={{display:"flex", flexDirection:"column", gap:"0.1rem"}}>
                            <span>{label}</span>
                            {sublabel && <span style={{fontSize:"0.68rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>{sublabel}</span>}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Notification Bell — shown when logged in */}
          {user && (
            <div ref={notifRef} style={{position:"relative"}}>
              <button
                onClick={()=>setNotifOpen(o=>!o)}
                style={{...btnReset, position:"relative", width:"38px", height:"38px", borderRadius:"50%", background:notifOpen?T.parchment:T.white, border:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s", fontSize:"1rem"}}
              >
                🔔
                {unreadCount > 0 && (
                  <span style={{position:"absolute", top:"-2px", right:"-2px", width:"18px", height:"18px", borderRadius:"50%", background:T.danger, color:"white", fontSize:"0.6rem", fontWeight:"700", display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid white"}}>
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div style={{position:"absolute", right:0, top:"calc(100% + 8px)", background:T.white, border:`1px solid ${T.border}`, borderRadius:"12px", boxShadow:"0 8px 32px rgba(0,0,0,0.12)", width:"320px", overflow:"hidden", zIndex:200}}>
                  <div style={{padding:"0.875rem 1rem", borderBottom:`1px solid ${T.borderLight}`, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                    <span style={{fontSize:"0.82rem", fontWeight:"600", color:T.charcoal}}>Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={async () => {
                        await markAllNotificationsRead(user.id);
                        setNotifications(prev => prev.map(n => ({...n, read:true})));
                      }} style={{...btnReset, fontSize:"0.72rem", color:T.forest, fontWeight:"500"}}>
                        Mark all read
                      </button>
                    )}
                  </div>
                  {(!notifications || notifications.length === 0) ? (
                    <div style={{padding:"1.5rem", textAlign:"center", color:T.muted, fontSize:"0.82rem"}}>No notifications yet</div>
                  ) : (
                    <div style={{maxHeight:"320px", overflowY:"auto"}}>
                      {notifications.slice(0,10).map(n => (
                        <div key={n.id} onClick={async () => {
                          if (!n.read) {
                            await markNotificationRead(n.id);
                            setNotifications(prev => prev.map(x => x.id===n.id ? {...x, read:true} : x));
                          }
                        }} style={{padding:"0.875rem 1rem", borderBottom:`1px solid ${T.borderLight}`, background:n.read?T.white:"#f8faf8", cursor:"pointer", transition:"background 0.1s"}}>
                          <div style={{fontSize:"0.8rem", fontWeight:n.read?"400":"600", color:T.charcoal, marginBottom:"0.25rem"}}>{n.subject}</div>
                          <div style={{fontSize:"0.72rem", color:T.muted, lineHeight:"1.4"}}>{n.body?.slice(0,120)}{n.body?.length > 120 ? "…" : ""}</div>
                          <div style={{fontSize:"0.68rem", color:T.muted, marginTop:"0.25rem", fontFamily:"'DM Mono', monospace"}}>{new Date(n.created_at).toLocaleDateString()}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Account dropdown — shown whenever user is logged in */}
          {user && (
            <div ref={menuRef} style={{position:"relative"}}>
              <button
                onClick={()=>setMenuOpen(o=>!o)}
                style={{...btnReset, display:"flex", alignItems:"center", gap:"0.5rem", padding:"0.375rem 0.75rem", borderRadius:"8px", border:`1px solid ${T.border}`, background:menuOpen?T.parchment:T.white, transition:"all 0.15s"}}
              >
                <div style={{width:"28px", height:"28px", borderRadius:"50%", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:"0.75rem", fontWeight:"700", flexShrink:0}}>
                  {(profile?.onboarding_data?.fullName || estate?.ownerName || user.email?.split("@")[0] || "Account")[0]?.toUpperCase() || "U"}
                </div>
                <span style={{fontSize:"0.8rem", color:T.slate, fontWeight:"500", maxWidth:"120px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                  {profile?.onboarding_data?.fullName || estate?.ownerName || user.email?.split("@")[0] || "Account"}
                </span>
                <span style={{fontSize:"0.6rem", color:T.muted}}>{menuOpen ? "▲" : "▼"}</span>
              </button>
              {menuOpen && (
                <div style={{position:"absolute", right:0, top:"calc(100% + 8px)", background:T.white, border:`1px solid ${T.border}`, borderRadius:"12px", boxShadow:"0 8px 32px rgba(0,0,0,0.12)", minWidth:"220px", overflow:"hidden", zIndex:200}}>
                  <div style={{padding:"0.875rem 1rem", borderBottom:`1px solid ${T.borderLight}`}}>
                    <div style={{fontSize:"0.68rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.05em", marginBottom:"0.25rem"}}>SIGNED IN AS</div>
                    <div style={{fontSize:"0.82rem", color:T.muted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{user.email}</div>
                  </div>
                  <button
                    onClick={()=>{ setMenuOpen(false); navTo("account"); }}
                    style={{...btnReset, width:"100%", textAlign:"left", padding:"0.75rem 1rem", fontSize:"0.875rem", color:T.charcoal, display:"flex", alignItems:"center", gap:"0.625rem"}}
                    onMouseEnter={e=>e.currentTarget.style.background=T.parchment}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  >
                    <span>⚙</span> Account Settings
                  </button>
                  <button
                    onClick={()=>{ setMenuOpen(false); navTo("app-pending-roles"); }}
                    style={{...btnReset, width:"100%", textAlign:"left", padding:"0.75rem 1rem", fontSize:"0.875rem", color:T.charcoal, display:"flex", alignItems:"center", gap:"0.625rem"}}
                    onMouseEnter={e=>e.currentTarget.style.background=T.parchment}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  >
                    <span>📋</span>
                    <span style={{flex:1}}>Pending Roles</span>
                    {pendingAckCount > 0 && (
                      <span style={{display:"inline-flex", alignItems:"center", justifyContent:"center", minWidth:"18px", height:"18px", borderRadius:"9px", background:T.danger, color:"white", fontSize:"0.65rem", fontWeight:"700", padding:"0 5px", lineHeight:1}}>
                        {pendingAckCount}
                      </span>
                    )}
                  </button>
                  <div style={{height:"1px", background:T.borderLight, margin:"0 1rem"}} />
                  <button
                    onClick={()=>{ setMenuOpen(false); onSignOut(); }}
                    style={{...btnReset, width:"100%", textAlign:"left", padding:"0.75rem 1rem", fontSize:"0.875rem", color:T.danger, display:"flex", alignItems:"center", gap:"0.625rem"}}
                    onMouseEnter={e=>e.currentTarget.style.background="#fff5f5"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  >
                    <span>→</span> Sign Out
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Sign In / Get Started — only when logged out and not in app */}
          {!user && !isApp && (
            <>
              <button onClick={()=>navTo("login")} style={{...btnReset, padding:"0.5rem 1rem", fontSize:"0.875rem", color:T.slate, fontWeight:"500"}}>Sign In</button>
              <button onClick={()=>navTo("signup")} style={{...filledBtn, padding:"0.5rem 1.25rem", fontSize:"0.875rem"}}>
                Get Started →
              </button>
            </>
          )}
        </div>
        )}
      </div>

      {/* Mobile dropdown panel */}
      {isMobile && mobileOpen && (
        <div style={{background:T.white, borderTop:`1px solid ${T.border}`, boxShadow:"0 8px 24px rgba(0,0,0,0.08)", padding:"0.5rem 1rem 1rem"}}>
          {links.map(l => (
            <button key={l.id} onClick={()=>navTo(l.id)} style={{
              ...btnReset, display:"block", width:"100%", textAlign:"left", padding:"0.75rem 0.5rem", borderRadius:"8px", fontSize:"0.9rem",
              color: page===l.id ? T.forest : T.charcoal,
              fontWeight: page===l.id ? "600" : "400",
              background: page===l.id ? T.parchment : "transparent",
            }}>{l.label}</button>
          ))}

          <div style={{height:"1px", background:T.borderLight, margin:"0.5rem 0"}} />

          {isApp && !user && (
            <button onClick={()=>navTo("home")} style={{...btnReset, display:"block", width:"100%", textAlign:"left", padding:"0.75rem 0.5rem", fontSize:"0.9rem", color:T.slate}}>← Back to Site</button>
          )}

          {user && !isApp && (
            <button
              onClick={()=>navTo(estate?.signupMode==="executor" ? "app-dashboard" : "app-vault")}
              style={{...filledBtn, display:"block", width:"100%", textAlign:"center", padding:"0.75rem 1rem", fontSize:"0.9rem", marginTop:"0.5rem"}}
            >
              {(profile?.signup_mode === 'invited' || profile?.invited_estate_id) ? 'View Estate →' : 'My Estate →'}
            </button>
          )}

          {user && isApp && (accessibleEstates||[]).length > 1 && (
            <>
              {(accessibleEstates||[]).map(entry => {
                const isActive = activeEstateId === entry.estateId;
                const label = entry.isOwn ? 'My Estate' : `${entry.estateName}'s Estate`;
                const sublabel = (!entry.isOwn && entry.role) ? entry.role.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) : null;
                return (
                  <button
                    key={entry.estateId}
                    onClick={() => { setActiveEstateId(entry.estateId); setMobileOpen(false); navTo(entry.isOwn ? 'app-vault' : 'app-dashboard'); }}
                    style={{...btnReset, display:"flex", alignItems:"center", gap:"0.5rem", width:"100%", textAlign:"left", padding:"0.75rem 0.5rem", fontSize:"0.9rem", color:isActive?T.forest:T.charcoal, fontWeight:isActive?"600":"400"}}
                  >
                    {isActive && <span style={{color:T.forest}}>✓</span>}
                    <span style={{display:"flex", flexDirection:"column", gap:"0.05rem"}}>
                      <span>{label}</span>
                      {sublabel && <span style={{fontSize:"0.72rem", color:T.muted}}>{sublabel}</span>}
                    </span>
                  </button>
                );
              })}
            </>
          )}

          {user && (
            <>
              <div style={{height:"1px", background:T.borderLight, margin:"0.5rem 0"}} />
              <button
                onClick={()=>setNotifOpen(o=>!o)}
                style={{...btnReset, display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", padding:"0.75rem 0.5rem", fontSize:"0.9rem", color:T.charcoal}}
              >
                <span>🔔 Notifications</span>
                {unreadCount > 0 && (
                  <span style={{background:T.danger, color:"white", fontSize:"0.65rem", fontWeight:"700", borderRadius:"50%", width:"18px", height:"18px", display:"flex", alignItems:"center", justifyContent:"center"}}>
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div style={{border:`1px solid ${T.borderLight}`, borderRadius:"10px", overflow:"hidden", marginBottom:"0.5rem"}}>
                  {(!notifications || notifications.length === 0) ? (
                    <div style={{padding:"1.25rem", textAlign:"center", color:T.muted, fontSize:"0.82rem"}}>No notifications yet</div>
                  ) : (
                    <div style={{maxHeight:"280px", overflowY:"auto"}}>
                      {notifications.slice(0,10).map(n => (
                        <div key={n.id} onClick={async () => {
                          if (!n.read) {
                            await markNotificationRead(n.id);
                            setNotifications(prev => prev.map(x => x.id===n.id ? {...x, read:true} : x));
                          }
                        }} style={{padding:"0.75rem 0.875rem", borderBottom:`1px solid ${T.borderLight}`, background:n.read?T.white:"#f8faf8"}}>
                          <div style={{fontSize:"0.8rem", fontWeight:n.read?"400":"600", color:T.charcoal, marginBottom:"0.25rem"}}>{n.subject}</div>
                          <div style={{fontSize:"0.72rem", color:T.muted, lineHeight:"1.4"}}>{n.body?.slice(0,120)}{n.body?.length > 120 ? "…" : ""}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div style={{padding:"0.5rem 0.5rem 0.25rem", fontSize:"0.68rem", fontFamily:"'DM Mono', monospace", color:T.muted}}>{user.email}</div>
              <button
                onClick={()=>{ setMobileOpen(false); navTo("account"); }}
                style={{...btnReset, display:"block", width:"100%", textAlign:"left", padding:"0.75rem 0.5rem", fontSize:"0.9rem", color:T.charcoal}}
              >
                ⚙ Account Settings
              </button>
              <button
                onClick={()=>{ setMobileOpen(false); onSignOut(); }}
                style={{...btnReset, display:"block", width:"100%", textAlign:"left", padding:"0.75rem 0.5rem", fontSize:"0.9rem", color:T.danger}}
              >
                → Sign Out
              </button>
            </>
          )}

          {!user && !isApp && (
            <div style={{display:"flex", flexDirection:"column", gap:"0.5rem", marginTop:"0.5rem"}}>
              <button onClick={()=>navTo("login")} style={{...btnReset, padding:"0.75rem", fontSize:"0.9rem", color:T.slate, fontWeight:"500", border:`1px solid ${T.border}`, borderRadius:"8px"}}>Sign In</button>
              <button onClick={()=>navTo("signup")} style={{...filledBtn, padding:"0.75rem", fontSize:"0.9rem"}}>
                Get Started →
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}

// ─── ACCOUNT PAGE ─────────────────────────────────────────────────────────────
function AccountPage({ navTo, user, onSignOut, profile, setProfile, estateSupabaseId, ownerSettings, setOwnerSettings, ownEstateId, ownEstate, setActiveEstateMode }) {
  const [emailForm, setEmailForm] = useState({ newEmail:"" });
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  const [pwForm, setPwForm] = useState({ newPassword:"", confirmPassword:"" });
  const [pwStatus, setPwStatus] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteStatus, setDeleteStatus] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Two-Factor Authentication
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [mfaStatusLoading, setMfaStatusLoading] = useState(true);
  const [showMfaEnrollModal, setShowMfaEnrollModal] = useState(false);
  const [mfaDisableConfirm, setMfaDisableConfirm] = useState(false);
  const [mfaDisableLoading, setMfaDisableLoading] = useState(false);
  const [mfaDisableStatus, setMfaDisableStatus] = useState(null);

  const refreshMfaCardStatus = async () => {
    setMfaStatusLoading(true);
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      const verified = (data?.totp || []).find(f => f.status === 'verified');
      setMfaEnabled(!!verified);
      setMfaFactorId(verified?.id || null);
    } catch(e) {
      setMfaEnabled(false);
      setMfaFactorId(null);
    } finally {
      setMfaStatusLoading(false);
    }
  };

  useEffect(() => { refreshMfaCardStatus(); }, []);

  const handleDisableMfa = async () => {
    if (!mfaFactorId) return;
    setMfaDisableLoading(true); setMfaDisableStatus(null);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: mfaFactorId });
      if (error) { setMfaDisableStatus({ ok:false, msg: error.message }); }
      else { setMfaDisableConfirm(false); await refreshMfaCardStatus(); }
    } finally { setMfaDisableLoading(false); }
  };

  // Annual review reminder
  const defaultReviewDate = user?.created_at
    ? (() => { const d = new Date(user.created_at); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0,10); })()
    : "";
  const currentReviewDate = profile?.annual_review_custom_date || defaultReviewDate;
  const [reviewDate, setReviewDate] = useState(currentReviewDate);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  const handleSaveReviewDate = async () => {
    if (!reviewDate) { setReviewStatus({ ok:false, msg:"Please select a date." }); return; }
    setReviewLoading(true); setReviewStatus(null);
    try {
      const { data, error } = await updateProfile(user.id, { annual_review_custom_date: reviewDate });
      if (error) { setReviewStatus({ ok:false, msg:"Could not save. The feature may still be in setup." }); }
      else { if (data && setProfile) setProfile(data); setReviewStatus({ ok:true, msg:"Reminder date saved." }); }
    } finally { setReviewLoading(false); }
  };

  const signedUpAt = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" })
    : "—";

  const handleEmailChange = async () => {
    if (!emailForm.newEmail) { setEmailStatus({ ok:false, msg:"Please enter a new email address." }); return; }
    setEmailLoading(true); setEmailStatus(null);
    try {
      const { error } = await updateEmail(emailForm.newEmail);
      if (error) { setEmailStatus({ ok:false, msg:error.message }); }
      else { setEmailStatus({ ok:true, msg:"Check your new inbox — a confirmation link has been sent." }); setEmailForm({ newEmail:"" }); }
    } finally { setEmailLoading(false); }
  };

  const handlePasswordChange = async () => {
    if (!pwForm.newPassword) { setPwStatus({ ok:false, msg:"Please enter a new password." }); return; }
    if (pwForm.newPassword.length < 6) { setPwStatus({ ok:false, msg:"Password must be at least 6 characters." }); return; }
    if (pwForm.newPassword !== pwForm.confirmPassword) { setPwStatus({ ok:false, msg:"Passwords do not match." }); return; }
    setPwLoading(true); setPwStatus(null);
    try {
      const { error } = await updatePassword(pwForm.newPassword);
      if (error) { setPwStatus({ ok:false, msg:error.message }); }
      else { setPwStatus({ ok:true, msg:"Password updated successfully." }); setPwForm({ newPassword:"", confirmPassword:"" }); }
    } finally { setPwLoading(false); }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== "DELETE") { setDeleteStatus({ ok:false, msg:'Type DELETE (all caps) to confirm.' }); return; }
    setDeleteLoading(true); setDeleteStatus(null);
    try {
      await onSignOut();
    } finally { setDeleteLoading(false); }
  };

  const cardStyle = { background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"2rem", marginBottom:"1.5rem" };
  const sectionTitle = { fontFamily:"'Playfair Display', serif", fontSize:"1.25rem", fontWeight:"700", color:T.forest, marginBottom:"0.375rem" };
  const sectionSub = { fontSize:"0.82rem", color:T.muted, marginBottom:"1.5rem", lineHeight:"1.55" };
  const StatusMsg = ({ status }) => status ? (
    <div style={{ background:status.ok?"#f0faf5":"#fef0f0", border:`1px solid ${status.ok?"#c3e6cb":"#f5c6cb"}`, borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.82rem", color:status.ok?T.success:T.danger, marginTop:"0.75rem" }}>
      {status.ok ? "✓ " : "⚠ "}{status.msg}
    </div>
  ) : null;

  return (
    <div style={{ background:T.parchment, minHeight:"100vh", padding:"3rem 1rem" }}>
      <div style={{ maxWidth:"640px", margin:"0 auto" }}>

        {/* Back + Header */}
        <button onClick={()=>navTo("app-vault")} style={{...btnReset, fontSize:"0.875rem", color:T.muted, marginBottom:"2rem", display:"flex", alignItems:"center", gap:"0.375rem"}}>
          ← Back
        </button>
        <div style={{ marginBottom:"2.5rem" }}>
          <p style={{ fontFamily:"'DM Mono', monospace", fontSize:"0.72rem", letterSpacing:"0.1em", color:T.gold, marginBottom:"0.5rem" }}>YOUR ACCOUNT</p>
          <h1 style={{ fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.5rem" }}>Account Settings</h1>
          <p style={{ color:T.muted, fontSize:"0.9rem" }}>Manage your email, password, and account details.</p>
        </div>

        {/* Executor Emergency Access Settings — shown for anyone who has their own estate,
            including invited users who have since upgraded/created their own estate */}
        {(profile?.signup_mode !== 'invited' || ownEstateId) && (
          <ExecutorAccessSettings estateId={estateSupabaseId} initialSettings={ownerSettings} onSaved={(s) => setOwnerSettings(s)} />
        )}

        {/* Account Information */}
        <div style={cardStyle}>
          <h2 style={sectionTitle}>Account Information</h2>
          <p style={sectionSub}>Your current account details.</p>
          <div style={{ display:"flex", flexDirection:"column", gap:"0.875rem" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"0.75rem 1rem", background:T.parchment, borderRadius:"8px" }}>
              <span style={{ fontSize:"0.78rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.04em" }}>EMAIL</span>
              <span style={{ fontSize:"0.875rem", color:T.charcoal, fontWeight:"500" }}>{user?.email || "—"}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"0.75rem 1rem", background:T.parchment, borderRadius:"8px" }}>
              <span style={{ fontSize:"0.78rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.04em" }}>MEMBER SINCE</span>
              <span style={{ fontSize:"0.875rem", color:T.charcoal, fontWeight:"500" }}>{signedUpAt}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"0.75rem 1rem", background:T.parchment, borderRadius:"8px" }}>
              <span style={{ fontSize:"0.78rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.04em" }}>PLAN</span>
              <span style={{ fontSize:"0.875rem", color:T.charcoal, fontWeight:"500" }}>
                {(profile?.signup_mode !== 'invited' || ownEstateId)
                  ? "Estate Plan Active"
                  : "Invited Collaborator — No Cost"}
              </span>
            </div>
          </div>
        </div>

        {/* Two-Factor Authentication */}
        {showMfaEnrollModal && (
          <div onClick={e=>{if(e.target===e.currentTarget){setShowMfaEnrollModal(false);refreshMfaCardStatus();}}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.48)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:"1rem"}}>
            <div style={{background:T.white,borderRadius:"20px",padding:"2rem",maxWidth:"480px",width:"100%",position:"relative",boxShadow:"0 8px 40px rgba(0,0,0,0.18)"}}>
              <button onClick={()=>{setShowMfaEnrollModal(false);refreshMfaCardStatus();}} style={{...btnReset,position:"absolute",top:"1rem",right:"1.25rem",fontSize:"1.25rem",color:T.muted,lineHeight:1}} aria-label="Close">×</button>
              <MfaEnrollWall
                allowSkip={false}
                onVerified={()=>{ setShowMfaEnrollModal(false); refreshMfaCardStatus(); }}
                onSignOut={onSignOut}
              />
            </div>
          </div>
        )}

        <div style={cardStyle}>
          <h2 style={sectionTitle}>Two-Factor Authentication</h2>
          <p style={sectionSub}>Protect your account and Vault with an authenticator app like Google Authenticator, Authy, or a password manager with TOTP support.</p>

          {mfaStatusLoading ? (
            <p style={{fontSize:"0.82rem",color:T.muted}}>Checking status…</p>
          ) : mfaEnabled ? (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"0.75rem 1rem",background:"#f0faf5",borderRadius:"8px",marginBottom:"1rem"}}>
                <span style={{fontSize:"0.78rem",fontFamily:"'DM Mono', monospace",color:T.muted,letterSpacing:"0.04em"}}>STATUS</span>
                <span style={{fontSize:"0.875rem",fontWeight:"600",color:T.success}}>Enabled ✓</span>
              </div>
              {!mfaDisableConfirm ? (
                <button onClick={()=>{setMfaDisableConfirm(true);setMfaDisableStatus(null);}} style={{...btnReset,fontSize:"0.82rem",color:T.danger,textDecoration:"underline"}}>Disable 2FA</button>
              ) : (
                <div style={{background:"#fef3f0",border:`1px solid #f5c6b8`,borderRadius:"10px",padding:"1rem"}}>
                  <p style={{fontSize:"0.85rem",color:T.charcoal,marginBottom:"0.875rem",fontWeight:"500"}}>Are you sure? This will reduce your account's security.</p>
                  <div style={{display:"flex",gap:"0.75rem"}}>
                    <button onClick={handleDisableMfa} disabled={mfaDisableLoading} style={{...btnReset,background:T.danger,color:"white",padding:"0.5rem 1.25rem",borderRadius:"8px",fontSize:"0.82rem",fontWeight:"600",opacity:mfaDisableLoading?0.6:1}}>
                      {mfaDisableLoading ? "Disabling…" : "Yes, disable"}
                    </button>
                    <button onClick={()=>{setMfaDisableConfirm(false);setMfaDisableStatus(null);}} style={{...btnReset,background:T.parchment,color:T.charcoal,padding:"0.5rem 1.25rem",borderRadius:"8px",fontSize:"0.82rem",fontWeight:"500",border:`1px solid ${T.border}`}}>Cancel</button>
                  </div>
                  {mfaDisableStatus && <StatusMsg status={mfaDisableStatus} />}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"0.75rem 1rem",background:T.parchment,borderRadius:"8px",marginBottom:"1rem"}}>
                <span style={{fontSize:"0.78rem",fontFamily:"'DM Mono', monospace",color:T.muted,letterSpacing:"0.04em"}}>STATUS</span>
                <span style={{fontSize:"0.875rem",fontWeight:"600",color:T.danger}}>Not enabled</span>
              </div>
              <button onClick={()=>setShowMfaEnrollModal(true)} style={{...btnReset,background:T.forest,color:"white",padding:"0.625rem 1.5rem",borderRadius:"10px",fontSize:"0.875rem",fontWeight:"600"}}>Enable 2FA</button>
            </div>
          )}
        </div>

        {/* Annual Review Reminder */}
        <div style={cardStyle}>
          <h2 style={sectionTitle}>Annual Review Reminder</h2>
          <p style={sectionSub}>HeirOS will remind you each year to review your estate plan.</p>
          <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"0.75rem 1rem", background:T.parchment, borderRadius:"8px" }}>
              <span style={{ fontSize:"0.78rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.04em" }}>REMINDER EMAIL</span>
              <span style={{ fontSize:"0.875rem", color:T.charcoal, fontWeight:"500" }}>{user?.email || "—"}</span>
            </div>
            <div>
              <label style={labelStyle}>Annual Review Date</label>
              <input
                type="date"
                value={reviewDate}
                onChange={e => setReviewDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <StatusMsg status={reviewStatus} />
            <button onClick={handleSaveReviewDate} disabled={reviewLoading} style={{...filledBtn, alignSelf:"flex-start", padding:"0.75rem 1.5rem", opacity:reviewLoading?0.7:1}}>
              {reviewLoading ? "Saving…" : "Save Reminder Date →"}
            </button>
            <p style={{ fontSize:"0.75rem", color:T.muted, fontFamily:"'DM Mono', monospace", letterSpacing:"0.02em", borderTop:`1px solid ${T.borderLight}`, paddingTop:"0.75rem" }}>
              Email delivery coming soon — we will notify you when this feature is active.
            </p>
          </div>
        </div>

        {/* Connected Estate — owners who haven't yet linked a spouse estate */}
        {profile?.signup_mode === 'owner' && !ownEstate?.linked_spouse_estate_id && (
          <div style={cardStyle}>
            <h2 style={sectionTitle}>Connected Estate</h2>
            <p style={sectionSub}>Set up a connected estate for your spouse or partner. You'll each be automatically listed as each other's Executor, Medical POA, and Power of Attorney.</p>
            <button
              onClick={() => navTo('app-add-spouse')}
              style={{...filledBtn, padding:"0.625rem 1.5rem", fontSize:"0.875rem"}}
            >
              Set Up Spouse's Estate →
            </button>
          </div>
        )}

        {/* Estate Access — shown for invited collaborators */}
        {profile?.invited_role && (
          <EstateAccessSection user={user} profile={profile} setProfile={setProfile} navTo={navTo} />
        )}

        {/* Start Your Own Estate — invited users without an own estate */}
        {profile?.signup_mode === 'invited' && !ownEstateId && (
          <div style={cardStyle}>
            <h2 style={sectionTitle}>Start Your Own Estate</h2>
            <p style={sectionSub}>You're collaborating on someone else's estate. Start your own to build a private vault, create a will, and plan ahead for your family.</p>
            <div style={{display:"flex", flexDirection:"column", gap:"0.625rem", marginBottom:"1.5rem"}}>
              {["Personal vault for your documents & passwords", "Planning-ahead checklist (all 50 states)", "Artificial Intelligence (AI) estate guide (unlimited questions)"].map(f => (
                <div key={f} style={{display:"flex", alignItems:"center", gap:"0.625rem", fontSize:"0.82rem", color:T.charcoal}}>
                  <span style={{color:T.forest, fontWeight:"700", flexShrink:0}}>✓</span>{f}
                </div>
              ))}
            </div>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"0.75rem"}}>
              <span style={{fontSize:"0.78rem", fontFamily:"'DM Mono', monospace", color:T.gold, fontWeight:"600"}}>ONE-TIME PAYMENT — $299</span>
              <button
                onClick={() => {
                  const link = process.env.REACT_APP_STRIPE_PAYMENT_LINK;
                  if (link) { localStorage.setItem('heiros_invited_upgrade', 'true'); window.location.href = `${link}?source=invited_upgrade&client_reference_id=${user.id}&prefilled_email=${encodeURIComponent(user.email || '')}`; }
                }}
                style={{...filledBtn, padding:"0.625rem 1.5rem", fontSize:"0.875rem"}}
              >
                Get Started →
              </button>
            </div>
          </div>
        )}

        {/* My Estate — invited users who have already upgraded */}
        {profile?.signup_mode === 'invited' && ownEstateId && (
          <div style={cardStyle}>
            <h2 style={sectionTitle}>My Estate</h2>
            <p style={sectionSub}>You have your own estate set up in addition to the one you're collaborating on.</p>
            <div style={{background:T.parchment, borderRadius:"10px", padding:"0.875rem 1.125rem", marginBottom:"1rem", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"0.75rem"}}>
              <div>
                <div style={{fontSize:"0.65rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.06em", marginBottom:"0.25rem"}}>YOUR ESTATE</div>
                <div style={{fontSize:"0.9rem", fontWeight:"600", color:T.charcoal}}>{ownEstate?.ownerName || user?.email?.split('@')[0] || "My Estate"}</div>
              </div>
              <button
                onClick={() => { if (setActiveEstateMode) setActiveEstateMode('own'); navTo('app-vault'); }}
                style={{...outlineBtn, padding:"0.5rem 1rem", fontSize:"0.8rem"}}
              >
                Switch to My Estate →
              </button>
            </div>
          </div>
        )}

        {/* Change Email */}
        <div style={cardStyle}>
          <h2 style={sectionTitle}>Change Email Address</h2>
          <p style={sectionSub}>We'll send a confirmation link to your new address before switching.</p>
          <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
            <div>
              <label style={labelStyle}>Current Email</label>
              <div style={{...inputStyle, color:T.muted, display:"flex", alignItems:"center"}}>{user?.email || "—"}</div>
            </div>
            <div>
              <label style={labelStyle}>New Email Address</label>
              <input
                type="email"
                value={emailForm.newEmail}
                onChange={e=>setEmailForm({newEmail:e.target.value})}
                placeholder="newemail@example.com"
                style={inputStyle}
                onKeyDown={e=>e.key==="Enter"&&handleEmailChange()}
              />
            </div>
            <StatusMsg status={emailStatus} />
            <button onClick={handleEmailChange} disabled={emailLoading} style={{...filledBtn, alignSelf:"flex-start", padding:"0.75rem 1.5rem", opacity:emailLoading?0.7:1}}>
              {emailLoading ? "Sending…" : "Update Email →"}
            </button>
          </div>
        </div>

        {/* Change Password */}
        <div style={cardStyle}>
          <h2 style={sectionTitle}>Change Password</h2>
          <p style={sectionSub}>Choose a strong password of at least 6 characters.</p>
          <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
            <div>
              <label style={labelStyle}>New Password</label>
              <input
                type="password"
                value={pwForm.newPassword}
                onChange={e=>setPwForm({...pwForm, newPassword:e.target.value})}
                placeholder="At least 6 characters"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                type="password"
                value={pwForm.confirmPassword}
                onChange={e=>setPwForm({...pwForm, confirmPassword:e.target.value})}
                placeholder="Repeat new password"
                style={inputStyle}
                onKeyDown={e=>e.key==="Enter"&&handlePasswordChange()}
              />
            </div>
            <StatusMsg status={pwStatus} />
            <button onClick={handlePasswordChange} disabled={pwLoading} style={{...filledBtn, alignSelf:"flex-start", padding:"0.75rem 1.5rem", opacity:pwLoading?0.7:1}}>
              {pwLoading ? "Updating…" : "Update Password →"}
            </button>
          </div>
        </div>


        {/* Danger Zone */}
        <div style={{...cardStyle, border:`1px solid #f5c6cb`, background:"#fffafa", marginBottom:"3rem"}}>
          <h2 style={{...sectionTitle, color:T.danger}}>Danger Zone</h2>
          <p style={sectionSub}>Permanently delete your account and all associated data. This cannot be undone.</p>
          <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
            <div>
              <label style={{...labelStyle, color:T.danger}}>Type DELETE to confirm</label>
              <input
                value={deleteConfirm}
                onChange={e=>setDeleteConfirm(e.target.value)}
                placeholder='Type DELETE here'
                style={{...inputStyle, border:`1px solid #f5c6cb`}}
              />
            </div>
            <StatusMsg status={deleteStatus} />
            <button
              onClick={handleDeleteAccount}
              disabled={deleteLoading || deleteConfirm !== "DELETE"}
              style={{...btnReset, alignSelf:"flex-start", padding:"0.75rem 1.5rem", borderRadius:"9px", fontSize:"0.875rem", fontWeight:"500", background:deleteConfirm==="DELETE"?"#c0392b":"transparent", color:deleteConfirm==="DELETE"?"white":T.danger, border:`1px solid ${T.danger}`, opacity:deleteLoading?0.7:1, transition:"all 0.15s"}}
            >
              {deleteLoading ? "Deleting…" : "Delete My Account"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
function HomePage({ navTo }) {
  return (
    <div>
      {/* Hero */}
      <section style={{background:`linear-gradient(160deg, ${T.forestDark} 0%, ${T.forest} 60%, ${T.forestLight} 100%)`, color:"white", padding:"6rem 2rem 5rem", textAlign:"center", position:"relative", overflow:"hidden"}}>
        <div style={{position:"absolute", inset:0, opacity:0.04, backgroundImage:"repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.5) 40px, rgba(255,255,255,0.5) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.5) 40px, rgba(255,255,255,0.5) 41px)"}} />
        <div style={{position:"relative", maxWidth:"720px", margin:"0 auto"}}>
          <div className="fade-up" style={{display:"inline-block", background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:"20px", padding:"0.375rem 1rem", fontSize:"0.8rem", letterSpacing:"0.08em", marginBottom:"2rem", fontFamily:"'DM Mono', monospace"}}>PLAN AHEAD · SETTLE ESTATES · SUPPORT YOUR FAMILY</div>
          <h1 className="fade-up-2" style={{fontFamily:"'Playfair Display', serif", fontSize:"clamp(2.5rem, 6vw, 4rem)", fontWeight:"700", lineHeight:"1.1", letterSpacing:"-0.02em", marginBottom:"1.5rem"}}>
            Estate planning for the living.<br/><em style={{color:T.goldLight}}>Settlement support for the grieving.</em>
          </h1>
          <p className="fade-up-3" style={{fontSize:"1.125rem", opacity:0.85, lineHeight:"1.7", marginBottom:"2.5rem", maxWidth:"560px", margin:"0 auto 2.5rem"}}>
            Build your vault and organize your estate before you die — so your family isn't left scrambling. Already lost someone? We guide executors through every step of settlement — so nothing falls through the cracks.
          </p>
          <div className="fade-up-4" style={{display:"flex", gap:"1rem", justifyContent:"center", flexWrap:"wrap"}}>
            <button onClick={()=>navTo("signup")} style={{...filledBtn, background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color:"white", padding:"0.875rem 2rem", fontSize:"1rem"}}>Get Started →</button>
            <button onClick={()=>navTo("demo")} style={{...outlineBtn, borderColor:"rgba(255,255,255,0.3)", color:"white", padding:"0.875rem 1.5rem", fontSize:"1rem"}}>How It Works</button>
          </div>
          <p style={{marginTop:"1.25rem", fontSize:"0.8rem", opacity:0.6, fontFamily:"'DM Mono', monospace"}}>$299 flat fee to settle</p>
        </div>
      </section>

      {/* Stats bar */}
      <section style={{background:T.tan, borderBottom:`1px solid ${T.border}`}}>
        <div style={{maxWidth:"1000px", margin:"0 auto", padding:"2rem", display:"flex", flexWrap:"wrap", justifyContent:"space-around", gap:"1.5rem"}}>
          {[
            {n:"2.8M", l:"Americans die per year"},
            {n:"570 hrs", l:"Average time to settle an estate"},
            {n:"92%", l:"Of executors feel unprepared"},
            {n:"$299", l:"HeirOS flat fee"},
          ].map((s,i)=>(
            <div key={i} style={{textAlign:"center"}}>
              <div style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest}}>{s.n}</div>
              <div style={{fontSize:"0.8rem", color:T.muted, marginTop:"0.25rem"}}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section style={{padding:"5rem 2rem", maxWidth:"1100px", margin:"0 auto"}}>
        <div style={{textAlign:"center", marginBottom:"3.5rem"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.25rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.75rem"}}>Everything a family needs to settle an estate</h2>
          <p style={{color:T.muted, fontSize:"1rem", maxWidth:"500px", margin:"0 auto"}}>No legal jargon. No guesswork. Just a clear path forward.</p>
        </div>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:"1.5rem"}}>
          {[
            { icon:"✓", title:"Guided Checklist", desc:"Step-by-step tasks organized by urgency, category, and your state's specific requirements. Never wonder what to do next." },
            { icon:"⊟", title:"Document Generator", desc:"Pre-filled legal notification letters for the Social Security Administration (SSA), banks, employers, and more. Ready to send in minutes, not days." },
            { icon:"◈", title:"Artificial Intelligence (AI) Estate Guide", desc:"Ask anything about settling the estate. Get plain-English answers 24/7, with full context about your specific situation." },
            { icon:"⊕", title:"Family Collaboration", desc:"Invite siblings and family members to a shared dashboard. No more group text chaos or duplicated effort." },
          ].map((f,i)=>(
            <div key={i} style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"14px", padding:"1.75rem", transition:"box-shadow 0.2s, transform 0.2s"}}>
              <div style={{width:"44px", height:"44px", background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"10px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.25rem", color:T.forest, marginBottom:"1rem"}}>{f.icon}</div>
              <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.1rem", fontWeight:"600", color:T.charcoal, marginBottom:"0.5rem"}}>{f.title}</h3>
              <p style={{fontSize:"0.875rem", color:T.muted, lineHeight:"1.6"}}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section style={{background:T.parchment, padding:"5rem 2rem", borderTop:`1px solid ${T.border}`, borderBottom:`1px solid ${T.border}`}}>
        <div style={{maxWidth:"800px", margin:"0 auto"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.25rem", fontWeight:"700", color:T.forest, textAlign:"center", marginBottom:"3rem", letterSpacing:"-0.02em"}}>How HeirOS works</h2>
          <div style={{display:"flex", flexDirection:"column", gap:"1.5rem"}}>
            {[
              { n:"01", title:"Tell us about the estate", desc:"A 5-minute intake form captures the deceased's information, the executor's details, and estate basics. This pre-fills every document and customizes your checklist." },
              { n:"02", title:"Follow your personalized checklist", desc:"HeirOS generates a state-specific, prioritized task list covering every administrative and legal requirement. Each task includes clear instructions and timing guidance." },
              { n:"03", title:"Generate and send documents", desc:"Notification letters for government agencies, banks, and employers are pre-filled with your information. Copy, print, or email them directly." },
              { n:"04", title:"Get answers when you're stuck", desc:"Our AI estate guide answers questions in plain English, 24/7. For complex legal questions, we connect you with vetted estate attorneys at flat rates." },
            ].map((s,i)=>(
              <div key={i} style={{display:"flex", gap:"1.5rem", alignItems:"flex-start"}}>
                <div style={{fontFamily:"'Playfair Display', serif", fontSize:"2.5rem", fontWeight:"700", color:T.gold, lineHeight:1, flexShrink:0, width:"48px"}}>{s.n}</div>
                <div style={{paddingTop:"0.25rem"}}>
                  <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.1rem", fontWeight:"600", color:T.forest, marginBottom:"0.375rem"}}>{s.title}</h3>
                  <p style={{fontSize:"0.875rem", color:T.muted, lineHeight:"1.65"}}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog preview */}
      <section style={{padding:"5rem 2rem", maxWidth:"1100px", margin:"0 auto"}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:"2.5rem", flexWrap:"wrap", gap:"1rem"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em"}}>From the HeirOS blog</h2>
          <button onClick={()=>navTo("blog")} style={{...btnReset, color:T.gold, fontSize:"0.875rem", fontWeight:"500"}}>View all articles →</button>
        </div>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))", gap:"1.5rem"}}>
          {BLOG_POSTS.slice(0,3).map(post=>(
            <BlogCard key={post.id} post={post} navTo={navTo} />
          ))}
        </div>
      </section>

      {/* For Attorneys */}
      <section style={{background:T.parchment, padding:"5rem 2rem", borderTop:`1px solid ${T.border}`}}>
        <div style={{maxWidth:"960px", margin:"0 auto"}}>
          <div style={{textAlign:"center", marginBottom:"3rem"}}>
            <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.72rem", letterSpacing:"0.12em", color:T.muted, marginBottom:"0.75rem"}}>FOR ESTATE ATTORNEYS</p>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.875rem"}}>Recommend HeirOS to your clients.</h2>
            <p style={{color:T.muted, fontSize:"1rem", lineHeight:"1.75", maxWidth:"580px", margin:"0 auto"}}>
              HeirOS is not a legal services competitor — it's the organizational infrastructure that makes your engagements more efficient. Clients who use HeirOS arrive prepared, so your time is spent on legal strategy, not administrative back-and-forth.
            </p>
          </div>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(210px, 1fr))", gap:"1.25rem", marginBottom:"2.5rem"}}>
            {[
              { icon:"📂", title:"Organized clients", desc:"Documents gathered, timelines mapped, and basic questions answered before the first meeting." },
              { icon:"⏱", title:"Reduced admin overhead", desc:"Less time spent explaining process basics. More time on legal work that only you can do." },
              { icon:"🤝", title:"Free referrals", desc:"HeirOS refers families who need legal help to attorneys in our network — at no cost to you." },
              { icon:"⚖️", title:"Complementary, not competitive", desc:"HeirOS handles administration. You handle legal counsel. A better outcome for the client." },
            ].map((f,i) => (
              <div key={i} style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"14px", padding:"1.5rem"}}>
                <div style={{fontSize:"1.5rem", marginBottom:"0.75rem"}}>{f.icon}</div>
                <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1rem", fontWeight:"600", color:T.forest, marginBottom:"0.375rem"}}>{f.title}</h3>
                <p style={{fontSize:"0.84rem", color:T.muted, lineHeight:"1.6"}}>{f.desc}</p>
              </div>
            ))}
          </div>
          <div style={{textAlign:"center"}}>
            <button onClick={()=>navTo("attorneys")} style={{...filledBtn, background:`linear-gradient(135deg, ${T.forest}, ${T.forestDark})`, color:"white", padding:"0.875rem 2rem", fontSize:"0.95rem"}}>Learn about the Attorney Network →</button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{background:`linear-gradient(135deg, ${T.forest}, ${T.forestDark})`, color:"white", padding:"5rem 2rem", textAlign:"center"}}>
        <div style={{maxWidth:"560px", margin:"0 auto"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.25rem", fontWeight:"700", letterSpacing:"-0.02em", marginBottom:"1rem"}}>You have enough to carry right now.</h2>
          <p style={{opacity:0.8, lineHeight:"1.7", marginBottom:"2.5rem", fontSize:"1rem"}}>Let HeirOS handle the paperwork, the process, and the paperwork while you focus on your family.</p>
          <button onClick={()=>navTo("signup")} style={{...filledBtn, background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color:"white", padding:"1rem 2.5rem", fontSize:"1.05rem"}}>Begin Estate Settlement for $299 →</button>
        </div>
      </section>
    </div>
  );
}

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
function SetNewPasswordPage({ navTo }) {
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async () => {
    setStatus(null);
    if (!form.password) { setStatus({ ok:false, msg:"Please enter a new password." }); return; }
    if (form.password.length < 6) { setStatus({ ok:false, msg:"Password must be at least 6 characters." }); return; }
    if (form.password !== form.confirm) { setStatus({ ok:false, msg:"Passwords do not match." }); return; }
    setLoading(true);
    try {
      const { error } = await updatePassword(form.password);
      if (error) { setStatus({ ok:false, msg:error.message }); }
      else { setStatus({ ok:true, msg:"Password updated successfully." }); setForm({ password:"", confirm:"" }); }
    } finally { setLoading(false); }
  };

  return (
    <div style={{minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem", background:T.parchment}}>
      <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"20px", padding:"2.5rem", width:"100%", maxWidth:"420px", boxShadow:"0 4px 40px rgba(0,0,0,0.08)"}}>
        <div style={{textAlign:"center", marginBottom:"2rem"}}>
          <div style={{width:"52px", height:"52px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"13px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1rem", color:"white", fontSize:"1.5rem", fontFamily:"'Playfair Display', serif", fontWeight:"700"}}>H</div>
          <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest}}>Set a new password</h1>
          <p style={{fontSize:"0.85rem", color:T.muted, marginTop:"0.5rem"}}>Choose a new password for your account.</p>
        </div>

        {status?.ok ? (
          <div>
            <div style={{background:"#f0f9f4", border:"1px solid #c3e6cb", borderRadius:"10px", padding:"0.875rem 1rem", marginBottom:"1.5rem", fontSize:"0.85rem", color:T.success, lineHeight:"1.6"}}>
              ✓ Your password has been updated. You can now sign in.
            </div>
            <button onClick={()=>navTo("login")} style={{...filledBtn, width:"100%", padding:"0.875rem"}}>Sign In →</button>
          </div>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
            <div>
              <label style={labelStyle}>New Password</label>
              <div style={{position:"relative"}}>
                <input value={form.password} onChange={e=>setForm({...form,password:e.target.value})} type={showPw?"text":"password"} placeholder="••••••••" style={{...inputStyle, paddingRight:"2.75rem"}} onKeyDown={e=>e.key==="Enter"&&handleSubmit()} />
                <button type="button" onClick={()=>setShowPw(s=>!s)} style={{...btnReset, position:"absolute", right:"0.75rem", top:"50%", transform:"translateY(-50%)", color:T.muted, fontSize:"0.78rem", fontWeight:"500", fontFamily:"'DM Mono', monospace"}}>{showPw?"Hide":"Show"}</button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Confirm Password</label>
              <input value={form.confirm} onChange={e=>setForm({...form,confirm:e.target.value})} type={showPw?"text":"password"} placeholder="••••••••" style={inputStyle} onKeyDown={e=>e.key==="Enter"&&handleSubmit()} />
            </div>
            {status && !status.ok && <div style={{background:"#fef0f0", border:"1px solid #f5c6cb", borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.82rem", color:T.danger}}>{status.msg}</div>}
            <button onClick={handleSubmit} disabled={loading} style={{...filledBtn, width:"100%", padding:"0.875rem", fontSize:"0.95rem", marginTop:"0.25rem", opacity:loading?0.7:1}}>
              {loading ? "Updating…" : "Set New Password →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginPage({ navTo, estate, onSignIn }) {
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ email:"", password:"", name:"" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSubmit = async () => {
    setError("");
    if (!form.email || !form.password) { setError("Please enter your email and password."); return; }
    setLoading(true);
    try {
      if (tab === "login") {
        const { data, error: err } = await signIn(form.email, form.password);
        if (err) { setError(err.message); return; }
        if (data?.user && onSignIn) onSignIn(data.user);
        navTo("app-dashboard");
      } else {
        if (!form.name) { setError("Please enter your full name."); setLoading(false); return; }
        if (!termsAccepted) { setError("Please agree to the Terms of Use and Privacy Policy to continue."); setLoading(false); return; }
        const { data, error: err } = await signUp(form.email, form.password);
        if (err) { setError(err.message); return; }
        if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) { setError("An account with this email already exists. Please sign in instead."); return; }
        if (data?.user) { await updateProfile(data.user.id, { terms_accepted_at: new Date().toISOString(), onboarding_data: { fullName: form.name.trim() } }); if (onSignIn) onSignIn(data.user); }
        navTo("signup");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async () => {
    setResetError("");
    if (!resetEmail) { setResetError("Please enter your email address."); return; }
    setResetLoading(true);
    try {
      const { error: err } = await sendPasswordReset(resetEmail);
      if (err) { setResetError(err.message); return; }
      setResetSent(true);
    } finally {
      setResetLoading(false);
    }
  };

  if (showReset) {
    return (
      <div style={{minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem", background:T.parchment}}>
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"20px", padding:"2.5rem", width:"100%", maxWidth:"420px", boxShadow:"0 4px 40px rgba(0,0,0,0.08)"}}>
          <div style={{textAlign:"center", marginBottom:"2rem"}}>
            <div style={{width:"52px", height:"52px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"13px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1rem", color:"white", fontSize:"1.5rem", fontFamily:"'Playfair Display', serif", fontWeight:"700"}}>H</div>
            <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest}}>Reset your password</h1>
          </div>

          {resetSent ? (
            <div>
              <div style={{background:"#f0f9f4", border:"1px solid #c3e6cb", borderRadius:"10px", padding:"0.875rem 1rem", marginBottom:"1.5rem", fontSize:"0.85rem", color:T.success, lineHeight:"1.6"}}>
                ✓ If an account exists for <strong>{resetEmail}</strong>, we've sent a password reset link. Check your inbox.
              </div>
              <button onClick={()=>{setShowReset(false); setResetSent(false); setResetEmail("");}} style={{...btnReset, fontSize:"0.85rem", color:T.forest, fontWeight:"600", textDecoration:"underline", textAlign:"center", width:"100%"}}>Back to Sign In</button>
            </div>
          ) : (
            <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
              <p style={{fontSize:"0.85rem", color:T.muted, lineHeight:"1.6"}}>Enter the email address associated with your account, and we'll send you a link to reset your password.</p>
              <div>
                <label style={labelStyle}>Email Address</label>
                <input value={resetEmail} onChange={e=>setResetEmail(e.target.value)} type="email" placeholder="you@email.com" style={inputStyle} onKeyDown={e=>e.key==="Enter"&&handleResetSubmit()} />
              </div>
              {resetError && <div style={{background:"#fef0f0", border:"1px solid #f5c6cb", borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.82rem", color:T.danger}}>{resetError}</div>}
              <button onClick={handleResetSubmit} disabled={resetLoading} style={{...filledBtn, width:"100%", padding:"0.875rem", fontSize:"0.95rem", opacity:resetLoading?0.7:1}}>
                {resetLoading ? "Sending…" : "Send Reset Link →"}
              </button>
              <button onClick={()=>{setShowReset(false); setResetError("");}} style={{...btnReset, fontSize:"0.8rem", color:T.muted, textAlign:"center"}}>Back to Sign In</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem", background:T.parchment}}>
      <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"20px", padding:"2.5rem", width:"100%", maxWidth:"420px", boxShadow:"0 4px 40px rgba(0,0,0,0.08)"}}>
        <div style={{textAlign:"center", marginBottom:"2rem"}}>
          <div style={{width:"52px", height:"52px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"13px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1rem", color:"white", fontSize:"1.5rem", fontFamily:"'Playfair Display', serif", fontWeight:"700"}}>H</div>
          <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest}}>Welcome to HeirOS</h1>
        </div>

        {estate && (
          <div style={{background:"#f0f9f4", border:"1px solid #c3e6cb", borderRadius:"10px", padding:"0.875rem 1rem", marginBottom:"1.5rem", fontSize:"0.85rem", color:T.success}}>
            ✓ Your estate for <strong>{estate.deceasedName}</strong> is saved. Sign in to continue.
          </div>
        )}

        <div style={{display:"flex", gap:"0", marginBottom:"1.75rem", background:T.parchment, borderRadius:"10px", padding:"4px"}}>
          {["login","register"].map(t=>(
            <button key={t} onClick={()=>{setTab(t); setError("");}} style={{...btnReset, flex:1, padding:"0.5rem", borderRadius:"7px", fontSize:"0.875rem", fontWeight:"500", background:tab===t?T.white:"transparent", color:tab===t?T.forest:T.muted, boxShadow:tab===t?"0 1px 4px rgba(0,0,0,0.08)":""}}>{t==="login"?"Sign In":"Create Account"}</button>
          ))}
        </div>

        <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
          {tab==="register" && (
            <div>
              <label style={labelStyle}>Full Name</label>
              <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Your full name" style={inputStyle} />
            </div>
          )}
          <div>
            <label style={labelStyle}>Email Address</label>
            <input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} type="email" placeholder="you@email.com" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Password</label>
            <div style={{position:"relative"}}>
              <input value={form.password} onChange={e=>setForm({...form,password:e.target.value})} type={showPassword?"text":"password"} placeholder="••••••••" style={{...inputStyle, paddingRight:"2.75rem"}} onKeyDown={e=>e.key==="Enter"&&handleSubmit()} />
              <button type="button" onClick={()=>setShowPassword(s=>!s)} aria-label={showPassword?"Hide password":"Show password"} style={{...btnReset, position:"absolute", right:"0.75rem", top:"50%", transform:"translateY(-50%)", color:T.muted, fontSize:"0.78rem", fontWeight:"500", fontFamily:"'DM Mono', monospace"}}>
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          {error && <div style={{background:"#fef0f0", border:"1px solid #f5c6cb", borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.82rem", color:T.danger}}>{error}</div>}
          {tab !== "login" && (
            <div style={{display:"flex", alignItems:"flex-start", gap:"0.625rem", padding:"0.75rem", background:T.parchment, borderRadius:"8px", border:`1px solid ${T.border}`}}>
              <input type="checkbox" id="lp-terms" checked={termsAccepted} onChange={e=>setTermsAccepted(e.target.checked)} style={{marginTop:"0.15rem", flexShrink:0, accentColor:T.forest, width:"16px", height:"16px", cursor:"pointer"}} />
              <label htmlFor="lp-terms" style={{fontSize:"0.8rem", color:T.charcoal, lineHeight:"1.5", cursor:"pointer"}}>
                I have read and agree to the{" "}
                <button onClick={()=>window.open(window.location.origin+window.location.pathname+'?page=terms','_blank')} style={{...btnReset, color:T.forest, fontWeight:"600", textDecoration:"underline", fontSize:"0.8rem"}}>Terms of Use</button>
                {" "}and{" "}
                <button onClick={()=>window.open(window.location.origin+window.location.pathname+'?page=privacy','_blank')} style={{...btnReset, color:T.forest, fontWeight:"600", textDecoration:"underline", fontSize:"0.8rem"}}>Privacy Policy</button>
              </label>
            </div>
          )}
          <button onClick={handleSubmit} disabled={loading||(tab!=="login"&&!termsAccepted)} style={{...filledBtn, width:"100%", padding:"0.875rem", fontSize:"0.95rem", marginTop:"0.5rem", opacity:(loading||(tab!=="login"&&!termsAccepted))?0.5:1}}>
            {loading ? "Please wait…" : tab==="login" ? "Sign In →" : "Create Account →"}
          </button>
          {tab==="login" && <button onClick={()=>{setShowReset(true); setResetEmail(form.email); setError("");}} style={{...btnReset, fontSize:"0.8rem", color:T.muted, textAlign:"center"}}>Forgot password?</button>}
          {!estate && <p style={{fontSize:"0.8rem", color:T.muted, textAlign:"center"}}>Don't have an account? <button onClick={()=>navTo("signup")} style={{...btnReset, color:T.gold, fontWeight:"500"}}>Start for $299</button></p>}
        </div>
      </div>
    </div>
  );
}

// ─── SCROLL REVEAL HELPER ───────────────────────────────────────────────────────
function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

function RevealSection({ children, style }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: "opacity 0.6s ease, transform 0.6s ease",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── DEMO MINI-UI MOCKUPS ───────────────────────────────────────────────────────
function DemoMockVault() {
  return (
    <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"1.5rem", boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
      <div style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.05em", marginBottom:"1rem"}}>YOUR VAULT</div>
      {[
        {icon:"🗂️", label:"Will & Estate Docs", count:"6 items"},
        {icon:"🩺", label:"Medical Information", count:"4 items"},
        {icon:"🏦", label:"Financial Accounts", count:"5 items"},
        {icon:"💻", label:"Digital Assets", count:"7 items"},
      ].map((r,i)=>(
        <div key={i} style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.75rem 0.5rem", borderBottom: i<3?`1px solid ${T.borderLight}`:"none"}}>
          <div style={{width:"36px", height:"36px", background:T.parchment, borderRadius:"9px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem"}}>{r.icon}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:"0.85rem", fontWeight:"500", color:T.charcoal}}>{r.label}</div>
            <div style={{fontSize:"0.72rem", color:T.muted}}>{r.count}</div>
          </div>
          <div style={{fontSize:"0.9rem", color:T.gold}}>🔒</div>
        </div>
      ))}
    </div>
  );
}

function DemoMockMedicalPOA() {
  return (
    <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"1.5rem", boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
      <div style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.05em", marginBottom:"1rem"}}>MEDICAL DOCUMENTS</div>
      {[
        {label:"Advance Directive"},
        {label:"HIPAA Authorization"},
        {label:"Medical History Summary"},
        {label:"Healthcare Proxy Form"},
      ].map((r,i)=>(
        <div key={i} style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.75rem 0.5rem", borderBottom: i<3?`1px solid ${T.borderLight}`:"none"}}>
          <div style={{width:"36px", height:"36px", background:T.parchment, borderRadius:"9px", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem"}}>📄</div>
          <div style={{flex:1}}>
            <div style={{fontSize:"0.85rem", fontWeight:"500", color:T.charcoal}}>{r.label}</div>
          </div>
          <span style={{fontSize:"0.68rem", color:T.success, fontWeight:"600", background:"#eaf6f0", borderRadius:"20px", padding:"0.2rem 0.6rem"}}>✓ Shared</span>
        </div>
      ))}
    </div>
  );
}

function DemoMockExecutorSafeguards() {
  const rows = [
    {name:"Backup Executor #2", status:"Approved ✓", color:T.success},
    {name:"Backup Executor #1", status:"Notified", color:T.gold},
    {name:"Primary Executor", status:"Pending", color:T.muted},
    {name:"You (Owner)", status:"Pending", color:T.muted},
  ];
  return (
    <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"1.5rem", boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
      <div style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.05em", marginBottom:"1rem"}}>EMERGENCY ACCESS CHAIN</div>
      <div style={{position:"relative", paddingLeft:"1.25rem"}}>
        <div style={{position:"absolute", left:"9px", top:"14px", bottom:"14px", width:"2px", background:T.border}} />
        {rows.map((r,i)=>(
          <div key={i} style={{position:"relative", display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.7rem 0"}}>
            <div style={{position:"absolute", left:"-1.25rem", width:"20px", height:"20px", borderRadius:"50%", background:r.color, border:`3px solid ${T.white}`, boxShadow:`0 0 0 1px ${T.border}`}} />
            <div style={{flex:1, marginLeft:"0.5rem"}}>
              <div style={{fontSize:"0.85rem", fontWeight:"500", color:T.charcoal}}>{r.name}</div>
            </div>
            <span style={{fontSize:"0.68rem", fontWeight:"600", color:r.color, fontFamily:"'DM Mono', monospace"}}>{r.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DemoMockAccessLevels() {
  const rows = [
    {name:"Attorney", access:"Documents only", toggles:[true,false,false]},
    {name:"Spouse", access:"Full Vault Access", toggles:[true,true,true]},
    {name:"Sister", access:"View Checklist Only", toggles:[true,false,false]},
  ];
  return (
    <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"1.5rem", boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
      <div style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.05em", marginBottom:"1rem"}}>PER-PERSON PERMISSIONS</div>
      {rows.map((r,i)=>(
        <div key={i} style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.75rem 0.5rem", borderBottom: i<rows.length-1?`1px solid ${T.borderLight}`:"none"}}>
          <div style={{flex:1}}>
            <div style={{fontSize:"0.85rem", fontWeight:"500", color:T.charcoal}}>{r.name}</div>
            <div style={{fontSize:"0.72rem", color:T.muted}}>{r.access}</div>
          </div>
          <div style={{display:"flex", gap:"0.35rem"}}>
            {r.toggles.map((on,j)=>(
              <div key={j} style={{width:"26px", height:"14px", borderRadius:"7px", background:on?T.success:T.border, position:"relative", flexShrink:0}}>
                <div style={{position:"absolute", top:"2px", left:on?"14px":"2px", width:"10px", height:"10px", borderRadius:"50%", background:"white", transition:"left 0.15s"}} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


function DemoMockPlaybook() {
  return (
    <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"1.5rem", boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
      <div style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.05em", marginBottom:"1rem"}}>EXECUTOR PLAYBOOK</div>
      {[
        {done:true, label:"Obtain certified death certificates"},
        {done:true, label:"Notify Social Security Administration"},
        {done:false, label:"File petition for probate"},
        {done:false, label:"Notify banks & close accounts"},
      ].map((s,i)=>(
        <div key={i} style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.625rem 0.25rem"}}>
          <div style={{width:"20px", height:"20px", borderRadius:"6px", border:`2px solid ${s.done?T.success:T.border}`, background:s.done?T.success:"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.7rem", color:"white", flexShrink:0}}>
            {s.done ? "✓" : ""}
          </div>
          <span style={{fontSize:"0.85rem", color:s.done?T.muted:T.charcoal, textDecoration:s.done?"line-through":"none"}}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function DemoMockCollaborators() {
  return (
    <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"1.5rem", boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
      <div style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.05em", marginBottom:"1rem"}}>SUCCESSION CHAIN</div>
      {[
        {name:"Jane Doe", role:"Primary Executor", active:true},
        {name:"Mark Doe", role:"Successor Executor", active:false},
        {name:"Alex Doe", role:"Family Collaborator", active:false},
      ].map((p,i)=>(
        <div key={i} style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.75rem 0.5rem", borderBottom: i<2?`1px solid ${T.borderLight}`:"none"}}>
          <div style={{width:"32px", height:"32px", borderRadius:"50%", background:p.active?`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`:T.parchment, color:p.active?"white":T.muted, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.75rem", fontWeight:"700"}}>
            {p.name[0]}
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:"0.85rem", fontWeight:"500", color:T.charcoal}}>{p.name}</div>
            <div style={{fontSize:"0.7rem", color:T.muted}}>{p.role}</div>
          </div>
          {p.active && <span style={{fontSize:"0.65rem", color:T.success, fontFamily:"'DM Mono', monospace"}}>ACTIVE</span>}
        </div>
      ))}
    </div>
  );
}

function DemoMockAIGuide() {
  return (
    <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"1.5rem", boxShadow:"0 8px 32px rgba(0,0,0,0.08)"}}>
      <div style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.muted, letterSpacing:"0.05em", marginBottom:"1rem"}}>AI ESTATE GUIDE</div>
      <div style={{background:T.parchment, borderRadius:"12px", padding:"1rem", marginBottom:"0.75rem", marginRight:"1.5rem"}}>
        <div style={{fontSize:"0.8rem", color:T.slate, lineHeight:"1.6"}}>Do I need to go through probate if there's a will?</div>
      </div>
      <div style={{background:T.forest, borderRadius:"12px", padding:"1rem", marginLeft:"1.5rem"}}>
        <div style={{fontSize:"0.8rem", color:"white", lineHeight:"1.6"}}>Not always — it depends on your state and how assets are titled. Based on the estate details you entered, here's what applies to your situation…</div>
      </div>
    </div>
  );
}

// ─── DEMO PAGE ────────────────────────────────────────────────────────────────
function DemoPage({ navTo }) {
  const features = [
    { title:"Personal Vault", desc:"Securely store legal documents, medical information, financial account details, and digital assets — all in one encrypted place your family can access when it matters most.", Mock: DemoMockVault },
    { title:"Medical Power of Attorney", desc:"Designate someone to access medical documents immediately — no waiting for a death report, no delay. Medical POA access is always-on from the moment they accept, with Primary and Backup succession just like your executor chain.", Mock: DemoMockMedicalPOA },

    { title:"Executor Playbook", desc:"A state-specific, step-by-step checklist that walks executors through every legal and administrative requirement — so nothing falls through the cracks.", Mock: DemoMockPlaybook },
    { title:"Built-In Executor Safeguards", desc:"Emergency access follows a strict approval chain — Backup Executor #2 requests, Backup #1 is notified first, then the Primary Executor, then you. Every step has a configurable response window (24, 48, or 72 hours), and you can deny any request instantly by email — no login required.", Mock: DemoMockExecutorSafeguards },
    { title:"Collaborators & Succession Chain", desc:"Invite family members, designate a successor executor, and keep everyone aligned with clear roles and shared visibility into estate progress.", Mock: DemoMockCollaborators },
    { title:"Different Access for Every Person", desc:"Not everyone needs the same access. Give your attorney document access, your spouse full vault visibility, and a family member view-only checklist access — all individually configurable per person.", Mock: DemoMockAccessLevels },
    { title:"AI Guide", desc:"Ask anything about settling an estate and get plain-English answers, 24/7 — fully aware of your specific estate's details and progress.", Mock: DemoMockAIGuide },
  ];

  return (
    <div>
      {/* Hero */}
      <section style={{background:`linear-gradient(160deg, ${T.forestDark} 0%, ${T.forest} 60%, ${T.forestLight} 100%)`, color:"white", padding:"6rem 2rem", textAlign:"center", position:"relative", overflow:"hidden"}}>
        <div style={{position:"absolute", inset:0, opacity:0.04, backgroundImage:"repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.5) 40px, rgba(255,255,255,0.5) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(255,255,255,0.5) 40px, rgba(255,255,255,0.5) 41px)"}} />
        <div style={{position:"relative", maxWidth:"720px", margin:"0 auto"}}>
          <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"clamp(2.25rem, 5.5vw, 3.5rem)", fontWeight:"700", lineHeight:"1.15", letterSpacing:"-0.02em", marginBottom:"1.25rem"}}>
            See HeirOS in Action
          </h1>
          <p style={{fontSize:"1.1rem", opacity:0.85, lineHeight:"1.7", maxWidth:"560px", margin:"0 auto"}}>
            A closer look at how HeirOS helps you plan ahead and settle an estate.
          </p>
        </div>
      </section>

      {/* Feature sections */}
      <div style={{maxWidth:"1100px", margin:"0 auto", padding:"4rem 2rem"}}>
        {features.map((f, i) => (
          <RevealSection key={f.title} style={{marginBottom: i < features.length - 1 ? "5rem" : 0}}>
            <div style={{
              display:"flex",
              flexWrap:"wrap",
              alignItems:"center",
              gap:"3rem",
              flexDirection: i % 2 === 0 ? "row" : "row-reverse",
            }}>
              <div style={{flex:"1 1 340px"}}>
                <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.9rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.875rem"}}>{f.title}</h2>
                <p style={{fontSize:"1rem", color:T.muted, lineHeight:"1.7"}}>{f.desc}</p>
              </div>
              <div style={{flex:"1 1 340px"}}>
                <f.Mock />
              </div>
            </div>
          </RevealSection>
        ))}
      </div>

      {/* Stats bar */}
      <section style={{background:T.tan, borderBottom:`1px solid ${T.border}`}}>
        <div style={{maxWidth:"1000px", margin:"0 auto", padding:"2rem", display:"flex", flexWrap:"wrap", justifyContent:"space-around", gap:"1.5rem"}}>
          {[
            {n:"2.8M", l:"Americans die per year"},
            {n:"570 hrs", l:"Average time to settle an estate"},
            {n:"92%", l:"Of executors feel unprepared"},
            {n:"$299", l:"HeirOS flat fee"},
          ].map((s,i)=>(
            <div key={i} style={{textAlign:"center"}}>
              <div style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest}}>{s.n}</div>
              <div style={{fontSize:"0.8rem", color:T.muted, marginTop:"0.25rem"}}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section style={{padding:"5rem 2rem", textAlign:"center"}}>
        <div style={{maxWidth:"600px", margin:"0 auto"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.25rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.875rem"}}>Ready to build yours?</h2>
          <p style={{fontSize:"1rem", color:T.muted, lineHeight:"1.7", marginBottom:"2rem"}}>Start planning ahead or begin settling an estate today — no hourly billing, no legal jargon.</p>
          <button onClick={()=>navTo("signup")} style={{...filledBtn, background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color:"white", padding:"0.875rem 2rem", fontSize:"1.05rem"}}>Get Started →</button>
        </div>
      </section>

      {/* Contact section */}
      <ContactSection />
    </div>
  );
}

// ─── CONTACT SECTION ──────────────────────────────────────────────────────────
function ContactSection() {
  const [form, setForm] = useState({ name:"", email:"", message:"", honeypot:"" });
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) return; // silently reject bots
    if (!form.email || !form.message) return;
    setStatus('loading');
    setErrorMsg("");
    try {
      const { error } = await supabase.functions.invoke('send-email', {
        body: { type: 'contact_inquiry', name: form.name, email: form.email, message: form.message },
      });
      if (error) throw error;
      setStatus('success');
      setForm({ name:"", email:"", message:"", honeypot:"" });
    } catch(e) {
      setStatus('error');
      setErrorMsg("Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <section id="contact" style={{background:T.parchment, borderTop:`1px solid ${T.border}`, padding:"5rem 2rem"}}>
      <div style={{maxWidth:"640px", margin:"0 auto"}}>
        <div style={{textAlign:"center", marginBottom:"2.5rem"}}>
          <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.72rem", letterSpacing:"0.1em", color:T.gold, marginBottom:"0.625rem"}}>GET IN TOUCH</p>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.625rem"}}>Questions before you get started?</h2>
          <p style={{color:T.muted, fontSize:"0.95rem"}}>We typically respond within 1 business day.</p>
        </div>

        {status === 'success' ? (
          <div style={{background:"#f0faf5", border:`1px solid #c3e6cb`, borderRadius:"14px", padding:"2.5rem", textAlign:"center"}}>
            <div style={{fontSize:"2rem", marginBottom:"0.75rem"}}>✓</div>
            <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.25rem", fontWeight:"700", color:T.forest, marginBottom:"0.5rem"}}>Thanks — we'll get back to you soon.</h3>
            <p style={{color:T.muted, fontSize:"0.9rem"}}>Keep an eye on your inbox.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"2rem", display:"flex", flexDirection:"column", gap:"1.25rem"}}>
            {/* Honeypot — hidden from real users, traps bots */}
            <input
              type="text"
              name="website"
              value={form.honeypot}
              onChange={e=>setForm({...form, honeypot:e.target.value})}
              tabIndex={-1}
              autoComplete="off"
              style={{position:"absolute", opacity:0, pointerEvents:"none", height:0, width:0}}
              aria-hidden="true"
            />
            <div>
              <label style={labelStyle}>Name</label>
              <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Your name" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email <span style={{color:T.danger}}>*</span></label>
              <input type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="you@example.com" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Question or Message <span style={{color:T.danger}}>*</span></label>
              <textarea required rows={5} value={form.message} onChange={e=>setForm({...form,message:e.target.value})} placeholder="What's on your mind?" style={{...inputStyle, resize:"vertical", lineHeight:"1.6"}} />
            </div>
            {status === 'error' && (
              <div style={{background:"#fef0f0", border:`1px solid #f5c6cb`, borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.82rem", color:T.danger}}>
                ⚠ {errorMsg}
              </div>
            )}
            <button type="submit" disabled={status==='loading' || !form.email || !form.message} style={{...filledBtn, padding:"0.875rem", fontSize:"0.95rem", opacity:(!form.email || !form.message || status==='loading')?0.6:1}}>
              {status === 'loading' ? 'Sending…' : 'Send Message →'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function ContactPage({ navTo }) {
  return (
    <div style={{background:T.parchment, minHeight:"100vh", padding:"3rem 1rem 0"}}>
      <div style={{maxWidth:"640px", margin:"0 auto", paddingBottom:"1rem"}}>
        <button onClick={()=>navTo("home")} style={{...btnReset, fontSize:"0.875rem", color:T.muted, marginBottom:"2rem", display:"flex", alignItems:"center", gap:"0.375rem"}}>← Back</button>
      </div>
      <ContactSection />
    </div>
  );
}

// ─── BLOG PAGE ────────────────────────────────────────────────────────────────
function BlogPage({ navTo }) {
  const [active, setActive] = useState("All");
  const cats = ["All", ...new Set(BLOG_POSTS.map(p=>p.category))];
  const filtered = active==="All" ? BLOG_POSTS : BLOG_POSTS.filter(p=>p.category===active);

  return (
    <div style={{maxWidth:"1100px", margin:"0 auto", padding:"4rem 2rem"}}>
      <div style={{marginBottom:"3rem"}}>
        <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.75rem", letterSpacing:"0.1em", color:T.gold, marginBottom:"0.75rem"}}>THE HEIROS BLOG</p>
        <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.5rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"1rem"}}>Estate guidance,<br/>in plain English.</h1>
        <p style={{color:T.muted, maxWidth:"480px", lineHeight:"1.65"}}>Practical guides to help executors and families navigate estate settlement with confidence.</p>
      </div>

      <div style={{display:"flex", gap:"0.5rem", marginBottom:"2.5rem", flexWrap:"wrap"}}>
        {cats.map(c=>(
          <button key={c} onClick={()=>setActive(c)} style={{...btnReset, padding:"0.375rem 1rem", borderRadius:"20px", fontSize:"0.8rem", border:`1px solid ${active===c?T.forest:T.border}`, background:active===c?T.forest:"transparent", color:active===c?T.white:T.muted}}>{c}</button>
        ))}
      </div>

      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px, 1fr))", gap:"1.5rem"}}>
        {filtered.map(post=><BlogCard key={post.id} post={post} navTo={navTo} />)}
      </div>
    </div>
  );
}

function BlogCard({ post, navTo }) {
  return (
    <div onClick={()=>navTo("blog-post-"+post.id)} style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"14px", padding:"1.75rem", cursor:"pointer", transition:"box-shadow 0.2s, transform 0.2s", display:"flex", flexDirection:"column", gap:"0.75rem"}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
        <span style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", color:T.gold, background:"#fef9f0", border:`1px solid ${T.tan}`, padding:"0.2rem 0.6rem", borderRadius:"4px"}}>{post.category}</span>
        <span style={{fontSize:"0.75rem", color:T.muted}}>{post.readTime} read</span>
      </div>
      <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.05rem", fontWeight:"600", color:T.charcoal, lineHeight:"1.4"}}>{post.title}</h3>
      <p style={{fontSize:"0.82rem", color:T.muted, lineHeight:"1.6", flex:1}}>{post.excerpt}</p>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:"0.5rem", borderTop:`1px solid ${T.borderLight}`}}>
        <span style={{fontSize:"0.75rem", color:T.muted}}>{post.date}</span>
        <span style={{fontSize:"0.8rem", color:T.gold, fontWeight:"500"}}>Read more →</span>
      </div>
    </div>
  );
}

function BlogPostPage({ navTo, postId }) {
  const post = BLOG_POSTS.find(p => p.id === postId) || BLOG_POSTS[0];
  const body = BLOG_BODIES[post.id] || BLOG_BODIES[1];
  return (
    <div style={{maxWidth:"720px", margin:"0 auto", padding:"4rem 2rem"}}>
      <button onClick={()=>navTo("blog")} style={{...btnReset, color:T.muted, fontSize:"0.875rem", marginBottom:"1.25rem", display:"block"}}>← Back to Blog</button>
      <span style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", color:T.gold}}>{post.category}</span>
      <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.25rem", fontWeight:"700", color:T.forest, lineHeight:"1.2", margin:"0.75rem 0 1rem", letterSpacing:"-0.02em"}}>{post.title}</h1>
      <div style={{display:"flex", gap:"1rem", alignItems:"center", marginBottom:"2.5rem", paddingBottom:"2rem", borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:"0.8rem", color:T.muted}}>{post.author}</span>
        <span style={{color:T.border}}>·</span>
        <span style={{fontSize:"0.8rem", color:T.muted}}>{post.date}</span>
        <span style={{color:T.border}}>·</span>
        <span style={{fontSize:"0.8rem", color:T.muted}}>{post.readTime} read</span>
      </div>
      {body.map((p,i)=>{
        const match = p.match(/^\*\*(.+?)\*\*\n\n([\s\S]*)$/);
        if (match) {
          const [, heading, rest] = match;
          return (
            <div key={i} style={{marginBottom:"1.25rem"}}>
              <p style={{fontSize:"1.05rem", lineHeight:"1.5", color:T.forest, fontWeight:"700", fontStyle:"italic", textDecoration:"underline", textDecorationColor:T.gold, textUnderlineOffset:"4px", marginBottom:"0.5rem"}}>{heading}</p>
              <p style={{fontSize:"0.975rem", lineHeight:"1.8", color:T.charcoal, fontWeight:"400", margin:0}}>{rest}</p>
            </div>
          );
        }
        return (
          <p key={i} style={{fontSize:"0.975rem", lineHeight:"1.8", color:T.charcoal, marginBottom:"1.25rem", fontWeight:"400"}}>{p.replace(/\*\*/g,"")}</p>
        );
      })}
      <div style={{background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"12px", padding:"1.5rem", marginTop:"2rem"}}>
        <p style={{fontFamily:"'Playfair Display', serif", fontWeight:"600", color:T.forest, marginBottom:"0.5rem"}}>Ready to start settling the estate?</p>
        <p style={{fontSize:"0.85rem", color:T.muted, marginBottom:"1rem"}}>HeirOS walks you through every step with state-specific guidance, auto-generated documents, and AI support.</p>
        <button onClick={()=>navTo("signup")} style={{...filledBtn, fontSize:"0.875rem"}}>Start for $299 →</button>
      </div>
    </div>
  );
}

// ─── FAQ PAGE ─────────────────────────────────────────────────────────────────
function FAQPage({ navTo }) {
  const [open, setOpen] = useState(null);
  return (
    <div style={{maxWidth:"760px", margin:"0 auto", padding:"4rem 2rem"}}>
      <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.75rem", letterSpacing:"0.1em", color:T.gold, marginBottom:"0.75rem"}}>FREQUENTLY ASKED QUESTIONS</p>
      <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.5rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.75rem"}}>Your questions, answered.</h1>
      <p style={{color:T.muted, marginBottom:"3rem", lineHeight:"1.65"}}>Everything you need to know about HeirOS and settling an estate. Can't find what you're looking for? <button onClick={()=>navTo("app-ai")} style={{...btnReset, color:T.gold, fontWeight:"500"}}>Ask our AI →</button></p>

      <div style={{display:"flex", flexDirection:"column", gap:"0.75rem"}}>
        {FAQ_ITEMS.map((item,i)=>(
          <div key={i} style={{background:T.white, border:`1px solid ${open===i?T.forest:T.borderLight}`, borderRadius:"12px", overflow:"hidden", transition:"border-color 0.2s"}}>
            <button onClick={()=>setOpen(open===i?null:i)} style={{...btnReset, width:"100%", padding:"1.25rem 1.5rem", display:"flex", justifyContent:"space-between", alignItems:"center", gap:"1rem", textAlign:"left"}}>
              <span style={{fontWeight:"500", color:T.charcoal, fontSize:"0.95rem", lineHeight:"1.4"}}>{item.q}</span>
              <span style={{color:T.forest, fontSize:"1.25rem", flexShrink:0, transform:open===i?"rotate(45deg)":"none", transition:"transform 0.2s"}}>+</span>
            </button>
            {open===i && (
              <div style={{padding:"0 1.5rem 1.25rem", borderTop:`1px solid ${T.borderLight}`}}>
                <p style={{fontSize:"0.875rem", color:T.muted, lineHeight:"1.7", paddingTop:"1rem"}}>{item.a}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"16px", padding:"2.5rem", marginTop:"3rem", textAlign:"center", color:"white"}}>
        <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"600", marginBottom:"0.75rem"}}>Still have questions?</h3>
        <p style={{opacity:0.8, fontSize:"0.9rem", marginBottom:"1.5rem"}}>Our support team typically responds within 1 business day.</p>
        <button onClick={()=>navTo("contact")} style={{...filledBtn, background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color:"white"}}>Send Us a Message →</button>
      </div>
    </div>
  );
}

// ─── PRIVACY POLICY PAGE ──────────────────────────────────────────────────────
function LegalPage({ eyebrow, title, subtitle, children, navTo }) {
  const h2S = {fontFamily:"'Playfair Display', serif", fontSize:"1.2rem", fontWeight:"700", color:T.forest, marginBottom:"0.625rem"};
  const pS  = {fontSize:"0.9rem", color:T.muted, lineHeight:"1.75", marginBottom:"0.625rem"};
  const liS = {fontSize:"0.9rem", color:T.muted, lineHeight:"1.75"};
  return (
    <div style={{background:T.parchment, minHeight:"100vh", padding:"4rem 1rem 6rem"}}>
      <div style={{maxWidth:"760px", margin:"0 auto"}}>
        <button onClick={()=>navTo("home")} style={{...btnReset, fontSize:"0.875rem", color:T.muted, marginBottom:"2rem", display:"flex", alignItems:"center", gap:"0.375rem"}}>← Back</button>
        <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.72rem", letterSpacing:"0.1em", color:T.gold, marginBottom:"0.75rem"}}>{eyebrow}</p>
        <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.25rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.5rem"}}>{title}</h1>
        {subtitle && <p style={{color:T.muted, fontSize:"0.9rem", marginBottom:"3rem", lineHeight:"1.65"}}>{subtitle}</p>}
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"2.5rem 2rem", display:"flex", flexDirection:"column", gap:"2rem"}}>
          {children({ h2S, pS, liS })}
        </div>
        <div style={{background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"12px", padding:"1.25rem 1.5rem", marginTop:"2rem", textAlign:"center"}}>
          <p style={{fontSize:"0.85rem", color:T.muted}}>Questions? Email us at <a href="mailto:support@heiros.estate" style={{color:T.forest, fontWeight:"500"}}>support@heiros.estate</a></p>
        </div>
      </div>
    </div>
  );
}

function TermsPage({ navTo }) {
  return (
    <LegalPage eyebrow="LEGAL" title="Terms of Use" subtitle="Last Updated: July 17, 2026" navTo={navTo}>
      {({ h2S, pS, liS }) => (<>
        <div>
          <h2 style={h2S}>1. Acceptance of Terms</h2>
          <p style={pS}>These Terms of Use ("Terms") govern your access to and use of HeirOS, a platform operated by HeirOS LLC ("HeirOS," "we," "us," or "our"), accessible at heiros.estate (the "Service"). By creating an account, making a payment, or otherwise using the Service, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, do not use the Service.</p>
          <p style={pS}>You must be at least 18 years old to use HeirOS.</p>
        </div>
        <div>
          <h2 style={h2S}>2. Description of Service</h2>
          <p style={pS}>HeirOS provides tools for estate planning ("Planning Ahead") and estate settlement ("Settling an Estate"), including a personal document vault, a settlement checklist and executor playbook, collaborator and permission management, and related document generation and AI assistance features (collectively, the "Service").</p>
        </div>
        <div>
          <h2 style={h2S}>3. No Legal, Financial, or Medical Advice</h2>
          <p style={pS}><strong>HeirOS is not a law firm, and use of the Service does not create an attorney-client relationship.</strong> The Service, including the AI Guide and any generated documents, does not constitute legal advice and is not a substitute for consultation with a licensed attorney in your jurisdiction.</p>
          <p style={pS}>Estate planning laws — including requirements for a will to be valid (such as witnessing, notarization, and formatting) — vary significantly by state and can change over time. <strong>You are solely responsible for ensuring that any document generated through the Service meets the legal requirements of your jurisdiction.</strong> We strongly recommend having any will or estate planning document reviewed by a licensed attorney before relying on it or attempting to execute it.</p>
          <p style={pS}>HeirOS similarly does not provide financial or medical advice. Information provided through the Service is for general informational purposes only.</p>
        </div>
        <div>
          <h2 style={h2S}>4. Account Registration and Security</h2>
          <p style={pS}>You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. You agree to provide accurate information during signup and to keep it up to date. HeirOS does not store your passwords directly and encourages use of a password manager. You are responsible for enabling and maintaining two-factor authentication where offered, and acknowledge that declining to do so may reduce the security of your account and its contents.</p>
        </div>
        <div>
          <h2 style={h2S}>5. Payment and Fees</h2>
          <p style={pS}>Access to the full Service requires a one-time payment as displayed at checkout. Payments are processed by Stripe; HeirOS does not store your full payment card information. All payments are final and non-refundable except as required by law or as HeirOS may agree to in its sole discretion.</p>
        </div>
        <div>
          <h2 style={h2S}>6. The Vault and Your Content</h2>
          <p style={pS}>You retain ownership of all documents, information, and content you upload or enter into the Service ("Your Content"). You grant HeirOS a limited license to store, process, and transmit Your Content solely as necessary to provide the Service (for example, generating documents, enabling access for collaborators and executors you designate, and processing documents through third-party AI services as described in our Privacy Policy).</p>
          <p style={pS}>You are solely responsible for the accuracy of Your Content and for determining who should be granted access to it through the Service's collaborator and permission features.</p>
        </div>
        <div>
          <h2 style={h2S}>7. AI-Generated Content</h2>
          <p style={pS}>Certain features of the Service (including the AI Guide and document analysis tools) use third-party artificial intelligence services to generate or analyze content based on information you provide. <strong>AI-generated content may contain errors, omissions, or inaccuracies and should be independently reviewed before use, especially for any document intended to have legal effect.</strong> HeirOS makes no warranty regarding the accuracy, completeness, or legal sufficiency of AI-generated content.</p>
        </div>
        <div>
          <h2 style={h2S}>8. Executor Access and Emergency Access</h2>
          <p style={pS}>The Service includes features allowing you to designate executors, backup executors, and other collaborators, and to configure automatic or time-based access to your Vault and estate information under circumstances you define (including after a reported death or after a configured response window has elapsed without action). <strong>You are responsible for understanding and correctly configuring these settings.</strong> HeirOS is not responsible for access granted or denied in accordance with your configured settings.</p>
        </div>
        <div>
          <h2 style={h2S}>9. Prohibited Uses</h2>
          <p style={pS}>You agree not to: use the Service for any unlawful purpose; impersonate any person or falsely claim an affiliation; attempt to gain unauthorized access to another user's account or data; interfere with or disrupt the Service; upload malicious code; or use the Service to harass, defraud, or harm any other person, including in connection with an actual death or estate.</p>
        </div>
        <div>
          <h2 style={h2S}>10. Intellectual Property</h2>
          <p style={pS}>The Service, including its design, software, and content (excluding Your Content), is owned by HeirOS and protected by intellectual property laws. You may not copy, modify, distribute, or reverse-engineer the Service except as expressly permitted.</p>
        </div>
        <div>
          <h2 style={h2S}>11. Termination</h2>
          <p style={pS}>You may stop using the Service and delete your account at any time through Account Settings. We may suspend or terminate your access to the Service if you violate these Terms. Upon termination, we will handle Your Content in accordance with our Privacy Policy and applicable law.</p>
        </div>
        <div>
          <h2 style={h2S}>12. Disclaimer of Warranties</h2>
          <p style={{...pS, fontWeight:"500", color:T.charcoal}}>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY DOCUMENT GENERATED THROUGH THE SERVICE WILL BE LEGALLY VALID IN YOUR JURISDICTION.</p>
        </div>
        <div>
          <h2 style={h2S}>13. Limitation of Liability</h2>
          <p style={{...pS, fontWeight:"500", color:T.charcoal}}>TO THE MAXIMUM EXTENT PERMITTED BY LAW, HEIROS AND ITS OPERATORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF DATA, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO HEIROS IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
        </div>
        <div>
          <h2 style={h2S}>14. Indemnification</h2>
          <p style={pS}>You agree to indemnify and hold harmless HeirOS from any claims, damages, or expenses arising from your use of the Service, Your Content, or your violation of these Terms.</p>
        </div>
        <div>
          <h2 style={h2S}>15. Governing Law and Dispute Resolution</h2>
          <p style={pS}>These Terms are governed by the laws of the State of Utah, without regard to conflict of law principles.</p>
        </div>
        <div>
          <h2 style={h2S}>16. Changes to These Terms</h2>
          <p style={pS}>We may update these Terms from time to time. We will notify you of material changes (for example, via email or in-app notice). Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>
        </div>
        <div>
          <h2 style={h2S}>17. Contact</h2>
          <p style={pS}>Questions about these Terms can be sent to <a href="mailto:support@heiros.estate" style={{color:T.forest, fontWeight:"500"}}>support@heiros.estate</a>.</p>
        </div>
      </>)}
    </LegalPage>
  );
}

function PrivacyPage({ navTo }) {
  return (
    <LegalPage eyebrow="LEGAL" title="Privacy Policy" subtitle="Last Updated: July 17, 2026" navTo={navTo}>
      {({ h2S, pS, liS }) => (<>
        <div>
          <h2 style={h2S}>1. Introduction</h2>
          <p style={pS}>This Privacy Policy explains how HeirOS ("we," "us," "our") collects, uses, stores, and shares information when you use heiros.estate (the "Service"). By using the Service, you agree to the practices described here.</p>
        </div>
        <div>
          <h2 style={h2S}>2. Information We Collect</h2>
          <p style={pS}><strong>Account Information:</strong> name, email address, and password (HeirOS does not itself store your password in plain text; authentication is handled securely via our infrastructure provider).</p>
          <p style={pS}><strong>Estate and Vault Information:</strong> information you choose to enter or upload, which may include legal documents (wills, deeds, insurance policies), financial account details, digital asset information, medical information, personal messages, contact information for family members and executors, and uploaded files.</p>
          <p style={pS}><strong>Payment Information:</strong> payments are processed by Stripe. HeirOS does not directly store your full card number.</p>
          <p style={pS}><strong>Usage Information:</strong> general information about how you interact with the Service, for the purpose of improving the product and diagnosing issues.</p>
        </div>
        <div>
          <h2 style={h2S}>3. How We Use Information</h2>
          <p style={pS}>We use the information you provide to: operate and provide the Service (including the Vault and settlement checklist); enable the collaborator, executor, and emergency access features you configure; send transactional emails (invitations, access requests, account notifications); process payments; provide customer support; and improve and secure the Service.</p>
          <p style={pS}><strong>We do not sell your personal information.</strong></p>
        </div>
        <div>
          <h2 style={h2S}>4. AI Processing Disclosure</h2>
          <p style={pS}>Certain features of the Service — including the AI Guide and document analysis for uploaded documents — send relevant information you provide to Anthropic's Claude API for the purpose of generating or analyzing content. This processing happens server-side through our own infrastructure. Under Anthropic's Commercial Terms of Service, Anthropic may not train its models on data submitted through the API. You should be aware that any information you submit to AI-powered features is processed by this third-party service.</p>
        </div>
        <div>
          <h2 style={h2S}>5. How We Share Information</h2>
          <ul style={{paddingLeft:"1.25rem", display:"flex", flexDirection:"column", gap:"0.5rem", marginTop:"0.25rem"}}>
            <li style={liS}><strong>With people you invite:</strong> collaborators, executors, and backup executors you explicitly invite can access the categories of information you grant them permission to see.</li>
            <li style={liS}><strong>Service providers:</strong> we use Supabase (database, authentication, and file storage), Stripe (payment processing), Resend (transactional email), and Anthropic (AI processing). These providers process data on our behalf and are bound by their own privacy and security obligations.</li>
            <li style={liS}><strong>Legal requirements:</strong> we may disclose information if required by law, subpoena, or to protect the rights, property, or safety of HeirOS, our users, or others.</li>
            <li style={liS}><strong>Business transfers:</strong> if HeirOS is involved in a merger, acquisition, or sale of assets, information may be transferred as part of that transaction, subject to standard protections.</li>
          </ul>
          <p style={{...pS, marginTop:"0.75rem"}}>We do not share your Vault contents with anyone you have not explicitly granted access to.</p>
        </div>
        <div>
          <h2 style={h2S}>6. Data Storage and Security</h2>
          <p style={pS}>Your information is stored using Supabase, with Row Level Security policies restricting access to your own data and to collaborators you have explicitly authorized. Uploaded files are stored in encrypted storage with access-controlled signed URLs. While we take reasonable measures to protect your information, no method of electronic storage or transmission is 100% secure.</p>
          <p style={pS}><strong>HeirOS does not store your passwords for other services or accounts</strong> — we encourage use of a dedicated password manager for that purpose.</p>
        </div>
        <div>
          <h2 style={h2S}>7. Data Retention</h2>
          <p style={pS}>We retain your information for as long as your account is active or as needed to provide the Service. If you delete your account, we will delete or anonymize your personal information within 30 days, except where retention is required by law (for example, payment records for tax purposes).</p>
        </div>
        <div>
          <h2 style={h2S}>8. Your Rights and Choices</h2>
          <p style={pS}>You may access, update, or delete most of your information directly through Account Settings. You may request deletion of your account and associated data at any time. Depending on your state of residence, you may have additional rights under applicable privacy law. To exercise these rights, contact us at <a href="mailto:support@heiros.estate" style={{color:T.forest, fontWeight:"500"}}>support@heiros.estate</a>.</p>
        </div>
        <div>
          <h2 style={h2S}>9. Cookies and Tracking</h2>
          <p style={pS}>We do not use analytics or tracking cookies within the authenticated portions of the Service. Our public marketing pages may use limited, privacy-conscious analytics tools to understand general visitor traffic. We may use essential cookies or local/session storage necessary for the Service to function, such as maintaining your login session. HeirOS does not permit third-party advertising trackers on any part of the Service.</p>
        </div>
        <div>
          <h2 style={h2S}>10. Children's Privacy</h2>
          <p style={pS}>The Service is not directed to individuals under 18, and we do not knowingly collect information from anyone under 18.</p>
        </div>
        <div>
          <h2 style={h2S}>11. Changes to This Policy</h2>
          <p style={pS}>We may update this Privacy Policy from time to time. We will notify you of material changes via email or in-app notice. Continued use of the Service after changes take effect constitutes acceptance of the revised policy.</p>
        </div>
        <div>
          <h2 style={h2S}>12. Contact Us</h2>
          <p style={pS}>Questions about this Privacy Policy can be sent to <a href="mailto:support@heiros.estate" style={{color:T.forest, fontWeight:"500"}}>support@heiros.estate</a>.</p>
        </div>
      </>)}
    </LegalPage>
  );
}

// ─── DOCUMENTS PAGE (public info) ─────────────────────────────────────────────
function DocumentsPage({ navTo, estate }) {
  // docKey maps to a key in DOC_TEMPLATES — null means coming soon
  const docs = [
    { icon:"🏛", title:"Social Security Administration Notification",       docKey:"ssa",      desc:"Notify the Social Security Administration (SSA) of the death, stop benefit payments, and inquire about survivor benefits.",                         who:"Executor",           when:"Within 1 week",      required:true  },
    { icon:"🏦", title:"Bank & Financial Institution Notice", docKey:"bank",     desc:"Notify all banks and investment accounts to freeze accounts and provide balances as of date of death.",            who:"Executor",           when:"Within 2 weeks",     required:true  },
    { icon:"💼", title:"Employer Notification Letter",        docKey:"employer", desc:"Notify the employer to cease payroll and initiate claims for life insurance, 401(k), and final wages.",            who:"Executor",           when:"Within 2 weeks",     required:true  },
    { icon:"📋", title:"Internal Revenue Service (IRS) Final Return Notice",             docKey:"irs",      desc:"Notifies the IRS of the taxpayer's death and identifies the estate representative.",                               who:"Executor/Tax Prep",  when:"By April 15",        required:true  },
    { icon:"⚖️", title:"Probate Court Petition",              docKey:"probate",  desc:"Petition to open probate and be appointed as administrator of the estate.",                                        who:"Executor/Attorney",  when:"Within 30 days",     required:false },
    { icon:"📰", title:"Creditor Notice Letter",              docKey:"creditor", desc:"Formal notice alerting creditors of the death and the deadline to submit claims against the estate.",              who:"Executor",           when:"Within 30–60 days",  required:true  },
    { icon:"🏡", title:"Property Title Transfer",             docKey:null,       desc:"Documentation to transfer real estate titles to heirs or beneficiaries through probate.",                          who:"Executor/Attorney",  when:"After probate",      required:false },
    { icon:"🔒", title:"Social Media Memorial Request",       docKey:null,       desc:"Requests to memorialize or delete Facebook, Instagram, and Google accounts.",                                     who:"Family Member",      when:"Within 30 days",     required:false },
  ];

  const handleGenerate = (doc) => {
    if (!doc.docKey) return; // coming soon — button is disabled
    if (estate) {
      // Already signed up: go straight to the app doc generator with this doc pre-selected
      navTo("app-docs-" + doc.docKey);
    } else {
      navTo("signup");
    }
  };

  return (
    <div style={{maxWidth:"1100px", margin:"0 auto", padding:"4rem 2rem"}}>
      <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.75rem", letterSpacing:"0.1em", color:T.gold, marginBottom:"0.75rem"}}>DOCUMENT CENTER</p>
      <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.5rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"1rem"}}>Estate documents,<br/>pre-filled and ready to send.</h1>
      <p style={{color:T.muted, marginBottom:"1rem", maxWidth:"560px", lineHeight:"1.65"}}>HeirOS generates the letters and forms you need — automatically populated with your estate details. No legal research. No blank templates to fumble through.</p>

      {!estate && (
        <div style={{background:"#fefbf0", border:`1px solid ${T.tan}`, borderRadius:"10px", padding:"1rem 1.25rem", marginBottom:"2.5rem", fontSize:"0.85rem", color:T.goldDark, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"0.75rem"}}>
          <span>⚡ Sign up to unlock auto-filled documents with your estate's information.</span>
          <button onClick={()=>navTo("signup")} style={{...filledBtn, padding:"0.5rem 1rem", fontSize:"0.8rem"}}>Start for $299 →</button>
        </div>
      )}

      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:"1rem", marginBottom:"3rem"}}>
        {docs.map((d,i)=>(
          <div key={i} style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"12px", padding:"1.5rem", display:"flex", flexDirection:"column", gap:"0.75rem", opacity:d.docKey===null?0.65:1}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start"}}>
              <span style={{fontSize:"1.5rem"}}>{d.icon}</span>
              <div style={{display:"flex", gap:"0.375rem", alignItems:"center"}}>
                {d.docKey===null && <span style={{fontSize:"0.6rem", background:T.parchment, color:T.muted, padding:"0.2rem 0.5rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace"}}>Coming Soon</span>}
                {d.required
                  ? <span style={{fontSize:"0.65rem", background:"#fef0f0", color:T.danger, padding:"0.2rem 0.5rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace"}}>Required</span>
                  : <span style={{fontSize:"0.65rem", background:T.parchment, color:T.muted, padding:"0.2rem 0.5rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace"}}>Situational</span>}
              </div>
            </div>
            <h3 style={{fontWeight:"600", color:T.charcoal, fontSize:"0.95rem", lineHeight:"1.3"}}>{d.title}</h3>
            <p style={{fontSize:"0.8rem", color:T.muted, lineHeight:"1.55", flex:1}}>{d.desc}</p>
            <div style={{fontSize:"0.72rem", color:T.muted, borderTop:`1px solid ${T.borderLight}`, paddingTop:"0.625rem", display:"flex", justifyContent:"space-between"}}>
              <span>By: {d.who}</span>
              <span>⏱ {d.when}</span>
            </div>
            <button
              onClick={()=>handleGenerate(d)}
              disabled={d.docKey===null}
              style={{...outlineBtn, fontSize:"0.8rem", padding:"0.5rem 0.875rem", color:d.docKey===null?T.muted:T.forest, borderColor:d.docKey===null?T.border:T.forest, cursor:d.docKey===null?"default":"pointer"}}>
              {estate ? "Generate →" : "Unlock →"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── HELPFUL LINKS PAGE ───────────────────────────────────────────────────────
function LinksPage({ navTo }) {
  return (
    <div style={{maxWidth:"1000px", margin:"0 auto", padding:"4rem 2rem"}}>
      <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.75rem", letterSpacing:"0.1em", color:T.gold, marginBottom:"0.75rem"}}>RESOURCE CENTER</p>
      <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.5rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"1rem"}}>Helpful links &<br/>trusted resources.</h1>
      <p style={{color:T.muted, marginBottom:"3rem", maxWidth:"540px", lineHeight:"1.65"}}>Official government resources, legal references, and support organizations — curated and verified by the HeirOS team.</p>

      {HELPFUL_LINKS.map((section,i)=>(
        <div key={i} style={{marginBottom:"3rem"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.35rem", fontWeight:"600", color:T.forest, marginBottom:"1.25rem", paddingBottom:"0.75rem", borderBottom:`2px solid ${T.tan}`}}>{section.category}</h2>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:"1rem"}}>
            {section.links.map((link,j)=>(
              <a key={j} href={link.url} target="_blank" rel="noopener noreferrer" style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"12px", padding:"1.25rem 1.5rem", textDecoration:"none", display:"flex", flexDirection:"column", gap:"0.5rem", transition:"box-shadow 0.2s, border-color 0.2s"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start"}}>
                  <span style={{fontSize:"0.9rem", fontWeight:"600", color:T.forest, lineHeight:"1.3", flex:1}}>{link.title}</span>
                  <span style={{color:T.gold, marginLeft:"0.5rem", flexShrink:0}}>↗</span>
                </div>
                <p style={{fontSize:"0.8rem", color:T.muted, lineHeight:"1.55"}}>{link.desc}</p>
              </a>
            ))}
          </div>
        </div>
      ))}

      <div style={{background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"14px", padding:"2rem", textAlign:"center"}}>
        <p style={{fontFamily:"'Playfair Display', serif", fontWeight:"600", color:T.forest, fontSize:"1.1rem", marginBottom:"0.5rem"}}>Need more personalized guidance?</p>
        <p style={{fontSize:"0.85rem", color:T.muted, marginBottom:"1.25rem"}}>Our AI estate guide answers questions specific to your state and situation.</p>
        <button onClick={()=>navTo("signup")} style={{...filledBtn, fontSize:"0.875rem"}}>Start with HeirOS →</button>
      </div>
    </div>
  );
}

// ─── PRICING PAGE ─────────────────────────────────────────────────────────────
function PricingPage({ navTo }) {
  return (
    <div style={{maxWidth:"900px", margin:"0 auto", padding:"4rem 2rem"}}>
      <div style={{textAlign:"center", marginBottom:"3.5rem"}}>
        <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.75rem", letterSpacing:"0.1em", color:T.gold, marginBottom:"0.75rem"}}>SIMPLE, HONEST PRICING</p>
        <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2.5rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"1rem"}}>One-time payment. Everything included.</h1>
        <p style={{color:T.muted, maxWidth:"460px", margin:"0 auto", lineHeight:"1.65"}}>No percentage of the estate. No hourly billing. No upsells you have to decline.</p>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))", gap:"1.5rem", marginBottom:"3rem"}}>

        {/* $299 Individual */}
        <div style={{background:T.forest, border:`1px solid ${T.forest}`, borderRadius:"16px", padding:"2rem", color:T.white, position:"relative"}}>
          <div style={{position:"absolute", top:"-12px", left:"50%", transform:"translateX(-50%)", background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color:"white", fontSize:"0.7rem", padding:"0.3rem 0.875rem", borderRadius:"20px", fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", whiteSpace:"nowrap"}}>RECOMMENDED</div>
          <div style={{marginBottom:"1.5rem"}}>
            <div style={{fontSize:"0.8rem", opacity:0.7, marginBottom:"0.25rem", fontFamily:"'DM Mono', monospace"}}>HEIROS</div>
            <div style={{fontFamily:"'Playfair Display', serif", fontSize:"2.25rem", fontWeight:"700", letterSpacing:"-0.02em"}}>$299</div>
            <div style={{fontSize:"0.8rem", opacity:0.6}}>One-time per estate</div>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"0.625rem", marginBottom:"2rem"}}>
            {["Complete guided checklist (all 50 states)","Auto-generated legal letters","Artificial Intelligence (AI) estate guide (unlimited questions)","Family collaboration dashboard","Document generator","Email support","Attorney referral network"].map((f,j)=>(
              <div key={j} style={{display:"flex", gap:"0.625rem", alignItems:"flex-start", fontSize:"0.85rem", opacity:0.9}}>
                <span style={{color:T.goldLight, flexShrink:0}}>✓</span>
                {f}
              </div>
            ))}
          </div>
          <button onClick={()=>navTo("signup")} style={{...filledBtn, background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color:"white", width:"100%", padding:"0.875rem"}}>Get Started →</button>
        </div>

        {/* $399 Couple's Bundle */}
        <div style={{background:T.white, border:`2px solid ${T.gold}`, borderRadius:"16px", padding:"2rem", color:T.charcoal, position:"relative"}}>
          <div style={{position:"absolute", top:"-12px", left:"50%", transform:"translateX(-50%)", background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color:"white", fontSize:"0.7rem", padding:"0.3rem 0.875rem", borderRadius:"20px", fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", whiteSpace:"nowrap"}}>BEST VALUE</div>
          <div style={{marginBottom:"1.5rem"}}>
            <div style={{fontSize:"0.8rem", opacity:0.7, marginBottom:"0.25rem", fontFamily:"'DM Mono', monospace"}}>COUPLE'S BUNDLE</div>
            <div style={{fontFamily:"'Playfair Display', serif", fontSize:"2.25rem", fontWeight:"700", letterSpacing:"-0.02em", color:T.forest}}>$399</div>
            <div style={{fontSize:"0.8rem", opacity:0.6}}>Two linked estates — one payment</div>
            <div style={{fontSize:"0.75rem", color:T.muted, marginTop:"0.2rem"}}>under $200 / person</div>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"0.625rem", marginBottom:"1rem"}}>
            {["Complete guided checklist (all 50 states)","Auto-generated legal letters","Artificial Intelligence (AI) estate guide (unlimited questions)","Family collaboration dashboard","Document generator","Email support","Attorney referral network"].map((f,j)=>(
              <div key={j} style={{display:"flex", gap:"0.625rem", alignItems:"flex-start", fontSize:"0.85rem", opacity:0.75}}>
                <span style={{color:T.success, flexShrink:0}}>✓</span>
                {f}
              </div>
            ))}
          </div>
          <div style={{fontSize:"0.7rem", fontWeight:"600", color:T.forest, margin:"0.75rem 0 0.5rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.05em"}}>— PLUS FOR BOTH SPOUSES:</div>
          <div style={{display:"flex", flexDirection:"column", gap:"0.625rem", marginBottom:"2rem"}}>
            {["Both spouses get their own full HeirOS account","Estates are pre-linked from day one","Optionally name each other as Executor, Medical POA & Power of Attorney","Joint vault sharing available"].map((f,j)=>(
              <div key={j} style={{display:"flex", gap:"0.625rem", alignItems:"flex-start", fontSize:"0.85rem", opacity:0.75}}>
                <span style={{color:T.gold, flexShrink:0}}>✦</span>
                {f}
              </div>
            ))}
          </div>
          <button onClick={()=>navTo("signup")} style={{...filledBtn, background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color:"white", width:"100%", padding:"0.875rem"}}>Get the Couple's Bundle →</button>
        </div>

      </div>

      <div style={{background:T.parchment, borderRadius:"14px", padding:"2rem", border:`1px solid ${T.border}`}}>
        <h3 style={{fontFamily:"'Playfair Display', serif", fontWeight:"600", color:T.forest, marginBottom:"1.25rem", fontSize:"1.1rem"}}>Frequently asked pricing questions</h3>
        {[
          { q:"Is this really a flat fee?", a:"Yes. Both plans are one-time flat fees — $299 for a single estate, $399 for the Couple's Bundle (two linked estates). No percentage of the estate value, no hourly billing, and no surprise charges. The only exception: attorney fees through our referral network, which are separate and disclosed upfront." },
          { q:"What if I need an attorney?", a:"We'll tell you honestly when that is. For the 15–20% of estates that genuinely need legal representation, we connect you with vetted estate attorneys at flat rates — typically far below standard hourly billing." },
          { q:"Can I get a refund?", a:"Yes. If you start HeirOS and find it doesn't meet your needs within 14 days, we'll refund your purchase in full, no questions asked." },
        ].map((item,i)=>(
          <div key={i} style={{marginBottom:"1.25rem", paddingBottom:"1.25rem", borderBottom:i<2?`1px solid ${T.border}`:"none"}}>
            <p style={{fontWeight:"500", color:T.charcoal, marginBottom:"0.375rem", fontSize:"0.9rem"}}>{item.q}</p>
            <p style={{fontSize:"0.85rem", color:T.muted, lineHeight:"1.6"}}>{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SIGNUP FLOW ──────────────────────────────────────────────────────────────
function SignupFlow({ navTo, onSignIn, inviteParams }) {
  const [mode, setMode] = useState(null); // null | "owner" | "executor" | "invited"

  // ── Non-invited account creation state ────────────────────────────────────
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // ── Invited 3-step flow state ─────────────────────────────────────────────
  const [invStep, setInvStep] = useState(1); // 1 | 2 | 3
  const [invEmail, setInvEmail] = useState("");
  const [invCode, setInvCode] = useState("");
  const [invLoading, setInvLoading] = useState(false);
  const [invError, setInvError] = useState("");
  const [foundInvitation, setFoundInvitation] = useState(null);
  const [invPassword, setInvPassword] = useState("");
  const [invConfirm, setInvConfirm] = useState("");
  const [invTermsAccepted, setInvTermsAccepted] = useState(false);
  // Sign-in fallback — shown when email is already registered
  const [invitedSignInMode, setInvitedSignInMode] = useState(false);
  const [invSignInPassword, setInvSignInPassword] = useState("");

  // ── On mount: if inviteParams passed from email deep link, pre-fill and auto-lookup ──
  useEffect(() => {
    // Couple-signup ($399 bundle): spouse 2 is always an owner — skip the chooser entirely.
    if (localStorage.getItem('heiros_couple_signup') === 'true') {
      setMode("owner");
      return;
    }
    if (!inviteParams?.code || !inviteParams?.email) return;
    setMode("invited");
    setInvEmail(inviteParams.email);
    setInvCode(inviteParams.code);
    // Auto-run the lookup so user lands directly on Step 2 (confirmation)
    setInvLoading(true);
    findInvitationByCode(inviteParams.email, inviteParams.code).then(({ data, error: err }) => {
      setInvLoading(false);
      if (err || !data) {
        setInvError("We couldn't find an invitation for this email and code. Please check and try again.");
      } else {
        setFoundInvitation(data);
        setInvStep(2);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Rate limiting helpers ─────────────────────────────────────────────────
  const checkRateLimit = () => {
    const lockKey = 'heiros_invite_locked_until';
    const lockedUntil = localStorage.getItem(lockKey);
    if (lockedUntil && new Date() < new Date(lockedUntil)) {
      const minutesLeft = Math.ceil((new Date(lockedUntil) - new Date()) / 60000);
      return { allowed: false, minutesLeft };
    }
    return { allowed: true };
  };

  const recordFailedAttempt = () => {
    const key = 'heiros_invite_attempts';
    const lockKey = 'heiros_invite_locked_until';
    const attempts = JSON.parse(localStorage.getItem(key) || '[]');
    const now = new Date();
    const recentAttempts = attempts.filter(t => new Date(t) > new Date(now - 60 * 60 * 1000));
    recentAttempts.push(now.toISOString());
    localStorage.setItem(key, JSON.stringify(recentAttempts));
    if (recentAttempts.length >= 5) {
      const lockUntil = new Date(now.getTime() + 60 * 60 * 1000);
      localStorage.setItem(lockKey, lockUntil.toISOString());
      localStorage.removeItem(key);
      return { locked: true, minutesLeft: 60 };
    }
    return { locked: false, attemptsLeft: 5 - recentAttempts.length };
  };

  const clearFailedAttempts = () => {
    localStorage.removeItem('heiros_invite_attempts');
    localStorage.removeItem('heiros_invite_locked_until');
  };

  // ── Non-invited account creation ──────────────────────────────────────────
  const handleCreate = async () => {
    setError("");
    if (!email.trim()) { setError("Please enter your email address."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (!termsAccepted) { setError("Please agree to the Terms of Use and Privacy Policy to continue."); return; }
    setLoading(true);
    try {
      const { data, error: err } = await signUp(email.trim(), password);
      if (err) { setError(err.message); return; }
      if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) { setError("An account with this email already exists. Please sign in instead."); return; }
      if (data?.user) {
        if (data.session) {
          const { data: prof } = await updateProfile(data.user.id, { signup_mode: mode, terms_accepted_at: new Date().toISOString() });
          onSignIn(data.user, prof || null);
        } else {
          setAwaitingConfirmation(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Invited step 1: find invitation by code ───────────────────────────────
  const handleFindInvitation = async () => {
    setInvError("");
    if (!invEmail.trim()) { setInvError("Please enter your email address."); return; }
    if (!invCode.trim()) { setInvError("Please enter your invite code."); return; }
    const rateLimit = checkRateLimit();
    if (!rateLimit.allowed) {
      setInvError(`Too many failed attempts. Please try again in ${rateLimit.minutesLeft} minute${rateLimit.minutesLeft !== 1 ? "s" : ""}.`);
      return;
    }
    setInvLoading(true);
    const { data, error: err } = await findInvitationByCode(invEmail.trim(), invCode.trim());
    if (err || !data) {
      const attempt = recordFailedAttempt();
      if (attempt.locked) {
        setInvError("Too many failed attempts. You are locked out for 1 hour.");
      } else {
        setInvError(`No invitation found. Check your email and code. ${attempt.attemptsLeft} attempt${attempt.attemptsLeft !== 1 ? "s" : ""} remaining.`);
      }
      setInvLoading(false);
      return;
    }
    clearFailedAttempts();
    setFoundInvitation(data);
    setInvStep(2);
    setInvLoading(false);
  };

  // ── Invited step 3: create account ───────────────────────────────────────
  const handleCreateInvited = async () => {
    setInvError("");
    if (invPassword.length < 8) { setInvError("Password must be at least 8 characters."); return; }
    if (invPassword !== invConfirm) { setInvError("Passwords do not match."); return; }
    if (!invTermsAccepted) { setInvError("Please agree to the Terms of Use and Privacy Policy to continue."); return; }
    setInvLoading(true);
    try {
      const { data, error: err } = await supabase.auth.signUp({
        email: invEmail.trim(),
        password: invPassword,
      });
      if (err) { setInvError(err.message); return; }
      if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        // Email already registered — switch to sign-in form instead of dead-end error
        setInvitedSignInMode(true);
        return;
      }
      if (data?.user) {
        // ── Couple-signup safety net ────────────────────────────────────────────
        // A couple-signup invitee should NEVER be processed as an invited guest.
        // Their account must be created as an owner so handleOnboardingComplete
        // (and claimPreCreatedEstate) can run. This guard fires if they somehow
        // reached this form via a SpouseSetupScreen invite link rather than via
        // the ?couple_code= URL (which our mount-effect now intercepts first).
        const isCoupleSignup = localStorage.getItem('heiros_couple_signup') === 'true';
        if (isCoupleSignup) {
          if (data.session) {
            const { data: prof } = await updateProfile(data.user.id, {
              signup_mode: 'owner',
              terms_accepted_at: new Date().toISOString(),
            });
            // Claim the pre-created estate immediately so the profile gets
            // has_paid, own_estate_id, and spouse_setup_done set in the DB.
            // handleOnboardingComplete will call it again (harmlessly — the
            // finder checks user_id IS NULL and will find nothing the second
            // time) and will also create the role invitations and call saveEstate.
            const spouseEstateIdForClaim = localStorage.getItem('heiros_couple_inviter_estate_id') || undefined;
            await claimPreCreatedEstate(data.user.id, invEmail.trim(), spouseEstateIdForClaim);
            onSignIn(data.user, prof || null);
          } else {
            // Email confirmation pending — pre-write owner profile so the gate
            // shows Onboarding (not payment wall) after confirmation.
            try {
              await supabase.from('profiles').upsert(
                { id: data.user.id, signup_mode: 'owner', terms_accepted_at: new Date().toISOString() },
                { onConflict: 'id' },
              );
            } catch (e) {
              console.warn('[HeirOS] couple-signup pre-confirm profile upsert failed:', e);
            }
            setAwaitingConfirmation(true);
          }
          return;
        }
        // ── Normal invited path ─────────────────────────────────────────────────
        const updates = {
          signup_mode: 'invited',
          has_paid: true,
          onboarding_complete: true,
          invited_estate_id: foundInvitation.estate_id,
          invited_role: foundInvitation.role,
        };
        // Set localStorage flags immediately — before any async ops or navigation.
        // The profile loading effect reads these and patches the profile inline,
        // so there is never a render cycle where the payment wall can appear.
        localStorage.setItem('heiros_is_invited', 'true');
        localStorage.setItem('heiros_invited_estate_id', foundInvitation.estate_id);

        if (data.session) {
          // Session available immediately — apply profile updates now
          await acceptInvitation(foundInvitation.id, data.user.id);
          const { data: prof } = await updateProfile(data.user.id, updates);
          // Pass optimistic profile if DB didn't return one (row may not exist yet)
          onSignIn(data.user, prof || { ...updates, id: data.user.id });
        } else {
          // Email confirmation required.
          // Write profile updates directly to Supabase using upsert — this works
          // even before confirmation since the auth user row exists.
          try {
            // Only write if profile doesn't already exist with a signup_mode
            // to prevent accidentally overwriting an existing owner profile
            // Always upsert — the auth trigger creates a blank profile row
            // immediately, so we can't use existence as a guard. Instead we
            // check that the user ID matches the invited email to prevent
            // accidentally overwriting a real owner profile.
            await supabase.from('profiles').upsert({
                id: data.user.id,
                signup_mode: 'invited',
                has_paid: true,
                onboarding_complete: true,
                invited_estate_id: foundInvitation.estate_id,
                invited_role: foundInvitation.role,
              }, { onConflict: 'id' });
          } catch (e) {
            console.warn('[HeirOS] pre-confirm profile upsert failed:', e);
          }
          setAwaitingConfirmation(true);
        }
      }
    } finally {
      setInvLoading(false);
    }
  };

  // ── Sign in and accept invitation (for already-registered email) ─────────
  const handleSignInAndAccept = async () => {
    setInvError("");
    if (!invSignInPassword) { setInvError("Please enter your password."); return; }
    setInvLoading(true);
    try {
      const { data, error: err } = await supabase.auth.signInWithPassword({ email: invEmail.trim(), password: invSignInPassword });
      if (err) { setInvError(err.message); return; }
      if (!data?.user) { setInvError("Sign-in failed. Please try again."); return; }
      const userId = data.user.id;
      // Accept the invitation for this user
      await supabase.from('invitations').update({
        status: 'accepted',
        accepted_by: userId,
        accepted_at: new Date().toISOString(),
      }).eq('id', foundInvitation.id);
      // Load profile FIRST — before touching localStorage — so we can check owner status
      const { data: existingProf } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
      const isExistingOwner = existingProf?.signup_mode === 'owner' || !!existingProf?.own_estate_id;
      // Couple-signup safety net: same as handleCreateInvited — never let a couple-signup
      // invitee be recorded as an invited guest, even on the sign-in-and-accept path.
      const isCoupleSignupSignIn = localStorage.getItem('heiros_couple_signup') === 'true';
      if (isCoupleSignupSignIn) {
        // They're an owner claiming a pre-created estate, not a collaborator joining someone else's.
        const profileUpdates: Record<string, unknown> = { signup_mode: 'owner' };
        if (!existingProf?.own_estate_id) {
          // Only claim if not already done — claimPreCreatedEstate is idempotent on the estate
          // side but profile own_estate_id may already be set if this is a retry.
          const spouseEstateIdForClaim = localStorage.getItem('heiros_couple_inviter_estate_id') || undefined;
          await claimPreCreatedEstate(userId, invEmail.trim(), spouseEstateIdForClaim);
        }
        const { data: updatedProf } = await supabase.from('profiles').update(profileUpdates).eq('id', userId).select().maybeSingle();
        onSignIn(data.user, updatedProf || existingProf || null);
        return;
      }
      // Safety-net localStorage flags — only for genuinely invited (non-owner) accounts
      if (!isExistingOwner) {
        localStorage.setItem('heiros_is_invited', 'true');
        localStorage.setItem('heiros_invited_estate_id', foundInvitation.estate_id);
      }
      const profileUpdates = { has_paid: true, onboarding_complete: true };
      // Only overwrite signup_mode / invited fields for non-owner accounts
      if (!isExistingOwner) {
        profileUpdates.signup_mode = 'invited';
        profileUpdates.invited_estate_id = foundInvitation.estate_id;
        profileUpdates.invited_role = foundInvitation.role;
      }
      const { data: updatedProf } = await supabase.from('profiles').update(profileUpdates).eq('id', userId).select().maybeSingle();
      // Pass the accepted estate_id as a third argument so the main App can immediately
      // switch to that estate and navigate — needed for existing owners whose signup_mode
      // never changes to 'invited', so the normal post-sign-in navigation branch is a no-op.
      onSignIn(data.user, updatedProf || existingProf || null, foundInvitation.estate_id);
    } finally {
      setInvLoading(false);
    }
  };

  // ── Awaiting email confirmation ────────────────────────────────────────────
  if (awaitingConfirmation) {
    return (
      <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"3rem 2.5rem", width:"100%", maxWidth:"440px", boxShadow:"0 8px 48px rgba(0,0,0,0.08)", textAlign:"center"}}>
          <div style={{fontSize:"3rem", marginBottom:"1.25rem"}}>📬</div>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.6rem", fontWeight:"700", color:T.forest, marginBottom:"0.75rem"}}>Check your email</h2>
          <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.65", marginBottom:"0.5rem"}}>
            We sent a confirmation link to <strong>{email || invEmail}</strong>.
          </p>
          <p style={{color:T.muted, fontSize:"0.875rem", lineHeight:"1.6"}}>
            Click the link in that email to activate your account.
          </p>
        </div>
      </div>
    );
  }

  // ── Mode chooser ───────────────────────────────────────────────────────────
  if (!mode) {
    const cards = [
      { id:"owner",    icon:"📋", title:"I'm planning ahead", sub:"Set up your estate plan, will, and executor instructions while you have time." },
      { id:"executor", icon:"⚖️",  title:"A loved one has passed", sub:"Get a guided checklist and tools to settle their estate step by step." },
    ];
    return (
      <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
        <div style={{width:"100%", maxWidth:"520px"}}>
          <div style={{textAlign:"center", marginBottom:"2.5rem"}}>
            <div style={{width:"56px", height:"56px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"14px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.25rem", color:"white", fontSize:"1.5rem", fontFamily:"'Playfair Display', serif", fontWeight:"700"}}>H</div>
            <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.5rem"}}>Get started with HeirOS</h1>
            <p style={{color:T.muted, fontSize:"0.95rem"}}>How can we help you today?</p>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
            {cards.map(c => (
              <button key={c.id} onClick={() => setMode(c.id)} style={{...btnReset, background:T.white, border:`2px solid ${T.border}`, borderRadius:"14px", padding:"1.25rem 1.5rem", textAlign:"left", cursor:"pointer"}}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.forest; e.currentTarget.style.boxShadow = `0 4px 20px rgba(44,74,62,0.10)`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
                <div style={{display:"flex", alignItems:"center", gap:"1rem"}}>
                  <span style={{fontSize:"1.75rem"}}>{c.icon}</span>
                  <div>
                    <div style={{fontWeight:"600", color:T.charcoal, marginBottom:"0.25rem"}}>{c.title}</div>
                    <div style={{fontSize:"0.82rem", color:T.muted, lineHeight:"1.5"}}>{c.sub}</div>
                  </div>
                  <span style={{marginLeft:"auto", color:T.muted, fontSize:"1.1rem"}}>›</span>
                </div>
              </button>
            ))}
          </div>
          <p style={{textAlign:"center", marginTop:"1.75rem", fontSize:"0.82rem", color:T.muted}}>Already have an account?{" "}<button onClick={() => navTo("login")} style={{...btnReset, color:T.forest, fontWeight:"600", textDecoration:"underline"}}>Sign in</button></p>
        </div>
      </div>
    );
  }

  // ── Invited flow ───────────────────────────────────────────────────────────
  if (mode === "invited") {
    const rateLimitCheck = checkRateLimit();
    const isLocked = !rateLimitCheck.allowed;

    // Step 1 — Find invitation
    if (invStep === 1) return (
      <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"2.5rem 2rem", width:"100%", maxWidth:"440px", boxShadow:"0 8px 48px rgba(0,0,0,0.08)"}}>
          <div style={{textAlign:"center", marginBottom:"2rem"}}>
            <div style={{fontSize:"2.5rem", marginBottom:"0.75rem"}}>✉️</div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.375rem"}}>Find Your Invitation</h2>
            <p style={{color:T.muted, fontSize:"0.85rem"}}>Enter the email and code from your invitation email.</p>
          </div>
          {isLocked && (
            <div style={{background:"#fef0f0", border:"1px solid #f5c6cb", borderRadius:"8px", padding:"0.875rem 1rem", fontSize:"0.85rem", color:T.danger, marginBottom:"1rem", textAlign:"center"}}>
              Too many failed attempts. Try again in {rateLimitCheck.minutesLeft} minute{rateLimitCheck.minutesLeft !== 1 ? "s" : ""}.
            </div>
          )}
          <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
            <div>
              <label style={labelStyle}>Your email address</label>
              <input type="email" value={invEmail} onChange={e => setInvEmail(e.target.value)} placeholder="you@example.com" style={{...inputStyle, background:isLocked?"#f8f8f8":""}} disabled={isLocked} autoFocus />
            </div>
            <div>
              <label style={labelStyle}>Invite code</label>
              <input value={invCode} onChange={e => setInvCode(e.target.value.toUpperCase())} placeholder="e.g. HEIR-K7M2X9" style={{...inputStyle, fontFamily:"'DM Mono', monospace", letterSpacing:"0.05em", background:isLocked?"#f8f8f8":""}} disabled={isLocked} onKeyDown={e => e.key === "Enter" && !isLocked && handleFindInvitation()} />
            </div>
            {invError && <div style={{background:"#fef0f0", border:"1px solid #f5c6cb", borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.82rem", color:T.danger}}>{invError}</div>}
            <button onClick={handleFindInvitation} disabled={invLoading || isLocked} style={{...filledBtn, width:"100%", padding:"0.875rem", opacity:(invLoading||isLocked)?0.6:1, marginTop:"0.25rem"}}>
              {invLoading ? "Searching…" : "Find My Invitation →"}
            </button>
          </div>
          <div style={{marginTop:"1.5rem", paddingTop:"1.25rem", borderTop:`1px solid ${T.borderLight}`, textAlign:"center"}}>
            <p style={{fontSize:"0.8rem", color:T.muted}}>Already have an account?{" "}<button onClick={() => navTo("login")} style={{...btnReset, color:T.forest, fontWeight:"600", textDecoration:"underline"}}>Sign in</button></p>
            <button onClick={() => setMode(null)} style={{...btnReset, marginTop:"0.75rem", fontSize:"0.78rem", color:T.muted}}>← Back to options</button>
          </div>
        </div>
      </div>
    );

    // Sign-in-and-accept mode — must be checked before invStep===2 because that block
    // is an early return; if this check came after it would never be reached.
    if (invitedSignInMode) return (
      <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"2.5rem 2rem", width:"100%", maxWidth:"440px", boxShadow:"0 8px 48px rgba(0,0,0,0.08)"}}>
          <div style={{textAlign:"center", marginBottom:"2rem"}}>
            <div style={{fontSize:"2rem", marginBottom:"0.75rem"}}>👋</div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.6rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.5rem"}}>You already have an account</h2>
            <p style={{color:T.muted, fontSize:"0.875rem", lineHeight:"1.6"}}>Sign in with your existing HeirOS account to accept this invitation.</p>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
            <div>
              <label style={labelStyle}>Email address</label>
              <input type="email" value={invEmail} readOnly style={{...inputStyle, background:"#f8f8f8", color:T.muted, cursor:"not-allowed"}} />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" value={invSignInPassword} onChange={e => setInvSignInPassword(e.target.value)} placeholder="Your HeirOS password" style={inputStyle} autoFocus onKeyDown={e => e.key === "Enter" && handleSignInAndAccept()} />
            </div>
            {invError && <div style={{background:"#fef0f0", border:"1px solid #f5c6cb", borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.82rem", color:T.danger}}>{invError}</div>}
            <button onClick={handleSignInAndAccept} disabled={invLoading} style={{...filledBtn, width:"100%", padding:"0.875rem", opacity:invLoading?0.5:1, marginTop:"0.25rem"}}>
              {invLoading ? "Signing in…" : "Sign In & Accept Invitation →"}
            </button>
          </div>
          <div style={{marginTop:"1.5rem", paddingTop:"1.25rem", borderTop:`1px solid ${T.borderLight}`, textAlign:"center", display:"flex", flexDirection:"column", gap:"0.5rem"}}>
            <button onClick={() => navTo("login")} style={{...btnReset, fontSize:"0.8rem", color:T.forest, fontWeight:"600", textDecoration:"underline"}}>Forgot your password? Sign in normally →</button>
            <button onClick={() => { setInvitedSignInMode(false); setInvError(""); }} style={{...btnReset, fontSize:"0.78rem", color:T.muted}}>← Back</button>
          </div>
        </div>
      </div>
    );

    // Step 2 — Confirmation
    if (invStep === 2) {
      const inv = foundInvitation;
      const ownerName = inv?.estates?.owner_name || "Your family member";
      const permissions = inv?.permissions || {};
      return (
        <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
          <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"2.5rem 2rem", width:"100%", maxWidth:"440px", boxShadow:"0 8px 48px rgba(0,0,0,0.08)"}}>
            <div style={{textAlign:"center", marginBottom:"1.75rem"}}>
              <div style={{fontSize:"2.5rem", marginBottom:"0.75rem"}}>🎉</div>
              <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.375rem"}}>Invitation Found!</h2>
              <p style={{color:T.muted, fontSize:"0.875rem"}}>Here are the details of your invitation.</p>
            </div>
            <div style={{background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"12px", padding:"1.25rem", marginBottom:"1.25rem"}}>
              <div style={{fontSize:"0.82rem", color:T.charcoal, marginBottom:"0.75rem"}}>
                <strong>{ownerName}</strong> has invited you as <strong style={{textTransform:"capitalize"}}>{inv?.role?.replace(/_/g," ") || "Collaborator"}</strong>
              </div>
              {Object.keys(permissions).some(k => permissions[k]) && (
                <div style={{display:"flex", flexWrap:"wrap", gap:"0.375rem", marginBottom:"0.75rem"}}>
                  {permissions.view_checklist && <span style={{fontSize:"0.72rem", background:`${T.forest}12`, color:T.forest, padding:"0.2rem 0.625rem", borderRadius:"20px"}}>👁 View checklist</span>}
                  {permissions.edit_checklist && <span style={{fontSize:"0.72rem", background:`${T.forest}12`, color:T.forest, padding:"0.2rem 0.625rem", borderRadius:"20px"}}>✅ Help with tasks</span>}
                  {permissions.view_documents && <span style={{fontSize:"0.72rem", background:`${T.forest}12`, color:T.forest, padding:"0.2rem 0.625rem", borderRadius:"20px"}}>📄 View documents</span>}
                  {permissions.view_vault && <span style={{fontSize:"0.72rem", background:`${T.forest}12`, color:T.forest, padding:"0.2rem 0.625rem", borderRadius:"20px"}}>🔒 Vault access</span>}
                  {permissions.view_medical_documents && <span style={{fontSize:"0.72rem", background:`${T.forest}12`, color:T.forest, padding:"0.2rem 0.625rem", borderRadius:"20px"}}>🩺 Medical documents</span>}
                </div>
              )}
              <div style={{display:"inline-block", background:"#f0faf5", border:"1px solid #c3e6cb", borderRadius:"8px", padding:"0.375rem 0.875rem", fontSize:"0.78rem", color:"#2c7a5e", fontWeight:"600"}}>
                ✓ No payment required — you have been invited as a guest
              </div>
            </div>
            <button onClick={() => setInvStep(3)} style={{...filledBtn, width:"100%", padding:"0.875rem"}}>
              Create My Free Account →
            </button>
            <div style={{marginTop:"1rem", padding:"1rem", background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"10px", textAlign:"center"}}>
              <p style={{fontSize:"0.82rem", color:T.charcoal, marginBottom:"0.625rem", fontWeight:"500"}}>Already have a HeirOS account?</p>
              <button onClick={() => setInvitedSignInMode(true)} style={{...filledBtn, width:"100%", padding:"0.75rem", background:"white", color:T.forest, border:`1.5px solid ${T.forest}`, fontSize:"0.875rem"}}>
                Sign in to accept this invitation →
              </button>
            </div>
            <button onClick={() => setInvStep(1)} style={{...btnReset, display:"block", marginTop:"0.875rem", fontSize:"0.78rem", color:T.muted, width:"100%", textAlign:"center"}}>← Back</button>
          </div>
        </div>
      );
    }


    // Step 3 — Account creation
    return (
      <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"2.5rem 2rem", width:"100%", maxWidth:"440px", boxShadow:"0 8px 48px rgba(0,0,0,0.08)"}}>
          <div style={{textAlign:"center", marginBottom:"2rem"}}>
            <div style={{display:"inline-block", background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"20px", padding:"0.25rem 0.75rem", fontSize:"0.75rem", color:T.muted, marginBottom:"1rem"}}>Invited Access</div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.375rem"}}>Create your HeirOS account</h2>
            <p style={{color:T.muted, fontSize:"0.85rem"}}>No payment required — you're joining as a guest.</p>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
            <div>
              <label style={labelStyle}>Email address</label>
              <input type="email" value={invEmail} readOnly style={{...inputStyle, background:"#f8f8f8", color:T.muted, cursor:"not-allowed"}} />
            </div>
            <div>
              <label style={labelStyle}>Password <span style={{color:T.muted, fontWeight:"400"}}>(min. 8 characters)</span></label>
              <input type="password" value={invPassword} onChange={e => setInvPassword(e.target.value)} placeholder="At least 8 characters" style={inputStyle} autoFocus />
            </div>
            <div>
              <label style={labelStyle}>Confirm password</label>
              <input type="password" value={invConfirm} onChange={e => setInvConfirm(e.target.value)} placeholder="Repeat your password" style={inputStyle} onKeyDown={e => e.key === "Enter" && handleCreateInvited()} />
            </div>
            {invError && <div style={{background:"#fef0f0", border:"1px solid #f5c6cb", borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.82rem", color:T.danger}}>{invError}</div>}
            <div style={{display:"flex", alignItems:"flex-start", gap:"0.625rem", padding:"0.75rem", background:T.parchment, borderRadius:"8px", border:`1px solid ${T.border}`}}>
              <input type="checkbox" id="inv-terms" checked={invTermsAccepted} onChange={e=>setInvTermsAccepted(e.target.checked)} style={{marginTop:"0.15rem", flexShrink:0, accentColor:T.forest, width:"16px", height:"16px", cursor:"pointer"}} />
              <label htmlFor="inv-terms" style={{fontSize:"0.8rem", color:T.charcoal, lineHeight:"1.5", cursor:"pointer"}}>
                I have read and agree to the{" "}
                <button onClick={()=>window.open(window.location.origin+window.location.pathname+'?page=terms','_blank')} style={{...btnReset, color:T.forest, fontWeight:"600", textDecoration:"underline", fontSize:"0.8rem"}}>Terms of Use</button>
                {" "}and{" "}
                <button onClick={()=>window.open(window.location.origin+window.location.pathname+'?page=privacy','_blank')} style={{...btnReset, color:T.forest, fontWeight:"600", textDecoration:"underline", fontSize:"0.8rem"}}>Privacy Policy</button>
              </label>
            </div>
            <button onClick={handleCreateInvited} disabled={invLoading||!invTermsAccepted} style={{...filledBtn, width:"100%", padding:"0.875rem", opacity:(invLoading||!invTermsAccepted)?0.5:1, marginTop:"0.25rem"}}>
              {invLoading ? "Creating account…" : "Create Account & Accept Invitation →"}
            </button>
          </div>
          <div style={{marginTop:"1.5rem", paddingTop:"1.25rem", borderTop:`1px solid ${T.borderLight}`, textAlign:"center"}}>
            <button onClick={() => setInvStep(2)} style={{...btnReset, fontSize:"0.78rem", color:T.muted}}>← Back</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Non-invited account creation ───────────────────────────────────────────
  const modeLabel = mode === "owner" ? "Planning Ahead" : "Settling an Estate";
  return (
    <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
      <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"2.5rem 2rem", width:"100%", maxWidth:"440px", boxShadow:"0 8px 48px rgba(0,0,0,0.08)"}}>
        <div style={{textAlign:"center", marginBottom:"2rem"}}>
          <div style={{display:"inline-block", background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"20px", padding:"0.25rem 0.75rem", fontSize:"0.75rem", color:T.muted, marginBottom:"1rem"}}>{modeLabel}</div>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.375rem"}}>Create your HeirOS account</h2>
          <p style={{color:T.muted, fontSize:"0.85rem"}}>{localStorage.getItem('heiros_couple_signup') === 'true' ? 'Your estate is ready — no payment needed.' : "You'll complete payment on the next step."}</p>
        </div>
        <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
          <div>
            <label style={labelStyle}>Email address</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle} autoFocus />
          </div>
          <div>
            <label style={labelStyle}>Password <span style={{color:T.muted, fontWeight:"400"}}>(min. 8 characters)</span></label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Confirm password</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat your password" style={inputStyle} onKeyDown={e => e.key === "Enter" && handleCreate()} />
          </div>
          {error && <div style={{background:"#fef0f0", border:"1px solid #f5c6cb", borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.82rem", color:T.danger}}>{error}</div>}
          <div style={{display:"flex", alignItems:"flex-start", gap:"0.625rem", padding:"0.75rem", background:T.parchment, borderRadius:"8px", border:`1px solid ${T.border}`}}>
            <input type="checkbox" id="sf-terms" checked={termsAccepted} onChange={e=>setTermsAccepted(e.target.checked)} style={{marginTop:"0.15rem", flexShrink:0, accentColor:T.forest, width:"16px", height:"16px", cursor:"pointer"}} />
            <label htmlFor="sf-terms" style={{fontSize:"0.8rem", color:T.charcoal, lineHeight:"1.5", cursor:"pointer"}}>
              I have read and agree to the{" "}
              <button onClick={()=>window.open(window.location.origin+window.location.pathname+'?page=terms','_blank')} style={{...btnReset, color:T.forest, fontWeight:"600", textDecoration:"underline", fontSize:"0.8rem"}}>Terms of Use</button>
              {" "}and{" "}
              <button onClick={()=>window.open(window.location.origin+window.location.pathname+'?page=privacy','_blank')} style={{...btnReset, color:T.forest, fontWeight:"600", textDecoration:"underline", fontSize:"0.8rem"}}>Privacy Policy</button>
            </label>
          </div>
          <button onClick={handleCreate} disabled={loading||!termsAccepted} style={{...filledBtn, width:"100%", padding:"0.875rem", opacity:(loading||!termsAccepted)?0.5:1, marginTop:"0.25rem"}}>
            {loading ? "Creating account…" : "Create Account →"}
          </button>
        </div>
        <div style={{marginTop:"1.5rem", paddingTop:"1.25rem", borderTop:`1px solid ${T.borderLight}`, textAlign:"center"}}>
          <p style={{fontSize:"0.8rem", color:T.muted}}>Already have an account?{" "}<button onClick={() => navTo("login")} style={{...btnReset, color:T.forest, fontWeight:"600", textDecoration:"underline"}}>Sign in</button></p>
          <button onClick={() => setMode(null)} style={{...btnReset, marginTop:"0.75rem", fontSize:"0.78rem", color:T.muted}}>← Back to options</button>
        </div>
      </div>
    </div>
  );
}

// ─── PAYMENT WALL ─────────────────────────────────────────────────────────────
// ─── HELP SYSTEM PROMPT ──────────────────────────────────────────────────────
const HELP_SYSTEM_PROMPT = `You are the HeirOS Help Assistant, a knowledgeable and friendly guide for the HeirOS estate planning platform. You help users understand how to use HeirOS features and navigate estate planning concepts.

You can help users with:

ESTATE PLANNING SETUP
- How to complete the Planning Ahead checklist (21 tasks across 3 phases)
- Phase 1: Get Your House in Order (legal docs, vault setup, financial accounts)
- Phase 2: Protect Your Family (executor setup, collaborator invites, will building)
- Phase 3: Annual Review (yearly check-ins and updates)

VAULT
- Adding items to the vault across 9 categories: Will & Estate Docs, Financial Accounts, Insurance Policies, Property & Vehicles, Business Interests & Ownership, Digital Assets, Personal Messages, Medical Information, Key Contacts
- Uploading documents (PDF/DOC/DOCX up to 10MB) to vault items
- Understanding access levels: Executor (full read access after death), Trusted Person (specific categories), Emergency Only (medical emergencies)
- Switching between tile and list view
- Using the Business Interests category for AI document analysis

COLLABORATORS
- How to invite collaborators using the HEIR-XXXXXX invite code system
- Difference between executor roles and general collaborators
- Understanding the executor succession chain (Primary, First Backup, Second Backup)
- Managing permissions: view_checklist, edit_checklist, view_documents, download_documents, view_vault, view_family
- How invited users access the estate
- How to resend invitations and edit permissions for pending invites

EXECUTOR EMERGENCY ACCESS
- How the emergency access request system works
- The approval hierarchy (Backup 2 → Backup 1 → Primary → Owner)
- Response window options (24h, 48h, 72h)
- Immediate access option for primary executor
- What happens when the response window expires

DEATH REPORTING
- How executors report a death
- The 72-hour cancellation window for owners
- What happens after death is confirmed (settlement checklist activates)

ESTATE SETTLEMENT
- How the Settlement checklist works (14 tasks across Immediate, Government, Legal, Financial, Digital, Tax categories)
- Document generator for legal letters (SSA, banks, employers, IRS, probate courts, creditors)
- How invited collaborators can help with settlement

ACCOUNT & SETTINGS
- Changing email and password
- Setting annual review dates
- Configuring executor response windows
- Password manager setup (Bitwarden, 1Password, Proton Pass)

GENERAL ESTATE PLANNING CONCEPTS
- What an executor does and how to choose one
- Difference between a will and a trust
- Why digital asset planning matters
- How to talk to family members about estate plans
- When to involve an estate attorney

Always be warm, clear, and reassuring. Estate planning can feel overwhelming — your job is to make it feel manageable. Give step-by-step instructions when walking users through features. If a question is outside the scope of HeirOS or requires legal advice, acknowledge it warmly and suggest they consult an estate attorney.`;

// ─── EMAIL VERIFICATION WALL ──────────────────────────────────────────────────
function EmailVerificationWall({ user, onSignOut }) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try { await supabase.auth.resend({ type: 'signup', email: user.email }); } catch {}
    setResending(false);
    setResent(true);
    setTimeout(() => setResent(false), 3000);
  };

  return (
    <div style={{minHeight:"calc(100vh - 72px)", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"2rem"}}>
      <div style={{background:T.white, borderRadius:"20px", padding:"3rem 2.5rem", maxWidth:"440px", width:"100%", textAlign:"center", border:`1px solid ${T.border}`, boxShadow:"0 4px 24px rgba(0,0,0,0.07)"}}>
        <div style={{width:"56px", height:"56px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"14px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.5rem"}}>
          <span style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:"white"}}>H</span>
        </div>
        <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, margin:"0 0 0.875rem"}}>Check Your Email</h1>
        <p style={{color:T.muted, fontSize:"0.925rem", lineHeight:"1.65", margin:"0 0 1.75rem"}}>
          We sent a confirmation link to{" "}
          <strong style={{color:T.charcoal}}>{user.email}</strong>.{" "}
          Please click the link to activate your account.
        </p>
        {resent && (
          <div style={{background:`${T.success}14`, border:`1px solid ${T.success}44`, borderRadius:"8px", padding:"0.625rem 1rem", marginBottom:"1rem", color:T.success, fontSize:"0.875rem", fontWeight:"500"}}>
            ✓ Email resent!
          </div>
        )}
        <button onClick={handleResend} disabled={resending} style={{...filledBtn, width:"100%", marginBottom:"0.875rem", opacity:resending?0.7:1}}>
          {resending ? "Resending…" : "Resend Email"}
        </button>
        <button onClick={onSignOut} style={{...btnReset, fontSize:"0.875rem", color:T.muted, textDecoration:"underline"}}>
          Back to Sign In
        </button>
      </div>
    </div>
  );
}

// ─── MFA ENROLLMENT WALL (mandatory TOTP setup) ────────────────────────────────
function MfaEnrollWall({ onVerified, onSignOut, allowSkip = true, onSkip, requiredMessage }) {
  const [loading, setLoading] = useState(true);
  const [factorId, setFactorId] = useState(null);
  const [qrUri, setQrUri] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const [enrollAttempt, setEnrollAttempt] = useState(0);

  // Guards against the enroll effect firing more than once for the same
  // enrollment attempt (e.g. React StrictMode double-invoking effects, or the
  // effect re-running for an unrelated reason). Tracks which attempt number
  // currently has an in-flight (or completed) enroll() call.
  const enrollStartedRef = useRef(null);
  // True while an enroll() call is in flight for the current attempt — used
  // to disable/guard any UI that could re-trigger enrollment (e.g. "Try Again").
  const [enrolling, setEnrolling] = useState(false);

  // On every mount: sweep ALL unverified TOTP factors from any past session
  // before any new enrollment is requested. Prevents hitting Supabase's 10-factor cap.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.auth.mfa.listFactors();
        const stale = (data?.totp || []).filter(f => f.status === 'unverified');
        for (const f of stale) {
          try { await supabase.auth.mfa.unenroll({ factorId: f.id }); } catch {}
        }
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (enrollStartedRef.current === enrollAttempt) {
      console.warn('[HeirOS] MFA enroll() skipped — already started for this attempt (session in flight or already resolved).');
      return;
    }
    enrollStartedRef.current = enrollAttempt;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setEnrolling(true);
      setError(null);
      try {
        // Clean up any leftover unverified TOTP factors from a previous attempt
        // so re-enrollment doesn't collide with a stale factor.
        const { data: factorsData, error: listError } = await supabase.auth.mfa.listFactors();
        if (listError) throw listError;
        const stale = (factorsData?.totp || []).filter(f => f.factor_type === 'totp' && f.status === 'unverified');
        for (const f of stale) {
          try { await supabase.auth.mfa.unenroll({ factorId: f.id }); } catch {}
        }
        const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: `totp-${Date.now()}` });
        if (error) throw error;
        if (cancelled) return;
        setFactorId(data.id);
        setQrUri(data.totp?.uri || null);
        setSecret(data.totp?.secret || null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to start two-factor setup. Please try again.');
      } finally {
        if (!cancelled) { setLoading(false); setEnrolling(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [enrollAttempt]);

  const handleRetryEnroll = () => {
    if (enrolling) {
      console.warn('[HeirOS] MFA enroll() retry blocked — a previous enrollment call has not resolved yet.');
      return;
    }
    setEnrollAttempt(a => a + 1);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || !factorId) return;
    setVerifying(true);
    setError(null);
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
      if (error) throw error;
      onVerified();
    } catch (e) {
      setError(e.message || 'Invalid code. Please check your authenticator app and try again.');
      setCode('');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div style={{minHeight:"calc(100vh - 72px)", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"2rem"}}>
      <div style={{background:T.white, borderRadius:"20px", padding:"3rem 2.5rem", maxWidth:"460px", width:"100%", textAlign:"center", border:`1px solid ${T.border}`, boxShadow:"0 4px 24px rgba(0,0,0,0.07)"}}>
        <div style={{width:"56px", height:"56px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"14px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.5rem"}}>
          <span style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:"white"}}>🔒</span>
        </div>
        <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, margin:"0 0 0.625rem"}}>Set Up Two-Factor Authentication</h1>
        {requiredMessage && (
          <div style={{background:"#fff8e6", border:"1px solid #e8cf8a", borderRadius:"8px", padding:"0.75rem 1rem", marginBottom:"1.25rem", color:"#8a6d1f", fontSize:"0.85rem", fontWeight:"500"}}>
            {requiredMessage}
          </div>
        )}
        <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.6", margin:"0 0 1.75rem"}}>
          Scan this QR code with Google Authenticator or Authy, then enter the 6-digit code to confirm setup.
        </p>

        {loading ? (
          <div style={{padding:"2rem 0"}}>
            <div style={{width:"36px", height:"36px", border:`3px solid ${T.forestLight}`, borderTopColor:T.forest, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto"}} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : qrUri ? (
          <>
            <div style={{background:T.parchment, borderRadius:"14px", padding:"1.25rem", marginBottom:"1.25rem", display:"flex", justifyContent:"center"}}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrUri)}&size=200x200`}
                alt="Scan this QR code with your authenticator app"
                width="200"
                height="200"
                style={{borderRadius:"8px", background:"white", padding:"0.5rem"}}
              />
            </div>
            {secret && (
              <div style={{marginBottom:"1.25rem"}}>
                <p style={{fontSize:"0.75rem", color:T.muted, marginBottom:"0.375rem"}}>Can't scan? Enter this code manually:</p>
                <div
                  style={{fontFamily:"'DM Mono', monospace", fontSize:"0.8rem", color:T.charcoal, background:T.parchment, borderRadius:"8px", padding:"0.625rem 0.875rem", wordBreak:"break-all", letterSpacing:"0.03em", userSelect:"all", cursor:"text"}}
                  title="Click to select, then copy"
                >
                  {secret}
                </div>
              </div>
            )}
            {qrUri && (
              <div style={{marginBottom:"1rem"}}>
                <a
                  href={qrUri}
                  style={{display:"inline-block", fontSize:"0.82rem", fontWeight:"500", color:T.forest, textDecoration:"none", border:`1px solid ${T.forest}`, borderRadius:"8px", padding:"0.55rem 1rem"}}
                >
                  📲 Open in Authenticator App
                </a>
              </div>
            )}
            {(() => {
              const ua = navigator.userAgent || '';
              const isIOS = /iPhone|iPad|iPod/.test(ua);
              const isAndroid = /Android/.test(ua);
              return (
                <div style={{marginBottom:"1.5rem"}}>
                  <p style={{fontSize:"0.75rem", color:T.muted, marginBottom:"0.375rem"}}>Don't have an authenticator app?</p>
                  {isIOS ? (
                    <div style={{display:"flex", gap:"1rem", justifyContent:"center"}}>
                      <a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer" style={{fontSize:"0.75rem", color:T.muted, textDecoration:"underline"}}>Get Google Authenticator</a>
                      <a href="https://apps.apple.com/app/authy/id494168017" target="_blank" rel="noopener noreferrer" style={{fontSize:"0.75rem", color:T.muted, textDecoration:"underline"}}>Get Authy</a>
                    </div>
                  ) : isAndroid ? (
                    <div style={{display:"flex", gap:"1rem", justifyContent:"center"}}>
                      <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer" style={{fontSize:"0.75rem", color:T.muted, textDecoration:"underline"}}>Get Google Authenticator</a>
                      <a href="https://play.google.com/store/apps/details?id=com.authy.authy" target="_blank" rel="noopener noreferrer" style={{fontSize:"0.75rem", color:T.muted, textDecoration:"underline"}}>Get Authy</a>
                    </div>
                  ) : (
                    <p style={{fontSize:"0.75rem", color:T.muted, margin:0}}>Search your phone's app store for "Google Authenticator" or "Authy"</p>
                  )}
                </div>
              );
            })()}
            <form onSubmit={handleVerify}>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0,6))}
                style={{width:"100%", textAlign:"center", fontSize:"1.5rem", letterSpacing:"0.5rem", fontFamily:"'DM Mono', monospace", padding:"0.875rem", borderRadius:"10px", border:`1px solid ${T.border}`, marginBottom:"1rem", boxSizing:"border-box"}}
              />
              {error && (
                <div style={{background:"#fdecea", border:"1px solid #f5c6c2", borderRadius:"8px", padding:"0.625rem 1rem", marginBottom:"1rem", color:"#c0392b", fontSize:"0.85rem"}}>
                  {error}
                </div>
              )}
              <button type="submit" disabled={verifying || code.length !== 6} style={{...filledBtn, width:"100%", opacity:(verifying || code.length !== 6) ? 0.6 : 1}}>
                {verifying ? "Verifying…" : "Confirm & Enable"}
              </button>
            </form>
            {allowSkip && (
              <button onClick={onSkip} style={{...btnReset, display:"block", width:"100%", fontSize:"0.95rem", fontWeight:"600", color:T.forest, background:T.parchment, border:`1.5px solid ${T.forest}`, borderRadius:"10px", padding:"0.75rem", marginTop:"1rem"}}>
                Skip for now →
              </button>
            )}
          </>
        ) : (
          <div>
            <div style={{background:"#fdecea", border:"1px solid #f5c6c2", borderRadius:"8px", padding:"0.875rem 1rem", marginBottom:"1rem", color:"#c0392b", fontSize:"0.875rem"}}>
              {error || 'Something went wrong setting up two-factor authentication.'}
            </div>
            <button onClick={handleRetryEnroll} disabled={enrolling} style={{...filledBtn, width:"100%", opacity:enrolling?0.6:1}}>
              {enrolling ? "Starting…" : "Try Again"}
            </button>
          </div>
        )}

        <button onClick={onSignOut} style={{...btnReset, fontSize:"0.85rem", color:T.muted, textDecoration:"underline", marginTop:"1.5rem"}}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ─── MFA VERIFICATION WALL (returning users with TOTP already enrolled) ───────
function MfaVerifyWall({ factorId, onVerified, onSignOut }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || !factorId) return;
    setVerifying(true);
    setError(null);
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;
      const { error: verifyError } = await supabase.auth.mfa.verify({ factorId, challengeId: challenge.id, code });
      if (verifyError) throw verifyError;
      onVerified();
    } catch (e) {
      setError(e.message || 'Invalid code. Please try again.');
      setCode('');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div style={{minHeight:"calc(100vh - 72px)", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"2rem"}}>
      <div style={{background:T.white, borderRadius:"20px", padding:"3rem 2.5rem", maxWidth:"420px", width:"100%", textAlign:"center", border:`1px solid ${T.border}`, boxShadow:"0 4px 24px rgba(0,0,0,0.07)"}}>
        <div style={{width:"56px", height:"56px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"14px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.5rem"}}>
          <span style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:"white"}}>🔒</span>
        </div>
        <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, margin:"0 0 0.625rem"}}>Two-Factor Verification</h1>
        <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.6", margin:"0 0 1.75rem"}}>
          Enter the 6-digit code from your authenticator app to continue.
        </p>
        <form onSubmit={handleVerify}>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            autoFocus
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0,6))}
            style={{width:"100%", textAlign:"center", fontSize:"1.5rem", letterSpacing:"0.5rem", fontFamily:"'DM Mono', monospace", padding:"0.875rem", borderRadius:"10px", border:`1px solid ${T.border}`, marginBottom:"1rem", boxSizing:"border-box"}}
          />
          {error && (
            <div style={{background:"#fdecea", border:"1px solid #f5c6c2", borderRadius:"8px", padding:"0.625rem 1rem", marginBottom:"1rem", color:"#c0392b", fontSize:"0.85rem"}}>
              {error}
            </div>
          )}
          <button type="submit" disabled={verifying || code.length !== 6 || !factorId} style={{...filledBtn, width:"100%", opacity:(verifying || code.length !== 6 || !factorId) ? 0.6 : 1}}>
            {verifying ? "Verifying…" : "Verify"}
          </button>
        </form>
        <button onClick={onSignOut} style={{...btnReset, fontSize:"0.85rem", color:T.muted, textDecoration:"underline", marginTop:"1.5rem"}}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ─── HELP PANEL ───────────────────────────────────────────────────────────────
function HelpPanel({ user, profile }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('kb');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [issueForm, setIssueForm] = useState({ name: '', email: '', category: 'Bug Report', subject: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIssueForm(prev => ({
        ...prev,
        name: profile?.full_name || prev.name || '',
        email: user?.email || prev.email || '',
      }));
    } else {
      setMessages([]);
      setInput('');
      setTab('kb');
      setSubmitSuccess(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking, open]);

  const sendMessage = async () => {
    if (!input.trim() || thinking) return;
    const userMsg = { role: 'user', content: input.trim() };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput('');
    setThinking(true);
    try {
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        body: { system: HELP_SYSTEM_PROMPT, messages: newMsgs, max_tokens: 800 },
      });
      if (error) throw new Error(error.message || 'Request failed');
      const reply = data?.content?.[0]?.text || "Sorry, I couldn't process that. Please try again.";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting right now. Please try again in a moment." }]);
    } finally {
      setThinking(false);
    }
  };

  const submitIssue = async () => {
    if (!issueForm.subject.trim() || !issueForm.description.trim()) return;
    setSubmitting(true);
    try {
      const { name, email, category, subject, description } = issueForm;
      const { error } = await supabase.functions.invoke('submit-issue', {
        body: {
          title: `[${category}] ${subject}`,
          body: `**Reported by:** ${name || 'Unknown'} (${email || 'No email provided'})\n**Category:** ${category}\n\n${description}`,
          labels: [category === 'Bug Report' ? 'bug' : category === 'Feature Request' ? 'enhancement' : 'feedback'],
        },
      });
      if (error) throw error;
      setSubmitSuccess(true);
      setIssueForm(prev => ({ ...prev, subject: '', description: '' }));
    } catch {
      setSubmitSuccess(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating ? button */}
      <button
        onClick={() => setOpen(true)}
        title="Help Center"
        style={{position:'fixed', bottom:'28px', right:'28px', width:'52px', height:'52px', borderRadius:'50%', background:T.forest, color:'white', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem', fontWeight:'700', fontFamily:"'DM Sans', sans-serif", boxShadow:'0 4px 16px rgba(44,74,62,0.38)', zIndex:8900, transition:'transform 0.15s, box-shadow 0.15s'}}
        onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.08)';e.currentTarget.style.boxShadow='0 6px 22px rgba(44,74,62,0.45)';}}
        onMouseLeave={e=>{e.currentTarget.style.transform='scale(1)';e.currentTarget.style.boxShadow='0 4px 16px rgba(44,74,62,0.38)';}}
      >
        ?
      </button>

      {/* Backdrop */}
      {open && <div onClick={()=>setOpen(false)} style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.32)', zIndex:8901}} />}

      {/* Slide-out panel */}
      <div style={{position:'fixed', top:0, right:0, width:'min(420px, 100vw)', height:'100vh', background:T.white, boxShadow:'-4px 0 32px rgba(0,0,0,0.12)', zIndex:8902, display:'flex', flexDirection:'column', transform:open?'translateX(0)':'translateX(100%)', transition:'transform 0.28s cubic-bezier(0.4,0,0.2,1)'}}>

        {/* Header */}
        <div style={{padding:'1.25rem 1.5rem', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:'1.25rem', fontWeight:'700', color:T.forest, margin:0}}>Help Center</h2>
          <button onClick={()=>setOpen(false)} style={{...btnReset, fontSize:'1.5rem', color:T.muted, lineHeight:1, padding:'0 4px'}}>×</button>
        </div>

        {/* Tabs */}
        <div style={{display:'flex', borderBottom:`1px solid ${T.border}`, flexShrink:0}}>
          {[['kb','Knowledge Base'],['issue','Submit an Issue']].map(([id, label]) => (
            <button key={id} onClick={()=>setTab(id)} style={{...btnReset, flex:1, padding:'0.75rem 1rem', fontSize:'0.84rem', fontWeight:tab===id?'600':'400', color:tab===id?T.forest:T.muted, borderBottom:tab===id?`2px solid ${T.forest}`:'2px solid transparent', transition:'all 0.15s', background:'transparent'}}>
              {label}
            </button>
          ))}
        </div>

        {/* Knowledge Base tab */}
        {tab === 'kb' ? (
          <div style={{flex:1, display:'flex', flexDirection:'column', overflow:'hidden'}}>
            <div style={{flex:1, overflowY:'auto', padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.875rem'}}>
              {messages.length === 0 && !thinking && (
                <div style={{textAlign:'center', padding:'2.5rem 1rem', color:T.muted}}>
                  <div style={{fontSize:'2rem', marginBottom:'0.75rem'}}>💬</div>
                  <p style={{margin:'0 0 0.375rem', fontWeight:'600', color:T.slate, fontSize:'0.9rem'}}>Ask anything about HeirOS</p>
                  <p style={{margin:0, fontSize:'0.8rem', lineHeight:1.6}}>Features, estate planning concepts, or getting started.</p>
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} style={{display:'flex', justifyContent:msg.role==='user'?'flex-end':'flex-start'}}>
                  <div style={{maxWidth:'84%', padding:'0.7rem 1rem', borderRadius:msg.role==='user'?'14px 14px 4px 14px':'14px 14px 14px 4px', background:msg.role==='user'?T.forest:T.white, color:msg.role==='user'?'white':T.charcoal, fontSize:'0.875rem', lineHeight:'1.55', border:msg.role==='assistant'?`1px solid ${T.border}`:'none', whiteSpace:'pre-wrap', wordBreak:'break-word'}}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {thinking && (
                <div style={{display:'flex', justifyContent:'flex-start'}}>
                  <div style={{padding:'0.7rem 1rem', borderRadius:'14px 14px 14px 4px', background:T.white, border:`1px solid ${T.border}`}}>
                    <span style={{display:'inline-flex', gap:'4px', alignItems:'center'}}>
                      {[0, 0.2, 0.4].map((delay, i) => (
                        <span key={i} style={{width:'6px', height:'6px', borderRadius:'50%', background:T.muted, animation:`helpDot 1.2s ease-in-out ${delay}s infinite`}}/>
                      ))}
                    </span>
                    <style>{`@keyframes helpDot{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-5px)}}`}</style>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef}/>
            </div>
            <div style={{padding:'0.875rem 1.25rem', borderTop:`1px solid ${T.border}`, flexShrink:0, display:'flex', gap:'0.5rem', background:T.white}}>
              <input
                value={input}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage();}}}
                placeholder="Ask anything about HeirOS..."
                style={{...inputStyle, flex:1, padding:'0.6rem 0.875rem', fontSize:'0.875rem'}}
              />
              <button onClick={sendMessage} disabled={!input.trim()||thinking} style={{...filledBtn, padding:'0.6rem 1rem', flexShrink:0, fontSize:'1rem', opacity:(!input.trim()||thinking)?0.45:1}}>↑</button>
            </div>
          </div>

        ) : (
          /* Submit an Issue tab */
          <div style={{flex:1, overflowY:'auto', padding:'1.25rem'}}>
            {submitSuccess ? (
              <div style={{textAlign:'center', padding:'3rem 1rem'}}>
                <div style={{width:'52px', height:'52px', borderRadius:'50%', background:`${T.success}14`, border:`2px solid ${T.success}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1.25rem', fontSize:'1.25rem', color:T.success}}>✓</div>
                <p style={{fontFamily:"'Playfair Display', serif", fontSize:'1.1rem', color:T.forest, fontWeight:'700', margin:'0 0 0.5rem'}}>Issue Submitted</p>
                <p style={{fontSize:'0.875rem', color:T.muted, lineHeight:1.6, margin:'0 0 1.75rem'}}>Your issue has been submitted. We'll be in touch soon.</p>
                <button onClick={()=>setSubmitSuccess(false)} style={{...outlineBtn, fontSize:'0.82rem'}}>Submit Another</button>
              </div>
            ) : (
              <div style={{display:'flex', flexDirection:'column', gap:'0.875rem'}}>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem'}}>
                  <div>
                    <label style={labelStyle}>Name</label>
                    <input value={issueForm.name} onChange={e=>setIssueForm(p=>({...p,name:e.target.value}))} style={{...inputStyle, padding:'0.6rem 0.875rem', fontSize:'0.875rem'}} placeholder="Your name"/>
                  </div>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input value={issueForm.email} onChange={e=>setIssueForm(p=>({...p,email:e.target.value}))} style={{...inputStyle, padding:'0.6rem 0.875rem', fontSize:'0.875rem'}} placeholder="your@email.com"/>
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Category</label>
                  <select value={issueForm.category} onChange={e=>setIssueForm(p=>({...p,category:e.target.value}))} style={{...inputStyle, padding:'0.6rem 0.875rem', fontSize:'0.875rem'}}>
                    {['Bug Report','Billing','Feature Request','General Question'].map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Subject</label>
                  <input value={issueForm.subject} onChange={e=>setIssueForm(p=>({...p,subject:e.target.value}))} style={{...inputStyle, padding:'0.6rem 0.875rem', fontSize:'0.875rem'}} placeholder="Brief summary"/>
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea value={issueForm.description} onChange={e=>setIssueForm(p=>({...p,description:e.target.value}))} rows={4} style={{...inputStyle, padding:'0.6rem 0.875rem', fontSize:'0.875rem', resize:'vertical'}} placeholder="Describe your issue or question in detail..."/>
                </div>
                <button onClick={submitIssue} disabled={submitting||!issueForm.subject.trim()||!issueForm.description.trim()} style={{...filledBtn, opacity:(submitting||!issueForm.subject.trim()||!issueForm.description.trim())?0.55:1}}>
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ─── FEEDBACK BUTTON ──────────────────────────────────────────────────────────
function FeedbackButton({ user, currentPage }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('Bug Report');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const FEEDBACK_TYPES = [
    { key: 'Bug Report', icon: '🐛' },
    { key: 'Feature Request', icon: '✨' },
    { key: 'Feedback', icon: '💬' },
  ];

  const resetForm = () => {
    setType('Bug Report');
    setTitle('');
    setDescription('');
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError('Please fill in both the title and description.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const userEmail = user?.email || 'unknown';
    try {
      const { error: fnError } = await supabase.functions.invoke('submit-issue', {
        body: {
          title: `[${type}] ${title}`,
          body: `**Type:** ${type}\n**Reported by:** ${userEmail}\n**Page:** ${currentPage}\n\n**Description:**\n${description}`,
          labels: [type === 'Bug Report' ? 'bug' : type === 'Feature Request' ? 'enhancement' : 'feedback'],
        },
      });
      if (fnError) throw fnError;
      resetForm();
      setOpen(false);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (e) {
      setError(e.message || 'Something went wrong submitting your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating 💬 button — sits just above the ? help button */}
      <button
        onClick={() => setOpen(true)}
        title="Share Feedback"
        style={{position:'fixed', bottom:'92px', right:'28px', width:'52px', height:'52px', borderRadius:'50%', background:T.forest, color:'white', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem', fontWeight:'700', fontFamily:"'DM Sans', sans-serif", boxShadow:'0 4px 16px rgba(44,74,62,0.38)', zIndex:8900, transition:'transform 0.15s, box-shadow 0.15s'}}
        onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.08)';e.currentTarget.style.boxShadow='0 6px 22px rgba(44,74,62,0.45)';}}
        onMouseLeave={e=>{e.currentTarget.style.transform='scale(1)';e.currentTarget.style.boxShadow='0 4px 16px rgba(44,74,62,0.38)';}}
      >
        💬
      </button>

      {open && (
        <div onClick={e=>{if(e.target===e.currentTarget) handleClose();}} style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.42)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999, padding:"1rem"}}>
          <div style={{background:T.white, borderRadius:"20px", padding:"2rem", width:"100%", maxWidth:"460px", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:T.forest, margin:"0 0 1.25rem"}}>Share Feedback</h2>

            <div style={{display:"flex", gap:"0.5rem", marginBottom:"1.25rem", flexWrap:"wrap"}}>
              {FEEDBACK_TYPES.map(({key, icon}) => (
                <button
                  key={key}
                  onClick={() => setType(key)}
                  style={{
                    ...btnReset,
                    padding:"0.5rem 1rem",
                    borderRadius:"999px",
                    fontSize:"0.82rem",
                    fontWeight:"600",
                    border: type===key ? "none" : `1px solid ${T.border}`,
                    background: type===key ? T.forest : "transparent",
                    color: type===key ? T.cream : T.charcoal,
                    transition:"all 0.15s",
                  }}
                >
                  {icon} {key}
                </button>
              ))}
            </div>

            <div style={{marginBottom:"1rem"}}>
              <label style={labelStyle}>Title</label>
              <input
                type="text"
                placeholder="Brief summary"
                value={title}
                onChange={e=>setTitle(e.target.value)}
                style={{...inputStyle, padding:"0.6rem 0.875rem", fontSize:"0.875rem"}}
              />
            </div>

            <div style={{marginBottom:"1rem"}}>
              <label style={labelStyle}>Description</label>
              <textarea
                rows={4}
                placeholder="Describe the issue or idea in detail..."
                value={description}
                onChange={e=>setDescription(e.target.value)}
                style={{...inputStyle, padding:"0.6rem 0.875rem", fontSize:"0.875rem", resize:"vertical"}}
              />
            </div>

            <div style={{marginBottom:"1.25rem", display:"flex", flexDirection:"column", gap:"0.25rem"}}>
              <span style={{fontSize:"0.78rem", color:T.muted}}>Reported by: {user?.email || 'unknown'}</span>
              <span style={{fontSize:"0.78rem", color:T.muted}}>Page: {currentPage}</span>
            </div>

            {error && (
              <div style={{background:"#fdecea", border:"1px solid #f5c6c2", borderRadius:"8px", padding:"0.625rem 1rem", marginBottom:"1rem", color:"#c0392b", fontSize:"0.85rem"}}>
                {error}
              </div>
            )}

            <div style={{display:"flex", alignItems:"center", justifyContent:"flex-end", gap:"1.25rem"}}>
              <button onClick={handleClose} style={{...btnReset, fontSize:"0.875rem", color:T.muted}}>Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} style={{...filledBtn, padding:"0.625rem 1.5rem", fontSize:"0.875rem", opacity:submitting?0.7:1}}>
                {submitting ? "Submitting…" : "Submit →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showToast && (
        <div style={{position:"fixed", bottom:"2rem", left:"50%", transform:"translateX(-50%)", background:T.forest, color:"white", padding:"0.75rem 1.5rem", borderRadius:"10px", fontSize:"0.85rem", fontWeight:"500", boxShadow:"0 4px 20px rgba(0,0,0,0.15)", zIndex:9999, pointerEvents:"none"}}>
          Thanks! Your feedback has been submitted.
        </div>
      )}
    </>
  );
}

// ─── SPOUSE SETUP ─────────────────────────────────────────────────────────────
function SpouseSetupScreen({ user, profile, estateSupabaseId, onDismiss, onComplete }) {
  const [spouseName,  setSpouseName]  = useState('');
  const [spouseEmail, setSpouseEmail] = useState('');
  const [loading,     setLoading]     = useState(null); // 'free' | 'bundle' | null
  const [error,       setError]       = useState('');
  const [invited,     setInvited]     = useState(false);
  const [step,        setStep]        = useState('qualify'); // 'qualify' | 'choose' | 'bundle-roles'
  const [roles,       setRoles]       = useState({ executor: false, medicalPoa: false, powerOfAttorney: false });

  const BUNDLE_LINK = process.env.REACT_APP_STRIPE_COUPLE_BUNDLE_PAYMENT_LINK;
  const ownerName   = profile?.onboarding_data?.fullName || user?.email?.split('@')[0] || 'Your partner';

  const validate = () => {
    if (!spouseName.trim())  return "Please enter your spouse's name.";
    const spouseEmailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!spouseEmail.trim() || !spouseEmailRe.test(spouseEmail.trim())) return "Please enter a valid email address.";
    return null;
  };

  const handleFreeInvite = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(''); setLoading('free');
    try {
      const { data: inv, error: invErr, invite_code } = await createInvitation(
        user.id,
        estateSupabaseId,
        { email: spouseEmail, name: spouseName, role: 'Couple Invite', is_couple_invite: true, is_couple_bundle: false },
        { inviterName: ownerName },
      );
      if (invErr || (!inv && !invite_code)) { setError('Failed to send invite. Please try again.'); return; }
      // Send email immediately for the free path
      sendEmail(spouseEmail, 'couple_invite', {
        inviteeName:  spouseName,
        inviterName:  ownerName,
        inviteCode:   inv?.invite_code || invite_code || '',
        isBundlePaid: false,
      });
      setInvited(true);
      await onComplete();
    } finally {
      setLoading(null);
    }
  };

  const checkRow = (checked, onChange, label, sub) => (
    <label key={label} style={{display:"flex", alignItems:"flex-start", gap:"0.75rem", padding:"0.875rem 1rem", borderRadius:"10px", border:`1px solid ${checked ? T.forest : T.border}`, background:checked ? '#f0faf5' : T.white, cursor:"pointer", marginBottom:"0.625rem", transition:"all 0.15s"}}>
      <input type="checkbox" checked={checked} onChange={onChange}
        style={{marginTop:"0.15rem", accentColor:T.forest, width:"16px", height:"16px", flexShrink:0, cursor:"pointer"}} />
      <div>
        <div style={{fontSize:"0.875rem", fontWeight:"600", color:T.charcoal, lineHeight:"1.4"}}>{label}</div>
        <div style={{fontSize:"0.75rem", color:T.muted, marginTop:"0.1rem"}}>{sub}</div>
      </div>
    </label>
  );

  const handleBundle = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    if (!BUNDLE_LINK) {
      setError("Couple's Bundle payment link is not configured yet. Please use the free invite option for now.");
      return;
    }
    setError(''); setLoading('bundle');
    try {
      const selectedRoles = [
        roles.executor        && 'Named as executor',
        roles.medicalPoa      && 'Medical POA',
        roles.powerOfAttorney && 'Power of Attorney',
      ].filter(Boolean);
      // Create invite row; the Stripe webhook will mark it paid and email the spouse.
      const { data: inv, error: invErr, invite_code } = await createInvitation(
        user.id,
        estateSupabaseId,
        { email: spouseEmail, name: spouseName, role: 'Couple Invite', is_couple_invite: true, is_couple_bundle: true, couple_roles: selectedRoles },
        { inviterName: ownerName },
      );
      if (invErr || (!inv && !invite_code)) { setError('Failed to create invite. Please try again.'); return; }
      await onComplete();
      // Redirect to Stripe — pass invite code as client_reference_id so the webhook can find it
      const code = inv?.invite_code || invite_code || '';
      window.location.href = `${BUNDLE_LINK}?client_reference_id=${code}&prefilled_email=${encodeURIComponent(spouseEmail)}`;
    } finally {
      setLoading(null);
    }
  };

  if (step === 'qualify') {
    return (
      <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"3rem 2.5rem", width:"100%", maxWidth:"520px", boxShadow:"0 8px 48px rgba(0,0,0,0.10)"}}>
          <div style={{textAlign:"center", marginBottom:"2rem"}}>
            <div style={{width:"56px", height:"56px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"14px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.25rem", fontSize:"1.5rem"}}>💍</div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.75rem"}}>Should you set up a connected estate for your spouse?</h2>
            <p style={{color:T.muted, fontSize:"0.875rem", lineHeight:"1.7"}}>
              If your spouse shares assets, accounts, or responsibilities with you, a connected estate automatically lists you both as each other's Executor, Medical POA, and Power of Attorney, and lets either of you manage both estates from one login. If your spouse already has their own separate plan, you can skip this for now.
            </p>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"0.75rem"}}>
            <button onClick={() => setStep('choose')} style={{...filledBtn, width:"100%", padding:"0.875rem", fontSize:"0.95rem"}}>
              Yes, set up a connected estate →
            </button>
            <button onClick={onDismiss} style={{...outlineBtn, width:"100%", padding:"0.875rem", fontSize:"0.95rem"}}>
              Not right now
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'bundle-roles') {
    return (
      <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"3rem 2.5rem", width:"100%", maxWidth:"520px", boxShadow:"0 8px 48px rgba(0,0,0,0.10)"}}>
          <button onClick={() => setStep('choose')} style={{background:"none", border:"none", padding:0, cursor:"pointer", fontSize:"0.78rem", color:T.muted, marginBottom:"1.5rem"}}>← Back</button>
          <div style={{textAlign:"center", marginBottom:"1.75rem"}}>
            <div style={{fontSize:"1.5rem", marginBottom:"0.5rem"}}>🤝</div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:T.forest, marginBottom:"0.375rem"}}>Automatic role setup</h2>
            <p style={{color:T.muted, fontSize:"0.85rem", lineHeight:"1.6"}}>Would you like to automatically name each other in any of these roles? Only checked roles will be created.</p>
          </div>
          <div style={{marginBottom:"1.5rem"}}>
            {checkRow(roles.executor,        e => setRoles(r => ({...r, executor:        e.target.checked})), 'Primary Executor',          'Manages the estate and carries out the will')}
            {checkRow(roles.medicalPoa,      e => setRoles(r => ({...r, medicalPoa:      e.target.checked})), 'Medical Power of Attorney', 'Makes healthcare decisions if incapacitated')}
            {checkRow(roles.powerOfAttorney, e => setRoles(r => ({...r, powerOfAttorney: e.target.checked})), 'Power of Attorney',         'Handles financial & legal decisions if incapacitated')}
          </div>
          <div style={{background:T.parchment, borderRadius:"10px", padding:"0.875rem 1rem", fontSize:"0.78rem", color:T.muted, lineHeight:"1.6", marginBottom:"1.5rem"}}>
            <strong style={{color:T.charcoal}}>None selected is fine.</strong> You can add or update roles from your estate settings at any time.
          </div>
          {error && <p style={{color:"#c0392b", fontSize:"0.82rem", marginBottom:"1rem"}}>{error}</p>}
          <button onClick={handleBundle} disabled={loading === 'bundle'} style={{display:"block", width:"100%", padding:"0.75rem", borderRadius:"10px", border:"none", cursor:"pointer", fontFamily:"'DM Sans', sans-serif", fontSize:"0.95rem", fontWeight:"600", color:"white", background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, opacity: loading === 'bundle' ? 0.6 : 1, transition:"opacity 0.15s"}}>
            {loading === 'bundle' ? 'Setting up…' : 'Continue to Payment →'}
          </button>
          <p style={{textAlign:"center", marginTop:"0.75rem", fontSize:"0.72rem", color:T.muted}}>Secure payment · Stripe · 14-day money-back guarantee</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
      <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"3rem 2.5rem", width:"100%", maxWidth:"520px", boxShadow:"0 8px 48px rgba(0,0,0,0.10)"}}>
        {invited ? (
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:"2.5rem", marginBottom:"1rem"}}>💌</div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.6rem", color:T.forest, marginBottom:"0.75rem"}}>Invite sent!</h2>
            <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.6", marginBottom:"1.5rem"}}>
              We've sent {spouseName} an invite to create their own estate on HeirOS. Once they join, you'll each be listed as Primary Executor, Medical POA, and Power of Attorney on the other's estate.
            </p>
            <button onClick={onDismiss} style={{...filledBtn, padding:"0.875rem 2rem"}}>Continue to My Estate →</button>
          </div>
        ) : (
          <>
            <div style={{textAlign:"center", marginBottom:"2rem"}}>
              <div style={{width:"56px", height:"56px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"14px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.25rem", fontSize:"1.5rem"}}>💍</div>
              <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.5rem"}}>Set up your spouse's estate</h1>
              <p style={{color:T.muted, fontSize:"0.875rem", lineHeight:"1.6"}}>
                Your estate plan works best when your spouse has one too. You'll each be automatically listed as Primary Executor, Medical POA, and Power of Attorney on the other's estate.
              </p>
            </div>

            <div style={{display:"flex", flexDirection:"column", gap:"0.875rem", marginBottom:"1.5rem"}}>
              <div>
                <label style={labelStyle}>Spouse / partner name</label>
                <input value={spouseName}  onChange={e => setSpouseName(e.target.value)}  placeholder="Full name"            style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Spouse / partner email</label>
                <input type="email" value={spouseEmail} onChange={e => setSpouseEmail(e.target.value)} placeholder="email@example.com" style={inputStyle} />
              </div>
            </div>

            {error && <p style={{color:T.danger, fontSize:"0.82rem", marginBottom:"1rem"}}>{error}</p>}

            <div style={{display:"flex", flexDirection:"column", gap:"0.875rem", marginBottom:"1.5rem"}}>
              {/* Couple's Bundle */}
              <div style={{background:T.parchment, border:`2px solid ${T.gold}`, borderRadius:"14px", padding:"1.25rem 1.5rem"}}>
                <div style={{fontSize:"0.65rem", fontFamily:"'DM Mono', monospace", color:T.gold, letterSpacing:"0.08em", fontWeight:"600", marginBottom:"0.375rem"}}>BEST VALUE</div>
                <div style={{fontSize:"1rem", fontWeight:"600", color:T.charcoal, marginBottom:"0.25rem"}}>Couple's Bundle — $100 add-on</div>
                <p style={{fontSize:"0.8rem", color:T.muted, marginBottom:"1rem", lineHeight:"1.5"}}>Pay a one-time $100 add-on. Your spouse creates their full estate at no extra charge — the $299 payment is covered by the bundle.</p>
                <button onClick={() => { const err = validate(); if (err) { setError(err); return; } setError(''); setStep('bundle-roles'); }} style={{...filledBtn, width:"100%", padding:"0.75rem", background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`}}>
                  Upgrade to Couple's Bundle — $100 →
                </button>
              </div>

            </div>

            <div style={{textAlign:"center"}}>
              <button onClick={onDismiss} style={{...btnReset, fontSize:"0.8rem", color:T.muted}}>Skip for now</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PaymentWall({ profile, user, onSignOut }) {
  const PAYMENT_LINK        = process.env.REACT_APP_STRIPE_PAYMENT_LINK;
  const COUPLE_PAYMENT_LINK = process.env.REACT_APP_STRIPE_COUPLE_SIGNUP_PAYMENT_LINK;

  const [step,         setStep]         = useState('choose'); // 'choose' | 'bundle-form' | 'bundle-roles'
  const [spouse1Name,  setSpouse1Name]  = useState('');
  const [spouse1Email, setSpouse1Email] = useState(user?.email || '');
  const [spouse2Name,  setSpouse2Name]  = useState('');
  const [spouse2Email, setSpouse2Email] = useState('');
  const [roles,        setRoles]        = useState({ executor: false, medicalPoa: false, powerOfAttorney: false });
  const [busy,         setBusy]         = useState(false);
  const [formErr,      setFormErr]      = useState('');

  const features = [
    "Complete estate settlement checklist (all 50 states)",
    "Artificial Intelligence (AI)-powered document generator",
    "Personal vault for documents and wishes",
    "Family collaboration dashboard",
    "AI estate guide (unlimited questions)",
    "Vetted attorney network",
  ];

  const handleSolo = () => {
    if (PAYMENT_LINK) {
      localStorage.setItem("heiros_pending_mode", profile?.signup_mode || "owner");
      window.location.href = `${PAYMENT_LINK}?client_reference_id=${user.id}&prefilled_email=${encodeURIComponent(user.email || '')}`;
    }
  };

  const handleBundleFormContinue = () => {
    setFormErr('');
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!spouse1Name.trim() || !spouse1Email.trim() || !spouse2Name.trim() || !spouse2Email.trim()) {
      setFormErr('Please fill in all four fields.');
      return;
    }
    if (!emailRe.test(spouse1Email) || !emailRe.test(spouse2Email)) {
      setFormErr('Please enter valid email addresses for both spouses.');
      return;
    }
    if (spouse1Email.toLowerCase() === spouse2Email.toLowerCase()) {
      setFormErr('Both spouses must have different email addresses.');
      return;
    }
    setStep('bundle-roles');
  };

  const handleBundlePayment = async () => {
    if (!COUPLE_PAYMENT_LINK || !user?.id) return;
    setBusy(true);
    setFormErr('');
    const selectedRoles = [
      roles.executor        && 'Named as executor',   // canonical role string — matches the invite UI
      roles.medicalPoa      && 'Medical POA',
      roles.powerOfAttorney && 'Power of Attorney',
    ].filter(Boolean);
    const { data: record, error, invite_code } = await createCoupleSignupRecord(user.id, {
      spouse1Name:   spouse1Name.trim(),
      spouse1Email:  spouse1Email.trim(),
      spouse2Name:   spouse2Name.trim(),
      spouse2Email:  spouse2Email.trim(),
      selectedRoles,
    });
    if (error || !record) {
      setFormErr('Could not save your details. Please try again.');
      setBusy(false);
      return;
    }
    window.location.href = `${COUPLE_PAYMENT_LINK}?client_reference_id=${invite_code}&prefilled_email=${encodeURIComponent(spouse1Email.trim())}`;
  };

  const fldStyle = { ...inputStyle, marginBottom: 0 };
  const checkRow = (checked, onChange, label, sub) => (
    <label key={label} style={{display:"flex", alignItems:"flex-start", gap:"0.75rem", padding:"0.875rem 1rem", borderRadius:"10px", border:`1px solid ${checked ? T.forest : T.border}`, background:checked ? '#f0faf5' : T.white, cursor:"pointer", marginBottom:"0.625rem", transition:"all 0.15s"}}>
      <input type="checkbox" checked={checked} onChange={onChange}
        style={{marginTop:"0.15rem", accentColor:T.forest, width:"16px", height:"16px", flexShrink:0, cursor:"pointer"}} />
      <div>
        <div style={{fontSize:"0.875rem", fontWeight:"600", color:T.charcoal, lineHeight:"1.4"}}>{label}</div>
        <div style={{fontSize:"0.75rem", color:T.muted, marginTop:"0.1rem"}}>{sub}</div>
      </div>
    </label>
  );

  // ── Step: choose plan ──────────────────────────────────────────────────────
  if (step === 'choose') {
    return (
      <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
        <div style={{width:"100%", maxWidth:"540px"}}>
          <div style={{textAlign:"center", marginBottom:"2rem"}}>
            <div style={{width:"56px", height:"56px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"14px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 1.25rem", color:"white", fontSize:"1.5rem", fontFamily:"'Playfair Display', serif", fontWeight:"700"}}>H</div>
            <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.375rem"}}>Choose your plan</h1>
            <p style={{color:T.muted, fontSize:"0.875rem"}}>One-time payment. No subscription. No recurring charges.</p>
          </div>

          {/* $299 Solo */}
          <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"20px", padding:"1.75rem 2rem", marginBottom:"1rem", boxShadow:"0 2px 16px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.125rem"}}>
              <div>
                <div style={{fontFamily:"'Playfair Display', serif", fontSize:"1.25rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Single Estate</div>
                <div style={{fontSize:"0.8rem", color:T.muted}}>Just you — one full estate plan</div>
              </div>
              <div style={{fontFamily:"'Playfair Display', serif", fontSize:"1.6rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.03em"}}>$299</div>
            </div>
            <div style={{marginBottom:"1.25rem"}}>
              {features.map((f, i) => (
                <div key={i} style={{display:"flex", alignItems:"center", gap:"0.625rem", padding:"0.3rem 0", borderBottom:i < features.length-1 ? `1px solid ${T.borderLight}` : "none"}}>
                  <span style={{color:T.forest, fontWeight:"700", fontSize:"0.7rem", flexShrink:0}}>✓</span>
                  <span style={{fontSize:"0.8rem", color:T.charcoal}}>{f}</span>
                </div>
              ))}
            </div>
            <button onClick={handleSolo} style={{...filledBtn, width:"100%", padding:"0.875rem", fontSize:"0.95rem"}}>
              Pay $299 &amp; Get Started →
            </button>
          </div>

          {/* $399 Couple's Bundle */}
          <div style={{background:T.white, border:`2px solid ${T.gold}`, borderRadius:"20px", padding:"1.75rem 2rem", boxShadow:"0 2px 20px rgba(184,147,90,0.15)", position:"relative"}}>
            <div style={{position:"absolute", top:"-12px", left:"50%", transform:"translateX(-50%)", background:`linear-gradient(135deg, ${T.gold}, ${T.goldLight})`, color:"white", fontSize:"0.65rem", fontWeight:"700", letterSpacing:"0.08em", padding:"0.25rem 0.875rem", borderRadius:"20px", whiteSpace:"nowrap"}}>BEST VALUE</div>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.125rem"}}>
              <div>
                <div style={{fontFamily:"'Playfair Display', serif", fontSize:"1.25rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Couple's Bundle</div>
                <div style={{fontSize:"0.8rem", color:T.muted}}>Two linked estates — one payment</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"'Playfair Display', serif", fontSize:"1.6rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.03em"}}>$399</div>
                <div style={{fontSize:"0.7rem", color:T.muted}}>$200 / person</div>
              </div>
            </div>
            <div style={{background:T.parchment, borderRadius:"10px", padding:"0.875rem 1rem", marginBottom:"1.25rem", fontSize:"0.8rem", color:T.charcoal, lineHeight:"1.6"}}>
              <div style={{fontWeight:"600", color:T.forest, marginBottom:"0.375rem"}}>Everything in Single, for both of you — plus:</div>
              {["Both spouses get their own full HeirOS account", "Estates are pre-linked from day one", "Optionally name each other as Executor, Medical POA & Power of Attorney", "Joint vault sharing available"].map((f, i) => (
                <div key={i} style={{display:"flex", alignItems:"center", gap:"0.5rem", padding:"0.2rem 0"}}>
                  <span style={{color:T.gold, fontWeight:"700", fontSize:"0.7rem", flexShrink:0}}>✦</span>
                  <span>{f}</span>
                </div>
              ))}
            </div>
            {COUPLE_PAYMENT_LINK ? (
              <button onClick={() => { setFormErr(''); setStep('bundle-form'); }}
                style={{...filledBtn, width:"100%", padding:"0.875rem", fontSize:"0.95rem", background:`linear-gradient(135deg, ${T.gold}, ${T.goldLight})`, boxShadow:`0 4px 16px rgba(184,147,90,0.30)`}}>
                Set Up Couple's Bundle →
              </button>
            ) : (
              <div style={{textAlign:"center", fontSize:"0.78rem", color:T.muted, padding:"0.75rem"}}>Couple's Bundle coming soon</div>
            )}
          </div>

          <p style={{textAlign:"center", marginTop:"1rem", fontSize:"0.72rem", color:T.muted}}>Secure payment powered by Stripe · 14-day money-back guarantee</p>
          {onSignOut && (
            <div style={{textAlign:"center", marginTop:"0.75rem"}}>
              <button onClick={onSignOut} style={{...btnReset, fontSize:"0.75rem", color:T.muted}}>Sign out of {user?.email}</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Step: collect both spouses' details ───────────────────────────────────
  if (step === 'bundle-form') {
    return (
      <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"2.5rem 2rem", width:"100%", maxWidth:"480px", boxShadow:"0 8px 40px rgba(0,0,0,0.09)"}}>
          <button onClick={() => setStep('choose')} style={{...btnReset, fontSize:"0.78rem", color:T.muted, marginBottom:"1.5rem"}}>← Back</button>
          <div style={{textAlign:"center", marginBottom:"1.75rem"}}>
            <div style={{fontSize:"1.5rem", marginBottom:"0.5rem"}}>💍</div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:T.forest, marginBottom:"0.375rem"}}>Tell us about both of you</h2>
            <p style={{color:T.muted, fontSize:"0.85rem"}}>Your spouse will receive an invitation to create their linked estate.</p>
          </div>

          <div style={{marginBottom:"1.25rem"}}>
            <p style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", color:T.gold, marginBottom:"0.75rem"}}>YOUR DETAILS</p>
            <label style={labelStyle}>Your full name</label>
            <input value={spouse1Name} onChange={e => setSpouse1Name(e.target.value)}
              placeholder="Jane Smith" style={{...fldStyle, marginBottom:"0.75rem"}} />
            <label style={labelStyle}>Your email</label>
            <input value={spouse1Email} onChange={e => setSpouse1Email(e.target.value)}
              type="email" placeholder="jane@example.com" style={fldStyle} />
          </div>

          <div style={{borderTop:`1px solid ${T.borderLight}`, paddingTop:"1.25rem", marginBottom:"1.5rem"}}>
            <p style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", color:T.gold, marginBottom:"0.75rem"}}>SPOUSE'S DETAILS</p>
            <label style={labelStyle}>Spouse's full name</label>
            <input value={spouse2Name} onChange={e => setSpouse2Name(e.target.value)}
              placeholder="John Smith" style={{...fldStyle, marginBottom:"0.75rem"}} />
            <label style={labelStyle}>Spouse's email</label>
            <input value={spouse2Email} onChange={e => setSpouse2Email(e.target.value)}
              type="email" placeholder="john@example.com" style={fldStyle} />
          </div>

          {formErr && <p style={{color:T.danger, fontSize:"0.8rem", marginBottom:"1rem"}}>{formErr}</p>}

          <button onClick={handleBundleFormContinue}
            style={{...filledBtn, width:"100%", padding:"0.875rem", fontSize:"0.95rem", background:`linear-gradient(135deg, ${T.gold}, ${T.goldLight})`}}>
            Continue →
          </button>
        </div>
      </div>
    );
  }

  // ── Step: choose reciprocal roles ─────────────────────────────────────────
  return (
    <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
      <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"24px", padding:"2.5rem 2rem", width:"100%", maxWidth:"480px", boxShadow:"0 8px 40px rgba(0,0,0,0.09)"}}>
        <button onClick={() => setStep('bundle-form')} style={{...btnReset, fontSize:"0.78rem", color:T.muted, marginBottom:"1.5rem"}}>← Back</button>
        <div style={{textAlign:"center", marginBottom:"1.75rem"}}>
          <div style={{fontSize:"1.5rem", marginBottom:"0.5rem"}}>🤝</div>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:T.forest, marginBottom:"0.375rem"}}>Automatic role setup</h2>
          <p style={{color:T.muted, fontSize:"0.85rem", lineHeight:"1.6"}}>Would you like to automatically name each other in any of these roles? Only checked roles will be created.</p>
        </div>

        <div style={{marginBottom:"1.5rem"}}>
          {checkRow(roles.executor,        e => setRoles(r => ({...r, executor:        e.target.checked})), 'Primary Executor',       'Manages the estate and carries out the will')}
          {checkRow(roles.medicalPoa,      e => setRoles(r => ({...r, medicalPoa:      e.target.checked})), 'Medical Power of Attorney', 'Makes healthcare decisions if incapacitated')}
          {checkRow(roles.powerOfAttorney, e => setRoles(r => ({...r, powerOfAttorney: e.target.checked})), 'Power of Attorney',      'Handles financial & legal decisions if incapacitated')}
        </div>

        <div style={{background:T.parchment, borderRadius:"10px", padding:"0.875rem 1rem", fontSize:"0.78rem", color:T.muted, lineHeight:"1.6", marginBottom:"1.5rem"}}>
          <strong style={{color:T.charcoal}}>None selected is fine.</strong> You can add or update roles from your estate settings at any time.
        </div>

        {formErr && <p style={{color:T.danger, fontSize:"0.8rem", marginBottom:"1rem"}}>{formErr}</p>}

        <button onClick={handleBundlePayment} disabled={busy}
          style={{...filledBtn, width:"100%", padding:"0.875rem", fontSize:"0.95rem", background:`linear-gradient(135deg, ${T.gold}, ${T.goldLight})`, opacity:busy?0.7:1}}>
          {busy ? 'Saving…' : 'Continue to Payment →'}
        </button>
        <p style={{textAlign:"center", marginTop:"0.75rem", fontSize:"0.72rem", color:T.muted}}>Secure payment · Stripe · 14-day money-back guarantee</p>
      </div>
    </div>
  );
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────
// ─── ONBOARDING SUB-COMPONENTS ───────────────────────────────────────────────
// Defined at module top level — NOT inside Onboarding — so React never sees a
// new component type on re-render and never unmounts/remounts input elements.

function OB_ProgressBar({ step, totalSteps }) {
  return (
    <div style={{width:"100%", maxWidth:"560px", marginBottom:"2rem"}}>
      <div style={{display:"flex", gap:"0.5rem", marginBottom:"0.5rem"}}>
        {Array.from({length: totalSteps}).map((_, i) => (
          <div key={i} style={{width:"28px", height:"28px", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.72rem", fontWeight:"600", background: i <= step ? T.forest : T.borderLight, color: i <= step ? "white" : T.muted, flexShrink:0}}>
            {i < step ? "✓" : i + 1}
          </div>
        ))}
      </div>
      <div style={{height:"4px", background:T.borderLight, borderRadius:"2px", overflow:"hidden"}}>
        <div style={{height:"100%", background:`linear-gradient(90deg, ${T.forest}, ${T.forestLight})`, width:totalSteps > 1 ? `${(step / (totalSteps - 1)) * 100}%` : "100%", transition:"width 0.3s ease"}} />
      </div>
      <p style={{fontSize:"0.75rem", color:T.muted, marginTop:"0.375rem"}}>Step {step + 1} of {totalSteps}</p>
    </div>
  );
}

function OB_Card({ title, sub, children, mode, step, totalSteps }) {
  return (
    <div style={{minHeight:"80vh", background:T.parchment, display:"flex", flexDirection:"column", alignItems:"center", padding:"3rem 1rem 4rem"}}>
      {mode !== "invited" && <OB_ProgressBar step={step} totalSteps={totalSteps} />}
      <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"20px", padding:"2.5rem 2rem", width:"100%", maxWidth:"560px", boxShadow:"0 4px 24px rgba(0,0,0,0.07)"}}>
        <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.6rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.375rem"}}>{title}</h2>
        {sub && <p style={{color:T.muted, fontSize:"0.875rem", marginBottom:"1.5rem", lineHeight:"1.55"}}>{sub}</p>}
        {children}
      </div>
    </div>
  );
}

function OB_Fld({ label, note, children }) {
  return (
    <div style={{marginBottom:"1rem"}}>
      <label style={labelStyle}>{label}{note && <span style={{fontWeight:"400", color:T.muted}}> {note}</span>}</label>
      {children}
    </div>
  );
}

function OB_ToggleRow({ field, options, data, set }) {
  return (
    <div style={{display:"flex", gap:"0.625rem", marginTop:"0.375rem", flexWrap:"wrap"}}>
      {options.map(v => (
        <button key={v} onClick={() => set({ [field]: v })} style={{...btnReset, flex:"1 1 auto", padding:"0.625rem 0.5rem", borderRadius:"8px", border:`2px solid ${data[field]===v?T.forest:T.border}`, background:data[field]===v?T.parchment:T.white, color:data[field]===v?T.forest:T.charcoal, fontWeight:data[field]===v?"600":"400", fontSize:"0.85rem", cursor:"pointer", minWidth:"60px"}}>{v}</button>
      ))}
    </div>
  );
}

function OB_NextBtn({ label, disabled, saving, advance }) {
  return (
    <button onClick={() => !disabled && !saving && advance()} disabled={!!disabled || saving} style={{...filledBtn, width:"100%", padding:"0.875rem", marginTop:"1.5rem", opacity:(disabled||saving)?0.5:1}}>
      {saving ? "Saving…" : (label || "Continue →")}
    </button>
  );
}

function OB_BackBtn({ back }) {
  return (
    <button onClick={back} style={{...btnReset, marginTop:"0.875rem", fontSize:"0.82rem", color:T.muted, display:"block"}}>← Back</button>
  );
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────
function Onboarding({ user, profile, onComplete, setProfile, modeOverride }) {
  const mode = modeOverride || profile?.signup_mode || "owner";
  const totalSteps = mode === "invited" ? 1 : mode === "owner" ? 3 : 4;
  const US_STATES = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"];
  const ESTATE_VALUES = ["Under $50,000","$50,000 – $150,000","$150,000 – $500,000","$500,000 – $1,000,000","$1,000,000 – $3,000,000","Over $3,000,000","Not sure"];

  const [step, setStep] = useState(() => Math.min(profile?.onboarding_data?.step || 0, totalSteps - 1));
  const [data, setData] = useState(() => {
    const saved = profile?.onboarding_data || {};
    if (mode === "owner") return { ...saved, state: saved.state || "Utah" };
    if (mode === "executor") return { ...saved, deceasedState: saved.deceasedState || "Utah" };
    return saved;
  });
  const [saving, setSaving] = useState(false);

  // Pre-fill fullName from the most recent invitation where invited_email = user's email.
  // Priority: existing data.fullName (user typed / prior session) > profile.onboarding_data.fullName
  // (already in data via useState) > invited_name from the invitation record > empty string.
  // The double-guard inside the .then() ensures we never overwrite a value the user typed
  // while the async call was in flight.
  useEffect(() => {
    if (data.fullName) return;
    if (!user?.email) return;
    supabase
      .from('invitations')
      .select('invited_name')
      .ilike('invited_email', user.email.toLowerCase().trim())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data: inv }) => {
        if (inv?.invited_name) {
          setData(prev => prev.fullName ? prev : { ...prev, fullName: inv.invited_name });
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const _parsedDob = (profile?.onboarding_data?.dob || "").split("-");
  const [dobMonth, setDobMonth] = useState(_parsedDob[1] ? parseInt(_parsedDob[1], 10) : "");
  const [dobDay,   setDobDay]   = useState(_parsedDob[2] ? parseInt(_parsedDob[2], 10) : "");
  const [dobYear,  setDobYear]  = useState(_parsedDob[0] ? parseInt(_parsedDob[0], 10) : "");

  const updateDob = (m, d, y) => {
    if (m && d && y) {
      const mm = String(m).padStart(2, "0");
      const dd = String(d).padStart(2, "0");
      set({ dob: `${y}-${mm}-${dd}` });
    }
  };

  const set = (updates) => setData(prev => ({ ...prev, ...updates }));

  const saveStep = async (merged, nextStep) => {
    const payload = { ...merged, step: nextStep };
    const { data: prof } = await updateProfile(user.id, { onboarding_data: payload });
    if (prof && setProfile) setProfile(prof);
  };

  const advance = async (extra = {}) => {
    const merged = { ...data, ...extra };
    setData(merged);
    if (step < totalSteps - 1) {
      setSaving(true);
      await saveStep(merged, step + 1);
      setSaving(false);
      setStep(s => s + 1);
    } else {
      setSaving(true);
      const form = { ...merged, signupMode: mode, ownerName: merged.ownerName || merged.fullName || '', deceasedName: merged.deceasedName || merged.yourName || "Estate" };
      await onComplete(form);
      setSaving(false);
    }
    window.scrollTo(0, 0);
  };

  const back = () => { setStep(s => Math.max(0, s - 1)); window.scrollTo(0, 0); };

  // ══════════════════════════════════════════════════════════════════════════
  // PLANNING AHEAD (owner) — 4 steps
  // ══════════════════════════════════════════════════════════════════════════
  if (mode === "owner") {
    if (step === 0) return (
      <OB_Card title="About You" sub="This information helps personalize your estate plan." mode={mode} step={step} totalSteps={totalSteps}>
        <OB_Fld label="Full legal name">
          <input value={data.fullName||""} onChange={e=>set({fullName:e.target.value})} placeholder="Jane Marie Smith" style={inputStyle} />
          <p style={{fontSize:"0.75rem", color:"#8a6020", background:"#fef9f0", border:"1px solid #f5ddb8", borderLeft:"3px solid #e8a020", borderRadius:"6px", padding:"0.45rem 0.75rem", marginTop:"0.5rem", lineHeight:"1.5"}}>⚠️ Enter your name <strong>exactly as it appears on your government-issued ID</strong>. This is used in your legal estate documents and cannot easily be changed later.</p>
        </OB_Fld>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:"0.75rem"}}>
          <OB_Fld label="Date of birth">
            <div style={{display:"grid", gridTemplateColumns:"3fr 2fr 3fr", gap:"0.5rem"}}>
              <select value={dobMonth} onChange={e=>{ const m=parseInt(e.target.value,10); setDobMonth(m); const maxDay=new Date(dobYear||2000,m,0).getDate(); const d=dobDay>maxDay?maxDay:dobDay; setDobDay(d); updateDob(m,d,dobYear); }} style={{...inputStyle, padding:"0.75rem 0.5rem", minWidth:0}}>
                <option value="">Month</option>
                {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((mn,i)=><option key={i+1} value={i+1}>{mn}</option>)}
              </select>
              <select value={dobDay} onChange={e=>{ const d=parseInt(e.target.value,10); setDobDay(d); updateDob(dobMonth,d,dobYear); }} style={{...inputStyle, padding:"0.75rem 0.4rem", minWidth:"60px", textAlign:"center"}}>
                <option value="">Day</option>
                {Array.from({length: dobMonth ? new Date(dobYear||2000,dobMonth,0).getDate() : 31},(_,i)=>i+1).map(d=><option key={d} value={d}>{d}</option>)}
              </select>
              <select value={dobYear} onChange={e=>{ const y=parseInt(e.target.value,10); setDobYear(y); updateDob(dobMonth,dobDay,y); }} style={{...inputStyle, padding:"0.75rem 0.5rem", minWidth:0}}>
                <option value="">Year</option>
                {Array.from({length:2026-1920-13+1},(_,i)=>2013-i).map(y=><option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </OB_Fld>
          <OB_Fld label="Phone number"><input type="tel" value={data.phone||""} onChange={e=>set({phone:formatPhone(e.target.value)})} placeholder="(801) 555-0100" style={{...inputStyle, minWidth:0}} /></OB_Fld>
        </div>
        <OB_Fld label="State of residence">
          <select value={data.state} onChange={e=>set({state:e.target.value})} style={inputStyle}>
            {US_STATES.map(s=><option key={s}>{s}</option>)}
          </select>
        </OB_Fld>
        <OB_NextBtn disabled={!data.fullName} label="Continue →" saving={saving} advance={advance} />
      </OB_Card>
    );

    if (step === 1) return (
      <OB_Card title="Your Estate" sub="A quick snapshot helps us tailor the right tools for you." mode={mode} step={step} totalSteps={totalSteps}>
        <OB_Fld label="Do you have a will?"><OB_ToggleRow field="hasWill" options={["Yes","No","Working on it"]} data={data} set={set} /></OB_Fld>
        <OB_Fld label="Do you own real estate?"><OB_ToggleRow field="hasProperty" options={["Yes","No"]} data={data} set={set} /></OB_Fld>
        <OB_Fld label="Estimated estate value">
          <select value={data.estateValue||""} onChange={e=>set({estateValue:e.target.value})} style={inputStyle}>
            <option value="">Select a range…</option>
            {ESTATE_VALUES.map(v=><option key={v}>{v}</option>)}
          </select>
        </OB_Fld>
        <OB_NextBtn disabled={!data.hasWill||!data.hasProperty} saving={saving} advance={advance} />
        <OB_BackBtn back={back} />
      </OB_Card>
    );

    if (step === 2) return (
      <OB_Card title="You're all set!" sub="Here's a summary of what you've entered." mode={mode} step={step} totalSteps={totalSteps}>
        <div style={{background:T.parchment, borderRadius:"12px", padding:"1.25rem 1.5rem", marginBottom:"1.5rem", display:"flex", flexDirection:"column", gap:"0.625rem"}}>
          {[["Name", data.fullName], ["Date of birth", data.dob], ["State", data.state], ["Has will", data.hasWill], ["Real estate", data.hasProperty], ["Estate value", data.estateValue]].filter(([,v])=>v).map(([k,v])=>(
            <div key={k} style={{display:"flex", justifyContent:"space-between", fontSize:"0.875rem"}}>
              <span style={{color:T.muted}}>{k}</span><span style={{fontWeight:"500", color:T.charcoal}}>{v}</span>
            </div>
          ))}
        </div>
        <button onClick={()=>!saving&&advance()} disabled={saving} style={{...filledBtn, width:"100%", padding:"0.875rem", background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, opacity:saving?0.5:1}}>
          {saving ? "Setting up…" : "Enter HeirOS →"}
        </button>
        <OB_BackBtn back={back} />
      </OB_Card>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // SETTLING AN ESTATE (executor) — 4 steps
  // ══════════════════════════════════════════════════════════════════════════
  if (mode === "executor") {
    if (step === 0) return (
      <OB_Card title="About the Deceased" sub="This helps us build the right checklist for your situation." mode={mode} step={step} totalSteps={totalSteps}>
        <OB_Fld label="Full legal name"><input value={data.deceasedName||""} onChange={e=>set({deceasedName:e.target.value})} placeholder="Full legal name" style={inputStyle} /></OB_Fld>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0.75rem"}}>
          <OB_Fld label="Date of death"><input type="date" value={data.dateOfDeath||""} onChange={e=>set({dateOfDeath:e.target.value})} style={inputStyle} /></OB_Fld>
          <OB_Fld label="State of residence">
            <select value={data.deceasedState} onChange={e=>set({deceasedState:e.target.value})} style={inputStyle}>
              {US_STATES.map(s=><option key={s}>{s}</option>)}
            </select>
          </OB_Fld>
        </div>
        <OB_Fld label="Did they have a will?"><OB_ToggleRow field="deceasedHadWill" options={["Yes","No","Unknown"]} data={data} set={set} /></OB_Fld>
        <OB_Fld label="Did they own real estate?"><OB_ToggleRow field="deceasedHadRealEstate" options={["Yes","No","Unknown"]} data={data} set={set} /></OB_Fld>
        <OB_Fld label="Estimated estate value">
          <select value={data.estateValue||""} onChange={e=>set({estateValue:e.target.value})} style={inputStyle}>
            <option value="">Select a range…</option>
            {ESTATE_VALUES.map(v=><option key={v}>{v}</option>)}
          </select>
        </OB_Fld>
        <OB_NextBtn disabled={!data.deceasedName||!data.deceasedHadWill||!data.deceasedHadRealEstate} saving={saving} advance={advance} />
      </OB_Card>
    );

    if (step === 1) return (
      <OB_Card title="About You" sub="As the executor, your contact information is required to proceed." mode={mode} step={step} totalSteps={totalSteps}>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0.75rem"}}>
          <OB_Fld label="Your full name"><input value={data.yourName||""} onChange={e=>set({yourName:e.target.value})} placeholder="Full name" style={inputStyle} /></OB_Fld>
          <OB_Fld label="Relationship to deceased"><input value={data.relationship||""} onChange={e=>set({relationship:e.target.value})} placeholder="e.g. Spouse, Child" style={inputStyle} /></OB_Fld>
        </div>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0.75rem"}}>
          <OB_Fld label="Your email"><input type="email" value={data.yourEmail||""} onChange={e=>set({yourEmail:e.target.value})} placeholder="you@example.com" style={inputStyle} /></OB_Fld>
          <OB_Fld label="Your phone"><input type="tel" value={data.yourPhone||""} onChange={e=>set({yourPhone:formatPhone(e.target.value)})} placeholder="(801) 555-0100" style={inputStyle} /></OB_Fld>
        </div>
        <OB_Fld label="Mailing address"><input value={data.yourAddress||""} onChange={e=>set({yourAddress:e.target.value})} placeholder="123 Main St, City, State 00000" style={inputStyle} /></OB_Fld>
        <OB_NextBtn disabled={!data.yourName} saving={saving} advance={advance} />
        <OB_BackBtn back={back} />
      </OB_Card>
    );

    if (step === 2) return (
      <OB_Card title="Backup Executors" sub="Optional — someone who can step in if needed." mode={mode} step={step} totalSteps={totalSteps}>
        <p style={{fontSize:"0.8rem", fontWeight:"600", color:T.charcoal, marginBottom:"0.625rem", textTransform:"uppercase", letterSpacing:"0.05em"}}>Backup #1 <span style={{fontWeight:"400", color:T.muted}}>(optional)</span></p>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"0.75rem", marginBottom:"1rem"}}>
          <OB_Fld label="Name"><input value={data.ex1Name||""} onChange={e=>set({ex1Name:e.target.value})} placeholder="Full name" style={inputStyle} /></OB_Fld>
          <OB_Fld label="Email"><input type="email" value={data.ex1Email||""} onChange={e=>set({ex1Email:e.target.value})} placeholder="email@example.com" style={inputStyle} /></OB_Fld>
          <OB_Fld label="Relationship"><input value={data.ex1Rel||""} onChange={e=>set({ex1Rel:e.target.value})} placeholder="e.g. Sibling" style={inputStyle} /></OB_Fld>
        </div>
        <p style={{fontSize:"0.8rem", fontWeight:"600", color:T.charcoal, margin:"0.25rem 0 0.625rem", textTransform:"uppercase", letterSpacing:"0.05em"}}>Backup #2 <span style={{fontWeight:"400", color:T.muted}}>(optional)</span></p>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"0.75rem"}}>
          <OB_Fld label="Name"><input value={data.ex2Name||""} onChange={e=>set({ex2Name:e.target.value})} placeholder="Full name" style={inputStyle} /></OB_Fld>
          <OB_Fld label="Email"><input type="email" value={data.ex2Email||""} onChange={e=>set({ex2Email:e.target.value})} placeholder="email@example.com" style={inputStyle} /></OB_Fld>
          <OB_Fld label="Relationship"><input value={data.ex2Rel||""} onChange={e=>set({ex2Rel:e.target.value})} placeholder="e.g. Sibling" style={inputStyle} /></OB_Fld>
        </div>
        <OB_NextBtn saving={saving} advance={advance} />
        <OB_BackBtn back={back} />
      </OB_Card>
    );

    if (step === 3) return (
      <OB_Card title="Your checklist is ready" sub="Here's a summary of what you entered." mode={mode} step={step} totalSteps={totalSteps}>
        <div style={{background:T.parchment, borderRadius:"12px", padding:"1.25rem 1.5rem", marginBottom:"1.5rem", display:"flex", flexDirection:"column", gap:"0.625rem"}}>
          {[["Deceased", data.deceasedName], ["Date of death", data.dateOfDeath], ["State", data.deceasedState], ["Had will", data.deceasedHadWill], ["Real estate", data.deceasedHadRealEstate], ["Estate value", data.estateValue], ["Your name", data.yourName], ["Relationship", data.relationship]].filter(([,v])=>v).map(([k,v])=>(
            <div key={k} style={{display:"flex", justifyContent:"space-between", fontSize:"0.875rem"}}>
              <span style={{color:T.muted}}>{k}</span><span style={{fontWeight:"500", color:T.charcoal}}>{v}</span>
            </div>
          ))}
        </div>
        <button onClick={()=>!saving&&advance()} disabled={saving} style={{...filledBtn, width:"100%", padding:"0.875rem", background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, opacity:saving?0.5:1}}>
          {saving ? "Setting up…" : "Begin Settlement →"}
        </button>
        <OB_BackBtn back={back} />
      </OB_Card>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // INVITED — single welcome step
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div style={{minHeight:"80vh", background:T.parchment, display:"flex", alignItems:"center", justifyContent:"center", padding:"3rem 1rem"}}>
      <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"20px", padding:"3rem 2rem", width:"100%", maxWidth:"440px", boxShadow:"0 4px 24px rgba(0,0,0,0.07)", textAlign:"center"}}>
        <div style={{fontSize:"3rem", marginBottom:"1rem"}}>🎉</div>
        <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.5rem"}}>You're in!</h2>
        <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.65", marginBottom:"2rem"}}>Your account has been created. You can now view and collaborate on the estate that was shared with you.</p>
        <button onClick={()=>advance({ signupMode:"invited", deceasedName:"Shared Estate" })} disabled={saving} style={{...filledBtn, width:"100%", padding:"0.875rem", background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, opacity:saving?0.5:1}}>
          {saving ? "Setting up…" : "Enter HeirOS →"}
        </button>
      </div>
    </div>
  );
}


// ─── APP PAGES ────────────────────────────────────────────────────────────────
function AppShell({ children, estate, tasks, page, navTo, invitedPermissions, invitedRole, invitedRoles }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  useEffect(() => { setSidebarOpen(false); }, [page]);
  const progress = tasks.length ? Math.round((tasks.filter(t=>t.done).length/tasks.length)*100) : 0;
  const isInvitedUser = !!invitedPermissions;
  const p = invitedPermissions || {};
  // For planning-ahead estates only use ownerName (not deceasedName which is unrelated)
  const ownerName = estate?.signupMode === 'owner'
    ? (estate?.ownerName || estate?.owner_name || null)
    : (estate?.deceasedName || estate?.deceased_name || estate?.ownerName || estate?.owner_name || null);
  // Fall back to single-role array when invitedRoles isn't threaded through yet
  const roles = (invitedRoles && invitedRoles.length > 0) ? invitedRoles : (invitedRole ? [invitedRole] : []);
  const hasExecutorRoleShell = roles.includes('Named as executor') || roles.includes('Executor');
  const isExecutorInSettlement = isInvitedUser && hasExecutorRoleShell && estate?.status === 'active_settlement';
  // "Medical Documents" only when user is EXCLUSIVELY Medical POA — holding executor too means full vault
  const isMedicalPoaOnly = isInvitedUser && roles.includes('Medical POA') && !hasExecutorRoleShell;
  // Single vault tab: label/gate depend on whether we're viewing our own estate or an invited one
  const sideVaultLabel = (!isInvitedUser) ? "My Vault"
    : isMedicalPoaOnly ? "Medical Documents"
    : (ownerName ? `${ownerName}'s Vault` : "Estate Vault");
  const sideVaultPermKey = (!isInvitedUser) ? null
    : isMedicalPoaOnly ? "view_medical_documents"
    : "view_vault";
  const allTabs = [
    {id:"app-dashboard", label:"Checklist",  icon:"✓",  permKey:"view_checklist"},
    {id:"app-vault",     label:sideVaultLabel, icon:"🔒", permKey:sideVaultPermKey},
    {id:"app-collab",    label:"Collaborators",     icon:"⊕",  permKey:"view_family"},
    {id:"app-docs",      label:"Documents",  icon:"⊟",  permKey:"view_documents"},
    {id:"app-ai",        label:"AI Guide",   icon:"◈",  permKey:null},
    {id:"app-playbook",  label:"Executor Playbook", icon:"📋", permKey:null, executorOnly:true},
  ];
  const tabs = allTabs.filter(tab => {
    if (tab.executorOnly) return isExecutorInSettlement;
    if (!isInvitedUser) return true;
    if (!tab.permKey) return true;
    if (tab.permKey === 'view_vault' || tab.permKey === 'view_medical_documents') return p[tab.permKey] === true;
    return p[tab.permKey] !== false;
  });
  return (
    <div style={{display:"flex", minHeight:"calc(100vh - 72px)"}}>
      {isMobile && (
        <button
          onClick={()=>setSidebarOpen(o=>!o)}
          aria-label="Toggle menu"
          style={{...btnReset, position:"fixed", top:"84px", left:"1rem", zIndex:301, width:"42px", height:"42px", borderRadius:"10px", background:T.forestDark, color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem", boxShadow:"0 2px 12px rgba(0,0,0,0.18)"}}
        >
          {sidebarOpen ? "✕" : "☰"}
        </button>
      )}
      {isMobile && sidebarOpen && (
        <div onClick={()=>setSidebarOpen(false)} style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:299}} />
      )}
      <aside style={isMobile ? {
          width:"240px", background:T.forestDark, color:"white", padding:"1.5rem", flexShrink:0, display:"flex", flexDirection:"column",
          position:"fixed", top:0, bottom:0, left:0, zIndex:300, paddingTop:"5.5rem", overflowY:"auto",
          transform:sidebarOpen?"translateX(0)":"translateX(-100%)", transition:"transform 0.25s ease",
        } : {width:"240px", background:T.forestDark, color:"white", padding:"1.5rem", flexShrink:0, display:"flex", flexDirection:"column"}}>
        {estate && (
          <div style={{background:"rgba(255,255,255,0.07)", borderRadius:"10px", padding:"1rem", marginBottom:"1.5rem"}}>
            <div style={{fontSize:"0.6rem", letterSpacing:"0.1em", opacity:0.5, fontFamily:"'DM Mono', monospace", marginBottom:"0.25rem"}}>
              {estate.signupMode === "owner" ? "PLANNING AHEAD FOR" : "ESTATE OF"}
            </div>
            <div style={{fontFamily:"'Playfair Display', serif", fontSize:"0.95rem", fontWeight:"600", marginBottom:"0.375rem"}}>
              {estate.signupMode === "owner" ? estate.ownerName : estate.deceasedName}
            </div>
            {estate.signupMode === "owner" && (
              <div style={{fontSize:"0.65rem", opacity:0.5, marginBottom:"0.5rem", fontFamily:"'DM Mono', monospace"}}>
                {estate.collaborators?.length > 0 ? `${estate.collaborators.length} family member${estate.collaborators.length!==1?"s":""} invited` : "Vault & planning active"}
              </div>
            )}
            {estate.signupMode !== "owner" && (
              <>
                <div style={{height:"4px", background:"rgba(255,255,255,0.15)", borderRadius:"2px", overflow:"hidden"}}>
                  <div style={{height:"100%", width:`${progress}%`, background:`linear-gradient(90deg, ${T.gold}, ${T.goldLight})`, borderRadius:"2px"}} />
                </div>
                <div style={{fontSize:"0.65rem", opacity:0.5, marginTop:"0.375rem", fontFamily:"'DM Mono', monospace"}}>{progress}% complete</div>
              </>
            )}
          </div>
        )}
        <nav style={{display:"flex", flexDirection:"column", gap:"0.25rem", flex:1}}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>navTo(t.id)} style={{...btnReset, padding:"0.75rem 1rem", borderRadius:"8px", color:(page===t.id||(t.id==="app-docs"&&page.startsWith("app-docs")))?"white":"rgba(255,255,255,0.55)", background:(page===t.id||(t.id==="app-docs"&&page.startsWith("app-docs")))?"rgba(255,255,255,0.12)":"transparent", display:"flex", alignItems:"center", gap:"0.75rem", fontSize:"0.875rem", fontWeight:(page===t.id||(t.id==="app-docs"&&page.startsWith("app-docs")))?"500":"400"}}>
              <span style={{fontSize:"1rem", opacity:0.8}}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </nav>
        <div style={{paddingTop:"1rem", borderTop:"1px solid rgba(255,255,255,0.1)"}}>
          <div style={{fontSize:"0.65rem", opacity:0.35, lineHeight:"1.5", fontFamily:"'DM Mono', monospace"}}>HeirOS is not a law firm. Consult an attorney for complex legal matters.</div>
        </div>
      </aside>
      <main style={{flex:1, background:T.cream, overflowY:"auto", display:"flex", flexDirection:"column"}}>
        <div style={{padding:"2.5rem", flex:1}}>
          {children}
        </div>
      </main>
    </div>
  );
}

function LockedMyVault({ navTo, estate, invitedPermissions, invitedRole, user }) {
  const PAYMENT_LINK = process.env.REACT_APP_STRIPE_PAYMENT_LINK;
  const handleUpgrade = () => {
    if (PAYMENT_LINK) {
      localStorage.setItem('heiros_invited_upgrade', 'true');
      window.location.href = `${PAYMENT_LINK}?source=invited_upgrade&client_reference_id=${user.id}&prefilled_email=${encodeURIComponent(user.email || '')}`;
    }
  };
  return (
    <AppShell estate={estate} tasks={[]} page="app-my-vault" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
      <div style={{display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"50vh", gap:"1.75rem", textAlign:"center", padding:"2rem"}}>
        <div style={{width:"72px", height:"72px", borderRadius:"50%", background:`linear-gradient(135deg, ${T.parchment}, ${T.cream})`, border:`2px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2rem"}}>🔒</div>
        <div>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.6rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em", marginBottom:"0.5rem"}}>Your Personal Vault</h2>
          <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.7", maxWidth:"420px", margin:"0 auto"}}>Store your own documents, passwords, wishes, and estate plans in a private vault that belongs to you — separate from the estate you're helping settle.</p>
        </div>
        <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"1.75rem 2rem", maxWidth:"380px", width:"100%", boxShadow:"0 4px 16px rgba(0,0,0,0.06)"}}>
          <p style={{fontSize:"0.72rem", fontFamily:"'DM Mono', monospace", color:T.gold, letterSpacing:"0.08em", marginBottom:"0.75rem"}}>ONE-TIME UPGRADE — $299</p>
          <div style={{display:"flex", flexDirection:"column", gap:"0.5rem", marginBottom:"1.25rem"}}>
            {["Personal vault for docs & passwords", "Planning checklist (all 50 states)", "AI estate guide (unlimited questions)"].map(f => (
              <div key={f} style={{display:"flex", alignItems:"center", gap:"0.5rem", fontSize:"0.8rem", color:T.charcoal, textAlign:"left"}}>
                <span style={{color:T.forest, fontWeight:"700", flexShrink:0}}>✓</span>{f}
              </div>
            ))}
          </div>
          <button onClick={handleUpgrade} style={{...filledBtn, width:"100%", padding:"0.75rem", fontSize:"0.875rem"}}>
            Start My Estate →
          </button>
        </div>
        <button onClick={() => navTo('app-dashboard')} style={{...btnReset, fontSize:"0.8rem", color:T.muted}}>← Back to Estate</button>
      </div>
    </AppShell>
  );
}

const PLAN_CAT_COLORS = { Legal:"#8e44ad", Vault:"#2c4a3e", Financial:"#27ae60", Family:"#2980b9", "Annual Review":"#b8935a" };

// ─── TASK MODAL ───────────────────────────────────────────────────────────────
function TaskModal({ mode, initialTask, defaultCategory, existingCategories, onSave, onDelete, onClose }) {
  const [title, setTitle] = useState(initialTask?.title || '');
  const [category, setCategory] = useState(initialTask?.category || defaultCategory || existingCategories[0] || '');
  const [newCat, setNewCat] = useState('');
  const [urgency, setUrgency] = useState(['Low','Normal','High'].includes(initialTask?.urgency) ? initialTask.urgency : 'Normal');
  const [notes, setNotes] = useState(initialTask?.notes || '');
  const [subtasks, setSubtasks] = useState(initialTask?.subtasks || []);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const finalCat = category === '__new__' ? newCat.trim() : category;
  const canSave = title.trim() && finalCat;
  const canDelete = mode === 'edit' && initialTask && (initialTask.isCustom || initialTask.isOverride);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    await onSave({ title: title.trim(), category: finalCat, urgency, notes: notes.trim(), subtasks: subtasks.filter(s => s.title.trim()) });
    setSaving(false);
  };

  return (
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.42)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:"1rem"}}>
      <div style={{background:T.white,borderRadius:"14px",padding:"1.75rem",width:"100%",maxWidth:"500px",maxHeight:"88vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <h2 style={{fontFamily:"'Playfair Display', serif",fontSize:"1.35rem",fontWeight:"700",color:T.forest,marginBottom:"1.5rem"}}>{mode==='add'?'Add Task':'Edit Task'}</h2>

        <label style={labelStyle}>Title *</label>
        <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Task title..." style={{...inputStyle,marginBottom:"1rem"}} />

        <label style={labelStyle}>Category *</label>
        <select value={category} onChange={e=>setCategory(e.target.value)} style={{...inputStyle,marginBottom:category==='__new__'?"0.375rem":"1rem"}}>
          {existingCategories.map(c=><option key={c} value={c}>{c}</option>)}
          <option value="__new__">+ Create new category...</option>
        </select>
        {category==='__new__' && <input value={newCat} onChange={e=>setNewCat(e.target.value)} placeholder="New category name..." style={{...inputStyle,marginBottom:"1rem"}} />}

        <label style={labelStyle}>Urgency</label>
        <div style={{display:"flex",gap:"0.5rem",marginBottom:"1rem"}}>
          {['Low','Normal','High'].map(u=>(
            <button key={u} onClick={()=>setUrgency(u)} style={{...btnReset,flex:1,padding:"0.4rem",borderRadius:"20px",fontSize:"0.78rem",fontWeight:"500",border:`1px solid ${u==='High'?T.danger:u==='Normal'?T.forest:T.border}`,background:urgency===u?(u==='High'?T.danger:u==='Normal'?T.forest:T.muted):'transparent',color:urgency===u?'white':u==='High'?T.danger:u==='Normal'?T.forest:T.muted}}>{u}</button>
          ))}
        </div>

        <label style={labelStyle}>Notes</label>
        <textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Add any notes or context..." rows={3} style={{...inputStyle,resize:"vertical",fontFamily:"'DM Sans', sans-serif",marginBottom:"1.25rem"}} />

        <div style={{marginBottom:"1.5rem"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.5rem"}}>
            <label style={{...labelStyle,marginBottom:0}}>Subtasks</label>
            <button onClick={()=>setSubtasks(p=>[...p,{id:Date.now().toString(36)+Math.random().toString(36).slice(2),title:'',done:false}])} style={{...btnReset,fontSize:"0.75rem",color:T.forest,fontWeight:"500"}}>+ Add</button>
          </div>
          {subtasks.map((st,i)=>(
            <div key={st.id} style={{display:"flex",gap:"0.5rem",marginBottom:"0.375rem",alignItems:"center"}}>
              <input value={st.title} onChange={e=>setSubtasks(p=>p.map(s=>s.id===st.id?{...s,title:e.target.value}:s))} placeholder={`Subtask ${i+1}...`} style={{flex:1,padding:"0.4rem 0.625rem",borderRadius:"6px",border:`1px solid ${T.border}`,fontSize:"0.82rem",color:T.charcoal,outline:"none",fontFamily:"inherit"}} />
              <button onClick={()=>setSubtasks(p=>p.filter(s=>s.id!==st.id))} style={{...btnReset,color:T.muted,fontSize:"0.85rem"}}>✕</button>
            </div>
          ))}
        </div>

        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"0.75rem",flexWrap:"wrap"}}>
          <div>
            {canDelete && !confirmDel && <button onClick={()=>setConfirmDel(true)} style={{...btnReset,fontSize:"0.78rem",color:T.danger}}>Delete task</button>}
            {canDelete && confirmDel && (
              <div style={{display:"flex",gap:"0.5rem",alignItems:"center"}}>
                <span style={{fontSize:"0.72rem",color:T.muted}}>Are you sure?</span>
                <button onClick={async()=>{setDeleting(true);await onDelete(initialTask);setDeleting(false);}} disabled={deleting} style={{...btnReset,fontSize:"0.72rem",color:"white",background:T.danger,padding:"0.275rem 0.625rem",borderRadius:"5px"}}>{deleting?"Deleting...":"Yes, delete"}</button>
                <button onClick={()=>setConfirmDel(false)} style={{...btnReset,fontSize:"0.72rem",color:T.muted}}>Cancel</button>
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:"0.5rem"}}>
            <button onClick={onClose} style={{...btnReset,padding:"0.5rem 1rem",borderRadius:"8px",fontSize:"0.82rem",border:`1px solid ${T.border}`,color:T.muted}}>Cancel</button>
            <button onClick={handleSave} disabled={!canSave||saving} style={{...btnReset,padding:"0.5rem 1.25rem",borderRadius:"8px",fontSize:"0.82rem",background:canSave?T.forest:"#ccc",color:"white",fontWeight:"500",opacity:saving?0.7:1}}>{saving?"Saving...":"Save Task"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppDashboard({ tasks, onToggle, progress, estate, navTo, deathReport, invitedPermissions, invitedRole, setTasks, estateSupabaseId, user }) {
  const isOwner = estate?.signupMode === "owner";
  const isInvitedUser = !!invitedPermissions || !!invitedRole;
  const [filter, setFilter] = useState("All");
  const [taskModal, setTaskModal] = useState(null);
  const [hoveredTask, setHoveredTask] = useState(null);
  const [expandedSubtasks, setExpandedSubtasks] = useState(new Set());
  const [expandedPhases, setExpandedPhases] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  const togglePhase = (phaseKey) => {
    setExpandedPhases(prev => {
      const n = new Set(prev);
      if (n.has(phaseKey)) {
        n.delete(phaseKey);
      } else {
        n.add(phaseKey);
      }
      return n;
    });
  };

  const toggleCategory = (phaseKey, cat) => {
    const key = `${phaseKey}:${cat}`;
    setExpandedCategories(prev => {
      const n = new Set(prev);
      if (n.has(key)) {
        n.delete(key);
      } else {
        n.add(key);
      }
      return n;
    });
  };

  const isCatExpanded = (phaseKey, cat) => {
    const key = `${phaseKey}:${cat}`;
    // Default open — only collapsed if explicitly toggled closed
    return !expandedCategories.has(key);
  };
  const perms = invitedPermissions || {};
  const canViewChecklist = !invitedPermissions || perms.full_access || perms.view_checklist !== false;
  const canEditChecklist = !invitedPermissions || perms.full_access || perms.edit_checklist === true;

  const planningCats = [...new Set(["Legal","Vault","Financial","Family","Annual Review",...tasks.filter(t=>t.isCustom&&t.phase).map(t=>t.category)])];
  const settlementCats = [...new Set(["Immediate","Legal","Government","Financial","Digital","Tax",...tasks.filter(t=>t.isCustom&&!t.phase).map(t=>t.category)])];

  const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2);

  const handleSaveTask = async (taskData, editingTask) => {
    const taskId = editingTask?.id ?? uid();
    const localTask = {
      id: taskId,
      title: taskData.title,
      category: taskData.category,
      phase: taskModal?.phase || editingTask?.phase || null,
      done: editingTask?.done || false,
      urgency: taskData.urgency,
      description: editingTask?.description || '',
      action: editingTask?.action || null,
      notes: taskData.notes,
      subtasks: taskData.subtasks || [],
      isCustom: !editingTask || editingTask.isCustom,
      isOverride: !!(editingTask && !editingTask.isCustom),
      required: editingTask?.required || false,
      annual: editingTask?.annual || false,
    };
    if (editingTask) {
      setTasks(prev => prev.map(t => t.id === editingTask.id ? {...t, ...localTask} : t));
    } else {
      setTasks(prev => [...prev, localTask]);
    }
    if (estateSupabaseId && user) {
      await supabase.from('tasks').upsert({
        task_id: taskId,
        estate_id: estateSupabaseId,
        user_id: user.id,
        title: taskData.title,
        category: taskData.category,
        phase: localTask.phase,
        urgency: taskData.urgency,
        notes: taskData.notes,
        subtasks: taskData.subtasks || [],
        is_custom: localTask.isCustom,
        is_override: localTask.isOverride,
        done: localTask.done,
        required: localTask.required,
        annual: localTask.annual,
        description: editingTask?.description || null,
      }, { onConflict: 'estate_id,task_id' });
    }
    setTaskModal(null);
  };

  const handleDeleteTask = async (task) => {
    if (task.isOverride) {
      const orig = [...PLANNING_STEPS, ...CHECKLIST_STEPS].find(s => s.id === task.id);
      if (orig) setTasks(prev => prev.map(t => t.id === task.id ? {...orig, done: task.done, isCustom: false, isOverride: false} : t));
    } else {
      setTasks(prev => prev.filter(t => t.id !== task.id));
    }
    if (estateSupabaseId) {
      await supabase.from('tasks').delete().eq('estate_id', estateSupabaseId).eq('task_id', task.id);
    }
    setTaskModal(null);
  };

  const toggleSubtask = async (task, subtaskId) => {
    const newSubs = (task.subtasks || []).map(st => st.id === subtaskId ? {...st, done: !st.done} : st);
    setTasks(prev => prev.map(t => t.id === task.id ? {...t, subtasks: newSubs} : t));
    if (estateSupabaseId) {
      await supabase.from('tasks').update({ subtasks: newSubs }).eq('estate_id', estateSupabaseId).eq('task_id', task.id);
    }
  };

  const toggleSubExpand = (taskId) => {
    setExpandedSubtasks(prev => { const n = new Set(prev); n.has(taskId) ? n.delete(taskId) : n.add(taskId); return n; });
  };

  const renderTask = (task, catColMap) => {
    const colMap = catColMap || {};
    const catCol = colMap[task.category] || CAT_COLORS[task.category] || T.forest;
    const hasSubs = task.subtasks?.length > 0;
    const expanded = expandedSubtasks.has(task.id);
    return (
      <div key={task.id}>
        <div
          onMouseEnter={() => setHoveredTask(task.id)}
          onMouseLeave={() => setHoveredTask(null)}
          style={{background:T.white, border:`1px solid ${task.done?T.borderLight:T.border}`, borderRadius:"10px", padding:"1rem 1.25rem", display:"flex", gap:"1rem", opacity:task.done?0.6:1, transition:"all 0.15s"}}
        >
          <button onClick={() => canEditChecklist && onToggle(task.id)} style={{...btnReset, flexShrink:0, paddingTop:"2px", cursor:canEditChecklist?"pointer":"default"}}>
            {task.done
              ? <div style={{width:"22px", height:"22px", borderRadius:"50%", background:T.success, display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:"0.7rem", fontWeight:"700"}}>✓</div>
              : <div style={{width:"22px", height:"22px", borderRadius:"50%", border:`2px solid ${canEditChecklist?T.border:T.borderLight}`}}/>}
          </button>
          <div style={{flex:1, minWidth:0}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"0.75rem", flexWrap:"wrap"}}>
              <div style={{flex:1, minWidth:0}}>
                <div style={{display:"flex", alignItems:"center", gap:"0.4rem", flexWrap:"wrap", marginBottom:"0.3rem"}}>
                  <p style={{fontSize:"0.9rem", fontWeight:"500", color:task.done?T.muted:T.charcoal, textDecoration:task.done?"line-through":"none", margin:0, lineHeight:"1.4"}}>{task.title}</p>
                  {task.isCustom && <span style={{fontSize:"0.55rem", padding:"0.1rem 0.35rem", borderRadius:"3px", background:T.gold+"22", color:T.gold, fontFamily:"'DM Mono', monospace", letterSpacing:"0.05em", flexShrink:0}}>custom</span>}
                  {task.isOverride && <span style={{fontSize:"0.55rem", padding:"0.1rem 0.35rem", borderRadius:"3px", background:"#e8f4fd", color:"#2980b9", fontFamily:"'DM Mono', monospace", letterSpacing:"0.05em", flexShrink:0}}>edited</span>}
                </div>
                <div style={{display:"flex", alignItems:"center", gap:"0.4rem", flexWrap:"wrap"}}>
                  <span style={{fontSize:"0.63rem", padding:"0.15rem 0.45rem", borderRadius:"4px", background:catCol+"18", color:catCol, fontFamily:"'DM Mono', monospace", letterSpacing:"0.04em"}}>{task.category}</span>
                  <span style={{fontSize:"0.65rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>⏱ {task.urgency}</span>
                  {task.required && !task.done && <span style={{fontSize:"0.62rem", padding:"0.15rem 0.45rem", borderRadius:"4px", background:"#fff8e6", color:"#7a5a0a", fontFamily:"'DM Mono', monospace"}}>Required</span>}
                  {hasSubs && (
                    <button onClick={e=>{e.stopPropagation();toggleSubExpand(task.id);}} style={{...btnReset, fontSize:"0.63rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>
                      {expanded?"▼":"▶"} {task.subtasks.length} subtask{task.subtasks.length!==1?"s":""}
                    </button>
                  )}
                </div>
              </div>
              <div style={{display:"flex", alignItems:"flex-start", gap:"0.375rem", flexShrink:0}}>
                {task.action && !task.done && !isInvitedUser && (
                  <button onClick={e=>{e.stopPropagation();navTo(task.action.page);}} style={{...btnReset, padding:"0.35rem 0.75rem", borderRadius:"7px", fontSize:"0.75rem", fontWeight:"500", border:`1px solid ${T.forest}`, color:T.forest, background:"transparent", whiteSpace:"nowrap", transition:"background 0.15s,color 0.15s"}} onMouseEnter={e=>{e.currentTarget.style.background=T.forest;e.currentTarget.style.color="white";}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=T.forest;}}>
                    {task.action.label}
                  </button>
                )}
                {canEditChecklist && (
                  <button onClick={e=>{e.stopPropagation();setTaskModal({mode:'edit',task,phase:task.phase,checklist:isOwner&&!isInvitedUser?'planning':'settlement'});}} title="Edit task" style={{...btnReset, fontSize:"0.8rem", color:hoveredTask===task.id?T.muted:"transparent", transition:"color 0.12s", paddingTop:"2px", flexShrink:0}}>
                    ✏️
                  </button>
                )}
              </div>
            </div>
            {!task.done && task.description && <p style={{fontSize:"0.8rem", color:T.muted, lineHeight:"1.55", margin:"0.5rem 0 0"}}>{task.description}</p>}
            {task.notes && <p style={{fontSize:"0.75rem", color:T.muted, lineHeight:"1.5", margin:"0.375rem 0 0", fontStyle:"italic"}}>📝 {task.notes}</p>}
          </div>
        </div>
        {hasSubs && expanded && (
          <div style={{marginLeft:"46px", marginTop:"0.25rem", paddingLeft:"12px", borderLeft:`2px solid ${T.border}`, display:"flex", flexDirection:"column", gap:"0.25rem"}}>
            {task.subtasks.map(st => (
              <div key={st.id} style={{display:"flex", alignItems:"center", gap:"0.625rem", padding:"0.45rem 0.75rem", background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"7px"}}>
                <button onClick={() => canEditChecklist && toggleSubtask(task, st.id)} style={{...btnReset, flexShrink:0, cursor:canEditChecklist?"pointer":"default"}}>
                  {st.done
                    ? <div style={{width:"16px", height:"16px", borderRadius:"50%", background:T.success, display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:"0.55rem", fontWeight:"700"}}>✓</div>
                    : <div style={{width:"16px", height:"16px", borderRadius:"50%", border:`2px solid ${T.border}`}}/>}
                </button>
                <span style={{fontSize:"0.82rem", color:st.done?T.muted:T.charcoal, textDecoration:st.done?"line-through":"none", flex:1}}>{st.title}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── EXECUTOR / INVITED / SETTLEMENT MODE ────────────────────────────────────
  // Only show the Settlement Checklist when the estate is in active settlement.
  // B6: canViewChecklist was previously enforced only inside the
  // active_settlement branch below. On a planning-phase estate the code fell
  // through to the Planning Ahead checklist with no view check, so a
  // collaborator whose role denies view_checklist (Medical POA, for example)
  // could read checklist content they have no access to. Checked here, before
  // either branch, so both paths are covered. Owners are unaffected because
  // invitedPermissions is null for them, which makes canViewChecklist true.
  if (!canViewChecklist) {
    return (
      <AppShell estate={estate} tasks={tasks} page="app-dashboard" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
        <PermissionDeniedBanner title="Checklist" message="You don't have permission to view the checklist. Contact the estate owner to request access." />
      </AppShell>
    );
  }

  // For invited users viewing a planning-phase estate, fall through to the
  // Planning Ahead checklist below (rendered read-only via canEditChecklist).
  if ((!isOwner || isInvitedUser) && estate?.status === 'active_settlement') {
    if (isInvitedUser && (invitedRole === 'Named as executor' || invitedRole === 'Executor')) {
      return (
        <AppShell estate={estate} tasks={tasks} page="app-dashboard" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
          <div style={{display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"320px", gap:"1.25rem", textAlign:"center", padding:"2rem"}}>
            <span style={{fontSize:"2.5rem"}}>📋</span>
            <div>
              <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, marginBottom:"0.5rem"}}>Your tasks are in the Executor Playbook</h2>
              <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.65", maxWidth:"400px", margin:"0 auto"}}>
                As the named executor, your settlement checklist lives in the Executor Playbook tab — designed specifically for your role.
              </p>
            </div>
            <button onClick={() => navTo("app-playbook")} style={{...filledBtn, padding:"0.625rem 1.5rem", fontSize:"0.875rem"}}>
              Go to Executor Playbook →
            </button>
          </div>
        </AppShell>
      );
    }
    if (!canViewChecklist) {
      return (
        <AppShell estate={estate} tasks={tasks} page="app-dashboard" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
          <PermissionDeniedBanner title="Checklist" message="You don't have permission to view the checklist. Contact the estate owner to request access." />
        </AppShell>
      );
    }
    const allSettleCats = [...new Set([...Object.keys(CAT_COLORS), ...tasks.filter(t=>t.isCustom).map(t=>t.category)])];
    const filterCats = ["All", ...allSettleCats];
    const displayedCats = filter === "All" ? allSettleCats : [filter];
    const done = tasks.filter(t=>t.done).length;
    const urgent = tasks.filter(t=>!t.done && t.category==="Immediate").length;

    return (
      <AppShell estate={estate} tasks={tasks} page="app-dashboard" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
        {taskModal && (
          <TaskModal
            mode={taskModal.mode}
            initialTask={taskModal.task}
            defaultCategory={taskModal.category}
            existingCategories={settlementCats}
            onSave={data => handleSaveTask(data, taskModal.mode==='edit' ? taskModal.task : null)}
            onDelete={handleDeleteTask}
            onClose={() => setTaskModal(null)}
          />
        )}
        {!canEditChecklist && (
          <div style={{background:"#fef9f0", border:`1px solid ${T.tan}`, borderRadius:"10px", padding:"0.75rem 1rem", marginBottom:"1.25rem", fontSize:"0.82rem", color:T.goldDark}}>
            👁 You have view-only access to this checklist. Tasks cannot be marked complete.
          </div>
        )}
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.75rem", flexWrap:"wrap", gap:"1rem"}}>
          <div>
            <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Settlement Checklist</h1>
            <p style={{color:T.muted, fontSize:"0.875rem"}}>{done} of {tasks.length} tasks complete{urgent>0 && <span style={{color:T.danger}}> · {urgent} need attention now</span>}</p>
          </div>
          <div style={{position:"relative", width:"60px", height:"60px"}}>
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle cx="30" cy="30" r="26" fill="none" stroke={T.parchment} strokeWidth="5"/>
              <circle cx="30" cy="30" r="26" fill="none" stroke={T.forest} strokeWidth="5" strokeDasharray={`${progress*1.634} 163.4`} strokeLinecap="round" transform="rotate(-90 30 30)" style={{transition:"stroke-dasharray 0.5s ease"}}/>
            </svg>
            <span style={{position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.7rem", fontWeight:"700", color:T.forest, fontFamily:"'DM Mono', monospace"}}>{progress}%</span>
          </div>
        </div>
        {/* Filter tabs */}
        <div style={{display:"flex", gap:"0.5rem", marginBottom:"1.5rem", flexWrap:"wrap"}}>
          {filterCats.map(c => {
            const col = c==="All" ? T.forest : CAT_COLORS[c] || T.muted;
            return (
              <button key={c} onClick={()=>setFilter(c)} style={{...btnReset, padding:"0.375rem 0.875rem", borderRadius:"20px", fontSize:"0.78rem", border:`1px solid ${filter===c?col:T.border}`, background:filter===c?col:"transparent", color:filter===c?"white":c==="All"?T.muted:col, fontWeight:filter===c?"500":"400"}}>
                {c}
              </button>
            );
          })}
        </div>
        {/* Category sections */}
        {displayedCats.map(cat => {
          const catTasks = tasks.filter(t => t.category === cat);
          if (!catTasks.length && filter !== cat) return null;
          const catColor = CAT_COLORS[cat] || T.forest;
          return (
            <div key={cat} style={{marginBottom:"1.75rem"}}>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"0.625rem"}}>
                <span style={{fontSize:"0.65rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.07em", color:catColor, textTransform:"uppercase", fontWeight:"600"}}>{cat}</span>
                {catTasks.length > 0 && <span style={{fontSize:"0.65rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>{catTasks.filter(t=>t.done).length}/{catTasks.length}</span>}
              </div>
              <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
                {catTasks.map(task => renderTask(task, CAT_COLORS))}
              </div>
              {canEditChecklist && (
                <button onClick={()=>setTaskModal({mode:'add', category:cat, checklist:'settlement'})} style={{...btnReset, marginTop:"0.5rem", fontSize:"0.73rem", color:T.muted, display:"flex", alignItems:"center", gap:"0.25rem"}}>
                  + Add Task
                </button>
              )}
            </div>
          );
        })}
      </AppShell>
    );
  }

  // ── PLANNING / OWNER MODE ──────────────────────────────────────────────────
  const customPlanCatExtras = tasks.filter(t=>t.isCustom&&t.phase).map(t=>t.category).filter(c=>!["Legal","Vault","Financial","Family","Annual Review"].includes(c));
  const PLAN_CATS = ["All","Legal","Vault","Financial","Family","Annual Review",...new Set(customPlanCatExtras)];
  const phases = [
    { key:"Phase 1", label:"Get Your House in Order" },
    { key:"Phase 2", label:"Protect Your Family" },
    { key:"Phase 3", label:"Annual Review" },
  ];

  const getTasksByPhase = (phaseKey) => {
    const phaseTasks = tasks.filter(t => t.phase === phaseKey);
    if (filter === "All") return phaseTasks;
    return phaseTasks.filter(t => t.category === filter);
  };

  const requiredTasks = tasks.filter(t => t.required);
  const completedRequired = requiredTasks.filter(t => t.done).length;
  const readinessScore = requiredTasks.length ? Math.round((completedRequired / requiredTasks.length) * 100) : 0;
  const scoreColor = readinessScore === 100 ? T.success : readinessScore >= 67 ? T.forest : readinessScore >= 34 ? T.gold : T.danger;
  const circR = 54;
  const circC = 2 * Math.PI * circR;
  const anyPhase3Overdue = tasks.some(t => t.annual && t.wasReset);

  return (
    <AppShell estate={estate} tasks={tasks} page="app-dashboard" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
      {taskModal && (
        <TaskModal
          mode={taskModal.mode}
          initialTask={taskModal.task}
          defaultCategory={taskModal.category}
          existingCategories={planningCats}
          onSave={data => handleSaveTask(data, taskModal.mode==='edit' ? taskModal.task : null)}
          onDelete={handleDeleteTask}
          onClose={() => setTaskModal(null)}
        />
      )}

      {/* Header */}
      <div style={{marginBottom:"1.75rem"}}>
        <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Estate Readiness</h1>
        <p style={{color:T.muted, fontSize:"0.875rem"}}>Track your progress toward a fully organized estate</p>
      </div>

      {/* Readiness score circle */}
      <div style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"16px", padding:"1.75rem 2rem", marginBottom:"2rem", display:"flex", alignItems:"center", gap:"2rem", flexWrap:"wrap"}}>
        <div style={{position:"relative", width:"128px", height:"128px", flexShrink:0}}>
          <svg width="128" height="128" viewBox="0 0 128 128">
            <circle cx="64" cy="64" r={circR} fill="none" stroke={T.borderLight} strokeWidth="9"/>
            <circle cx="64" cy="64" r={circR} fill="none" stroke={scoreColor} strokeWidth="9"
              strokeDasharray={`${readinessScore / 100 * circC} ${circC}`}
              strokeLinecap="round" transform="rotate(-90 64 64)"
              style={{transition:"stroke-dasharray 0.7s ease"}}/>
          </svg>
          <div style={{position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"1px"}}>
            <span style={{fontFamily:"'Playfair Display', serif", fontSize:"1.75rem", fontWeight:"700", color:scoreColor, lineHeight:1}}>{readinessScore}%</span>
          </div>
        </div>
        <div style={{flex:1, minWidth:"160px"}}>
          {readinessScore === 100 ? (
            <>
              <p style={{fontFamily:"'Playfair Display', serif", fontSize:"1.2rem", fontWeight:"700", color:T.success, marginBottom:"0.375rem"}}>Your estate is fully organized!</p>
              <p style={{fontSize:"0.875rem", color:T.muted}}>All essential tasks are complete. Review Phase 3 annually to keep everything current.</p>
            </>
          ) : (
            <>
              <p style={{fontFamily:"'Playfair Display', serif", fontSize:"1.2rem", fontWeight:"700", color:T.charcoal, marginBottom:"0.375rem"}}>Your estate is {readinessScore}% organized</p>
              <p style={{fontSize:"0.875rem", color:T.muted, marginBottom:"0.875rem"}}>{completedRequired} of {requiredTasks.length} essential tasks complete</p>
              <div style={{height:"6px", borderRadius:"3px", background:T.borderLight, overflow:"hidden", maxWidth:"220px"}}>
                <div style={{height:"100%", width:`${readinessScore}%`, background:scoreColor, borderRadius:"3px", transition:"width 0.7s ease"}}/>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Category filters */}
      <div style={{display:"flex", gap:"0.5rem", marginBottom:"1.75rem", flexWrap:"wrap"}}>
        {PLAN_CATS.map(c => {
          const col = c === "All" ? T.forest : PLAN_CAT_COLORS[c] || T.muted;
          return (
            <button key={c} onClick={()=>setFilter(c)} style={{...btnReset, padding:"0.375rem 0.875rem", borderRadius:"20px", fontSize:"0.78rem", border:`1px solid ${filter===c?col:T.border}`, background:filter===c?col:"transparent", color:filter===c?"white":c==="All"?T.muted:col, fontWeight:filter===c?"500":"400"}}>
              {c}
            </button>
          );
        })}
      </div>

      {/* Phase sections */}
      {phases.map(phase => {
        const allPhaseTasks = tasks.filter(t => t.phase === phase.key);
        if (allPhaseTasks.length === 0) return null;
        const phaseTasks = getTasksByPhase(phase.key);
        const phaseDone = allPhaseTasks.filter(t => t.done).length;
        const phaseOpen = expandedPhases.has(phase.key);
        const showOverdue = phase.key === "Phase 3" && anyPhase3Overdue;
        const phaseCats = [...new Set(allPhaseTasks.map(t => t.category))];

        return (
          <div key={phase.key} style={{marginBottom:"1rem", border:`1px solid ${T.border}`, borderRadius:"14px", overflow:"hidden", background:T.white}}>
            {/* Phase header — clickable */}
            <button
              onClick={() => togglePhase(phase.key)}
              style={{...btnReset, width:"100%", padding:"1rem 1.25rem", display:"flex", alignItems:"center", justifyContent:"space-between", gap:"1rem", cursor:"pointer", background:phaseOpen ? T.parchment+"88" : "transparent", transition:"background 0.15s"}}
            >
              <div style={{display:"flex", alignItems:"center", gap:"0.75rem", minWidth:0}}>
                <span style={{fontSize:"1rem", lineHeight:1, color:T.forest, flexShrink:0, transition:"transform 0.2s", display:"inline-block", transform:phaseOpen?"rotate(90deg)":"rotate(0deg)"}}>›</span>
                <div style={{textAlign:"left", minWidth:0}}>
                  <div style={{display:"flex", alignItems:"center", gap:"0.6rem", flexWrap:"wrap"}}>
                    <span style={{fontFamily:"'Playfair Display', serif", fontSize:"1rem", fontWeight:"700", color:T.forest}}>{phase.key} — {phase.label}</span>
                    {showOverdue && (
                      <span style={{fontSize:"0.6rem", padding:"0.15rem 0.5rem", borderRadius:"10px", background:T.gold+"22", color:T.goldDark, fontFamily:"'DM Mono', monospace", border:`1px solid ${T.gold}55`}}>Due for review</span>
                    )}
                  </div>
                  <div style={{fontSize:"0.72rem", color:T.muted, fontFamily:"'DM Mono', monospace", marginTop:"0.15rem"}}>
                    {phaseDone} of {allPhaseTasks.length} complete
                    {!phaseOpen && phaseCats.length > 0 && (
                      <span style={{marginLeft:"0.5rem", color:T.border}}>·</span>
                    )}
                    {!phaseOpen && phaseCats.map((cat, i) => (
                      <span key={cat} style={{marginLeft: i===0 ? "0.5rem" : "0.375rem", color: PLAN_CAT_COLORS[cat] || T.muted, fontSize:"0.65rem"}}>
                        {cat}{i < phaseCats.length - 1 ? "," : ""}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              {/* Mini progress bar */}
              <div style={{flexShrink:0, width:"60px", height:"5px", borderRadius:"3px", background:T.borderLight, overflow:"hidden"}}>
                <div style={{height:"100%", width:`${allPhaseTasks.length ? Math.round(phaseDone/allPhaseTasks.length*100) : 0}%`, background:T.forest, borderRadius:"3px", transition:"width 0.5s ease"}}/>
              </div>
            </button>

            {/* Phase body — only rendered when open */}
            {phaseOpen && (
              <div style={{padding:"0.75rem 1.25rem 1.25rem", borderTop:`1px solid ${T.borderLight}`}}>
                {phaseCats.map(cat => {
                  const catTasks = phaseTasks.filter(t => t.category === cat);
                  const allCatTasks = allPhaseTasks.filter(t => t.category === cat);
                  const catCol = PLAN_CAT_COLORS[cat] || T.forest;
                  const catOpen = isCatExpanded(phase.key, cat);
                  const catDone = allCatTasks.filter(t => t.done).length;

                  return (
                    <div key={cat} style={{marginBottom:"0.875rem", border:`1px solid ${catCol}22`, borderRadius:"10px", overflow:"hidden"}}>
                      {/* Category header — clickable */}
                      <button
                        onClick={() => toggleCategory(phase.key, cat)}
                        style={{...btnReset, width:"100%", padding:"0.6rem 0.875rem", display:"flex", alignItems:"center", gap:"0.5rem", cursor:"pointer", background:catCol+"0d", transition:"background 0.15s"}}
                      >
                        <span style={{fontSize:"0.8rem", color:catCol, flexShrink:0, transition:"transform 0.2s", display:"inline-block", transform:catOpen?"rotate(90deg)":"rotate(0deg)"}}>›</span>
                        <span style={{fontSize:"0.63rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.07em", color:catCol, textTransform:"uppercase", fontWeight:"600", flex:1, textAlign:"left"}}>{cat}</span>
                        <span style={{fontSize:"0.63rem", color:T.muted, fontFamily:"'DM Mono', monospace", flexShrink:0}}>{catDone}/{allCatTasks.length}</span>
                      </button>

                      {/* Category tasks — only rendered when open */}
                      {catOpen && (
                        <div style={{padding:"0.625rem 0.875rem 0.75rem", display:"flex", flexDirection:"column", gap:"0.5rem"}}>
                          {catTasks.map(task => renderTask(task, PLAN_CAT_COLORS))}
                          {canEditChecklist && (
                            <button
                              onClick={() => setTaskModal({mode:'add', phase:phase.key, category:cat, checklist:'planning'})}
                              style={{...btnReset, marginTop:"0.25rem", fontSize:"0.73rem", color:T.muted, display:"flex", alignItems:"center", gap:"0.25rem"}}
                            >
                              + Add Task
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </AppShell>
  );
}

function AppDocuments({ estate, navTo, initialDoc, invitedPermissions, invitedRole }) {
  const isInvitedUser = !!invitedPermissions || !!invitedRole;
  const perms = invitedPermissions || {};
  const canViewDocs = !invitedPermissions || perms.full_access || perms.view_documents !== false;
  const [active, setActive] = useState(initialDoc || null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const getDoc = () => active ? DOC_TEMPLATES[active] : null;
  const getText = () => getDoc()?.generate(estate || {}) || "";

  const copyText = () => {
    navigator.clipboard.writeText(getText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const downloadDocx = () => {
    const doc = getDoc();
    if (!doc) return;
    setDownloading(true);

    const text = getText();
    const title = doc.title;
    const deceasedName = estate?.deceasedName || "Deceased";

    // Convert plain text to Word XML paragraphs
    const lines = text.split("\n");
    const xmlParas = lines.map(line => {
      const escaped = line
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

      // Detect section headers (all caps lines or lines ending with colon)
      const isHeader = /^[A-Z][A-Z\s\d:&'(),./-]{4,}$/.test(line.trim()) && line.trim().length < 80;
      const isDivider = /^[─—=]{3,}/.test(line.trim());
      const isBlank = line.trim() === "";

      if (isBlank) {
        return `<w:p><w:pPr><w:spacing w:after="0"/></w:pPr></w:p>`;
      }
      if (isDivider) {
        return `<w:p><w:pPr><w:pBdr><w:bottom w:val="single" w:sz="4" w:space="1" w:color="CCCCCC"/></w:pBdr><w:spacing w:after="0"/></w:pPr></w:p>`;
      }
      if (isHeader) {
        return `<w:p>
          <w:pPr><w:spacing w:before="200" w:after="60"/></w:pPr>
          <w:r><w:rPr><w:b/><w:color w:val="2C4A3E"/></w:rPr><w:t xml:space="preserve">${escaped}</w:t></w:r>
        </w:p>`;
      }
      return `<w:p>
        <w:pPr><w:spacing w:after="0" w:line="276" w:lineRule="auto"/></w:pPr>
        <w:r><w:rPr><w:color w:val="2A2A2A"/></w:rPr><w:t xml:space="preserve">${escaped}</w:t></w:r>
      </w:p>`;
    }).join("\n");

    const wordXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<?mso-application progid="Word.Document"?>
<w:wordDocument xmlns:w="http://schemas.microsoft.com/office/word/2003/wordml"
                xmlns:o="urn:schemas-microsoft-com:office:office"
                xmlns:wx="http://schemas.microsoft.com/office/word/2003/auxHint">
  <w:body>
    <w:p>
      <w:pPr><w:spacing w:after="120"/></w:pPr>
      <w:r><w:rPr><w:b/><w:sz w:val="28"/><w:color w:val="2C4A3E"/></w:rPr>
        <w:t>${title.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</w:t>
      </w:r>
    </w:p>
    <w:p>
      <w:pPr><w:spacing w:after="240"/></w:pPr>
      <w:r><w:rPr><w:sz w:val="18"/><w:color w:val="7A8694"/><w:i/></w:rPr>
        <w:t>Estate of ${deceasedName.replace(/&/g,"&amp;")} · Generated by HeirOS · ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</w:t>
      </w:r>
    </w:p>
    <w:p><w:pPr><w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="B8935A"/></w:pBdr><w:spacing w:after="240"/></w:pPr></w:p>
    ${xmlParas}
    <w:p><w:pPr><w:spacing w:before="480"/></w:pPr>
      <w:r><w:rPr><w:sz w:val="16"/><w:color w:val="7A8694"/><w:i/></w:rPr>
        <w:t>Generated by HeirOS (herios.com) · This document is a starting template only. Replace all [bracket] placeholders before sending. HeirOS is not a law firm. Consult a licensed attorney for legal advice.</w:t>
      </w:r>
    </w:p>
  </w:body>
</w:wordDocument>`;

    const blob = new Blob([wordXml], {
      type: "application/msword;charset=utf-8"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeTitle = title.replace(/[^a-zA-Z0-9]/g, "_");
    a.href = url;
    a.download = `HeirOS_${safeTitle}_${deceasedName.replace(/\s+/g,"_")}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setTimeout(() => setDownloading(false), 1500);
  };

  if (!canViewDocs && (estate?.signupMode !== 'owner' || isInvitedUser)) {
    return (
      <AppShell estate={estate} tasks={[]} page="app-docs" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
        <PermissionDeniedBanner title="Documents" message="You don't have permission to view documents. Contact the estate owner to request access." />
      </AppShell>
    );
  }

  return (
    <AppShell estate={estate} tasks={[]} page="app-docs" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
      <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Document Generator</h1>
      <p style={{color:T.muted, fontSize:"0.875rem", marginBottom:"2rem"}}>Pre-filled letters, ready to print or send</p>

      <div style={{display:"flex", gap:"1.5rem", height:"calc(100vh - 260px)"}}>
        {/* Doc list sidebar */}
        <div style={{width:"220px", flexShrink:0, display:"flex", flexDirection:"column", gap:"0.5rem", overflowY:"auto"}}>
          {Object.entries(DOC_TEMPLATES).map(([key, doc]) => (
            <button
              key={key}
              onClick={() => { setActive(key); setCopied(false); }}
              style={{...btnReset, padding:"1rem", background:active===key ? T.parchment : T.white, border:`1px solid ${active===key ? T.forest : T.borderLight}`, borderRadius:"10px", textAlign:"left", display:"flex", gap:"0.75rem", alignItems:"center", transition:"all 0.15s"}}
            >
              <span style={{fontSize:"1.25rem"}}>{doc.icon}</span>
              <span style={{fontSize:"0.82rem", fontWeight:"500", color:active===key ? T.forest : T.charcoal, lineHeight:"1.3"}}>{doc.title}</span>
            </button>
          ))}
        </div>

        {/* Doc preview panel */}
        <div style={{flex:1, background:T.white, border:`1px solid ${T.border}`, borderRadius:"12px", overflow:"hidden", display:"flex", flexDirection:"column"}}>
          {!active ? (
            <div style={{flex:1, display:"flex", alignItems:"center", justifyContent:"center", color:T.muted, flexDirection:"column", gap:"0.75rem"}}>
              <span style={{fontSize:"2.5rem", opacity:0.2}}>⊟</span>
              <p style={{fontSize:"0.875rem"}}>Select a document to generate</p>
            </div>
          ) : (
            <>
              {/* Header with action buttons */}
              <div style={{padding:"0.875rem 1.25rem", borderBottom:`1px solid ${T.borderLight}`, display:"flex", justifyContent:"space-between", alignItems:"center", gap:"1rem", flexWrap:"wrap"}}>
                <div style={{display:"flex", alignItems:"center", gap:"0.625rem"}}>
                  <span style={{fontSize:"1.1rem"}}>{getDoc().icon}</span>
                  <span style={{fontWeight:"600", color:T.charcoal, fontSize:"0.9rem"}}>{getDoc().title}</span>
                </div>

                {/* Action buttons */}
                <div style={{display:"flex", gap:"0.5rem", flexWrap:"wrap"}}>
                  {/* Copy Text */}
                  <button
                    onClick={copyText}
                    style={{
                      ...btnReset,
                      display:"flex", alignItems:"center", gap:"0.375rem",
                      padding:"0.45rem 0.875rem", borderRadius:"7px", fontSize:"0.78rem", fontWeight:"500",
                      border:`1px solid ${copied ? T.success : T.border}`,
                      background: copied ? "#f0faf5" : T.parchment,
                      color: copied ? T.success : T.slate,
                      transition:"all 0.2s",
                    }}
                  >
                    <span>{copied ? "✓" : "⎘"}</span>
                    {copied ? "Copied!" : "Copy Text"}
                  </button>

                  {/* Download Word Doc */}
                  <button
                    onClick={downloadDocx}
                    disabled={downloading}
                    style={{
                      ...btnReset,
                      display:"flex", alignItems:"center", gap:"0.375rem",
                      padding:"0.45rem 0.875rem", borderRadius:"7px", fontSize:"0.78rem", fontWeight:"500",
                      border:`1px solid ${T.forest}`,
                      background: downloading ? T.parchment : T.forest,
                      color: downloading ? T.forest : "white",
                      transition:"all 0.2s",
                      cursor: downloading ? "default" : "pointer",
                    }}
                  >
                    <span>{downloading ? "⏳" : "↓"}</span>
                    {downloading ? "Downloading..." : "Download Word Doc"}
                  </button>
                </div>
              </div>

              {/* Document preview */}
              <pre style={{
                flex:1, padding:"1.5rem 2rem", overflowY:"auto",
                fontSize:"0.78rem", lineHeight:"1.85", color:T.charcoal,
                fontFamily:"'DM Mono', monospace", whiteSpace:"pre-wrap", margin:0,
                background:"#fdfcfb",
              }}>
                {getText()}
              </pre>

              {/* Footer disclaimer */}
              <div style={{padding:"0.625rem 1.25rem", background:T.parchment, borderTop:`1px solid ${T.borderLight}`, fontSize:"0.7rem", color:T.muted, display:"flex", alignItems:"center", gap:"0.5rem"}}>
                <span>⚠</span>
                <span>Replace all <strong>[bracket]</strong> items before sending. Consider attorney review for complex matters. HeirOS is not a law firm.</span>
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function AppCollaborate({ estate, setEstate, tasks, collaborators, onAdd, navTo, user, estateSupabaseId, invitations, setInvitations, deathReport, setDeathReport, invitedPermissions, invitedRole, activeAccessRequest, setActiveAccessRequest, ownerSettings, requireMfa, vault, onSaveVault }) {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [bulkExclusionInvite, setBulkExclusionInvite] = useState(null);
  const [showDeathModal, setShowDeathModal] = useState(false);
  const [inviteSent, setInviteSent] = useState(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [removeToast, setRemoveToast] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showCollaboratorDetail, setShowCollaboratorDetail] = useState(null); // { name, email }
  // Responsive: collapse three succession columns to stacked on narrow screens.
  const [isMobileCollab, setIsMobileCollab] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobileCollab(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const cats = [...new Set(tasks.map(t=>t.category))];
  const isOwner = estate?.signupMode === "owner" && !invitedPermissions;

  // Poll invitations every 15s while the Collaborators page is open so succession-chain
  // badges (e.g. "Awaiting acknowledgment" → "Active") and the roster update without a
  // manual reload. Mirrors the activeAccessRequest polling pattern in the parent.
  useEffect(() => {
    if (!estateSupabaseId) return;
    const poll = async () => {
      const { data } = await getInvitationsForEstate(estateSupabaseId);
      if (data) setInvitations(data);
    };
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [estateSupabaseId]); // eslint-disable-line react-hooks/exhaustive-deps
  const isInvitedUser = !!invitedPermissions || !!invitedRole;
  const isExecutor = estate?.signupMode === "executor";
  const aPerms = invitedPermissions || {};
  const canViewFamily = !invitedPermissions || aPerms.full_access || aPerms.view_family !== false;

  const EXEC_SLOT_META = [
    { rank:1, label:"Primary Executor", badge:"Serves first",       color:T.forest  },
    { rank:2, label:"First Backup",     badge:"Serves if #1 can't", color:T.gold    },
    { rank:3, label:"Second Backup",    badge:"Last resort backup", color:"#2980b9" },
  ];

  const executors = (invitations || [])
    .filter(inv => inv.executor_order != null && (inv.status === 'pending' || inv.status === 'accepted' || inv.status === 'pending_acknowledgment'))
    .sort((a, b) => (a.executor_order || 99) - (b.executor_order || 99))
    .map(inv => {
      const meta = EXEC_SLOT_META.find(m => m.rank === inv.executor_order) || EXEC_SLOT_META[0];
      return {
        name: inv.invited_name,
        email: inv.invited_email,
        relation: meta.label,
        rank: inv.executor_order,
        color: meta.color,
        badge: meta.badge,
        active: inv.executor_order === 1,
        invId: inv.id,
        deceased: inv.deceased === true,
        status: inv.status,
      };
    });

  // Derive the logged-in user's executor role from the executors array
  const userExec = !isOwner ? executors.find(ex => ex.email?.toLowerCase() === user?.email?.toLowerCase()) : null;
  const userExecutorRole = userExec ? (userExec.rank === 1 ? 'executor' : userExec.rank === 2 ? 'backup_executor_1' : 'backup_executor_2') : 'executor';

  const MPOA_SLOT_META = [
    { rank:1, label:"Primary Medical POA", badge:"Serves first",       color:"#1a6b8a"  },
    { rank:2, label:"First Backup",        badge:"Serves if #1 can't", color:T.gold     },
    { rank:3, label:"Second Backup",       badge:"Last resort backup",  color:"#6b4c8a"  },
  ];

  const medicalPoaInvitations = (invitations || [])
    .filter(inv => inv.role === 'Medical POA' && inv.medical_poa_order != null && (inv.status === 'pending' || inv.status === 'accepted' || inv.status === 'pending_acknowledgment'))
    .sort((a, b) => (a.medical_poa_order || 99) - (b.medical_poa_order || 99))
    .map(inv => {
      const meta = MPOA_SLOT_META.find(m => m.rank === inv.medical_poa_order) || MPOA_SLOT_META[0];
      return {
        name: inv.invited_name,
        email: inv.invited_email,
        relation: meta.label,
        rank: inv.medical_poa_order || 1,
        color: meta.color,
        badge: meta.badge,
        invId: inv.id,
        permissions: inv.permissions,
        deceased: inv.deceased === true,
        status: inv.status,
      };
    });

  const POA_SLOT_META = [
    { rank:1, label:"Primary Power of Attorney", badge:"Serves first",       color:"#7c4c8a" },
    { rank:2, label:"First Backup",              badge:"Serves if #1 can't", color:"#b8935a" },
    { rank:3, label:"Second Backup",             badge:"Last resort backup",  color:"#2c7a5e" },
  ];

  const poaInvitations = (invitations || [])
    .filter(inv => inv.role === 'Power of Attorney' && inv.poa_order != null && (inv.status === 'pending' || inv.status === 'accepted' || inv.status === 'pending_acknowledgment'))
    .sort((a, b) => (a.poa_order || 99) - (b.poa_order || 99))
    .map(inv => {
      const meta = POA_SLOT_META.find(m => m.rank === inv.poa_order) || POA_SLOT_META[0];
      return {
        name: inv.invited_name,
        email: inv.invited_email,
        relation: meta.label,
        rank: inv.poa_order || 1,
        color: meta.color,
        badge: meta.badge,
        invId: inv.id,
        permissions: inv.permissions,
        deceased: inv.deceased === true,
        status: inv.status,
      };
    });

  const handleReorderMedicalPoa = async (invId, currentRank, direction) => {
    const newRank = currentRank + direction;
    if (newRank < 1 || newRank > 3) return;
    const swapMpoa = medicalPoaInvitations.find(m => m.rank === newRank);
    if (!swapMpoa) return;
    setInvitations(prev => prev.map(inv => {
      if (inv.id === invId) return { ...inv, medical_poa_order: newRank };
      if (inv.id === swapMpoa.invId) return { ...inv, medical_poa_order: currentRank };
      return inv;
    }));
    await supabase.from('invitations').update({ medical_poa_order: newRank }).eq('id', invId);
    await supabase.from('invitations').update({ medical_poa_order: currentRank }).eq('id', swapMpoa.invId);
  };

  const handleRemoveMedicalPoa = async (invId, name) => {
    await supabase.from('invitations').delete().eq('id', invId);
    setInvitations(prev => prev.filter(inv => inv.id !== invId));
    setRemoveToast(name || 'Medical POA');
    setTimeout(() => setRemoveToast(null), 4000);
  };

  const handleReorderPoa = async (invId, currentRank, direction) => {
    const newRank = currentRank + direction;
    if (newRank < 1 || newRank > 3) return;
    const swapPoa = poaInvitations.find(p => p.rank === newRank);
    if (!swapPoa) return;
    setInvitations(prev => prev.map(inv => {
      if (inv.id === invId) return { ...inv, poa_order: newRank };
      if (inv.id === swapPoa.invId) return { ...inv, poa_order: currentRank };
      return inv;
    }));
    await supabase.from('invitations').update({ poa_order: newRank }).eq('id', invId);
    await supabase.from('invitations').update({ poa_order: currentRank }).eq('id', swapPoa.invId);
  };

  const handleRemovePoa = async (invId, name) => {
    await supabase.from('invitations').delete().eq('id', invId);
    setInvitations(prev => prev.filter(inv => inv.id !== invId));
    setRemoveToast(name || 'Power of Attorney');
    setTimeout(() => setRemoveToast(null), 4000);
  };

  const handleReorderExecutor = async (invId, currentRank, direction) => {
    const newRank = currentRank + direction;
    if (newRank < 1 || newRank > 3) return;
    const swapEx = executors.find(e => e.rank === newRank);
    if (!swapEx) return;
    setInvitations(prev => prev.map(inv => {
      if (inv.id === invId) return { ...inv, executor_order: newRank };
      if (inv.id === swapEx.invId) return { ...inv, executor_order: currentRank };
      return inv;
    }));
    await supabase.from('invitations').update({ executor_order: newRank }).eq('id', invId);
    await supabase.from('invitations').update({ executor_order: currentRank }).eq('id', swapEx.invId);
  };

  const handleInvite = (person) => {
    onAdd(person);
    setInviteSent(person);
    setShowInviteModal(false);
    setTimeout(() => setInviteSent(null), 5000);
  };

  const getInviteForEmail = (email) => email ? (invitations || []).find(inv => inv.invited_email?.toLowerCase() === email.toLowerCase()) : null;

  // All accepted invitation rows for a given email — used to populate PermissionsModal
  const getInvitationsForPersonEmail = (email) =>
    email ? (invitations || []).filter(inv =>
      inv.invited_email?.toLowerCase() === email.toLowerCase() && inv.status === 'accepted'
    ) : [];

  const handleRemoveCollaborator = async (person, index) => {
    if (person.id) await removeCollaboratorById(person.id);
    if (estateSupabaseId && person.email) await removeInvitationByEmail(estateSupabaseId, person.email);
    if (person.email) {
      sendEmail(person.email, 'access_revoked', {
        name: person.name || '',
        ownerName: estate?.ownerName || estate?.deceasedName || '',
        role: person.role || person.canCollaborate || 'collaborator',
      });
    }
    onAdd({ __removeIndex: index });
    setRemoveToast(person.name || 'Member');
    setTimeout(() => setRemoveToast(null), 4000);
  };

  const handleRemoveExecutor = async (invId, name) => {
    await supabase.from('invitations').delete().eq('id', invId);
    setInvitations(prev => prev.filter(inv => inv.id !== invId));
    setRemoveToast(name || 'Executor');
    setTimeout(() => setRemoveToast(null), 4000);
  };

  if (!canViewFamily && (!isOwner || isInvitedUser)) {
    return (
      <AppShell estate={estate} tasks={tasks} page="app-collab" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
        <PermissionDeniedBanner title="Family" message="You don't have permission to view the family collaboration page. Contact the estate owner to request access." />
      </AppShell>
    );
  }

  // ── Master roster ──────────────────────────────────────────────────────────
  // Every accepted invitation is shown here regardless of role.
  // Group by email so a person with multiple roles (e.g. Executor + POA) appears
  // as one row listing all their roles, not a duplicate row per role.
  const rosterByEmail = {};
  // Include pending and pending_acknowledgment, not just accepted. Filtering to
  // accepted alone hid every pending non-succession invitation completely: the
  // localStorage fallback below excludes anyone who has an invitation row, so a
  // pending invite fell through the gap and the owner could not see, resend, or
  // revoke it. CollaboratorCard already renders each status correctly.
  const ROSTER_STATUSES = ['accepted', 'pending', 'pending_acknowledgment'];
  (invitations || []).filter(inv => ROSTER_STATUSES.includes(inv.status)).forEach(inv => {
    const key = inv.invited_email?.toLowerCase();
    if (!key) return;
    if (!rosterByEmail[key]) {
      rosterByEmail[key] = {
        name: inv.invited_name,
        email: inv.invited_email,
        roles: [],
        id: inv.id,
        invitationId: inv.id,
        fromInvitation: true,
        inviteStatus: inv.status,
        permissions: inv.permissions,
      };
    }
    if (inv.role && !rosterByEmail[key].roles.includes(inv.role)) {
      rosterByEmail[key].roles.push(inv.role);
    }
  });
  const rosterMembers = Object.values(rosterByEmail).map(r => {
    const roleLabel = r.roles
      .map(role => role === 'Named as executor' ? 'Executor' : role)
      .join(' & ');
    return { ...r, role: roleLabel || 'Collaborator', canCollaborate: roleLabel || 'Collaborator' };
  });

  // Also include local-only collaborators (localStorage) who are not in the invitations table
  // at all (any status). Anyone with any invitation row — accepted, pending, or
  // pending_acknowledgment — is handled entirely through rosterByEmail above; the localStorage
  // entry must not create a duplicate row or surface a pending_acknowledgment entry.
  const rosterEmails = new Set(Object.keys(rosterByEmail));
  const allInvitationEmails = new Set(
    (invitations || []).map(inv => inv.invited_email?.toLowerCase()).filter(Boolean)
  );
  const localOnlyCollabs = collaborators.filter(c => {
    const email = (c.email || c.inviteEmail || c.emailAddress || '').toLowerCase();
    if (!email) return true; // no email — can't match; keep it
    return !rosterEmails.has(email) && !allInvitationEmails.has(email);
  });

  const allMembers = [...rosterMembers, ...localOnlyCollabs].sort((a, b) => {
    const an = (a.name || a.email || '').toLowerCase();
    const bn = (b.name || b.email || '').toLowerCase();
    if (an !== bn) return an.localeCompare(bn);
    return ((a.email || '').toLowerCase()).localeCompare((b.email || '').toLowerCase());
  });

  return (
    <AppShell estate={estate} tasks={tasks} page="app-collab" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
      {showInviteModal && <AddFamilyMemberModal estate={estate} isOwner={isOwner} onInvite={handleInvite} onClose={()=>setShowInviteModal(false)} user={user} estateSupabaseId={estateSupabaseId} setInvitations={setInvitations} onInviteCreated={(inv) => { if ((vault?.items?.length || 0) > 0) setBulkExclusionInvite(inv); }} />}
      {bulkExclusionInvite && <BulkExclusionModal invite={bulkExclusionInvite} vault={vault} onSaveVault={onSaveVault} estateSupabaseId={estateSupabaseId} onClose={()=>setBulkExclusionInvite(null)} />}
      {showDeathModal && <ReportDeathModal estate={estate} user={user} estateSupabaseId={estateSupabaseId} onClose={()=>setShowDeathModal(false)} onReported={(report) => { setDeathReport(report); setShowDeathModal(false); }} />}
      {showReplaceModal && replaceTarget && <ReplaceExecutorModal target={replaceTarget} estate={estate} user={user} estateSupabaseId={estateSupabaseId} onClose={()=>{ setShowReplaceModal(false); setReplaceTarget(null); }} onReplaced={(rank, name, email) => { const keyMap = { 1:['inviteExecutor','inviteExecutorEmail'], 2:['inviteBackup1Name','inviteBackup1Email'], 3:['inviteBackup2Name','inviteBackup2Email'] }; const [nk,ek] = keyMap[rank]||[]; if (nk) setEstate(prev => ({...(prev||{}), [nk]:name, [ek]:email})); setShowReplaceModal(false); setReplaceTarget(null); }} />}
      {showRequestModal && (
        <RequestFullAccessModal
          estate={estate}
          user={user}
          estateId={estateSupabaseId}
          invitations={invitations}
          ownerEmail={estate?.ownerEmail || ''}
          ownerResponseWindow={ownerSettings?.responseWindow || 72}
          grantImmediateAccess={ownerSettings?.grantImmediateAccess || false}
          userRole={userExecutorRole}
          onClose={() => setShowRequestModal(false)}
          onRequested={(req) => {
            setActiveAccessRequest(req);
            setShowRequestModal(false);
          }}
        />
      )}

      {/* Report Death banner for executor users */}
      {isExecutor && !deathReport && (
        <div style={{background:"#fdf6ee", border:`1px solid ${T.tan}`, borderRadius:"12px", padding:"1rem 1.25rem", marginBottom:"1.5rem", display:"flex", alignItems:"center", gap:"1rem", flexWrap:"wrap"}}>
          <div style={{flex:1}}>
            <p style={{fontSize:"0.875rem", fontWeight:"600", color:T.charcoal, marginBottom:"0.25rem"}}>Has {estate?.deceasedName || "the estate owner"} passed away?</p>
            <p style={{fontSize:"0.78rem", color:T.muted}}>As executor, you can report their passing to begin the 72-hour verification period before full estate access is granted.</p>
          </div>
          <button onClick={()=>setShowDeathModal(true)} style={{...btnReset, background:"#7c2d12", color:"white", padding:"0.625rem 1.25rem", borderRadius:"8px", fontSize:"0.82rem", fontWeight:"600", flexShrink:0}}>
            Report Passing →
          </button>
        </div>
      )}

      {/* Death report countdown banner for executor */}
      {isExecutor && deathReport?.status === "pending" && (() => {
        const deadline = new Date(deathReport.confirmation_deadline);
        const now = new Date();
        const hoursLeft = Math.max(0, Math.round((deadline - now) / (1000 * 60 * 60)));
        const pct = Math.max(0, Math.min(100, (hoursLeft / 72) * 100));
        return (
          <div style={{background:"#f0faf5", border:`1px solid #c3e6cb`, borderRadius:"12px", padding:"1.25rem", marginBottom:"1.5rem"}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.75rem"}}>
              <p style={{fontSize:"0.875rem", fontWeight:"600", color:T.forest}}>Death report filed — verification in progress</p>
              <span style={{fontSize:"0.78rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>{hoursLeft}h remaining</span>
            </div>
            <div style={{background:`${T.forest}20`, borderRadius:"100px", height:"8px", overflow:"hidden"}}>
              <div style={{height:"100%", width:`${100-pct}%`, background:T.forest, borderRadius:"100px", transition:"width 0.5s"}} />
            </div>
            <p style={{fontSize:"0.72rem", color:T.muted, marginTop:"0.625rem"}}>Full estate access unlocks automatically after the 72-hour period. {estate?.ownerName || "The owner"} has been notified and can cancel this report.</p>
          </div>
        );
      })()}

      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"2rem", flexWrap:"wrap", gap:"1rem"}}>
        <div>
          <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Estate Collaborators</h1>
          <p style={{color:T.muted, fontSize:"0.875rem"}}>{isOwner ? "Invite family to help organize your estate while you're alive." : "Keep everyone aligned — no group text chaos."}</p>
        </div>
        {(isOwner || (invitedRole === 'Named as executor' && estate?.status === 'active_settlement')) && (
          <button onClick={()=>setShowInviteModal(true)} style={{...filledBtn, display:"flex", alignItems:"center", gap:"0.5rem"}}>
            <span>+</span> Invite Collaborator
          </button>
        )}
      </div>

      {inviteSent && (
        <div style={{background:"#f0faf5", border:"1px solid #c3e6cb", borderRadius:"10px", padding:"0.875rem 1.25rem", marginBottom:"1.5rem", display:"flex", alignItems:"center", gap:"0.75rem"}}>
          <span style={{fontSize:"1.1rem"}}>✉️</span>
          <div style={{flex:1}}>
            <p style={{fontSize:"0.875rem", fontWeight:"600", color:T.success, marginBottom:"0.125rem"}}>Added {inviteSent.name}!</p>
            <p style={{fontSize:"0.78rem", color:T.muted}}>{inviteSent.email ? `An invite will be sent to ${inviteSent.email}.` : "No email provided — share your HeirOS link with them directly."}</p>
          </div>
          <button onClick={()=>setInviteSent(null)} style={{...btnReset, color:T.muted, fontSize:"1.2rem"}}>×</button>
        </div>
      )}

      {tasks.length > 0 && (
        <div style={{display:"flex", flexWrap:"wrap", gap:"0.75rem", marginBottom:"2.5rem"}}>
          {cats.map(cat=>{
            const ct=tasks.filter(t=>t.category===cat); const cd=ct.filter(t=>t.done).length;
            return <div key={cat} style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"10px", padding:"0.875rem 1.25rem", display:"flex", alignItems:"center", gap:"0.625rem"}}>
              <div style={{width:"8px", height:"8px", borderRadius:"50%", background:CAT_COLORS[cat]}} />
              <span style={{fontSize:"0.8rem", color:T.muted}}>{cat}</span>
              <span style={{fontSize:"0.8rem", fontWeight:"600", color:T.charcoal, marginLeft:"auto"}}>{cd}/{ct.length}</span>
            </div>;
          })}
        </div>
      )}


      {/* ── Succession chains — three columns on desktop, stacked on mobile ──────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobileCollab ? "1fr" : "repeat(3, 1fr)",
        gap: "1.25rem",
        marginBottom: "2.5rem",
        alignItems: "start",
      }}>

        {/* ─── Executor Succession ─── */}
        <div>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.875rem"}}>
            <h2 style={{fontSize:"0.95rem", fontWeight:"600", color:T.charcoal}}>Executor Succession</h2>
            <span style={{fontSize:"0.6rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>ORDER OF SUCCESSION</span>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"0"}}>
            {EXEC_SLOT_META.map((slot, i) => {
              const ex = executors.find(e => e.rank === slot.rank);
              return (
                <div key={slot.rank}>
                  {i > 0 && (
                    <div style={{display:"flex", alignItems:"center", gap:"0.4rem", padding:"0 0.875rem", height:"26px"}}>
                      <div style={{width:"20px", display:"flex", justifyContent:"center"}}><div style={{width:"2px", height:"100%", background:T.borderLight}}/></div>
                      <span style={{fontSize:"0.62rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>↓ if #{i} unable to serve</span>
                    </div>
                  )}
                  {ex ? (
                    <div
                      onClick={() => setShowCollaboratorDetail({name: ex.name, email: ex.email})}
                      role="button" tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && setShowCollaboratorDetail({name: ex.name, email: ex.email})}
                      style={{background:ex.deceased?"#f6f6f6":T.white, border:`1px solid ${ex.deceased?"#ccc":slot.color+"33"}`, borderRadius:"12px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"0.75rem", borderLeft:`3px solid ${ex.deceased?"#aaa":slot.color}`, opacity:ex.deceased?0.85:1, cursor:"pointer", transition:"background 0.12s"}}
                      onMouseEnter={e=>{ if(!ex.deceased) e.currentTarget.style.background="#f8faf8"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background=ex.deceased?"#f6f6f6":T.white; }}
                    >
                      <div style={{width:"28px", height:"28px", borderRadius:"50%", background:ex.deceased?"#aaa":slot.color, color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.78rem", fontWeight:"700", flexShrink:0}}>{slot.rank}</div>
                      <div style={{width:"36px", height:"36px", borderRadius:"50%", background:ex.deceased?"#ddd":`${slot.color}22`, border:`1px solid ${ex.deceased?"#ccc":slot.color+"44"}`, display:"flex", alignItems:"center", justifyContent:"center", color:ex.deceased?"#999":slot.color, fontWeight:"700", flexShrink:0, fontSize:"0.9rem"}}>{ex.name?.[0]?.toUpperCase()||"?"}</div>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{display:"flex", alignItems:"center", gap:"0.375rem", flexWrap:"wrap"}}>
                          <span style={{fontWeight:"500", color:ex.deceased?T.muted:T.charcoal, fontSize:"0.85rem", textDecoration:ex.deceased?"line-through":"none"}}>{ex.name}</span>
                          {ex.deceased && <span style={{fontSize:"0.58rem", background:"#fef2f2", color:"#991b1b", padding:"0.1rem 0.35rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace", border:"1px solid #fca5a5"}}>✖ Deceased</span>}
                        </div>
                        <div style={{display:"flex", alignItems:"center", gap:"0.375rem", flexWrap:"wrap", marginTop:"0.2rem"}}>
                          <span style={{fontSize:"0.62rem", fontFamily:"'DM Mono', monospace", color:ex.deceased?"#991b1b":ex.status==='pending_acknowledgment'?"#92400e":ex.status==='pending'?"#92400e":slot.rank===1?T.success:T.muted, background:ex.deceased?"#fef2f2":ex.status==='pending_acknowledgment'?"#fff7ed":ex.status==='pending'?"#fff7ed":slot.rank===1?"#f0faf5":T.parchment, padding:"0.12rem 0.45rem", borderRadius:"5px"}}>{ex.deceased?"✖ Deceased":ex.status==='pending_acknowledgment'?"⏳ Awaiting":ex.status==='pending'?"◷ Invited":slot.rank===1?"● Active":"○ Standby"}</span>
                          {isOwner && !ex.deceased && ex.status !== 'pending_acknowledgment' && (
                            <span style={{display:"flex", gap:"0.2rem"}} onClick={e => e.stopPropagation()}>
                              {slot.rank > 1 && executors.find(e2 => e2.rank === slot.rank - 1) && (
                                <button onClick={()=>handleReorderExecutor(ex.invId, slot.rank, -1)} style={{...btnReset, fontSize:"0.62rem", color:T.forest, border:`1px solid ${T.forest}44`, borderRadius:"4px", padding:"0.08rem 0.3rem", lineHeight:1}}>↑</button>
                              )}
                              {slot.rank < 3 && executors.find(e2 => e2.rank === slot.rank + 1) && (
                                <button onClick={()=>handleReorderExecutor(ex.invId, slot.rank, 1)} style={{...btnReset, fontSize:"0.62rem", color:T.forest, border:`1px solid ${T.forest}44`, borderRadius:"4px", padding:"0.08rem 0.3rem", lineHeight:1}}>↓</button>
                              )}
                            </span>
                          )}
                          {isOwner && ex.status === 'pending_acknowledgment' && (
                            <span onClick={e => e.stopPropagation()}>
                              <button onClick={()=>handleRemoveExecutor(ex.invId, ex.name)} style={{...btnReset, fontSize:"0.6rem", color:T.danger, textDecoration:"underline"}}>Cancel</button>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    isOwner ? (
                      <button onClick={()=>setShowInviteModal(true)} style={{...btnReset, width:"100%", background:"transparent", border:`2px dashed ${T.border}`, borderRadius:"12px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"0.75rem", opacity:0.7, cursor:"pointer", transition:"opacity 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.7"}>
                        <div style={{width:"28px", height:"28px", borderRadius:"50%", background:T.border, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.78rem", fontWeight:"700", color:T.muted, flexShrink:0}}>{slot.rank}</div>
                        <div style={{width:"36px", height:"36px", borderRadius:"50%", background:T.parchment, border:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", color:T.muted, fontSize:"0.8rem", flexShrink:0}}>?</div>
                        <div style={{textAlign:"left"}}>
                          <div style={{fontSize:"0.8rem", color:T.forest, fontWeight:"500"}}>+ Invite {slot.label}</div>
                          <div style={{fontSize:"0.65rem", color:T.muted}}>Empty — invite someone</div>
                        </div>
                      </button>
                    ) : (
                      <div style={{background:"transparent", border:`2px dashed ${T.border}`, borderRadius:"12px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"0.75rem", opacity:0.6}}>
                        <div style={{width:"28px", height:"28px", borderRadius:"50%", background:T.border, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.78rem", fontWeight:"700", color:T.muted, flexShrink:0}}>{slot.rank}</div>
                        <div>
                          <div style={{fontSize:"0.8rem", color:T.muted}}>Not visible to you</div>
                          <div style={{fontSize:"0.68rem", color:T.muted, marginTop:"0.2rem"}}>Only the estate owner can see who holds this role</div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
          {executors.length < 3 && isOwner && (
            <div style={{background:"#fefbf0", border:`1px solid ${T.tan}`, borderRadius:"8px", padding:"0.6rem 0.875rem", marginTop:"0.75rem", fontSize:"0.7rem", color:T.goldDark, lineHeight:"1.5"}}>
              ⚠ {3-executors.length} slot{3-executors.length!==1?"s":""} empty. <button onClick={()=>navTo("app-ai")} style={{...btnReset, color:T.gold, fontWeight:"500", textDecoration:"underline"}}>Why name backups? →</button>
            </div>
          )}
        </div>

        {/* ─── Medical Power of Attorney ─── */}
        <div>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.875rem"}}>
            <h2 style={{fontSize:"0.95rem", fontWeight:"600", color:T.charcoal}}>Medical POA</h2>
            <span style={{fontSize:"0.6rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>ORDER OF SUCCESSION</span>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"0"}}>
            {MPOA_SLOT_META.map((slot, i) => {
              const mp = medicalPoaInvitations.find(m => m.rank === slot.rank);
              return (
                <div key={slot.rank}>
                  {i > 0 && (
                    <div style={{display:"flex", alignItems:"center", gap:"0.4rem", padding:"0 0.875rem", height:"26px"}}>
                      <div style={{width:"20px", display:"flex", justifyContent:"center"}}><div style={{width:"2px", height:"100%", background:T.borderLight}}/></div>
                      <span style={{fontSize:"0.62rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>↓ if #{i} unable to serve</span>
                    </div>
                  )}
                  {mp ? (
                    <div
                      onClick={() => setShowCollaboratorDetail({name: mp.name, email: mp.email})}
                      role="button" tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && setShowCollaboratorDetail({name: mp.name, email: mp.email})}
                      style={{background:mp.deceased?"#f6f6f6":T.white, border:`1px solid ${mp.deceased?"#ccc":slot.color+"33"}`, borderRadius:"12px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"0.75rem", borderLeft:`3px solid ${mp.deceased?"#aaa":slot.color}`, opacity:mp.deceased?0.85:1, cursor:"pointer", transition:"background 0.12s"}}
                      onMouseEnter={e=>{ if(!mp.deceased) e.currentTarget.style.background="#f8faf8"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background=mp.deceased?"#f6f6f6":T.white; }}
                    >
                      <div style={{width:"28px", height:"28px", borderRadius:"50%", background:mp.deceased?"#aaa":slot.color, color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.78rem", fontWeight:"700", flexShrink:0}}>{slot.rank}</div>
                      <div style={{width:"36px", height:"36px", borderRadius:"50%", background:mp.deceased?"#ddd":`${slot.color}22`, border:`1px solid ${mp.deceased?"#ccc":slot.color+"44"}`, display:"flex", alignItems:"center", justifyContent:"center", color:mp.deceased?"#999":slot.color, fontWeight:"700", flexShrink:0, fontSize:"0.9rem"}}>{mp.name?.[0]?.toUpperCase()||"?"}</div>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{display:"flex", alignItems:"center", gap:"0.375rem", flexWrap:"wrap"}}>
                          <span style={{fontWeight:"500", color:mp.deceased?T.muted:T.charcoal, fontSize:"0.85rem", textDecoration:mp.deceased?"line-through":"none"}}>{mp.name}</span>
                          {mp.deceased && <span style={{fontSize:"0.58rem", background:"#fef2f2", color:"#991b1b", padding:"0.1rem 0.35rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace", border:"1px solid #fca5a5"}}>✖ Deceased</span>}
                        </div>
                        <div style={{display:"flex", alignItems:"center", gap:"0.375rem", flexWrap:"wrap", marginTop:"0.2rem"}}>
                          <span style={{fontSize:"0.62rem", fontFamily:"'DM Mono', monospace", color:mp.deceased?"#991b1b":mp.status==='pending_acknowledgment'?"#92400e":mp.status==='pending'?"#92400e":slot.rank===1?T.success:T.muted, background:mp.deceased?"#fef2f2":mp.status==='pending_acknowledgment'?"#fff7ed":mp.status==='pending'?"#fff7ed":slot.rank===1?"#f0faf5":T.parchment, padding:"0.12rem 0.45rem", borderRadius:"5px"}}>{mp.deceased?"✖ Deceased":mp.status==='pending_acknowledgment'?"⏳ Awaiting":mp.status==='pending'?"◷ Invited":slot.rank===1?"● Active":"○ Standby"}</span>
                          {isOwner && !mp.deceased && mp.status !== 'pending_acknowledgment' && (
                            <span style={{display:"flex", gap:"0.2rem"}} onClick={e => e.stopPropagation()}>
                              {slot.rank > 1 && medicalPoaInvitations.find(m2 => m2.rank === slot.rank - 1) && (
                                <button onClick={()=>handleReorderMedicalPoa(mp.invId, slot.rank, -1)} style={{...btnReset, fontSize:"0.62rem", color:T.forest, border:`1px solid ${T.forest}44`, borderRadius:"4px", padding:"0.08rem 0.3rem", lineHeight:1}}>↑</button>
                              )}
                              {slot.rank < 3 && medicalPoaInvitations.find(m2 => m2.rank === slot.rank + 1) && (
                                <button onClick={()=>handleReorderMedicalPoa(mp.invId, slot.rank, 1)} style={{...btnReset, fontSize:"0.62rem", color:T.forest, border:`1px solid ${T.forest}44`, borderRadius:"4px", padding:"0.08rem 0.3rem", lineHeight:1}}>↓</button>
                              )}
                            </span>
                          )}
                          {isOwner && mp.status === 'pending_acknowledgment' && (
                            <span onClick={e => e.stopPropagation()}>
                              <button onClick={()=>handleRemoveMedicalPoa(mp.invId, mp.name)} style={{...btnReset, fontSize:"0.6rem", color:T.danger, textDecoration:"underline"}}>Cancel</button>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    isOwner ? (
                      <button onClick={()=>setShowInviteModal(true)} style={{...btnReset, width:"100%", background:"transparent", border:`2px dashed ${T.border}`, borderRadius:"12px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"0.75rem", opacity:0.7, cursor:"pointer", transition:"opacity 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.7"}>
                        <div style={{width:"28px", height:"28px", borderRadius:"50%", background:T.border, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.78rem", fontWeight:"700", color:T.muted, flexShrink:0}}>{slot.rank}</div>
                        <div style={{width:"36px", height:"36px", borderRadius:"50%", background:T.parchment, border:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", color:T.muted, fontSize:"0.8rem", flexShrink:0}}>?</div>
                        <div style={{textAlign:"left"}}>
                          <div style={{fontSize:"0.8rem", color:"#1a6b8a", fontWeight:"500"}}>+ Invite {slot.label}</div>
                          <div style={{fontSize:"0.65rem", color:T.muted}}>Empty — invite someone</div>
                        </div>
                      </button>
                    ) : (
                      <div style={{background:"transparent", border:`2px dashed ${T.border}`, borderRadius:"12px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"0.75rem", opacity:0.6}}>
                        <div style={{width:"28px", height:"28px", borderRadius:"50%", background:T.border, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.78rem", fontWeight:"700", color:T.muted, flexShrink:0}}>{slot.rank}</div>
                        <div>
                          <div style={{fontSize:"0.8rem", color:T.muted}}>Not visible to you</div>
                          <div style={{fontSize:"0.68rem", color:T.muted, marginTop:"0.2rem"}}>Only the estate owner can see who holds this role</div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── Power of Attorney ─── */}
        <div>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.875rem"}}>
            <h2 style={{fontSize:"0.95rem", fontWeight:"600", color:T.charcoal}}>Power of Attorney</h2>
            <span style={{fontSize:"0.6rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>ORDER OF SUCCESSION</span>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"0"}}>
            {POA_SLOT_META.map((slot, i) => {
              const pa = poaInvitations.find(p => p.rank === slot.rank);
              return (
                <div key={slot.rank}>
                  {i > 0 && (
                    <div style={{display:"flex", alignItems:"center", gap:"0.4rem", padding:"0 0.875rem", height:"26px"}}>
                      <div style={{width:"20px", display:"flex", justifyContent:"center"}}><div style={{width:"2px", height:"100%", background:T.borderLight}}/></div>
                      <span style={{fontSize:"0.62rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>↓ if #{i} unable to serve</span>
                    </div>
                  )}
                  {pa ? (
                    <div
                      onClick={() => setShowCollaboratorDetail({name: pa.name, email: pa.email})}
                      role="button" tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && setShowCollaboratorDetail({name: pa.name, email: pa.email})}
                      style={{background:pa.deceased?"#f6f6f6":T.white, border:`1px solid ${pa.deceased?"#ccc":slot.color+"33"}`, borderRadius:"12px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"0.75rem", borderLeft:`3px solid ${pa.deceased?"#aaa":slot.color}`, opacity:pa.deceased?0.85:1, cursor:"pointer", transition:"background 0.12s"}}
                      onMouseEnter={e=>{ if(!pa.deceased) e.currentTarget.style.background="#f8faf8"; }}
                      onMouseLeave={e=>{ e.currentTarget.style.background=pa.deceased?"#f6f6f6":T.white; }}
                    >
                      <div style={{width:"28px", height:"28px", borderRadius:"50%", background:pa.deceased?"#aaa":slot.color, color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.78rem", fontWeight:"700", flexShrink:0}}>{slot.rank}</div>
                      <div style={{width:"36px", height:"36px", borderRadius:"50%", background:pa.deceased?"#ddd":`${slot.color}22`, border:`1px solid ${pa.deceased?"#ccc":slot.color+"44"}`, display:"flex", alignItems:"center", justifyContent:"center", color:pa.deceased?"#999":slot.color, fontWeight:"700", flexShrink:0, fontSize:"0.9rem"}}>{pa.name?.[0]?.toUpperCase()||"?"}</div>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{display:"flex", alignItems:"center", gap:"0.375rem", flexWrap:"wrap"}}>
                          <span style={{fontWeight:"500", color:pa.deceased?T.muted:T.charcoal, fontSize:"0.85rem", textDecoration:pa.deceased?"line-through":"none"}}>{pa.name}</span>
                          {pa.deceased && <span style={{fontSize:"0.58rem", background:"#fef2f2", color:"#991b1b", padding:"0.1rem 0.35rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace", border:"1px solid #fca5a5"}}>✖ Deceased</span>}
                        </div>
                        <div style={{display:"flex", alignItems:"center", gap:"0.375rem", flexWrap:"wrap", marginTop:"0.2rem"}}>
                          <span style={{fontSize:"0.62rem", fontFamily:"'DM Mono', monospace", color:pa.deceased?"#991b1b":pa.status==='pending_acknowledgment'?"#92400e":pa.status==='pending'?"#92400e":slot.rank===1?T.success:T.muted, background:pa.deceased?"#fef2f2":pa.status==='pending_acknowledgment'?"#fff7ed":pa.status==='pending'?"#fff7ed":slot.rank===1?"#f0faf5":T.parchment, padding:"0.12rem 0.45rem", borderRadius:"5px"}}>{pa.deceased?"✖ Deceased":pa.status==='pending_acknowledgment'?"⏳ Awaiting":pa.status==='pending'?"◷ Invited":slot.rank===1?"● Active":"○ Standby"}</span>
                          {isOwner && !pa.deceased && pa.status !== 'pending_acknowledgment' && (
                            <span style={{display:"flex", gap:"0.2rem"}} onClick={e => e.stopPropagation()}>
                              {slot.rank > 1 && poaInvitations.find(p2 => p2.rank === slot.rank - 1) && (
                                <button onClick={()=>handleReorderPoa(pa.invId, slot.rank, -1)} style={{...btnReset, fontSize:"0.62rem", color:T.forest, border:`1px solid ${T.forest}44`, borderRadius:"4px", padding:"0.08rem 0.3rem", lineHeight:1}}>↑</button>
                              )}
                              {slot.rank < 3 && poaInvitations.find(p2 => p2.rank === slot.rank + 1) && (
                                <button onClick={()=>handleReorderPoa(pa.invId, slot.rank, 1)} style={{...btnReset, fontSize:"0.62rem", color:T.forest, border:`1px solid ${T.forest}44`, borderRadius:"4px", padding:"0.08rem 0.3rem", lineHeight:1}}>↓</button>
                              )}
                            </span>
                          )}
                          {isOwner && pa.status === 'pending_acknowledgment' && (
                            <span onClick={e => e.stopPropagation()}>
                              <button onClick={()=>handleRemovePoa(pa.invId, pa.name)} style={{...btnReset, fontSize:"0.6rem", color:T.danger, textDecoration:"underline"}}>Cancel</button>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    isOwner ? (
                      <button onClick={()=>setShowInviteModal(true)} style={{...btnReset, width:"100%", background:"transparent", border:`2px dashed ${T.border}`, borderRadius:"12px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"0.75rem", opacity:0.7, cursor:"pointer", transition:"opacity 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.7"}>
                        <div style={{width:"28px", height:"28px", borderRadius:"50%", background:T.border, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.78rem", fontWeight:"700", color:T.muted, flexShrink:0}}>{slot.rank}</div>
                        <div style={{width:"36px", height:"36px", borderRadius:"50%", background:T.parchment, border:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", color:T.muted, fontSize:"0.8rem", flexShrink:0}}>?</div>
                        <div style={{textAlign:"left"}}>
                          <div style={{fontSize:"0.8rem", color:slot.color, fontWeight:"500"}}>+ Invite {slot.label}</div>
                          <div style={{fontSize:"0.65rem", color:T.muted}}>Empty — invite someone</div>
                        </div>
                      </button>
                    ) : (
                      <div style={{background:"transparent", border:`2px dashed ${T.border}`, borderRadius:"12px", padding:"0.875rem 1rem", display:"flex", alignItems:"center", gap:"0.75rem", opacity:0.6}}>
                        <div style={{width:"28px", height:"28px", borderRadius:"50%", background:T.border, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.78rem", fontWeight:"700", color:T.muted, flexShrink:0}}>{slot.rank}</div>
                        <div>
                          <div style={{fontSize:"0.8rem", color:T.muted}}>Not visible to you</div>
                          <div style={{fontSize:"0.68rem", color:T.muted, marginTop:"0.2rem"}}>Only the estate owner can see who holds this role</div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>{/* end succession grid */}

      <div>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem"}}>
          <h2 style={{fontSize:"1rem", fontWeight:"600", color:T.charcoal}}>
            Family Members & Collaborators
            {allMembers.length > 0 && (
              <span style={{marginLeft:"0.5rem", fontSize:"0.72rem", background:T.parchment, color:T.muted, padding:"0.15rem 0.5rem", borderRadius:"10px", fontFamily:"'DM Mono', monospace"}}>
                {allMembers.length}
              </span>
            )}
          </h2>
          {!isInvitedUser && <button onClick={()=>setShowInviteModal(true)} style={{...btnReset, fontSize:"0.78rem", color:T.forest, fontWeight:"500"}}>+ Add Collaborator</button>}
        </div>

        <div style={{display:"flex", flexDirection:"column", gap:"0.625rem"}}>

          {/* Roster: all accepted invitation roles merged per person, plus localStorage-only entries */}
          {allMembers.map((c, i) => (
            <CollaboratorCard
              key={i}
              person={c}
              isOwner={isOwner}
              isInvitedUser={isInvitedUser}
              estate={estate}
              invitation={getInviteForEmail(c.email)}
              estateId={estateSupabaseId}
              onEdit={(updated) => {
                onAdd({__replaceIndex: i, __data: updated});
              }}
              onRemove={!(c.fromInvitation || c.invitationId) ? () => handleRemoveCollaborator(c, i) : null}
              onOpenDetail={(c.fromInvitation || c.invitationId) && c.email
                ? () => setShowCollaboratorDetail({name: c.name, email: c.email})
                : null
              }
            />
          ))}

          {/* Empty state */}
          {allMembers.length === 0 && (
            <div style={{background:T.white, border:`2px dashed ${T.border}`, borderRadius:"12px", padding:"2.5rem", textAlign:"center"}}>
              <div style={{fontSize:"2rem", marginBottom:"0.75rem", opacity:0.3}}>👨‍👩‍👧‍👦</div>
              <p style={{fontWeight:"500", color:T.charcoal, marginBottom:"0.375rem"}}>No collaborators added yet</p>
              <p style={{fontSize:"0.82rem", color:T.muted, marginBottom:"1.25rem", maxWidth:"320px", margin:"0 auto 1.25rem"}}>
                {isOwner ? "Invite collaborators to view your planning progress and work together." : "Add collaborators to share the estate settlement workload."}
              </p>
              <button onClick={()=>setShowInviteModal(true)} style={{...filledBtn, fontSize:"0.85rem"}}>+ Invite First Collaborator</button>
            </div>
          )}
        </div>
      </div>

      {/* Remove success toast */}
      {removeToast && (
        <div style={{position:"fixed", bottom:"2rem", left:"50%", transform:"translateX(-50%)", background:T.forest, color:"white", padding:"0.75rem 1.5rem", borderRadius:"10px", fontSize:"0.85rem", fontWeight:"500", boxShadow:"0 4px 20px rgba(0,0,0,0.15)", zIndex:9999, pointerEvents:"none"}}>
          ✓ {removeToast} has been removed
        </div>
      )}

      {/* Collaborator Detail Modal — opened by clicking any occupied slot or roster card */}
      {showCollaboratorDetail && (
        <CollaboratorDetailModal
          personEmail={showCollaboratorDetail.email}
          personName={showCollaboratorDetail.name}
          invitations={invitations || []}
          isOwner={isOwner}
          estateId={estateSupabaseId}
          ownerResponseWindow={ownerSettings?.responseWindow || 72}
          invitedById={user?.id}
          inviterName={estate?.ownerName || user?.email || ''}
          onClose={() => setShowCollaboratorDetail(null)}
          onPermissionsSaved={(newPerms, invId) => {
            setInvitations(prev => prev.map(inv =>
              inv.id === invId ? {...inv, permissions: newPerms} : inv
            ));
          }}
          onRoleAdded={(newRows) => {
            setInvitations(prev => [...prev, ...newRows]);
          }}
          onRoleRemoved={(invId) => {
            setInvitations(prev => prev.filter(inv => inv.id !== invId));
            setRemoveToast(showCollaboratorDetail.name || 'Member');
            setTimeout(() => setRemoveToast(null), 4000);
          }}
          onCollaboratorRemoved={() => {
            const email = showCollaboratorDetail.email?.toLowerCase();
            setInvitations(prev => prev.filter(inv => inv.invited_email?.toLowerCase() !== email));
            setRemoveToast(showCollaboratorDetail.name || 'Member');
            setTimeout(() => setRemoveToast(null), 4000);
          }}
        />
      )}
    </AppShell>
  );
}

// ─── PERMISSION DENIED BANNER ────────────────────────────────────────────────
function PermissionDeniedBanner({ title, message }) {
  return (
    <div style={{background:"#fff8f0", border:`1px solid ${T.gold}`, borderRadius:"14px", padding:"2.5rem", textAlign:"center", maxWidth:"480px", margin:"4rem auto"}}>
      <div style={{fontSize:"2.5rem", marginBottom:"1rem"}}>🔒</div>
      <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, marginBottom:"0.5rem"}}>
        {title} Restricted
      </h2>
      <p style={{fontSize:"0.875rem", color:T.muted, lineHeight:"1.6"}}>{message}</p>
    </div>
  );
}

// ─── COLLABORATOR DETAIL MODAL ───────────────────────────────────────────────
// Single unified modal for any occupied succession slot or roster card.
// Uses one activeSubView state to guarantee exactly one panel is shown at a time,
// eliminating both z-index stacking and simultaneous-panel bugs.
function CollaboratorDetailModal({
  personEmail, personName, invitations, isOwner, estateId,
  ownerResponseWindow, invitedById, inviterName,
  onClose, onPermissionsSaved, onRoleAdded, onRoleRemoved, onCollaboratorRemoved,
}) {
  // ── Derived rows (live — re-computed each render from parent state) ──────
  const personInvitations = invitations.filter(inv =>
    inv.invited_email?.toLowerCase() === personEmail?.toLowerCase() &&
    inv.status === 'accepted'
  );
  const allPersonRows = invitations.filter(inv =>
    inv.invited_email?.toLowerCase() === personEmail?.toLowerCase()
  );

  const roleLabel = (inv) => {
    if (!inv) return 'Collaborator';
    if (inv.executor_order != null) {
      const r = inv.executor_order;
      return r === 1 ? 'Primary Executor' : r === 2 ? 'Executor Backup #1' : 'Executor Backup #2';
    }
    return inv.role || 'Collaborator';
  };
  const roleLabels = personInvitations.map(roleLabel);

  // ── Single view-switcher — only one sub-view is ever active ─────────────
  // Values: 'main' | 'permissions' | 'addRole' | 'removeRole' | 'removeCollaborator'
  const [activeSubView, setActiveSubView] = useState('main');
  const goBack = () => setActiveSubView('main');

  // ── Permissions sub-view state ───────────────────────────────────────────
  const [pSelInvId, setPSelInvId] = useState(() => personInvitations[0]?.id ?? null);
  const [pSaving, setPSaving] = useState(false);
  const [pSaved, setPSaved] = useState(false);
  const [pError, setPError] = useState(null);

  const pSelectedInv = personInvitations.find(inv => inv.id === pSelInvId) ?? personInvitations[0] ?? null;
  const pIsExecutor = pSelectedInv?.executor_order != null;
  const pIsMedicalPoa = pSelectedInv?.role === 'Medical POA';

  const parsePerms = (inv) => {
    const e = inv?.permissions ?? {};
    return {
      view_checklist:             e.view_checklist             !== false,
      edit_checklist:             e.edit_checklist             === true,
      view_documents:             e.view_documents             !== false,
      download_documents:         e.download_documents         === true,
      view_vault:                 e.view_vault                 === true,
      view_family:                e.view_family                !== false,
      full_access:                e.full_access                === true,
      view_medical_documents:     e.view_medical_documents     === true,
      download_medical_documents: e.download_medical_documents === true,
    };
  };
  const [perms, setPerms] = useState(() => parsePerms(pSelectedInv));

  // Re-initialise permissions state whenever the selected role changes
  useEffect(() => {
    const inv = personInvitations.find(i => i.id === pSelInvId) ?? null;
    setPerms(parsePerms(inv));
    setPSaved(false);
    setPError(null);
  }, [pSelInvId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also seed pSelInvId when the view activates (covers cases where it opens before invitations load)
  useEffect(() => {
    if (activeSubView === 'permissions' && !pSelInvId && personInvitations.length > 0) {
      setPSelInvId(personInvitations[0].id);
    }
  }, [activeSubView]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePerm = (key) => {
    setPerms(prev => {
      const next = { ...prev, [key]: !prev[key] };
      if (key === 'view_checklist' && !next.view_checklist) next.edit_checklist = false;
      if (key === 'view_documents' && !next.view_documents) next.download_documents = false;
      if (key === 'view_medical_documents' && !next.view_medical_documents) next.download_medical_documents = false;
      if (key === 'full_access' && next.full_access) {
        return { view_checklist:true, edit_checklist:true, view_documents:true,
                 download_documents:true, view_vault:true, view_family:true, full_access:true,
                 view_medical_documents: prev.view_medical_documents,
                 download_medical_documents: prev.download_medical_documents };
      }
      return next;
    });
  };

  const PERM_TOGGLES = [
    { key:"view_checklist",             label:"View Checklist",           desc:"Can see estate tasks and planning progress",                  disabledWhen:false,                           medicalPoaOnly:false },
    { key:"edit_checklist",             label:"Edit Checklist",           desc:"Can mark tasks complete",                                     disabledWhen:!perms.view_checklist,           medicalPoaOnly:false },
    { key:"view_documents",             label:"View Documents",           desc:"Can open pre-filled estate letters",                          disabledWhen:false,                           medicalPoaOnly:false },
    { key:"download_documents",         label:"Download Documents",       desc:"Can download Word documents to their device",                  disabledWhen:!perms.view_documents,           medicalPoaOnly:false },
    { key:"view_vault",                 label:"View Vault",               desc:"Can see vault items (executor access levels apply)",           disabledWhen:false,                           medicalPoaOnly:false },
    { key:"view_family",                label:"View Family Page",         desc:"Can see who else is involved in this estate",                 disabledWhen:false,                           medicalPoaOnly:false },
    { key:"view_medical_documents",     label:"View Medical Documents",   desc:"Can see medical docs, advance directive, and POA paperwork",  disabledWhen:false,                           medicalPoaOnly:true  },
    { key:"download_medical_documents", label:"Download Medical Docs",    desc:"Can download medical documents to their device",              disabledWhen:!perms.view_medical_documents,   medicalPoaOnly:true  },
  ].filter(t => pIsMedicalPoa ? t.medicalPoaOnly : !t.medicalPoaOnly);

  const invRoleLabel = (inv) => {
    if (!inv) return 'Unknown';
    if (inv.executor_order != null) return inv.executor_order === 1 ? 'Primary Executor' : `Executor Backup #${inv.executor_order - 1}`;
    return inv.role || 'Collaborator';
  };

  const handleSavePerms = async () => {
    if (!pSelectedInv?.id) { setPError("No invitation record found."); return; }
    setPSaving(true); setPError(null);
    try {
      const { error: err } = await updateInvitationPermissions(pSelectedInv.id, perms);
      if (err) throw err;
      setPSaved(true);
      onPermissionsSaved(perms, pSelectedInv.id);
      setTimeout(() => { setPSaved(false); goBack(); }, 1200);
    } catch { setPError("Failed to save permissions. Please try again."); }
    finally { setPSaving(false); }
  };

  // ── Add Role sub-view state ──────────────────────────────────────────────
  const existingRoles = new Set(
    invitations
      .filter(inv => inv.invited_email?.toLowerCase() === personEmail?.toLowerCase() && inv.status !== 'declined')
      .map(inv => inv.role)
  );
  const [arSelected, setArSelected] = useState({});
  const [arRanks, setArRanks] = useState({ 'Named as executor': 1, 'Medical POA': 1, 'Power of Attorney': 1 });
  const [arSubmitting, setArSubmitting] = useState(false);
  const [arError, setArError] = useState(null);
  const [arSuccess, setArSuccess] = useState(false);

  const handleAddRoles = async () => {
    const rows = ADD_ROLE_OPTIONS
      .filter(r => arSelected[r.value])
      .map(r => ({
        email: personEmail, name: personName, role: r.value, inviterName,
        permissions:
          r.value === 'Named as executor'  ? DEFAULT_EXECUTOR_PERMISSIONS  :
          r.value === 'Medical POA'        ? DEFAULT_MPOA_PERMISSIONS       :
          r.value === 'Power of Attorney'  ? DEFAULT_POA_PERMISSIONS        :
          r.value === 'Collaborator'       ? DEFAULT_COLLABORATOR_PERMISSIONS :
          r.value === 'Beneficiary'        ? DEFAULT_BENEFICIARY_PERMISSIONS  :
          {},
        ...(r.isSuccession && r.orderKey ? { [r.orderKey]: arRanks[r.value] || 1 } : {}),
      }));
    if (!rows.length) { setArError("Please select at least one role."); return; }
    setArSubmitting(true); setArError(null);
    try {
      const { data, error: err } = await createAddRoleInvitations(estateId, invitedById, rows);
      if (err) throw err;
      setArSuccess(true);
      onRoleAdded(data || []);
      const roleNames = rows.map(r => r.role);
      sendEmail(personEmail, 'role_acknowledgment_needed', {
        inviteeName: personName || personEmail, inviterName, roles: roleNames,
      }).then(({ error: emailErr }) => {
        if (emailErr) console.error('[HeirOS] role_acknowledgment_needed email failed:', emailErr);
      }).catch(e => console.error('[HeirOS] role_acknowledgment_needed email exception:', e));
      setTimeout(goBack, 1400);
    } catch { setArError("Failed to assign roles. Please try again."); }
    finally { setArSubmitting(false); }
  };

  // ── Remove Role sub-view state ───────────────────────────────────────────
  const [rrChecked, setRrChecked] = useState({});
  const [rrConfirm, setRrConfirm] = useState(false);
  const [rrRemoving, setRrRemoving] = useState(false);
  const rrSelected = Object.entries(rrChecked).filter(([,v]) => v).map(([k]) => k);

  const handleRemoveRoles = async () => {
    if (!rrSelected.length) return;
    setRrRemoving(true);
    for (const invId of rrSelected) {
      await supabase.from('invitations').delete().eq('id', invId);
      onRoleRemoved(invId);
    }
    setRrRemoving(false);
    const remaining = personInvitations.filter(inv => !rrSelected.includes(inv.id));
    if (remaining.length === 0) { onClose(); }
    else { setActiveSubView('main'); setRrChecked({}); setRrConfirm(false); }
  };

  // ── Remove Collaborator sub-view state ──────────────────────────────────
  const [rcRemoving, setRcRemoving] = useState(false);

  const handleRemoveCollaborator = async () => {
    setRcRemoving(true);
    for (const inv of allPersonRows) await supabase.from('invitations').delete().eq('id', inv.id);
    if (estateId && personEmail) await removeInvitationByEmail(estateId, personEmail);
    onCollaboratorRemoved();
    setRcRemoving(false);
    onClose();
  };

  // ── Shared style tokens ──────────────────────────────────────────────────
  const sT = { fontWeight:"600", color:T.charcoal, fontSize:"0.875rem" };
  const sS = { fontSize:"0.72rem", color:T.muted, marginTop:"0.1rem" };
  const rowBtn = { ...btnReset, width:"100%", textAlign:"left", padding:"0.75rem 1rem", borderRadius:"10px",
    display:"flex", justifyContent:"space-between", alignItems:"center", transition:"background 0.12s" };
  const backBtn = {
    ...btnReset, display:"inline-flex", alignItems:"center", gap:"0.3rem",
    fontSize:"0.78rem", color:T.muted, marginBottom:"1rem", fontWeight:"500",
  };

  // ── Rendered body per sub-view ───────────────────────────────────────────
  const renderSubView = () => {
    // ── PERMISSIONS ─────────────────────────────────────────────────────────
    if (activeSubView === 'permissions') return (
      <div>
        <button onClick={goBack} style={backBtn}>← Back</button>
        <div style={{fontFamily:"'Playfair Display', serif", fontSize:"1.15rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Edit Permissions</div>
        <p style={{fontSize:"0.78rem", color:T.muted, marginBottom:"1rem"}}>{personName || personEmail}'s access to this estate</p>

        {personInvitations.length === 0 ? (
          <div style={{background:"#fff8f0", border:`1px solid ${T.gold}`, borderRadius:"10px", padding:"1rem", fontSize:"0.82rem", color:"#9a6700"}}>
            No accepted invitation found. They need to accept their invitation first.
          </div>
        ) : (
          <>
            {personInvitations.length > 1 && (
              <div style={{marginBottom:"1rem"}}>
                <p style={{fontSize:"0.75rem", fontWeight:"600", color:T.muted, marginBottom:"0.4rem"}}>Editing permissions for:</p>
                <div style={{display:"flex", gap:"0.5rem", flexWrap:"wrap"}}>
                  {personInvitations.map(inv => (
                    <button key={inv.id} onClick={() => setPSelInvId(inv.id)}
                      style={{...btnReset, fontSize:"0.78rem", padding:"0.3rem 0.75rem", borderRadius:"6px", fontWeight:"500",
                        background: inv.id === pSelInvId ? T.forest : "transparent",
                        color: inv.id === pSelInvId ? "#fff" : T.charcoal,
                        border:`1px solid ${inv.id === pSelInvId ? T.forest : T.border}`}}>
                      {invRoleLabel(inv)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {pIsExecutor && (
              <div style={{marginBottom:"0.875rem"}}>
                <label style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.75rem 1rem",
                  background: perms.full_access ? "#f0faf5" : "#fffaf5",
                  border:`1px solid ${perms.full_access ? T.success : T.gold}`,
                  borderRadius:"10px", cursor:"pointer"}}>
                  <input type="checkbox" checked={!!perms.full_access} onChange={()=>togglePerm("full_access")}
                    style={{width:"1rem", height:"1rem", accentColor:T.forest}} />
                  <div>
                    <div style={{fontWeight:"600", color:T.forest, fontSize:"0.875rem"}}>Full Access</div>
                    <div style={{fontSize:"0.72rem", color:T.muted}}>
                      {perms.full_access ? "⚠ This executor has full access to everything" : "Grant all permissions at once"}
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div style={{display:"flex", flexDirection:"column", gap:"0.4rem", marginBottom:"1.25rem",
              opacity:perms.full_access?0.45:1, pointerEvents:perms.full_access?"none":"auto"}}>
              {PERM_TOGGLES.map(t => {
                const disabled = t.disabledWhen;
                return (
                  <label key={t.key} style={{display:"flex", alignItems:"flex-start", gap:"0.75rem",
                    padding:"0.625rem 1rem",
                    background: perms[t.key] && !disabled ? "#f8fbf9" : "transparent",
                    border:`1px solid ${perms[t.key] && !disabled ? T.forest+"33" : T.borderLight}`,
                    borderRadius:"8px", cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.45:1}}>
                    <input type="checkbox" checked={!!perms[t.key] && !disabled}
                      onChange={() => !disabled && togglePerm(t.key)} disabled={disabled}
                      style={{width:"1rem", height:"1rem", accentColor:T.forest, marginTop:"0.1rem", flexShrink:0}} />
                    <div>
                      <div style={{fontWeight:"500", color:T.charcoal, fontSize:"0.82rem"}}>{t.label}</div>
                      <div style={{fontSize:"0.7rem", color:T.muted}}>{disabled ? "Requires the parent permission to be on" : t.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            {pIsExecutor && (
              <div style={{background:T.parchment, borderRadius:"8px", padding:"0.75rem 1rem", marginBottom:"1rem", fontSize:"0.77rem", color:T.muted}}>
                <strong style={{color:T.forest}}>Emergency Access:</strong> If this executor requests full access and you don't respond within {ownerResponseWindow}h, access is auto-granted.
              </div>
            )}

            {pError && <p style={{color:T.danger, fontSize:"0.78rem", marginBottom:"0.625rem"}}>{pError}</p>}
            {pSaved && <p style={{color:T.success, fontSize:"0.78rem", marginBottom:"0.625rem"}}>✓ Permissions updated</p>}

            <div style={{display:"flex", justifyContent:"flex-end", gap:"0.625rem"}}>
              <button onClick={goBack} style={{...outlineBtn, fontSize:"0.82rem"}}>Cancel</button>
              <button onClick={handleSavePerms} disabled={pSaving||pSaved}
                style={{...filledBtn, fontSize:"0.82rem", opacity:(pSaving||pSaved)?0.6:1}}>
                {pSaving?"Saving…":pSaved?"Saved ✓":"Save Permissions"}
              </button>
            </div>
          </>
        )}
      </div>
    );

    // ── ADD ROLE ─────────────────────────────────────────────────────────────
    if (activeSubView === 'addRole') return (
      <div>
        <button onClick={goBack} style={backBtn}>← Back</button>
        <div style={{fontFamily:"'Playfair Display', serif", fontSize:"1.15rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Add Role</div>
        <p style={{fontSize:"0.78rem", color:T.muted, marginBottom:"0.875rem"}}>Assign additional roles to {personName || personEmail}. Each requires their acknowledgment before taking effect.</p>

        <div style={{display:"flex", flexDirection:"column", gap:"0.5rem", marginBottom:"1rem"}}>
          {ADD_ROLE_OPTIONS.map(opt => {
            const alreadyHas = existingRoles.has(opt.value);
            const isSelected = !!arSelected[opt.value];
            return (
              <div key={opt.value}>
                <label style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.625rem 1rem",
                  background: alreadyHas ? T.parchment : isSelected ? "#f0faf5" : "transparent",
                  border:`1px solid ${alreadyHas ? T.borderLight : isSelected ? T.forest+"44" : T.borderLight}`,
                  borderRadius:"10px", cursor:alreadyHas?"not-allowed":"pointer", opacity:alreadyHas?0.6:1}}>
                  <input type="checkbox" checked={isSelected && !alreadyHas} disabled={alreadyHas}
                    onChange={() => !alreadyHas && setArSelected(p=>({...p,[opt.value]:!p[opt.value]}))}
                    style={{width:"1rem", height:"1rem", accentColor:T.forest, flexShrink:0}} />
                  <div style={{flex:1}}>
                    <div style={{fontSize:"0.85rem", fontWeight:"500", color:T.charcoal}}>{opt.label}</div>
                    {alreadyHas && <div style={{fontSize:"0.68rem", color:T.muted}}>Already holds this role</div>}
                  </div>
                </label>
                {isSelected && !alreadyHas && opt.isSuccession && (
                  <div style={{padding:"0.5rem 1rem 0.625rem 2.75rem", borderLeft:`2px solid ${T.forest}22`}}>
                    <label style={{...labelStyle, marginBottom:"0.2rem"}}>Position</label>
                    <select value={arRanks[opt.value]||1}
                      onChange={e=>setArRanks(p=>({...p,[opt.value]:Number(e.target.value)}))}
                      style={{...inputStyle, padding:"0.45rem 0.75rem", fontSize:"0.8rem"}}>
                      {RANK_OPTIONS.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {arError && <p style={{color:T.danger, fontSize:"0.78rem", marginBottom:"0.625rem"}}>{arError}</p>}
        {arSuccess && <p style={{color:T.success, fontSize:"0.78rem", marginBottom:"0.625rem"}}>✓ Roles assigned — awaiting acknowledgment</p>}

        <div style={{display:"flex", justifyContent:"flex-end", gap:"0.625rem"}}>
          <button onClick={goBack} style={{...outlineBtn, fontSize:"0.82rem"}}>Cancel</button>
          <button onClick={handleAddRoles} disabled={arSubmitting||arSuccess}
            style={{...filledBtn, fontSize:"0.82rem", opacity:(arSubmitting||arSuccess)?0.6:1}}>
            {arSubmitting?"Assigning…":arSuccess?"Done ✓":"Assign Roles"}
          </button>
        </div>
        <p style={{fontSize:"0.7rem", color:T.muted, marginTop:"0.625rem", textAlign:"center"}}>
          {personName||"They"} will receive an email and must acknowledge each role before it takes effect.
        </p>
      </div>
    );

    // ── REMOVE ROLE ──────────────────────────────────────────────────────────
    if (activeSubView === 'removeRole') return (
      <div>
        <button onClick={goBack} style={backBtn}>← Back</button>
        <div style={{fontFamily:"'Playfair Display', serif", fontSize:"1.15rem", fontWeight:"700", color:T.charcoal, marginBottom:"0.25rem"}}>Remove Role</div>
        <p style={{fontSize:"0.78rem", color:T.muted, marginBottom:"1rem"}}>Select one or more of {personName}'s roles to remove.</p>

        {personInvitations.length === 0 ? (
          <p style={{fontSize:"0.8rem", color:T.muted}}>No active roles to remove.</p>
        ) : !rrConfirm ? (
          <>
            <div style={{display:"flex", flexDirection:"column", gap:"0.4rem", marginBottom:"1rem"}}>
              {personInvitations.map(inv => (
                <label key={inv.id} style={{display:"flex", alignItems:"center", gap:"0.75rem", cursor:"pointer",
                  padding:"0.625rem 1rem",
                  background: rrChecked[inv.id] ? "#fff8f0" : "transparent",
                  border:`1px solid ${rrChecked[inv.id] ? "#ead9b8" : T.borderLight}`,
                  borderRadius:"8px"}}>
                  <input type="checkbox" checked={!!rrChecked[inv.id]}
                    onChange={() => setRrChecked(p=>({...p,[inv.id]:!p[inv.id]}))}
                    style={{accentColor:T.danger, cursor:"pointer", width:"15px", height:"15px"}} />
                  <span style={{fontSize:"0.85rem", color:T.charcoal}}>{roleLabel(inv)}</span>
                </label>
              ))}
            </div>
            <button disabled={rrSelected.length === 0} onClick={() => setRrConfirm(true)}
              style={{...btnReset, fontSize:"0.8rem", color:rrSelected.length>0?T.danger:T.muted,
                border:`1px solid ${rrSelected.length>0?T.danger:T.muted}66`,
                borderRadius:"6px", padding:"0.35rem 0.875rem", fontWeight:"500",
                opacity:rrSelected.length>0?1:0.45, cursor:rrSelected.length>0?"pointer":"default"}}>
              Remove selected →
            </button>
          </>
        ) : (
          <>
            <div style={{background:"#fff8f0", border:"1px solid #ead9b8", borderRadius:"10px", padding:"1rem", marginBottom:"1rem"}}>
              <div style={{fontSize:"0.85rem", color:T.danger, fontWeight:"600", marginBottom:"0.25rem"}}>
                Remove {rrSelected.length === personInvitations.length ? "all roles from" : "the selected role(s) from"} {personName}?
              </div>
              <div style={{fontSize:"0.75rem", color:T.muted, lineHeight:"1.55"}}>
                {rrSelected.length === personInvitations.length
                  ? `${personName} will lose all access to this estate.`
                  : "They will keep their remaining roles and estate access."}
              </div>
            </div>
            <div style={{display:"flex", gap:"0.5rem"}}>
              <button onClick={handleRemoveRoles} disabled={rrRemoving}
                style={{...btnReset, fontSize:"0.8rem", color:"white", background:T.danger,
                  borderRadius:"6px", padding:"0.35rem 0.875rem", fontWeight:"500", opacity:rrRemoving?0.6:1}}>
                {rrRemoving?"Removing…":"Yes, Remove"}
              </button>
              <button onClick={() => setRrConfirm(false)} style={{...btnReset, fontSize:"0.8rem", color:T.muted}}>Back</button>
            </div>
          </>
        )}
      </div>
    );

    // ── REMOVE COLLABORATOR ──────────────────────────────────────────────────
    if (activeSubView === 'removeCollaborator') return (
      <div>
        <button onClick={goBack} style={backBtn}>← Back</button>
        <div style={{fontFamily:"'Playfair Display', serif", fontSize:"1.15rem", fontWeight:"700", color:T.danger, marginBottom:"0.25rem"}}>Remove Collaborator</div>
        <p style={{fontSize:"0.78rem", color:T.muted, marginBottom:"1.25rem"}}>This will fully remove {personName} from this estate.</p>

        <div style={{background:"#fff5f5", border:"1px solid #fca5a533", borderRadius:"10px", padding:"1rem", marginBottom:"1.25rem"}}>
          <div style={{fontSize:"0.85rem", color:T.danger, fontWeight:"600", marginBottom:"0.25rem"}}>Are you sure?</div>
          <div style={{fontSize:"0.75rem", color:T.muted, lineHeight:"1.6"}}>
            All of {personName}'s roles and estate access will be revoked immediately. This cannot be undone.
          </div>
        </div>

        <div style={{display:"flex", gap:"0.625rem"}}>
          <button onClick={handleRemoveCollaborator} disabled={rcRemoving}
            style={{...btnReset, fontSize:"0.82rem", color:"white", background:T.danger,
              borderRadius:"6px", padding:"0.4rem 1rem", fontWeight:"500", opacity:rcRemoving?0.6:1}}>
            {rcRemoving?"Removing…":"Yes, Remove Entirely"}
          </button>
          <button onClick={goBack} style={{...outlineBtn, fontSize:"0.82rem"}}>Cancel</button>
        </div>
      </div>
    );

    // ── MAIN MENU ────────────────────────────────────────────────────────────
    return (
      <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
        {personInvitations.length > 0 && (
          <button onClick={() => setActiveSubView('permissions')}
            style={{...rowBtn, background:T.parchment, border:`1px solid ${T.borderLight}`}}
            onMouseEnter={e=>e.currentTarget.style.background="#f0ede6"}
            onMouseLeave={e=>e.currentTarget.style.background=T.parchment}>
            <div><div style={sT}>Edit Permissions</div><div style={sS}>Adjust what {personName} can see and do</div></div>
            <span style={{color:T.muted, fontSize:"1.1rem"}}>›</span>
          </button>
        )}
        <button onClick={() => setActiveSubView('addRole')}
          style={{...rowBtn, background:T.parchment, border:`1px solid ${T.borderLight}`}}
          onMouseEnter={e=>e.currentTarget.style.background="#f0ede6"}
          onMouseLeave={e=>e.currentTarget.style.background=T.parchment}>
          <div><div style={sT}>Add Role</div><div style={sS}>Assign {personName} an additional role</div></div>
          <span style={{color:T.muted, fontSize:"1.1rem"}}>›</span>
        </button>
        {personInvitations.length > 0 && (
          <button onClick={() => setActiveSubView('removeRole')}
            style={{...rowBtn, background:T.parchment, border:`1px solid ${T.borderLight}`}}
            onMouseEnter={e=>e.currentTarget.style.background="#f0ede6"}
            onMouseLeave={e=>e.currentTarget.style.background=T.parchment}>
            <div><div style={sT}>Remove Role</div><div style={sS}>Remove one or more of {personName}'s roles</div></div>
            <span style={{color:T.muted, fontSize:"1.1rem"}}>›</span>
          </button>
        )}
        <div style={{height:"1px", background:T.borderLight, margin:"0.125rem 0"}} />
        <button onClick={() => setActiveSubView('removeCollaborator')}
          style={{...rowBtn, background:T.white, border:`1px solid #fca5a533`}}
          onMouseEnter={e=>e.currentTarget.style.background="#fff5f5"}
          onMouseLeave={e=>e.currentTarget.style.background=T.white}>
          <div><div style={{...sT, color:T.danger}}>Remove Collaborator</div><div style={sS}>Fully remove {personName} from this estate</div></div>
          <span style={{color:T.danger, fontSize:"1.1rem"}}>›</span>
        </button>
        <div style={{marginTop:"0.75rem", textAlign:"center"}}>
          <button onClick={onClose} style={{...btnReset, fontSize:"0.78rem", color:T.muted, textDecoration:"underline"}}>Close</button>
        </div>
      </div>
    );
  };

  return (
    <div style={modalOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{...modalBox, maxWidth:"500px", padding:"1.75rem", display:"flex", flexDirection:"column", gap:"0"}}>

        {/* ── Persistent header — always visible ── */}
        <div style={{display:"flex", alignItems:"flex-start", gap:"1rem", marginBottom:"1.25rem"}}>
          <div style={{width:"48px", height:"48px", borderRadius:"50%", background:`${T.forest}18`,
            border:`2px solid ${T.forest}33`, display:"flex", alignItems:"center", justifyContent:"center",
            color:T.forest, fontWeight:"700", fontSize:"1.1rem", flexShrink:0}}>
            {personName?.[0]?.toUpperCase() || "?"}
          </div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontWeight:"600", color:T.charcoal, fontSize:"1rem"}}>{personName}</div>
            {personEmail && <div style={{fontSize:"0.7rem", color:T.muted, fontFamily:"'DM Mono', monospace", marginTop:"0.1rem"}}>{personEmail}</div>}
            <div style={{marginTop:"0.4rem", display:"flex", flexWrap:"wrap", gap:"0.25rem"}}>
              {roleLabels.length > 0
                ? roleLabels.map((rl, ri) => (
                    <span key={ri} style={{fontSize:"0.62rem", background:`${T.forest}12`, color:T.forest,
                      padding:"0.15rem 0.45rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace"}}>{rl}</span>
                  ))
                : <span style={{fontSize:"0.62rem", color:T.muted, fontStyle:"italic"}}>
                    {allPersonRows.some(r => r.status === 'pending_acknowledgment') ? "⏳ Awaiting acknowledgment" : "No active roles"}
                  </span>
              }
            </div>
          </div>
          <button onClick={onClose} style={{...btnReset, fontSize:"1.4rem", color:T.muted, lineHeight:1, flexShrink:0, marginTop:"-0.2rem"}}>×</button>
        </div>

        <div style={{height:"1px", background:T.borderLight, marginBottom:"1.25rem"}} />

        {/* ── Sub-view body ── */}
        {isOwner
          ? renderSubView()
          : <div style={{fontSize:"0.82rem", color:T.muted, textAlign:"center", padding:"1rem 0"}}>
              Contact the estate owner to make changes to your roles or access.
            </div>
        }

      </div>
    </div>
  );
}

// ─── PERMISSIONS MODAL ────────────────────────────────────────────────────────
function PermissionsModal({ personName, personEmail, personInvitations, ownerResponseWindow, onClose, onSave }) {
  // personInvitations: array of accepted invitation rows for this person (passed directly from
  // the parent — no re-fetch needed). Each row has id, role, executor_order, permissions, etc.

  const [selectedInvId, setSelectedInvId] = useState(personInvitations[0]?.id ?? null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const selectedInv = personInvitations.find(inv => inv.id === selectedInvId) ?? personInvitations[0] ?? null;
  const isExecutorRole = selectedInv?.executor_order != null;
  const isMedicalPoaRole = selectedInv?.role === 'Medical POA';

  const parsePerms = (inv) => {
    const existing = inv?.permissions ?? {};
    return {
      view_checklist:             existing.view_checklist             !== false,
      edit_checklist:             existing.edit_checklist             === true,
      view_documents:             existing.view_documents             !== false,
      download_documents:         existing.download_documents         === true,
      view_vault:                 existing.view_vault                 === true,
      view_family:                existing.view_family                !== false,
      full_access:                existing.full_access                === true,
      view_medical_documents:     existing.view_medical_documents     === true,
      download_medical_documents: existing.download_medical_documents === true,
    };
  };

  const [perms, setPerms] = useState(() => parsePerms(selectedInv));

  // Reload perms when user switches roles in the selector
  useEffect(() => {
    const inv = personInvitations.find(i => i.id === selectedInvId) ?? null;
    setPerms(parsePerms(inv));
    setSaved(false);
    setError(null);
  }, [selectedInvId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key) => {
    setPerms(prev => {
      const next = { ...prev, [key]: !prev[key] };
      if (key === 'view_checklist' && !next.view_checklist) next.edit_checklist = false;
      if (key === 'view_documents' && !next.view_documents) next.download_documents = false;
      if (key === 'view_medical_documents' && !next.view_medical_documents) next.download_medical_documents = false;
      if (key === 'full_access' && next.full_access) {
        return { view_checklist:true, edit_checklist:true, view_documents:true,
                 download_documents:true, view_vault:true, view_family:true, full_access:true,
                 view_medical_documents: prev.view_medical_documents, download_medical_documents: prev.download_medical_documents };
      }
      return next;
    });
  };

  const TOGGLES = [
    { key:"view_checklist",             label:"View Checklist",                        desc:"Can see estate tasks and planning progress",                   disabledWhen: false,                            medicalPoaOnly: false },
    { key:"edit_checklist",             label:"Edit Checklist (check tasks off)",       desc:"Can mark tasks complete",                                      disabledWhen: !perms.view_checklist,            medicalPoaOnly: false },
    { key:"view_documents",             label:"View Documents",                        desc:"Can open pre-filled estate letters",                           disabledWhen: false,                            medicalPoaOnly: false },
    { key:"download_documents",         label:"Download Documents",                    desc:"Can download Word documents to their device",                   disabledWhen: !perms.view_documents,            medicalPoaOnly: false },
    { key:"view_vault",                 label:"View Vault",                            desc:"Can see vault items (executor access levels apply)",            disabledWhen: false,                            medicalPoaOnly: false },
    { key:"view_family",                label:"View Family Page",                      desc:"Can see who else is involved in this estate",                  disabledWhen: false,                            medicalPoaOnly: false },
    { key:"view_medical_documents",     label:"View Medical Documents",                desc:"Can see medical docs, advance directive, and POA paperwork",   disabledWhen: false,                            medicalPoaOnly: true  },
    { key:"download_medical_documents", label:"Download Medical Documents",            desc:"Can download medical documents to their device",               disabledWhen: !perms.view_medical_documents,    medicalPoaOnly: true  },
  ].filter(t => isMedicalPoaRole ? t.medicalPoaOnly : !t.medicalPoaOnly);

  // Human-readable label for each invitation row (used in the role selector)
  const invRoleLabel = (inv) => {
    if (!inv) return 'Unknown';
    if (inv.executor_order != null) {
      return inv.executor_order === 1 ? 'Primary Executor' : `Executor Backup #${inv.executor_order - 1}`;
    }
    return inv.role || 'Collaborator';
  };

  const handleSave = async () => {
    if (!selectedInv?.id) {
      setError("No invitation record found. Please try again.");
      return;
    }
    setSaving(true); setError(null);
    try {
      const { error: err } = await updateInvitationPermissions(selectedInv.id, perms);
      if (err) throw err;
      setSaved(true);
      onSave(perms, selectedInv.id);
      setTimeout(() => { setSaved(false); onClose(); }, 1200);
    } catch(e) {
      setError("Failed to save permissions. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalOverlay} onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={{...modalBox, maxWidth:"520px"}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.25rem"}}>
          <div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>
              Edit Permissions
            </h2>
            <p style={{fontSize:"0.825rem", color:T.muted}}>
              {personName || personEmail || "This person"}'s access to this estate
            </p>
          </div>
          <button onClick={onClose} style={{...btnReset, fontSize:"1.4rem", color:T.muted, lineHeight:1}}>×</button>
        </div>

        {personInvitations.length === 0 ? (
          <div style={{background:"#fff8f0", border:`1px solid ${T.gold}`, borderRadius:"10px", padding:"1rem", marginBottom:"1.25rem", fontSize:"0.82rem", color:"#9a6700"}}>
            No accepted invitation found for this person. They need to accept their invitation before you can edit their permissions.
          </div>
        ) : (
          <>
            {/* Role selector — shown only when the person holds multiple accepted roles */}
            {personInvitations.length > 1 && (
              <div style={{marginBottom:"1.25rem"}}>
                <p style={{fontSize:"0.78rem", fontWeight:"600", color:T.muted, marginBottom:"0.5rem"}}>
                  Editing permissions for:
                </p>
                <div style={{display:"flex", gap:"0.5rem", flexWrap:"wrap"}}>
                  {personInvitations.map(inv => (
                    <button
                      key={inv.id}
                      onClick={() => setSelectedInvId(inv.id)}
                      style={{...btnReset, fontSize:"0.8rem", padding:"0.35rem 0.875rem", borderRadius:"6px", fontWeight:"500",
                        background: inv.id === selectedInvId ? T.forest : "transparent",
                        color: inv.id === selectedInvId ? "#fff" : T.charcoal,
                        border: `1px solid ${inv.id === selectedInvId ? T.forest : T.border}`}}
                    >
                      {invRoleLabel(inv)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Executor: Full Access toggle at top */}
            {isExecutorRole && (
              <div style={{marginBottom:"1rem"}}>
                <label style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.875rem 1rem",
                  background: perms.full_access ? "#f0faf5" : "#fffaf5",
                  border:`1px solid ${perms.full_access ? T.success : T.gold}`,
                  borderRadius:"10px", cursor:"pointer"}}>
                  <input type="checkbox" checked={!!perms.full_access} onChange={()=>toggle("full_access")}
                    style={{width:"1rem", height:"1rem", accentColor:T.forest}} />
                  <div>
                    <div style={{fontWeight:"600", color:T.forest, fontSize:"0.9rem"}}>Full Access</div>
                    <div style={{fontSize:"0.75rem", color:T.muted}}>
                      {perms.full_access
                        ? "⚠ This executor has full access to everything"
                        : "Grant all permissions at once — overrides individual toggles"}
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Individual permission toggles */}
            <div style={{display:"flex", flexDirection:"column", gap:"0.5rem", marginBottom:"1.5rem",
              opacity: perms.full_access ? 0.45 : 1,
              pointerEvents: perms.full_access ? "none" : "auto"}}>
              {TOGGLES.map(t => {
                const isDisabled = t.disabledWhen;
                return (
                  <label key={t.key} style={{display:"flex", alignItems:"flex-start", gap:"0.75rem",
                    padding:"0.75rem 1rem",
                    background: perms[t.key] && !isDisabled ? "#f8fbf9" : "transparent",
                    border:`1px solid ${perms[t.key] && !isDisabled ? T.forest+"33" : T.borderLight}`,
                    borderRadius:"10px",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.45 : 1}}>
                    <input type="checkbox"
                      checked={!!perms[t.key] && !isDisabled}
                      onChange={() => !isDisabled && toggle(t.key)}
                      disabled={isDisabled}
                      style={{width:"1rem", height:"1rem", accentColor:T.forest, marginTop:"0.125rem", flexShrink:0}} />
                    <div>
                      <div style={{fontWeight:"500", color:T.charcoal, fontSize:"0.85rem"}}>{t.label}</div>
                      <div style={{fontSize:"0.72rem", color:T.muted}}>
                        {isDisabled ? `Requires "${TOGGLES.find(x=>x.key===(t.key==='edit_checklist'?'view_checklist':'view_documents'))?.label}" to be on` : t.desc}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {isExecutorRole && (
              <div style={{background:T.parchment, borderRadius:"10px", padding:"0.875rem 1rem", marginBottom:"1.25rem", fontSize:"0.8rem", color:T.muted}}>
                <strong style={{color:T.forest}}>Emergency Access:</strong> If this executor requests full access and you don't respond within {ownerResponseWindow}h, access is automatically granted per your account settings.
              </div>
            )}

            {error && <p style={{color:T.danger, fontSize:"0.8rem", marginBottom:"0.75rem"}}>{error}</p>}
            {saved && <p style={{color:T.success, fontSize:"0.8rem", marginBottom:"0.75rem"}}>✓ Permissions updated for {personName || personEmail}</p>}

            <div style={{display:"flex", justifyContent:"flex-end", gap:"0.75rem"}}>
              <button onClick={onClose} style={{...outlineBtn, fontSize:"0.875rem"}}>Cancel</button>
              <button onClick={handleSave} disabled={saving || saved} style={{...filledBtn, fontSize:"0.875rem", opacity:(saving||saved)?0.6:1}}>
                {saving ? "Saving…" : saved ? "Saved ✓" : "Save Permissions"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── REQUEST FULL ACCESS MODAL ────────────────────────────────────────────────
function RequestFullAccessModal({ estate, user, estateId, invitations, ownerEmail, ownerResponseWindow, grantImmediateAccess, userRole, onClose, onRequested }) {
  const [confirmed, setConfirmed] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState(null);

  // Compute escalation chain: skip deceased positions, find first live approver above the requester
  const userRank = userRole === 'backup_executor_2' ? 3 : userRole === 'backup_executor_1' ? 2 : 1;
  const allExecutorInvs = (invitations || [])
    .filter(inv => inv.executor_order != null && inv.status === 'accepted')
    .sort((a, b) => (a.executor_order || 99) - (b.executor_order || 99));
  // Positions above the requester, from most-junior-above to most-senior (e.g. backup1 before primary)
  const approversAbove = allExecutorInvs
    .filter(inv => (inv.executor_order || 99) < userRank)
    .sort((a, b) => (b.executor_order || 0) - (a.executor_order || 0));
  let chainEmail = ownerEmail;
  let chainName = estate?.ownerName || 'the estate owner';
  let chainEmailType = 'access_request_to_owner';
  for (const inv of approversAbove) {
    if (!inv.deceased) {
      chainEmail = inv.invited_email;
      chainName = inv.invited_name || inv.invited_email;
      chainEmailType = (inv.executor_order || 0) === 2 ? 'access_request_to_backup' : 'access_request_to_primary';
      break;
    }
  }

  const handleRequest = async () => {
    if (!confirmed) return;
    setRequesting(true); setError(null);
    try {
      const expiresAt = new Date(Date.now() + ownerResponseWindow * 60 * 60 * 1000).toISOString();
      const { data, error: err } = await createAccessRequest({
        estate_id: estateId,
        requested_by: user?.id,
        requester_name: user?.user_metadata?.full_name || user?.email,
        requester_role: userRole,
        current_approver_email: chainEmail,
        response_deadline: expiresAt,
      });
      if (err) throw err;

      if (grantImmediateAccess) {
        const inv = (invitations || []).find(i => i.invited_email?.toLowerCase() === user?.email?.toLowerCase());
        if (inv) await updateInvitationPermissions(inv.id, { full_access: true });
        onRequested({ ...data, status: 'approved_auto' });
      } else {
        const emailData = chainEmailType === 'access_request_to_owner'
          ? {
              ownerName: chainName,
              requestorName: user?.user_metadata?.full_name || user?.email,
              requestorRole: userRole,
              estateName: estate?.deceasedName || 'Estate',
              responseWindowHours: ownerResponseWindow,
              approveUrl: `${window.location.origin}?approve_access=${data?.id}`,
              denyUrl: `${window.location.origin}?deny_access=${data?.id}`,
            }
          : {
              requesterName: user?.user_metadata?.full_name || user?.email,
              ownerName: estate?.deceasedName || 'Estate',
              responseWindow: ownerResponseWindow,
              denyUrl: `${window.location.origin}?deny_access=${data?.id}`,
            };
        await sendEmail(chainEmail, chainEmailType, emailData);
        onRequested(data);
      }
    } catch(e) {
      setError("Failed to submit request. Please try again.");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div style={modalOverlay} onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={{...modalBox, maxWidth:"460px"}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.25rem"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.3rem", fontWeight:"700", color:T.forest}}>Request Full Access</h2>
          <button onClick={onClose} style={{...btnReset, fontSize:"1.4rem", color:T.muted, lineHeight:1}}>×</button>
        </div>

        <p style={{fontSize:"0.875rem", color:T.charcoal, lineHeight:"1.6", marginBottom:"1.25rem"}}>
          You're requesting full access to <strong>{estate?.deceasedName || "this estate"}</strong>. A notification will be sent to <strong>{chainName}</strong> ({chainEmail}).
        </p>

        {grantImmediateAccess ? (
          <div style={{background:"#f0faf5", border:`1px solid ${T.success}`, borderRadius:"10px", padding:"0.875rem 1rem", marginBottom:"1.25rem", fontSize:"0.82rem", color:T.charcoal}}>
            ✓ The estate owner has enabled <strong>immediate access</strong> for executor requests. Your access will be granted instantly.
          </div>
        ) : (
          <div style={{background:T.parchment, borderRadius:"10px", padding:"0.875rem 1rem", marginBottom:"1.25rem", fontSize:"0.82rem", color:T.muted}}>
            The owner has <strong>{ownerResponseWindow} hours</strong> to approve or deny. If they don't respond, access is auto-granted.
          </div>
        )}

        <label style={{display:"flex", alignItems:"flex-start", gap:"0.75rem", marginBottom:"1.5rem", cursor:"pointer"}}>
          <input type="checkbox" checked={confirmed} onChange={e=>setConfirmed(e.target.checked)} style={{width:"1rem", height:"1rem", accentColor:T.forest, marginTop:"0.15rem", flexShrink:0}} />
          <span style={{fontSize:"0.82rem", color:T.charcoal, lineHeight:"1.5"}}>
            I understand this request is logged and the estate owner will be notified immediately.
          </span>
        </label>

        {error && <p style={{color:T.danger, fontSize:"0.8rem", marginBottom:"0.75rem"}}>{error}</p>}

        <div style={{display:"flex", justifyContent:"flex-end", gap:"0.75rem"}}>
          <button onClick={onClose} style={{...outlineBtn, fontSize:"0.875rem"}}>Cancel</button>
          <button onClick={handleRequest} disabled={!confirmed || requesting} style={{...filledBtn, fontSize:"0.875rem", opacity:(!confirmed||requesting)?0.5:1}}>
            {requesting ? "Sending…" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DENY / APPROVE ACCESS PAGE ───────────────────────────────────────────────

// ─── EXECUTOR PLAYBOOK ────────────────────────────────────────────────────────

const PLAYBOOK_CAT_COLORS = {
  "Immediate":          "#c0392b",
  "Government & Legal": "#2980b9",
  "Financial":          "#27ae60",
  "Digital":            "#d4850a",
  "Tax":                "#2c4a3e",
  "Closing":            "#8e44ad",
};

const PLAYBOOK_STAGES = [
  {
    key: "Immediate",
    title: "Immediate — First 1–2 Weeks",
    icon: "⚡",
    framing: "The first two weeks are the most time-sensitive. Secure the estate, notify key parties, and gather the essential documents before anything else.",
    items: [
      { id: "playbook-immediate-1", title: "Obtain at least 10–15 certified copies of the death certificate from the funeral home or vital records office — you will need them for almost every institution." },
      { id: "playbook-immediate-2", title: "Locate the original will and any codicils. File the original with the local probate court even if probate may not be required." },
      { id: "playbook-immediate-3", title: "Secure all physical property: home, vehicles, storage units. Change locks if necessary and ensure insurance coverage remains active." },
      { id: "playbook-immediate-4", title: "Open a dedicated estate bank account to receive income and pay estate expenses. Do not commingle with personal funds." },
      { id: "playbook-immediate-5", title: "Begin a written inventory of all known assets (real estate, vehicles, bank accounts, investments, retirement accounts, personal property) and all known liabilities." },
      { id: "playbook-immediate-6", title: "Identify and notify all known beneficiaries named in the will." },
      { id: "playbook-immediate-7", title: "Notify the deceased's employer and ask about any final pay, benefits, or life insurance through work." },
      { id: "playbook-immediate-8", title: "Cancel or redirect time-sensitive services: utilities, subscriptions, mail (file a mail forwarding request with USPS)." },
      { id: "playbook-immediate-9", title: "Consult an estate attorney early — even a single session helps clarify whether probate is required and what your obligations are." },
    ],
  },
  {
    key: "Government & Legal",
    title: "Government & Legal Notifications",
    icon: "🏛️",
    framing: "Government agencies do not automatically learn of a death. Each must be notified separately. Start with Social Security and work through the list systematically.",
    items: [
      { id: "playbook-gov-1", title: "Notify the Social Security Administration. Any benefits paid after the month of death must be returned." },
      { id: "playbook-gov-2", title: "Apply for Letters Testamentary (or Letters of Administration) through the probate court — these grant you legal authority to act on behalf of the estate." },
      { id: "playbook-gov-3", title: "Obtain an Employer Identification Number (EIN) for the estate from the IRS at irs.gov. You will need this to open the estate account and file returns." },
      { id: "playbook-gov-4", title: "Notify Medicare and Medicaid if the deceased was enrolled. Return any benefits paid for the month of death." },
      { id: "playbook-gov-5", title: "Notify the Department of Veterans Affairs if the deceased was a veteran — survivors may be entitled to burial benefits." },
      { id: "playbook-gov-6", title: "Cancel the deceased's driver's license with the state Department of Motor Vehicles (DMV) and U.S. passport with the State Department." },
      { id: "playbook-gov-7", title: "Notify the voter registration office in their county to remove them from the rolls." },
      { id: "playbook-gov-8", title: "Publish a Notice to Creditors in a local newspaper if required by your state — this starts the clock on creditor claims." },
      { id: "playbook-gov-9", title: "Register the estate with the state's unclaimed property program if applicable." },
    ],
  },
  {
    key: "Financial",
    title: "Financial",
    icon: "💰",
    framing: "Financial closure is often the most time-consuming part of estate administration. Work systematically through every account and do not distribute assets to beneficiaries until all debts are settled.",
    items: [
      { id: "playbook-financial-1", title: "Contact all banks, credit unions, and brokerage firms to notify them of the death and begin the transfer or closure process. Bring a certified death certificate and Letters Testamentary." },
      { id: "playbook-financial-2", title: "Collect any life insurance proceeds. File claims with each insurer — these typically pass outside of probate directly to named beneficiaries." },
      { id: "playbook-financial-3", title: "Notify pension providers and retirement account administrators (IRA, 401k). Beneficiary designations on these accounts control distribution, not the will." },
      { id: "playbook-financial-4", title: "Request a credit report in the deceased's name to identify any unknown creditors or accounts." },
      { id: "playbook-financial-5", title: "Pay all legitimate creditor claims from estate funds before distributing assets to beneficiaries. Distributing first can make you personally liable." },
      { id: "playbook-financial-6", title: "If the deceased owned real estate, work with an estate attorney to transfer title. A deed transfer may require probate." },
      { id: "playbook-financial-7", title: "Notify any mortgage lender. Continue making payments to protect the property until the estate is resolved." },
      { id: "playbook-financial-8", title: "Cancel all credit cards after settling any balances owed." },
      { id: "playbook-financial-9", title: "Collect any money owed to the estate: unpaid wages, tax refunds, lawsuit settlements, or insurance claims." },
    ],
  },
  {
    key: "Digital",
    title: "Digital",
    icon: "💻",
    framing: "Digital assets are often overlooked. Act quickly — accounts are sometimes deleted after inactivity, and credentials may be time-sensitive.",
    items: [
      { id: "playbook-digital-1", title: "Download or preserve important digital files, photos, and documents from computers, cloud storage (Google Drive, iCloud, Dropbox), and email accounts while access is still possible." },
      { id: "playbook-digital-2", title: "Decide on each social media account: memorialization (Facebook allows this), deletion, or preservation per the family's wishes. Follow each platform's official process." },
      { id: "playbook-digital-3", title: "Cancel or transfer domain names, websites, or online business assets." },
      { id: "playbook-digital-4", title: "Identify and cancel recurring digital subscriptions: streaming, software, gaming, news, etc." },
      { id: "playbook-digital-5", title: "Notify online banking and investment portals — many have separate digital estate processes." },
      { id: "playbook-digital-6", title: "If the deceased held cryptocurrency, work with an estate attorney. Transfer requires private keys or exchange account access — this can be complex." },
      { id: "playbook-digital-7", title: "Cancel cloud phone plans and associated app subscriptions." },
      { id: "playbook-digital-8", title: "Check for any digital income: royalties, monetized content channels, affiliate accounts." },
    ],
  },
  {
    key: "Tax",
    title: "Tax",
    icon: "🧾",
    framing: "Tax obligations do not end at death. The estate may owe income taxes, and in some cases a federal or state estate tax. Miss a deadline and interest and penalties accrue.",
    items: [
      { id: "playbook-tax-1", title: "File the deceased's final federal income tax return (Form 1040) for the year of death. The due date is the normal April 15 deadline." },
      { id: "playbook-tax-2", title: "File the deceased's final state income tax return in any state where they had income or residency." },
      { id: "playbook-tax-3", title: "Determine if a federal estate tax return (Form 706) is required. For 2025, the federal estate tax exemption is approximately $13.6 million. Many states have lower thresholds." },
      { id: "playbook-tax-4", title: "If the estate earns income after death (interest, dividends, rent), file an estate income tax return (Form 1041) for each year the estate is open." },
      { id: "playbook-tax-5", title: "Notify beneficiaries of any income they receive from the estate that must be reported on their own returns (e.g., IRA distributions, interest income)." },
      { id: "playbook-tax-6", title: "Keep records of all estate expenses — executor fees, attorney fees, accounting fees, and certain losses may be deductible on estate tax returns." },
      { id: "playbook-tax-7", title: "Consider consulting a Certified Public Accountant (CPA) or tax attorney with estate experience, particularly if the estate includes business interests, real property in multiple states, or significant investment income." },
    ],
  },
  {
    key: "Closing",
    title: "Closing the Estate",
    icon: "✅",
    framing: "Closing is the final step. Do not rush it — distributing assets before all debts and taxes are resolved can expose you to personal liability as executor.",
    items: [
      { id: "playbook-closing-1", title: "Prepare a final accounting of all estate income received, expenses paid, and proposed distributions. This is typically required by the probate court and by beneficiaries." },
      { id: "playbook-closing-2", title: "Obtain signed receipts or releases from each beneficiary upon distribution — these protect you from future claims." },
      { id: "playbook-closing-3", title: "Distribute remaining assets to beneficiaries exactly as directed by the will (or by state intestacy law if no valid will exists)." },
      { id: "playbook-closing-4", title: "File a petition to close the estate with the probate court and attend any required hearing." },
      { id: "playbook-closing-5", title: "File all final tax returns for the estate and obtain tax clearance if required by your state." },
      { id: "playbook-closing-6", title: "Formally discharge your duties as executor — once the court issues an order closing the estate, your legal obligations end." },
      { id: "playbook-closing-7", title: "Retain all estate records (financial statements, tax returns, receipts, correspondence) for at least 7 years in case of future questions or audits." },
    ],
  },
];

function ExecutorPlaybook({ estate, navTo, invitedPermissions, invitedRole, estateSupabaseId, user }) {
  const [playBookCompletion, setPlayBookCompletion] = useState({}); // { [task_id]: { done, completed_at } }
  const [playBookFilter, setPlayBookFilter] = useState("All");
  const [pbLoading, setPbLoading] = useState(true);

  const allItems = PLAYBOOK_STAGES.flatMap(s => s.items);
  const totalTasks = allItems.length;
  const doneTasks = allItems.filter(item => playBookCompletion[item.id]?.done).length;
  const progress = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;

  useEffect(() => {
    if (!estateSupabaseId) return;
    (async () => {
      setPbLoading(true);
      try {
        const { data } = await supabase
          .from('tasks')
          .select('task_id, done, completed_at')
          .eq('estate_id', estateSupabaseId)
          .eq('source', 'playbook');
        if (data) {
          const map = {};
          data.forEach(row => { map[row.task_id] = { done: row.done, completed_at: row.completed_at }; });
          setPlayBookCompletion(map);
        }
      } catch(e) {}
      finally { setPbLoading(false); }
    })();
  }, [estateSupabaseId]);

  const toggleTask = async (taskId, taskTitle, stageName) => {
    const current = playBookCompletion[taskId] || {};
    const newDone = !current.done;
    const newCompleted = newDone ? new Date().toISOString() : null;
    setPlayBookCompletion(prev => ({ ...prev, [taskId]: { done: newDone, completed_at: newCompleted } }));
    try {
      await supabase.from('tasks').upsert({
        estate_id: estateSupabaseId,
        user_id: user?.id,
        task_id: taskId,
        done: newDone,
        source: 'playbook',
        completed_at: newCompleted,
        title: taskTitle,
        category: stageName,
      }, { onConflict: 'estate_id, task_id' });
    } catch(e) {
      setPlayBookCompletion(prev => ({ ...prev, [taskId]: current }));
    }
  };

  const filterKeys = ["All", ...PLAYBOOK_STAGES.map(s => s.key)];
  const visibleStages = playBookFilter === "All" ? PLAYBOOK_STAGES : PLAYBOOK_STAGES.filter(s => s.key === playBookFilter);

  return (
    <AppShell estate={estate} tasks={[]} page="app-playbook" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
      <div style={{maxWidth:"760px"}}>

        {/* Header */}
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.75rem", flexWrap:"wrap", gap:"1rem"}}>
          <div>
            <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem", letterSpacing:"-0.02em"}}>
              Executor Playbook
            </h1>
            <p style={{color:T.muted, fontSize:"0.875rem"}}>
              {pbLoading ? "Loading…" : `${doneTasks} of ${totalTasks} tasks complete`}
            </p>
          </div>
          <div style={{position:"relative", width:"60px", height:"60px"}}>
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle cx="30" cy="30" r="26" fill="none" stroke={T.parchment} strokeWidth="5"/>
              <circle cx="30" cy="30" r="26" fill="none" stroke={T.forest} strokeWidth="5" strokeDasharray={`${progress*1.634} 163.4`} strokeLinecap="round" transform="rotate(-90 30 30)" style={{transition:"stroke-dasharray 0.5s ease"}}/>
            </svg>
            <span style={{position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.7rem", fontWeight:"700", color:T.forest, fontFamily:"'DM Mono', monospace"}}>{progress}%</span>
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:"12px", padding:"1.25rem 1.5rem", marginBottom:"1.75rem", display:"flex", gap:"0.875rem", alignItems:"flex-start"}}>
          <span style={{fontSize:"1.25rem", flexShrink:0, marginTop:"0.1rem"}}>⚖️</span>
          <div>
            <p style={{fontSize:"0.82rem", fontWeight:"700", color:"#92400e", margin:"0 0 0.25rem", fontFamily:"'DM Sans', sans-serif"}}>This is not legal advice</p>
            <p style={{fontSize:"0.8rem", color:"#9a3412", lineHeight:"1.65", margin:0}}>
              This playbook is a general reference guide for common executor tasks. Every estate is different — state laws vary, and some situations require court involvement or professional guidance. Consult a licensed estate attorney before making decisions that affect beneficiaries or involve significant assets.
            </p>
          </div>
        </div>

        {/* Filter chips */}
        <div style={{display:"flex", gap:"0.5rem", marginBottom:"1.5rem", flexWrap:"wrap"}}>
          {filterKeys.map(key => {
            const col = key === "All" ? T.forest : PLAYBOOK_CAT_COLORS[key] || T.muted;
            const active = playBookFilter === key;
            return (
              <button key={key} onClick={() => setPlayBookFilter(key)} style={{...btnReset, padding:"0.375rem 0.875rem", borderRadius:"20px", fontSize:"0.78rem", border:`1px solid ${active ? col : T.border}`, background:active ? col : "transparent", color:active ? "white" : key === "All" ? T.muted : col, fontWeight:active ? "500" : "400"}}>
                {key}
              </button>
            );
          })}
        </div>

        {/* Stage sections */}
        {visibleStages.map(stage => {
          const catColor = PLAYBOOK_CAT_COLORS[stage.key] || T.forest;
          const stageDone = stage.items.filter(item => playBookCompletion[item.id]?.done).length;
          return (
            <div key={stage.key} style={{marginBottom:"1.75rem"}}>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"0.375rem"}}>
                <span style={{fontSize:"0.65rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.07em", color:catColor, textTransform:"uppercase", fontWeight:"600"}}>
                  {stage.icon} {stage.key}
                </span>
                <span style={{fontSize:"0.65rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>{stageDone}/{stage.items.length}</span>
              </div>
              <p style={{fontStyle:"italic", color:T.muted, fontSize:"0.82rem", lineHeight:"1.65", margin:"0 0 0.875rem"}}>
                {stage.framing}
              </p>
              <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
                {stage.items.map(item => {
                  const completion = playBookCompletion[item.id] || {};
                  const done = !!completion.done;
                  return (
                    <div key={item.id} style={{background:T.white, border:`1px solid ${done ? T.borderLight : T.border}`, borderRadius:"10px", padding:"0.875rem 1.25rem", display:"flex", gap:"1rem", opacity:done ? 0.65 : 1, transition:"all 0.15s"}}>
                      <button onClick={() => toggleTask(item.id, item.title, stage.key)} style={{...btnReset, flexShrink:0, paddingTop:"2px", cursor:"pointer"}}>
                        {done
                          ? <div style={{width:"22px", height:"22px", borderRadius:"50%", background:T.forest, display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:"0.7rem", fontWeight:"700"}}>✓</div>
                          : <div style={{width:"22px", height:"22px", borderRadius:"50%", border:`2px solid ${T.border}`}}/>}
                      </button>
                      <div style={{flex:1, minWidth:0}}>
                        <p style={{fontSize:"0.875rem", fontWeight:"500", color:done ? T.muted : T.charcoal, textDecoration:done ? "line-through" : "none", margin:0, lineHeight:"1.5"}}>{item.title}</p>
                        {done && completion.completed_at && (
                          <p style={{fontSize:"0.68rem", color:T.muted, margin:"0.25rem 0 0", fontFamily:"'DM Mono', monospace"}}>
                            Completed {new Date(completion.completed_at).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'})}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Footer note */}
        <p style={{fontSize:"0.78rem", color:T.muted, marginTop:"2rem", lineHeight:"1.6", textAlign:"center"}}>
          Need guidance on a specific task? Use the{" "}
          <button onClick={() => navTo("app-ai")} style={{...btnReset, color:T.gold, fontWeight:"500", textDecoration:"underline", fontSize:"0.78rem"}}>
            AI Guide
          </button>
          {" "}to ask questions about the estate.
        </p>
      </div>
    </AppShell>
  );
}

// ─── EXECUTOR ACCESS SETTINGS (for AccountPage) ───────────────────────────────
function ExecutorAccessSettings({ estateId, initialSettings, onSaved }) {
  const WINDOWS = [
    { value: 24, label: "24 hrs", desc: "24 hours" },
    { value: 48, label: "48 hrs", desc: "48 hours" },
    { value: 72, label: "72 hrs", desc: "72 hours" },
  ];
  const [responseWindow, setResponseWindow] = useState(initialSettings?.responseWindow || 72);
  const [grantImmediateAccess, setGrantImmediateAccess] = useState(initialSettings?.grantImmediateAccess || false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const { error: err } = await updateOwnerAccessSettings(estateId, { responseWindow, grantImmediateAccess });
      if (err) throw err;
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onSaved) onSaved({ responseWindow, grantImmediateAccess });
    } catch(e) {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const selectedWindow = WINDOWS.find(w => w.value === responseWindow);

  return (
    <div style={{
      background: T.white,
      border: `2px solid ${T.forest}22`,
      borderRadius: "16px",
      padding: "2rem",
      marginBottom: "1.5rem",
      boxShadow: "0 2px 16px rgba(44,78,55,0.06)",
    }}>
      {/* Header */}
      <div style={{display:"flex", alignItems:"flex-start", gap:"1rem", marginBottom:"1.5rem"}}>
        <div style={{
          width:"44px", height:"44px", borderRadius:"12px", flexShrink:0,
          background:`linear-gradient(135deg, ${T.forest}18, ${T.forest}08)`,
          border:`1.5px solid ${T.forest}30`,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:"1.3rem",
        }}>⚡</div>
        <div>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.25rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>
            Executor Emergency Access
          </h2>
          <p style={{fontSize:"0.82rem", color:T.muted, lineHeight:"1.55", margin:0}}>
            When your executor requests full estate access, you have a set window of time to approve or deny it. If you don't respond in time, access is automatically granted.
          </p>
        </div>
      </div>

      {/* Current state summary pill */}
      <div style={{
        background: grantImmediateAccess ? "#fff8f0" : "#f0faf5",
        border: `1px solid ${grantImmediateAccess ? T.gold+"55" : T.success+"44"}`,
        borderRadius: "10px",
        padding: "0.75rem 1rem",
        marginBottom: "1.5rem",
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
      }}>
        <span style={{fontSize:"1rem"}}>{grantImmediateAccess ? "⚡" : "🕐"}</span>
        <span style={{fontSize:"0.82rem", color:T.charcoal, fontWeight:"500"}}>
          {grantImmediateAccess
            ? "Access is granted immediately when requested — no waiting period."
            : `Your executor must wait ${selectedWindow?.desc || "72 hours"} for access if you don't respond.`}
        </span>
      </div>

      {/* Setting 1: Response window */}
      <div style={{marginBottom:"1.5rem", opacity: grantImmediateAccess ? 0.4 : 1, transition:"opacity 0.2s", pointerEvents: grantImmediateAccess ? "none" : "auto"}}>
        <div style={{marginBottom:"0.625rem"}}>
          <p style={{fontSize:"0.875rem", fontWeight:"600", color:T.charcoal, margin:"0 0 0.2rem"}}>
            Approval Window
          </p>
          <p style={{fontSize:"0.75rem", color:T.muted, margin:0}}>
            How long you have to respond before access is automatically granted.
          </p>
        </div>
        <div style={{display:"flex", gap:"0.625rem", flexWrap:"wrap"}}>
          {WINDOWS.map(w => (
            <button
              key={w.value}
              onClick={() => setResponseWindow(w.value)}
              style={{
                ...btnReset,
                padding: "0.625rem 1.25rem",
                borderRadius: "10px",
                fontSize: "0.875rem",
                fontWeight: responseWindow === w.value ? "700" : "400",
                color: responseWindow === w.value ? "white" : T.forest,
                background: responseWindow === w.value ? T.forest : "transparent",
                border: `1.5px solid ${responseWindow === w.value ? T.forest : T.borderLight}`,
                transition: "all 0.15s",
                minWidth: "80px",
              }}
            >
              {w.desc}
            </button>
          ))}
        </div>
        {!grantImmediateAccess && (
          <p style={{fontSize:"0.75rem", color:T.muted, marginTop:"0.625rem"}}>
            You'll receive an email when a request is made and have <strong>{selectedWindow?.desc}</strong> to approve or deny before access is auto-granted.
          </p>
        )}
      </div>

      {/* Setting 2: Immediate access toggle */}
      <div style={{
        background: grantImmediateAccess ? "#fff8f0" : T.parchment,
        border: `1.5px solid ${grantImmediateAccess ? T.gold+"55" : T.borderLight}`,
        borderRadius: "12px",
        padding: "1rem 1.125rem",
        marginBottom: "1.5rem",
        transition: "all 0.2s",
      }}>
        <label style={{display:"flex", alignItems:"flex-start", gap:"0.875rem", cursor:"pointer"}}>
          <input
            type="checkbox"
            checked={grantImmediateAccess}
            onChange={e => setGrantImmediateAccess(e.target.checked)}
            style={{width:"1.1rem", height:"1.1rem", accentColor:T.forest, marginTop:"0.15rem", flexShrink:0}}
          />
          <div>
            <p style={{fontSize:"0.875rem", fontWeight:"600", color:T.charcoal, margin:"0 0 0.25rem"}}>
              Skip the waiting period — grant access immediately
            </p>
            <p style={{fontSize:"0.775rem", color:T.muted, margin:0, lineHeight:"1.5"}}>
              When checked, executor access requests are approved the moment they're submitted — no approval window, no delay. Use this if you trust your executor to act without prior review.
            </p>
          </div>
        </label>
      </div>

      {error && (
        <div style={{background:"#fef0f0", border:"1px solid #f5c6cb", borderRadius:"8px", padding:"0.625rem 1rem", fontSize:"0.82rem", color:T.danger, marginBottom:"1rem"}}>
          ⚠ {error}
        </div>
      )}

      <div style={{display:"flex", alignItems:"center", gap:"1rem"}}>
        <button onClick={handleSave} disabled={saving} style={{...filledBtn, padding:"0.75rem 1.75rem", fontSize:"0.9rem", opacity:saving?0.6:1}}>
          {saving ? "Saving…" : "Save Settings"}
        </button>
        {saved && (
          <span style={{fontSize:"0.85rem", color:T.success, fontWeight:"500", display:"flex", alignItems:"center", gap:"0.3rem"}}>
            ✓ Saved
          </span>
        )}
      </div>
    </div>
  );
}

// ─── INLINE COPY BUTTON ───────────────────────────────────────────────────────
function InlineCopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{...btnReset, fontSize:"0.68rem", color:copied?T.success:T.forest, fontWeight:"600", border:`1px solid ${copied?T.success:T.forest}`, borderRadius:"5px", padding:"0.2rem 0.625rem", transition:"all 0.2s"}}
    >
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
}

// ─── COLLABORATOR CARD ───────────────────────────────────────────────────────
// Clicking the card opens CollaboratorDetailModal (via onOpenDetail) for all
// invitation-based people. onRemove is only used for localStorage-only people
// (no invitation) where no detail modal is available.
function CollaboratorCard({ person: c, isOwner, isInvitedUser, estate, onEdit, invitation, onRemove, estateId, onOpenDetail }) {
  const [showEdit, setShowEdit] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  const handleResendInvite = async () => {
    if (!invitation?.id || resending) return;
    setResending(true);
    try {
      await resendInvitation(invitation.id, { inviterName: estate?.ownerName || '' });
    } finally {
      setResending(false);
      setResent(true);
      setTimeout(() => setResent(false), 3000);
    }
  };

  const ACCESS_OPTIONS = isOwner ? [
    { id:"view-progress",  label:"View planning progress",  icon:"👁"  },
    { id:"collaborate",    label:"Help with planning",      icon:"🤝" },
    { id:"trusted-vault",  label:"Trusted vault access",    icon:"🔒" },
    { id:"executor",       label:"Named as executor",       icon:"⚖️" },
  ] : [
    { id:"view-progress",  label:"View settlement progress", icon:"👁"  },
    { id:"collaborate",    label:"Help settle the estate",   icon:"🤝" },
    { id:"beneficiary",    label:"Beneficiary — view only",  icon:"📋" },
    { id:"attorney",       label:"Attorney / Professional",  icon:"⚖️" },
  ];

  const ROLES = ["Spouse / Partner","Child","Sibling","Parent","Grandparent","Grandchild","Aunt / Uncle","Niece / Nephew","Friend","Attorney","Accountant","Other"];

  const [editForm, setEditForm] = useState({
    name: c.name || "",
    email: c.email || "",
    role: c.role || "",
    relationship: c.relationship || "",
    canCollaborate: ACCESS_OPTIONS.find(a => a.label === c.canCollaborate)?.id || "",
    sendInvite: true,
  });

  const handleSave = () => {
    const updated = {
      ...editForm,
      canCollaborate: ACCESS_OPTIONS.find(a => a.id === editForm.canCollaborate)?.label || editForm.canCollaborate,
      inviteStatus: editForm.sendInvite && editForm.email ? "invited" : c.inviteStatus || "added",
    };
    onEdit(updated);
    setSaved(true);
    setTimeout(() => { setSaved(false); setShowEdit(false); }, 1200);
  };

  return (
    <>
      <div
        onClick={onOpenDetail || undefined}
        style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"12px", padding:"1rem 1.25rem", display:"flex", alignItems:"center", gap:"1rem", cursor: onOpenDetail ? "pointer" : "default", transition:"background 0.12s"}}
        onMouseEnter={e=>{ if(onOpenDetail) e.currentTarget.style.background="#f8faf8"; }}
        onMouseLeave={e=>{ if(onOpenDetail) e.currentTarget.style.background=T.white; }}
      >
        <div style={{width:"44px", height:"44px", borderRadius:"50%", background:`${T.gold}22`, border:`1px solid ${T.gold}44`, display:"flex", alignItems:"center", justifyContent:"center", color:T.gold, fontWeight:"700", flexShrink:0, fontSize:"1.1rem"}}>
          {c.name?.[0]?.toUpperCase()}
        </div>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontWeight:"500", color:T.charcoal, fontSize:"0.9rem", marginBottom:"0.125rem"}}>{c.name}</div>
          <div style={{fontSize:"0.75rem", color:T.muted, display:"flex", gap:"0.75rem", flexWrap:"wrap"}}>
            {c.role && <span>{c.role}</span>}
            {c.email
              ? <span style={{fontFamily:"'DM Mono', monospace", fontSize:"0.7rem"}}>{c.email}</span>
              : <span style={{color:T.danger, fontSize:"0.7rem"}}>No email — <button onClick={e=>{e.stopPropagation();setShowEdit(true);}} style={{...btnReset, color:T.gold, fontSize:"0.7rem", fontWeight:"500", textDecoration:"underline"}}>add email to invite</button></span>
            }
            <span>Added {c.joined}</span>
          </div>
        </div>
        <div style={{display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"0.375rem", flexShrink:0}} onClick={e => e.stopPropagation()}>
          {/* Status badge */}
          <span style={{fontSize:"0.65rem", fontFamily:"'DM Mono', monospace",
            color: invitation?.status==="accepted" ? T.success : invitation?.status==="expired" ? T.danger : c.email ? T.gold : T.muted,
            background: invitation?.status==="accepted" ? "#f0faf5" : invitation?.status==="expired" ? "#fff5f5" : c.email ? "#fef9f0" : T.parchment,
            padding:"0.2rem 0.625rem", borderRadius:"6px"}}>
            {invitation?.status==="accepted" ? "● Accepted" : invitation?.status==="expired" ? "⚠ Expired" : invitation ? "✉ Invite sent" : c.inviteStatus==="accepted" ? "● Joined" : c.email ? "✉ Invited" : "○ No invite"}
          </span>
          {c.canCollaborate && (
            <span style={{fontSize:"0.62rem", color:T.forest, background:`${T.forest}12`, padding:"0.15rem 0.5rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace"}}>{c.canCollaborate}</span>
          )}
          {/* Edit contact info — always available */}
          {!isInvitedUser && <button onClick={e=>{e.stopPropagation();setShowEdit(true);}} style={{...btnReset, fontSize:"0.68rem", color:T.muted, textDecoration:"underline", marginTop:"0.125rem"}}>Edit</button>}
          {/* Resend invite — for pending invitations */}
          {isOwner && !isInvitedUser && invitation?.status === 'pending' && (
            resent
              ? <span style={{fontSize:"0.68rem", color:T.success, marginTop:"0.125rem"}}>✓ Invite resent!</span>
              : <button onClick={e=>{e.stopPropagation();handleResendInvite();}} disabled={resending} style={{...btnReset, fontSize:"0.68rem", color: resending ? T.muted : T.forest, textDecoration:"underline", marginTop:"0.125rem", opacity: resending ? 0.6 : 1}}>
                  {resending ? "Sending…" : "Resend Invite"}
                </button>
          )}
          {/* Manage → opens detail modal (invitation-based people) */}
          {onOpenDetail && isOwner && (
            <button onClick={e=>{e.stopPropagation();onOpenDetail();}} style={{...btnReset, fontSize:"0.68rem", color:T.forest, fontWeight:"500", border:`1px solid ${T.forest}44`, borderRadius:"5px", padding:"0.15rem 0.5rem", marginTop:"0.125rem"}}>Manage →</button>
          )}
          {/* Inline remove — only for localStorage-only people (no invitation) */}
          {isOwner && !isInvitedUser && onRemove && !onOpenDetail && (
            showRemoveConfirm ? (
              <div style={{display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"0.25rem", marginTop:"0.125rem"}}>
                <span style={{fontSize:"0.7rem", color:T.danger, fontWeight:"500"}}>Remove {c.name}?</span>
                <div style={{display:"flex", gap:"0.375rem"}}>
                  <button onClick={async()=>{setRemoving(true);if(onRemove)await onRemove();setRemoving(false);setShowRemoveConfirm(false);}} disabled={removing}
                    style={{...btnReset, fontSize:"0.65rem", color:T.danger, border:`1px solid ${T.danger}`, borderRadius:"4px", padding:"0.15rem 0.4rem", fontWeight:"500", opacity:removing?0.6:1}}>
                    {removing?"Removing…":"Yes, Remove"}
                  </button>
                  <button onClick={()=>setShowRemoveConfirm(false)} style={{...btnReset, fontSize:"0.65rem", color:T.muted}}>Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={()=>setShowRemoveConfirm(true)} style={{...btnReset, fontSize:"0.68rem", color:T.danger, textDecoration:"underline", marginTop:"0.125rem"}}>Remove</button>
            )
          )}
        </div>
      </div>

      {/* Invite code section — shown for owner only when a Supabase invitation exists and is pending */}
      {!isInvitedUser && invitation?.invite_code && invitation.status !== "accepted" && (
        <div style={{margin:"0.5rem 0 0", padding:"0.75rem 1rem", background:"#f8fbf9", borderRadius:"0 0 12px 12px", borderTop:`1px dashed ${T.borderLight}`, display:"flex", alignItems:"center", gap:"0.625rem", flexWrap:"wrap"}}>
          <span style={{fontSize:"0.7rem", color:T.muted}}>Share code:</span>
          <code style={{fontFamily:"'DM Mono', monospace", fontSize:"0.875rem", color:T.forest, fontWeight:"700", letterSpacing:"0.05em"}}>{invitation.invite_code}</code>
          <InlineCopyButton text={invitation.invite_code} />
        </div>
      )}


      {showEdit && (
        <div style={modalOverlay} onClick={()=>setShowEdit(false)}>
          <div style={{...modalBox, maxWidth:"480px"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem"}}>
              <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.25rem", fontWeight:"700", color:T.forest}}>Edit — {c.name}</h2>
              <button onClick={()=>setShowEdit(false)} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:"1rem", marginBottom:"1.5rem"}}>
              <div>
                <label style={labelStyle}>Full Name</label>
                <input value={editForm.name} onChange={e=>setEditForm({...editForm,name:e.target.value})} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Email Address</label>
                <input type="email" value={editForm.email} onChange={e=>setEditForm({...editForm,email:e.target.value})} placeholder="their@email.com" style={inputStyle} />
                {!c.email && editForm.email && <p style={{fontSize:"0.72rem", color:T.success, marginTop:"0.375rem"}}>✓ Adding email will let you send them an invite.</p>}
              </div>
              <div>
                <label style={labelStyle}>Relationship</label>
                <select value={editForm.relationship} onChange={e=>setEditForm({...editForm,relationship:e.target.value})} style={inputStyle}>
                  <option value="">Select...</option>
                  {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Role in Estate</label>
                <input value={editForm.role} onChange={e=>setEditForm({...editForm,role:e.target.value})} placeholder="e.g. Beneficiary, Sibling" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Access Level</label>
                <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
                  {ACCESS_OPTIONS.map(opt=>(
                    <label key={opt.id} style={{display:"flex", gap:"0.75rem", alignItems:"center", cursor:"pointer", padding:"0.625rem 0.875rem", borderRadius:"8px", border:`1px solid ${editForm.canCollaborate===opt.id?T.forest:T.border}`, background:editForm.canCollaborate===opt.id?`${T.forest}06`:T.white}}>
                      <input type="radio" name="edit_access" value={opt.id} checked={editForm.canCollaborate===opt.id} onChange={()=>setEditForm({...editForm,canCollaborate:opt.id})} />
                      <span style={{fontSize:"1rem"}}>{opt.icon}</span>
                      <span style={{fontSize:"0.85rem", fontWeight:editForm.canCollaborate===opt.id?"600":"400", color:editForm.canCollaborate===opt.id?T.forest:T.charcoal}}>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              {editForm.email && (
                <label style={{display:"flex", gap:"0.75rem", alignItems:"flex-start", cursor:"pointer", padding:"0.75rem", borderRadius:"8px", border:`1px solid ${editForm.sendInvite?T.forest:T.border}`, background:editForm.sendInvite?`${T.forest}06`:T.white}}>
                  <input type="checkbox" checked={editForm.sendInvite} onChange={e=>setEditForm({...editForm,sendInvite:e.target.checked})} style={{marginTop:"2px"}} />
                  <div>
                    <div style={{fontSize:"0.85rem", fontWeight:"500", color:editForm.sendInvite?T.forest:T.charcoal}}>✉️ Send invite email to {editForm.email}</div>
                    <div style={{fontSize:"0.75rem", color:T.muted}}>They'll get a secure link to join HeirOS as a collaborator.</div>
                  </div>
                </label>
              )}
            </div>
            <div style={{display:"flex", gap:"0.75rem"}}>
              <button onClick={()=>setShowEdit(false)} style={{...outlineBtn, flex:"0 0 auto"}}>Cancel</button>
              <button onClick={handleSave} style={{...filledBtn, flex:1, padding:"0.75rem"}}>
                {saved ? "✓ Saved!" : "Save Changes →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AddFamilyMemberModal({ estate, isOwner, onInvite, onClose, user, estateSupabaseId, setInvitations, onInviteCreated }) {
  const [form, setForm] = useState({ name:"", email:"", role:"", relationship:"", canCollaborate:"", sendInvite:true, executor_order:1, medical_poa_order:1, poa_order:1 });
  const [step, setStep] = useState(0);
  const [sent, setSent] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  const deceasedName = estate?.deceasedName || estate?.ownerName || "the estate";
  const ownerName = estate?.ownerName || estate?.executorName || user?.user_metadata?.full_name || user?.email || "your family member";

  // Canonical snake_case permission keys ONLY — these must match
  // INVITATION_PERM_KEYS (line ~467) exactly, or mergeInvitationRows() reads
  // undefined for every key and the collaborator silently gets no permissions.
  // This function previously emitted camelCase for most presets, which is why
  // invited executors saw no sidebar links and no vault access.
  const buildPermissions = (accessId) => {
    const base = {
      view_checklist: false, edit_checklist: false,
      view_documents: false, download_documents: false,
      view_vault: false, view_family: false, full_access: false,
      view_medical_documents: false, download_medical_documents: false,
    };
    if (accessId === "executor" || accessId === "attorney") return { ...base, view_checklist:true, edit_checklist:true, view_documents:true, download_documents:true, view_vault:true, view_family:true };
    if (accessId === "collaborate")   return { ...base, view_checklist:true, edit_checklist:true, view_documents:true, view_family:true };
    if (accessId === "view-progress") return { ...base, view_checklist:true };
    if (accessId === "trusted-vault") return { ...base, view_vault:true };
    if (accessId === "beneficiary")   return { ...base, view_checklist:true };
    if (accessId === "medical_poa")   return { ...base, view_medical_documents:true, download_medical_documents:true };
    if (accessId === "poa")           return { ...base, view_documents:true, download_documents:true, view_checklist:true, view_vault:true, view_family:true };
    return base;
  };

  const handleAddMember = async () => {
    setSaving(true);
    console.log('1. [HeirOS] handleAddMember — form submitted:', { form, userId: user?.id, estateSupabaseId });

    let token = null;
    if (user && estateSupabaseId && form.email && form.sendInvite) {
      const invitePayload = {
        email: form.email,
        name: form.name,
        role: ACCESS_OPTIONS.find(a=>a.id===form.canCollaborate)?.label || form.role || "collaborator",
        permissions: buildPermissions(form.canCollaborate),
        executor_order: form.canCollaborate === 'executor' ? form.executor_order : null,
        medical_poa_order: form.canCollaborate === 'medical_poa' ? form.medical_poa_order : null,
        poa_order: form.canCollaborate === 'poa' ? form.poa_order : null,
      };
      const inviterName = estate?.ownerName || estate?.executorName || user?.user_metadata?.full_name || user?.email || '';
      console.log('2. [HeirOS] handleAddMember — calling createInvitation:', { userId: user.id, estateSupabaseId, invitePayload, inviterName });

      const { data: inv, error: invErr, invite_code } = await createInvitation(user.id, estateSupabaseId, invitePayload, { inviterName });
      console.log('4. [HeirOS] handleAddMember — createInvitation returned:', { data: inv, error: invErr, invite_code });

      if (invErr && !inv) {
        console.error('[HeirOS] createInvitation DB error:', invErr);
        setInviteError(invErr?.message || 'Failed to create invitation. Please try again.');
        setSaving(false);
        return;
      }

      if (inv) {
        token = invite_code || null;
        if (setInvitations) setInvitations(prev => [{ ...inv, invite_code }, ...prev]);
        await logNotification(user.id, "invitation_sent", `Invitation sent to ${form.name}`,
          `You invited ${form.name} (${form.email}) to collaborate on your estate.`, form.email);
        if (onInviteCreated && invitePayload.role !== 'Named as executor') {
          onInviteCreated({ ...inv, invite_code });
        }
      }
    } else {
      console.warn('[HeirOS] handleAddMember — skipping Supabase invite:', {
        hasUser: !!user, userId: user?.id,
        estateSupabaseId,
        email: form.email,
        sendInvite: form.sendInvite,
        reason: !user ? 'no user' : !estateSupabaseId ? 'no estateSupabaseId' : !form.email ? 'no email' : 'sendInvite=false',
      });
    }
    setInviteCode(token);
    setSent(true);
    setSaving(false);
  };

  const ROLES = ["Spouse / Partner","Child","Sibling","Parent","Grandparent","Grandchild","Aunt / Uncle","Niece / Nephew","Friend","Attorney","Accountant","Other"];

  const ACCESS_OPTIONS = isOwner ? [
    { id:"view-progress",  label:"View planning progress",  desc:`Can see your estate checklist and vault progress — but not vault contents.`,           icon:"👁"  },
    { id:"collaborate",    label:"Help with planning",      desc:"Can view and comment on your checklist, suggest tasks, and track progress with you.", icon:"🤝" },
    { id:"trusted-vault",  label:"Trusted vault access",    desc:"Will be able to view designated vault items after your death.",                        icon:"🔒" },
    { id:"executor",       label:"Named as executor",       desc:"This person is or will be named as an executor of your estate.",                      icon:"⚖️" },
    { id:"medical_poa",    label:"Medical POA",             desc:"Can view and download your medical documents, advance directive, and POA paperwork.", icon:"🏥" },
    { id:"poa",            label:"Power of Attorney",       desc:"Named as financial/durable power of attorney for this estate.",                       icon:"📝" },
  ] : [
    { id:"view-progress",  label:"View settlement progress", desc:`Can see what tasks are complete for ${deceasedName}'s estate.`,                       icon:"👁"  },
    { id:"collaborate",    label:"Help settle the estate",   desc:"Can claim tasks, upload documents, and actively help with the settlement process.",    icon:"🤝" },
    { id:"beneficiary",    label:"Beneficiary — view only",  desc:"Can view their status as a beneficiary. Cannot edit or access documents.",            icon:"📋" },
    { id:"attorney",       label:"Attorney / Professional",  desc:"Full document and checklist access for professional advisors.",                        icon:"⚖️" },
  ];

  const canContinue = form.name.trim().length > 0;

  const handleConfirm = () => {
    onInvite({
      name: form.name, email: form.email,
      role: form.role || form.relationship || "Family Member",
      relationship: form.relationship,
      canCollaborate: ACCESS_OPTIONS.find(a=>a.id===form.canCollaborate)?.label || "",
      sendInvite: form.sendInvite && !!form.email,
      inviteStatus: form.sendInvite && form.email ? "invited" : "added",
      joined: new Date().toLocaleDateString(),
    });
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{...modalBox, maxWidth:"520px"}} onClick={e=>e.stopPropagation()}>

        {sent ? (
          <div style={{padding:"0.5rem 0"}}>
            <div style={{textAlign:"center", marginBottom:"1.5rem"}}>
              <div style={{fontSize:"3rem", marginBottom:"0.75rem"}}>✉️</div>
              <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, marginBottom:"0.5rem"}}>Invitation created!</h3>
              <p style={{color:T.muted, fontSize:"0.875rem", lineHeight:"1.65"}}><strong>{form.name}</strong> has been added as a collaborator.</p>
            </div>

            {/* Invite code — shown when Supabase invitation was created */}
            {inviteCode && (
              <div style={{background:"#f0faf5", border:`1px solid #c3e6cb`, borderRadius:"12px", padding:"1.25rem", marginBottom:"1rem"}}>
                <p style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.success, letterSpacing:"0.08em", marginBottom:"0.75rem"}}>INVITE CODE — share with {form.name}</p>
                <div style={{display:"flex", gap:"0.5rem", alignItems:"center"}}>
                  <code style={{flex:1, background:"white", border:`1px solid ${T.border}`, borderRadius:"6px", padding:"0.75rem 1rem", fontSize:"1.35rem", color:T.forest, fontFamily:"'DM Mono', monospace", fontWeight:"700", letterSpacing:"0.08em", textAlign:"center"}}>
                    {inviteCode}
                  </code>
                  <button onClick={() => {
                    navigator.clipboard.writeText(inviteCode);
                    setCopied(true); setTimeout(()=>setCopied(false), 2000);
                  }} style={{...btnReset, background:copied?T.forest:T.parchment, color:copied?"white":T.charcoal, border:`1px solid ${T.border}`, borderRadius:"6px", padding:"0.5rem 0.875rem", fontSize:"0.75rem", fontWeight:"500", flexShrink:0, transition:"all 0.2s"}}>
                    {copied ? "✓ Copied!" : "Copy"}
                  </button>
                </div>
                <p style={{fontSize:"0.72rem", color:T.muted, marginTop:"0.75rem", lineHeight:"1.5"}}>
                  {form.email ? `An email with this code has been sent to ${form.email}. ` : ""}
                  Send them the invitation email link to accept -- it contains their code. If they cannot find it, use Resend Invitation below.
                </p>
              </div>
            )}

            {!inviteCode && !form.email && (
              <div style={{background:"#fefbf0", border:`1px solid ${T.tan}`, borderRadius:"8px", padding:"0.75rem 1rem", fontSize:"0.78rem", color:T.goldDark, lineHeight:"1.55", marginBottom:"1rem"}}>
                ⚡ No email provided. Add their email so you can send them an invite code.
              </div>
            )}

            <div style={{display:"flex", gap:"0.75rem"}}>
              <button onClick={onClose} style={{...outlineBtn, flex:1}}>Close</button>
              <button onClick={handleConfirm} style={{...filledBtn, flex:1}}>Confirm & Save →</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.5rem"}}>
              <div>
                <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.35rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Invite a Collaborator</h2>
                <p style={{fontSize:"0.8rem", color:T.muted}}>{`Invite someone to collaborate on ${estate?.ownerName || 'this'} estate.`}</p>
              </div>
              <button onClick={onClose} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
            </div>

            <div style={{display:"flex", gap:"0.375rem", marginBottom:"1.75rem"}}>
              {["Details","Access","Invite"].map((s,i)=>(
                <div key={i} style={{display:"flex", alignItems:"center", gap:"0.375rem"}}>
                  <div style={{width:"22px", height:"22px", borderRadius:"50%", fontSize:"0.7rem", fontWeight:"700", display:"flex", alignItems:"center", justifyContent:"center", background:i<=step?T.forest:T.parchment, color:i<=step?"white":T.muted, border:`1px solid ${i<=step?T.forest:T.border}`}}>{i+1}</div>
                  <span style={{fontSize:"0.72rem", color:i<=step?T.forest:T.muted, fontWeight:i===step?"600":"400"}}>{s}</span>
                  {i<2 && <span style={{color:T.border}}>›</span>}
                </div>
              ))}
            </div>

            {step===0 && (
              <div style={{display:"flex", flexDirection:"column", gap:"1.125rem"}}>
                <div>
                  <label style={labelStyle}>Full Name <span style={{color:T.danger}}>*</span></label>
                  <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. Sarah Johnson" style={inputStyle} autoFocus />
                </div>
                <div>
                  <label style={labelStyle}>Email Address</label>
                  <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="sarah@email.com" style={inputStyle} />
                  <p style={{fontSize:"0.72rem", color:T.muted, marginTop:"0.375rem"}}>Used to send them an invite link to HeirOS. Optional but recommended.</p>
                </div>
                <div>
                  <label style={labelStyle}>Relationship</label>
                  <select value={form.relationship} onChange={e=>setForm({...form,relationship:e.target.value})} style={inputStyle}>
                    <option value="">Select relationship...</option>
                    {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Role in the Estate (optional)</label>
                  <input value={form.role} onChange={e=>setForm({...form,role:e.target.value})} placeholder="e.g. Beneficiary, Co-executor, Family advisor" style={inputStyle} />
                </div>
              </div>
            )}

            {step===1 && (
              <div style={{display:"flex", flexDirection:"column", gap:"0.75rem"}}>
                <p style={{fontSize:"0.85rem", color:T.charcoal, fontWeight:"500", marginBottom:"0.5rem"}}>What can {form.name||"this person"} do in HeirOS?</p>
                {ACCESS_OPTIONS.map(opt=>(
                  <label key={opt.id} style={{display:"flex", gap:"0.875rem", alignItems:"flex-start", cursor:"pointer", padding:"0.875rem 1rem", borderRadius:"10px", border:`2px solid ${form.canCollaborate===opt.id?T.forest:T.border}`, background:form.canCollaborate===opt.id?`${T.forest}06`:T.white, transition:"all 0.15s"}}>
                    <input type="radio" name="access" value={opt.id} checked={form.canCollaborate===opt.id} onChange={()=>setForm({...form,canCollaborate:opt.id})} style={{marginTop:"3px"}} />
                    <span style={{fontSize:"1.1rem", flexShrink:0}}>{opt.icon}</span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:"600", color:form.canCollaborate===opt.id?T.forest:T.charcoal, fontSize:"0.875rem", marginBottom:"0.25rem"}}>{opt.label}</div>
                      <div style={{fontSize:"0.78rem", color:T.muted, lineHeight:"1.5"}}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
                {form.canCollaborate === 'executor' && (
                  <div style={{background:`${T.forest}06`, border:`1px solid ${T.forest}33`, borderRadius:"10px", padding:"1rem", marginTop:"0.25rem"}}>
                    <p style={{fontSize:"0.8rem", fontWeight:"600", color:T.forest, marginBottom:"0.625rem"}}>⚖️ Which executor position?</p>
                    <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
                      {[
                        {val:1, label:"Primary Executor (1st)", desc:"Serves as the main executor of the estate"},
                        {val:2, label:"First Backup (2nd)",     desc:"Steps in if the primary executor is unable to serve"},
                        {val:3, label:"Second Backup (3rd)",    desc:"Last resort if both above can't serve"},
                      ].map(pos => (
                        <label key={pos.val} style={{display:"flex", gap:"0.75rem", alignItems:"flex-start", cursor:"pointer", padding:"0.625rem 0.875rem", borderRadius:"8px", border:`1px solid ${form.executor_order===pos.val?T.forest:T.border}`, background:form.executor_order===pos.val?`${T.forest}08`:T.white}}>
                          <input type="radio" name="executor_order" value={pos.val} checked={form.executor_order===pos.val} onChange={()=>setForm({...form,executor_order:pos.val})} style={{marginTop:"2px"}} />
                          <div>
                            <div style={{fontSize:"0.82rem", fontWeight:"600", color:form.executor_order===pos.val?T.forest:T.charcoal}}>{pos.label}</div>
                            <div style={{fontSize:"0.74rem", color:T.muted}}>{pos.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {form.canCollaborate === 'medical_poa' && (
                  <div style={{background:`${T.forest}06`, border:`1px solid ${T.forest}33`, borderRadius:"10px", padding:"1rem", marginTop:"0.25rem"}}>
                    <p style={{fontSize:"0.8rem", fontWeight:"600", color:T.forest, marginBottom:"0.625rem"}}>🏥 Which Medical POA position?</p>
                    <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
                      {[
                        {val:1, label:"Primary Medical POA (1st)", desc:"Serves as the main medical power of attorney"},
                        {val:2, label:"First Backup (2nd)",        desc:"Steps in if the primary cannot serve"},
                        {val:3, label:"Second Backup (3rd)",       desc:"Last resort if both above can't serve"},
                      ].map(pos => (
                        <label key={pos.val} style={{display:"flex", gap:"0.75rem", alignItems:"flex-start", cursor:"pointer", padding:"0.625rem 0.875rem", borderRadius:"8px", border:`1px solid ${form.medical_poa_order===pos.val?T.forest:T.border}`, background:form.medical_poa_order===pos.val?`${T.forest}08`:T.white}}>
                          <input type="radio" name="medical_poa_order" value={pos.val} checked={form.medical_poa_order===pos.val} onChange={()=>setForm({...form,medical_poa_order:pos.val})} style={{marginTop:"2px"}} />
                          <div>
                            <div style={{fontSize:"0.82rem", fontWeight:"600", color:form.medical_poa_order===pos.val?T.forest:T.charcoal}}>{pos.label}</div>
                            <div style={{fontSize:"0.74rem", color:T.muted}}>{pos.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {form.canCollaborate === 'poa' && (
                  <div style={{background:`${T.forest}06`, border:`1px solid ${T.forest}33`, borderRadius:"10px", padding:"1rem", marginTop:"0.25rem"}}>
                    <p style={{fontSize:"0.8rem", fontWeight:"600", color:T.forest, marginBottom:"0.625rem"}}>📝 Which Power of Attorney position?</p>
                    <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
                      {[
                        {val:1, label:"Primary POA (1st)",  desc:"Serves as the main financial/durable power of attorney"},
                        {val:2, label:"First Backup (2nd)", desc:"Steps in if the primary cannot serve"},
                        {val:3, label:"Second Backup (3rd)",desc:"Last resort if both above can't serve"},
                      ].map(pos => (
                        <label key={pos.val} style={{display:"flex", gap:"0.75rem", alignItems:"flex-start", cursor:"pointer", padding:"0.625rem 0.875rem", borderRadius:"8px", border:`1px solid ${form.poa_order===pos.val?T.forest:T.border}`, background:form.poa_order===pos.val?`${T.forest}08`:T.white}}>
                          <input type="radio" name="poa_order" value={pos.val} checked={form.poa_order===pos.val} onChange={()=>setForm({...form,poa_order:pos.val})} style={{marginTop:"2px"}} />
                          <div>
                            <div style={{fontSize:"0.82rem", fontWeight:"600", color:form.poa_order===pos.val?T.forest:T.charcoal}}>{pos.label}</div>
                            <div style={{fontSize:"0.74rem", color:T.muted}}>{pos.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step===2 && (
              <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
                <div style={{background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"12px", padding:"1.25rem"}}>
                  <p style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.gold, letterSpacing:"0.08em", marginBottom:"0.875rem"}}>SUMMARY</p>
                  {[["Name",form.name],["Email",form.email||"Not provided"],["Relationship",form.relationship||"Not specified"],["Role",form.role||"Family Member"],["Access",ACCESS_OPTIONS.find(a=>a.id===form.canCollaborate)?.label||"Not specified"],...(form.canCollaborate==='executor'?[["Executor Position",[{val:1,label:"Primary (1st)"},{val:2,label:"First Backup (2nd)"},{val:3,label:"Second Backup (3rd)"}].find(p=>p.val===form.executor_order)?.label||"Primary (1st)"]]:[] ),...(form.canCollaborate==='medical_poa'?[["Medical POA Position",[{val:1,label:"Primary (1st)"},{val:2,label:"First Backup (2nd)"},{val:3,label:"Second Backup (3rd)"}].find(p=>p.val===form.medical_poa_order)?.label||"Primary (1st)"]]:[] ),...(form.canCollaborate==='poa'?[["POA Position",[{val:1,label:"Primary (1st)"},{val:2,label:"First Backup (2nd)"},{val:3,label:"Second Backup (3rd)"}].find(p=>p.val===form.poa_order)?.label||"Primary (1st)"]]:[] )].map(([label,val])=>(
                    <div key={label} style={{display:"flex", justifyContent:"space-between", padding:"0.375rem 0", borderBottom:`1px solid ${T.borderLight}`, fontSize:"0.82rem"}}>
                      <span style={{color:T.muted}}>{label}</span>
                      <span style={{color:T.charcoal, fontWeight:"500", textAlign:"right", maxWidth:"60%"}}>{val}</span>
                    </div>
                  ))}
                </div>

                {form.email ? (
                  <label style={{display:"flex", gap:"0.875rem", alignItems:"flex-start", cursor:"pointer", padding:"0.875rem 1rem", borderRadius:"10px", border:`1px solid ${form.sendInvite?T.forest:T.border}`, background:form.sendInvite?`${T.forest}06`:T.white}}>
                    <input type="checkbox" checked={form.sendInvite} onChange={e=>setForm({...form,sendInvite:e.target.checked})} style={{marginTop:"3px"}} />
                    <div>
                      <div style={{fontWeight:"600", color:form.sendInvite?T.forest:T.charcoal, fontSize:"0.875rem", marginBottom:"0.25rem"}}>✉️ Send invite email to {form.email}</div>
                      <div style={{fontSize:"0.78rem", color:T.muted, lineHeight:"1.5"}}>They'll receive a secure link to create their HeirOS account and join as a collaborator.</div>
                    </div>
                  </label>
                ) : (
                  <div style={{background:"#fefbf0", border:`1px solid ${T.tan}`, borderRadius:"8px", padding:"0.75rem 1rem", fontSize:"0.78rem", color:T.goldDark, lineHeight:"1.55"}}>
                    ⚡ No email provided. They'll be added to your list but won't receive an automated invite. Go back to add their email.
                  </div>
                )}
              </div>
            )}

            {inviteError && (
              <div style={{marginTop:"1rem", padding:"0.625rem 0.875rem", background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:"0.5rem", color:"#b91c1c", fontSize:"0.82rem"}}>
                {inviteError}
              </div>
            )}
            <div style={{display:"flex", gap:"0.75rem", marginTop:"1.75rem", justifyContent:"space-between"}}>
              <div>{step>0 && <button onClick={()=>setStep(step-1)} style={{...outlineBtn, fontSize:"0.82rem", padding:"0.625rem 1rem"}}>← Back</button>}</div>
              <div>
                {step<2
                  ? <button onClick={()=>setStep(step+1)} disabled={!canContinue} style={{...filledBtn, fontSize:"0.875rem", padding:"0.625rem 1.5rem", opacity:!canContinue?0.5:1}}>Continue →</button>
                  : <button onClick={handleAddMember} disabled={saving} style={{...filledBtn, fontSize:"0.875rem", padding:"0.625rem 1.5rem", opacity:saving?0.6:1}}>{saving ? "Creating…" : form.sendInvite&&form.email ? "Add & Send Invite →" : "Add Member →"}</button>
                }
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AppAI({ estate, navTo, invitedPermissions, invitedRole }) {
  const [q, setQ] = useState(""); const [r, setR] = useState(""); const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const isInvitedUser = !!invitedPermissions || !!invitedRole;
  const isSettlementContext =
    estate?.signupMode === 'executor' ||
    (isInvitedUser && estate?.status === 'active_settlement');

  const ask = async () => {
    if (!q.trim() || loading) return;
    const question = q; setQ(""); setLoading(true);
    setHistory(h=>[...h, {role:"user", content:question}]);
    try {
      let ctx = "";
      if (estate) {
        if (isSettlementContext) {
          const subjectName = estate.signupMode === 'owner'
            ? (estate.ownerName || estate.owner_name || null)
            : (estate.deceasedName || estate.deceased_name || estate.ownerName || estate.owner_name || null);
          const residenceState = estate.deceasedState || estate.ownerState;
          const parts = [
            subjectName   ? `Settling estate of ${subjectName}` : null,
            estate.dateOfDeath  ? `died ${estate.dateOfDeath}`  : null,
            residenceState      ? residenceState                : null,
            estate.executorName ? `Executor: ${estate.executorName}` : null,
            estate.estateValue  ? `Value: ${estate.estateValue}` : null,
            estate.hasWill      ? `Has will: ${estate.hasWill}` : null,
            estate.hasProperty  ? `Real property: ${estate.hasProperty}` : null,
          ].filter(Boolean);
          ctx = parts.length ? `Estate context: ${parts.join('. ')}.` : "";
        } else {
          const parts = [
            (estate.ownerName || estate.owner_name) ? `Owner: ${estate.ownerName || estate.owner_name}` : null,
            estate.ownerState  ? `State: ${estate.ownerState}`        : null,
            estate.hasWill     ? `Has will: ${estate.hasWill}`        : null,
            estate.hasProperty ? `Has real property: ${estate.hasProperty}` : null,
            estate.estateValue ? `Estimated value: ${estate.estateValue}` : null,
            "This estate belongs to a living person who is planning ahead — do not refer to the estate owner as deceased.",
          ].filter(Boolean);
          ctx = parts.length ? `Estate context: ${parts.join('. ')}` : "";
        }
      }
      const msgs = [...history, {role:"user", content:question}];
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        body: {
          messages: msgs,
          system: isSettlementContext
            ? `You are HeirOS, a compassionate and knowledgeable estate settlement guide. Provide clear, practical guidance in plain English. Always remind users to consult a licensed attorney for legal advice specific to their situation. Be warm but concise. ${ctx}`
            : `You are HeirOS, a knowledgeable estate planning guide helping a living person organize their affairs. The user is planning ahead — do not refer to the estate owner as deceased. Provide clear, practical guidance in plain English. Always remind users to consult a licensed attorney for legal advice specific to their situation. Be warm but concise. ${ctx}`,
          max_tokens: 1000
        }
      });
      if (error) throw error;
      const rawText = data?.content?.[0]?.text;
      if (!rawText) console.error('[HeirOS] AI Guide returned no usable content', data);
      const text = rawText || "I couldn't process that. Please try again.";
      setHistory(h=>[...h, {role:"assistant", content:text}]);
    } catch(e) {
      console.error('[HeirOS] AI Guide request failed', e);
      const isResponse = typeof e?.context?.json === 'function';
      let body = null;
      try { if (isResponse) body = await e.context.json(); } catch (_) {}
      if (body) console.error('[HeirOS] AI Guide error body', body);
      let errMsg;
      if (!isResponse) {
        errMsg = "Couldn't reach the server. Please check your connection and try again.";
      } else if (typeof body?.error === 'string' && body.error.includes('not configured')) {
        errMsg = "The AI assistant isn't available right now. This is on our end, not yours. Please contact support@heiros.estate.";
      } else if (body?.error?.type?.includes?.('rate_limit')) {
        errMsg = "The AI assistant is busy right now. Please wait a moment and try again.";
      } else {
        errMsg = "Something went wrong on our end. Please try again, or contact support@heiros.estate if this keeps happening.";
      }
      setHistory(h=>[...h, {role:"assistant", content:errMsg}]);
    }
    setLoading(false);
  };

  const suggestions = isSettlementContext
    ? ["Do I need to go through probate?","What happens if there's no will?","How do I handle the deceased's debts?","When do I need a lawyer?","How do I transfer a house title after death?"]
    : ["What documents should I gather for my estate plan?","Who should I name as my executor?","What does a Power of Attorney cover?","When should I update my will?","What should I store in my estate vault?"];

  return (
    <AppShell estate={estate} tasks={[]} page="app-ai" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
      <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Ask HeirOS AI</h1>
      <p style={{color:T.muted, fontSize:"0.875rem", marginBottom:"1.5rem"}}>Estate guidance, plain English · Powered by Claude · Always consult an attorney for legal advice</p>

      <div style={{display:"flex", flexDirection:"column", height:"calc(100vh - 280px)", background:T.white, border:`1px solid ${T.border}`, borderRadius:"14px", overflow:"hidden"}}>
        <div style={{flex:1, overflowY:"auto", padding:"1.5rem", display:"flex", flexDirection:"column", gap:"1rem"}}>
          {history.length===0 && (
            <div>
              <p style={{fontSize:"0.82rem", color:T.muted, marginBottom:"1rem", fontFamily:"'DM Mono', monospace"}}>Common questions — click to ask:</p>
              <div style={{display:"flex", flexWrap:"wrap", gap:"0.5rem"}}>
                {suggestions.map((s,i)=><button key={i} onClick={()=>setQ(s)} style={{...btnReset, padding:"0.5rem 1rem", borderRadius:"20px", border:`1px solid ${T.border}`, fontSize:"0.8rem", color:T.slate, background:T.parchment}}>{s}</button>)}
              </div>
            </div>
          )}
          {history.map((msg,i)=>(
            <div key={i} style={{display:"flex", gap:"0.75rem", justifyContent:msg.role==="user"?"flex-end":"flex-start"}}>
              {msg.role==="assistant" && <div style={{width:"30px", height:"30px", borderRadius:"8px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:"0.8rem", fontWeight:"700", flexShrink:0, alignSelf:"flex-start"}}>H</div>}
              <div style={{maxWidth:"80%", padding:"0.875rem 1.125rem", borderRadius:msg.role==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px", background:msg.role==="user"?T.forest:T.parchment, color:msg.role==="user"?"white":T.charcoal, fontSize:"0.875rem", lineHeight:"1.65"}}>
                {msg.content.split('\n').map((line,j)=><p key={j} style={{margin:line?"0 0 0.5rem":"0"}}>{line}</p>)}
              </div>
            </div>
          ))}
          {loading && <div style={{display:"flex", gap:"0.5rem", alignItems:"center", color:T.muted, fontSize:"0.875rem"}}>
            <div style={{width:"30px", height:"30px", borderRadius:"8px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:"0.8rem", fontWeight:"700"}}>H</div>
            <div style={{display:"flex", gap:"4px"}}>{[0,1,2].map(i=><span key={i} style={{width:"6px", height:"6px", borderRadius:"50%", background:T.muted, animation:"pulse 1s infinite", animationDelay:`${i*0.2}s`}} />)}</div>
          </div>}
        </div>
        <div style={{padding:"1rem 1.25rem", borderTop:`1px solid ${T.borderLight}`, display:"flex", gap:"0.75rem"}}>
          <textarea value={q} onChange={e=>setQ(e.target.value)} placeholder={isSettlementContext ? "Ask anything about settling this estate..." : "Ask anything about planning your estate..."} rows={2}
            style={{...inputStyle, flex:1, resize:"none", lineHeight:"1.5"}}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();ask();}}} />
          <button onClick={ask} disabled={loading||!q.trim()} style={{...filledBtn, alignSelf:"flex-end", padding:"0.75rem 1.25rem", opacity:(loading||!q.trim())?0.5:1}}>{loading?"...":"Send"}</button>
        </div>
      </div>
    </AppShell>
  );
}

// ─── ATTORNEYS PAGE ───────────────────────────────────────────────────────────
const ATTORNEYS = [];

const STATES_LIST = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"];

function AttorneysPage({ navTo }) {
  const [filterState, setFilterState] = useState("All States");
  const [filterSpec, setFilterSpec] = useState("All");
  const [showApply, setShowApply] = useState(false);
  const [applyForm, setApplyForm] = useState({ name:"", firm:"", email:"", phone:"", states:"", specialties:"", bio:"", fee:"" });
  const [submitted, setSubmitted] = useState(false);

  const allSpecs = ["All", ...new Set(ATTORNEYS.flatMap(a => a.specialties))];

  const filtered = ATTORNEYS.filter(a => {
    const stateMatch = filterState === "All States" || a.states.includes(filterState);
    const specMatch = filterSpec === "All" || a.specialties.includes(filterSpec);
    return stateMatch && specMatch;
  });

  const handleApplySubmit = () => {
    if (!applyForm.name || !applyForm.email) return;
    setSubmitted(true);
  };

  return (
    <div style={{background:T.cream, minHeight:"100vh"}}>

      {/* Hero */}
      <div style={{background:`linear-gradient(160deg, ${T.forestDark}, ${T.forest})`, color:"white", padding:"4rem 2rem 3.5rem", textAlign:"center"}}>
        <div style={{maxWidth:"640px", margin:"0 auto"}}>
          <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.72rem", letterSpacing:"0.12em", opacity:0.6, marginBottom:"1rem"}}>VETTED ATTORNEY NETWORK</p>
          <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"clamp(2rem, 5vw, 3rem)", fontWeight:"700", letterSpacing:"-0.02em", marginBottom:"1rem", lineHeight:1.15}}>
            When you need a lawyer,<br/><em style={{color:T.goldLight}}>we know the right one.</em>
          </h1>
          <p style={{opacity:0.8, fontSize:"1rem", lineHeight:"1.7", maxWidth:"500px", margin:"0 auto 2rem"}}>
            Every attorney in the HeirOS network is personally vetted, offers transparent pricing, and has agreed to treat HeirOS families with the care they deserve.
          </p>
          <div style={{display:"flex", gap:"2rem", justifyContent:"center", flexWrap:"wrap"}}>
            {[
              { n: "Flat-fee", l: "transparent pricing" },
              { n: "Vetted", l: "by the HeirOS team" },
              { n: "Free consult", l: "for HeirOS users" },
            ].map((s,i) => (
              <div key={i} style={{textAlign:"center"}}>
                <div style={{fontFamily:"'Playfair Display', serif", fontSize:"1.25rem", fontWeight:"700", color:T.goldLight}}>{s.n}</div>
                <div style={{fontSize:"0.75rem", opacity:0.6, marginTop:"0.125rem"}}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{maxWidth:"1100px", margin:"0 auto", padding:"3rem 2rem"}}>

        {/* Filters */}
        <div style={{display:"flex", gap:"0.75rem", marginBottom:"2rem", flexWrap:"wrap", alignItems:"center"}}>
          <select
            value={filterState}
            onChange={e => setFilterState(e.target.value)}
            style={{...inputStyle, width:"auto", padding:"0.5rem 2rem 0.5rem 0.875rem", fontSize:"0.85rem", flex:"0 0 auto"}}
          >
            <option value="All States">All States</option>
            {STATES_LIST.map(s => <option key={s}>{s}</option>)}
          </select>
          <div style={{display:"flex", gap:"0.375rem", flexWrap:"wrap"}}>
            {allSpecs.map(s => (
              <button key={s} onClick={() => setFilterSpec(s)} style={{
                ...btnReset, padding:"0.375rem 0.875rem", borderRadius:"20px", fontSize:"0.78rem",
                border:`1px solid ${filterSpec===s ? T.forest : T.border}`,
                background: filterSpec===s ? T.forest : "transparent",
                color: filterSpec===s ? "white" : T.muted,
              }}>{s}</button>
            ))}
          </div>
        </div>

        {/* Attorney cards */}
        {filtered.length === 0 ? (
          <div style={{textAlign:"center", padding:"4rem 2rem", color:T.muted}}>
            <p style={{fontSize:"1.1rem", marginBottom:"0.5rem"}}>No attorneys found for that filter.</p>
            <p style={{fontSize:"0.875rem"}}>Try a different state or specialty, or <button onClick={()=>setFilterState("All States")} style={{...btnReset, color:T.gold, fontWeight:"500"}}>view all</button>.</p>
          </div>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:"1.25rem", marginBottom:"3rem"}}>
            {filtered.map((a, i) => (
              <AttorneyCard key={i} attorney={a} />
            ))}
          </div>
        )}

        {/* Coming soon callout */}
        <div style={{background:T.parchment, border:`2px dashed ${T.border}`, borderRadius:"16px", padding:"2rem", textAlign:"center", marginBottom:"3rem"}}>
          <p style={{fontFamily:"'Playfair Display', serif", fontSize:"1.2rem", fontWeight:"600", color:T.forest, marginBottom:"0.5rem"}}>More attorneys coming soon</p>
          <p style={{fontSize:"0.875rem", color:T.muted, maxWidth:"480px", margin:"0 auto 1.25rem", lineHeight:"1.65"}}>
            We're actively vetting estate attorneys across all 50 states. Each one is reviewed for responsiveness, transparent pricing, and genuine care for families in difficult situations.
          </p>
          <p style={{fontSize:"0.8rem", color:T.muted}}>
            Need an attorney in a state not listed? <button onClick={()=>setShowApply(true)} style={{...btnReset, color:T.gold, fontWeight:"500"}}>Let us know →</button>
          </p>
        </div>

        {/* Are you an attorney CTA */}
        <div style={{background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"20px", padding:"2.5rem", display:"flex", flexWrap:"wrap", gap:"2rem", alignItems:"center", justifyContent:"space-between"}}>
          <div style={{color:"white", maxWidth:"520px"}}>
            <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.7rem", letterSpacing:"0.1em", opacity:0.6, marginBottom:"0.625rem"}}>FOR ATTORNEYS</p>
            <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.6rem", fontWeight:"700", marginBottom:"0.75rem", letterSpacing:"-0.02em"}}>Join the HeirOS Attorney Network</h3>
            <p style={{opacity:0.85, fontSize:"0.9rem", lineHeight:"1.7", marginBottom:"0.875rem"}}>
              Clients who use HeirOS arrive at your office with documents organized, timelines clear, and basic administrative questions already answered. That means your time is spent on legal strategy — not explaining what a death certificate is or tracking down account statements.
            </p>
            <p style={{opacity:0.75, fontSize:"0.875rem", lineHeight:"1.65", marginBottom:"1.25rem"}}>
              A HeirOS-prepared client typically brings to a first meeting:
            </p>
            <div style={{display:"flex", flexDirection:"column", gap:"0.625rem", marginBottom:"1.5rem"}}>
              {[
                "A complete asset and liability inventory",
                "Copies of the will, death certificate, and key account documents",
                "A prioritized task checklist with deadlines already mapped",
                "Notification letters to agencies and institutions already drafted",
                "A clear list of open questions that genuinely need legal counsel",
              ].map((f,i) => (
                <div key={i} style={{display:"flex", gap:"0.625rem", alignItems:"flex-start", fontSize:"0.84rem", opacity:0.88}}>
                  <span style={{color:T.goldLight, flexShrink:0}}>✓</span>{f}
                </div>
              ))}
            </div>
            <div style={{display:"flex", flexWrap:"wrap", gap:"1.5rem"}}>
              {["Warm, pre-qualified referrals", "No referral fees", "Flat-fee preferred but not required", "Approved attorneys listed within 5 days"].map((f,i) => (
                <div key={i} style={{display:"flex", gap:"0.5rem", alignItems:"flex-start", fontSize:"0.82rem", opacity:0.85}}>
                  <span style={{color:T.goldLight, flexShrink:0}}>✓</span>{f}
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => setShowApply(true)} style={{
            background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color:"white",
            border:"none", borderRadius:"12px", padding:"1rem 2rem",
            fontSize:"0.95rem", fontWeight:"600", cursor:"pointer", fontFamily:"'DM Sans', sans-serif",
            flexShrink:0, whiteSpace:"nowrap",
          }}>
            Apply to Join →
          </button>
        </div>
      </div>

      {/* Apply modal */}
      {showApply && (
        <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem"}} onClick={()=>setShowApply(false)}>
          <div style={{background:T.white, borderRadius:"20px", padding:"2.5rem", maxWidth:"540px", width:"100%", maxHeight:"90vh", overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            {submitted ? (
              <div style={{textAlign:"center", padding:"2rem 0"}}>
                <div style={{fontSize:"3rem", marginBottom:"1rem"}}>✓</div>
                <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:T.forest, marginBottom:"0.75rem"}}>Application received!</h3>
                <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.65", marginBottom:"1.5rem"}}>Thank you for your interest in the HeirOS Attorney Network. We review all applications personally and will be in touch within 5 business days.</p>
                <button onClick={()=>{setShowApply(false); setSubmitted(false);}} style={{...filledBtn}}>Close</button>
              </div>
            ) : (
              <>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.75rem"}}>
                  <div>
                    <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Attorney Network Application</h3>
                    <p style={{fontSize:"0.8rem", color:T.muted}}>We review every application personally. You'll hear from us within 5 days.</p>
                  </div>
                  <button onClick={()=>setShowApply(false)} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
                </div>
                <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
                  {[
                    { key:"name",        label:"Full Name",              placeholder:"Jane Smith, Esq.",              type:"text"  },
                    { key:"firm",        label:"Law Firm Name",          placeholder:"Smith Estate Law",              type:"text"  },
                    { key:"email",       label:"Email Address",          placeholder:"jane@smithestate.com",          type:"email" },
                    { key:"phone",       label:"Phone Number",           placeholder:"(555) 000-0000",                type:"tel"   },
                    { key:"states",      label:"States Licensed In",     placeholder:"e.g. Utah, Nevada, Idaho",      type:"text"  },
                    { key:"specialties", label:"Practice Areas",         placeholder:"e.g. Probate, Trust Admin, Elder Law", type:"text" },
                    { key:"fee",         label:"Fee Structure",          placeholder:"e.g. Flat fee from $1,200 or $275/hr", type:"text" },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={labelStyle}>{f.label}</label>
                      <input
                        type={f.type}
                        value={applyForm[f.key]}
                        placeholder={f.placeholder}
                        onChange={e => setApplyForm({...applyForm, [f.key]: f.type==="tel" ? formatPhone(e.target.value) : e.target.value})}
                        style={inputStyle}
                      />
                    </div>
                  ))}
                  <div>
                    <label style={labelStyle}>Brief Bio (shown to families)</label>
                    <textarea
                      value={applyForm.bio}
                      placeholder="Tell families about your experience and approach. 2–4 sentences works best."
                      onChange={e => setApplyForm({...applyForm, bio: e.target.value})}
                      rows={4}
                      style={{...inputStyle, resize:"vertical", lineHeight:"1.55"}}
                    />
                  </div>
                  <button
                    onClick={handleApplySubmit}
                    disabled={!applyForm.name || !applyForm.email}
                    style={{...filledBtn, padding:"0.875rem", fontSize:"0.95rem", opacity:(!applyForm.name||!applyForm.email)?0.5:1, marginTop:"0.5rem"}}
                  >
                    Submit Application →
                  </button>
                  <p style={{fontSize:"0.72rem", color:T.muted, textAlign:"center", lineHeight:"1.5"}}>
                    By applying you agree to HeirOS's attorney partner standards, including transparent flat-fee pricing and a complimentary 30-minute consultation for HeirOS users.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AttorneyCard({ attorney: a }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"16px", overflow:"hidden", transition:"box-shadow 0.2s"}}>
      <div style={{padding:"1.75rem", display:"flex", gap:"1.5rem", flexWrap:"wrap"}}>

        {/* Avatar */}
        <div style={{width:"64px", height:"64px", borderRadius:"16px", background:`linear-gradient(135deg, ${a.color}22, ${a.color}44)`, border:`2px solid ${a.color}33`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:a.color, flexShrink:0}}>
          {a.initials}
        </div>

        {/* Main info */}
        <div style={{flex:1, minWidth:"220px"}}>
          <div style={{display:"flex", flexWrap:"wrap", alignItems:"center", gap:"0.5rem", marginBottom:"0.25rem"}}>
            <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.1rem", fontWeight:"700", color:T.charcoal}}>{a.name}</h3>
            <span style={{fontSize:"0.65rem", background:`${T.forest}15`, color:T.forest, padding:"0.2rem 0.5rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace", letterSpacing:"0.05em"}}>{a.badge}</span>
          </div>
          <p style={{fontSize:"0.85rem", color:T.gold, fontWeight:"500", marginBottom:"0.375rem"}}>{a.firm}</p>
          <p style={{fontSize:"0.8rem", color:T.muted, marginBottom:"0.75rem"}}>📍 {a.location} · Licensed in: {a.states.join(", ")}</p>

          {/* Specialties */}
          <div style={{display:"flex", flexWrap:"wrap", gap:"0.375rem", marginBottom:"0.875rem"}}>
            {a.specialties.map((s,i) => (
              <span key={i} style={{fontSize:"0.7rem", padding:"0.2rem 0.625rem", borderRadius:"20px", background:T.parchment, border:`1px solid ${T.border}`, color:T.slate}}>{s}</span>
            ))}
          </div>

          {/* Stars */}
          <div style={{display:"flex", alignItems:"center", gap:"0.375rem"}}>
            <div style={{display:"flex", gap:"1px"}}>
              {Array(a.rating).fill(0).map((_,i) => <span key={i} style={{color:"#f59e0b", fontSize:"0.85rem"}}>★</span>)}
            </div>
            <span style={{fontSize:"0.75rem", color:T.muted}}>{a.rating}.0 · {a.reviews} HeirOS referrals</span>
          </div>
        </div>

        {/* Right column */}
        <div style={{display:"flex", flexDirection:"column", gap:"0.625rem", alignItems:"flex-end", justifyContent:"space-between", minWidth:"180px"}}>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:"0.7rem", color:T.muted, fontFamily:"'DM Mono', monospace", marginBottom:"0.125rem"}}>FEE STRUCTURE</div>
            <div style={{fontSize:"0.9rem", fontWeight:"600", color:T.forest}}>{a.fee}</div>
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"0.375rem", width:"100%"}}>
            <a href={`mailto:${a.email}`} style={{...filledBtn, textAlign:"center", textDecoration:"none", padding:"0.625rem 1rem", fontSize:"0.82rem", display:"block"}}>
              Request Free Consult →
            </a>
            <button onClick={() => setExpanded(!expanded)} style={{...outlineBtn, fontSize:"0.78rem", padding:"0.5rem 1rem", color:T.slate, borderColor:T.border}}>
              {expanded ? "Hide Details ↑" : "View Details ↓"}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded bio + contact */}
      {expanded && (
        <div style={{borderTop:`1px solid ${T.borderLight}`, padding:"1.5rem 1.75rem", background:T.parchment, display:"flex", flexWrap:"wrap", gap:"2rem"}}>
          <div style={{flex:1, minWidth:"240px"}}>
            <p style={{fontSize:"0.72rem", fontFamily:"'DM Mono', monospace", color:T.gold, letterSpacing:"0.08em", marginBottom:"0.625rem"}}>ABOUT</p>
            <p style={{fontSize:"0.875rem", color:T.charcoal, lineHeight:"1.7"}}>{a.bio}</p>
          </div>
          <div style={{minWidth:"200px"}}>
            <p style={{fontSize:"0.72rem", fontFamily:"'DM Mono', monospace", color:T.gold, letterSpacing:"0.08em", marginBottom:"0.75rem"}}>CONTACT</p>
            <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
              <a href={`tel:${a.phone}`} style={{fontSize:"0.85rem", color:T.forest, textDecoration:"none", display:"flex", gap:"0.5rem", alignItems:"center"}}>
                <span>📞</span> {a.phone}
              </a>
              <a href={`mailto:${a.email}`} style={{fontSize:"0.85rem", color:T.forest, textDecoration:"none", display:"flex", gap:"0.5rem", alignItems:"center"}}>
                <span>✉</span> {a.email}
              </a>
              <a href={`https://${a.website}`} target="_blank" rel="noopener noreferrer" style={{fontSize:"0.85rem", color:T.forest, textDecoration:"none", display:"flex", gap:"0.5rem", alignItems:"center"}}>
                <span>🌐</span> {a.website}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── VAULT ────────────────────────────────────────────────────────────────────

const VAULT_CATEGORIES = [
  { id:"will",        label:"Will & Estate Docs",              icon:"📜", desc:"Will, trust documents, advance directive, power of attorney" },
  { id:"financial",   label:"Financial Accounts",              icon:"🏦", desc:"Bank accounts, investments, retirement, crypto" },
  { id:"insurance",   label:"Insurance Policies",              icon:"🛡", desc:"Life, health, home, auto, long-term care" },
  { id:"property",    label:"Property & Vehicles",             icon:"🏡", desc:"Deeds, titles, mortgages, storage units" },
  { id:"business",    label:"Business Interests & Ownership",  icon:"🏢", desc:"LLC, corporation, partnership interests & operating agreements" },
  { id:"digital",     label:"Digital Assets",                  icon:"💻", desc:"Email, social media, password manager, subscriptions" },
  { id:"personal",    label:"Personal Messages",               icon:"✉️",  desc:"Letters to loved ones, final wishes, life stories" },
  { id:"medical",     label:"Medical Information",             icon:"🏥", desc:"Doctors, medications, conditions, advance directive" },
  { id:"contacts",    label:"Key Contacts",                    icon:"📞", desc:"Attorney, accountant, financial advisor, clergy" },
  { id:"other",       label:"Other Important Info",            icon:"📁", desc:"Business interests, memberships, locker combinations" },
];

const VAULT_ACCESS = {
  executor:  { label:"Executor",        desc:"Full read access after death",        color:"#2c4a3e" },
  trusted:   { label:"Trusted Person",  desc:"Read access to specific categories",  color:"#b8935a" },
  emergency: { label:"Emergency Only",  desc:"Access only in medical emergency",    color:"#c0392b" },
};

const modalOverlay = { position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem", backdropFilter:"blur(4px)" };
const modalBox = { background:"#ffffff", borderRadius:"20px", padding:"2rem", width:"100%", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" };

const VAULT_SUBCATEGORIES = {
  will:      ['Last Will & Testament', 'Trust Documents', 'Power of Attorney', 'Healthcare Directive / Living Will', 'Beneficiary Designations'],
  financial: ['Checking & Savings', 'Investment & Brokerage', 'Retirement (401k, IRA)', 'Cryptocurrency', 'Loans & Debts', 'Credit Cards'],
  insurance: ['Life Insurance', 'Health Insurance', 'Home & Auto', 'Long-term Care', 'Other Policies'],
  property:  ['Real Estate', 'Vehicles', 'Valuable Personal Property', 'Storage Units'],
  business:  ['LLCs & Corporations', 'Partnerships', 'Intellectual Property', 'Business Agreements'],
  digital:   ['Email Accounts', 'Social Media', 'Subscriptions', 'Domain Names & Websites', 'Crypto Wallets', 'Online Storage'],
  personal:  ['Letters to Family', 'Letters to Friends', 'Final Wishes / Funeral Preferences', 'Video Messages'],
  medical:   ['Advance Directive / Living Will', 'Medical Power of Attorney Document', 'Diagnoses & Conditions', 'Medications', 'Doctors & Providers', 'Insurance Cards', 'Organ Donation Wishes'],
  contacts:  ['Attorneys', 'Financial Advisors', 'Accountants', 'Doctors', 'Insurance Agents', 'Clergy / Spiritual Advisors'],
  other:     ['Safe Combinations / Lock Codes', 'Memberships', 'Warranties & Receipts', 'Miscellaneous'],
};

function BulkExclusionModal({ invite, vault, onSaveVault, estateSupabaseId, onClose }) {
  const VAULT_CAT_LABELS = {
    financial:"💰 Financial Accounts", insurance:"🏥 Insurance Policies", property:"🏠 Real Estate & Property",
    business:"💼 Business Interests", digital:"💻 Digital Assets", medical:"🩺 Medical Documents",
    will:"📜 Wills & Trusts", personal:"💌 Personal Messages", contacts:"📋 Contacts", other:"📦 Other Items",
  };
  const items = vault?.items || [];
  const categories = [...new Set(items.map(i => i.category))].filter(Boolean);
  const [excluded, setExcluded] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const toggle = (cat) => setExcluded(prev => { const n = new Set(prev); if (n.has(cat)) n.delete(cat); else n.add(cat); return n; });
  const handleSave = async () => {
    setSaving(true);
    const invId = invite.id;
    const updatedItems = items.map(item => {
      if (!excluded.has(item.category)) return item;
      const existingExcl = item.excluded_invitation_ids || [];
      if (existingExcl.includes(invId)) return item;
      return { ...item, excluded_invitation_ids: [...existingExcl, invId] };
    });
    for (const item of updatedItems) {
      if ((item.excluded_invitation_ids || []).includes(invId)) {
        await supabase.from('vault_items').update({ excluded_invitation_ids: item.excluded_invitation_ids }).eq('id', item.id);
      }
    }
    if (onSaveVault) onSaveVault({ ...vault, items: updatedItems });
    setSaving(false);
    onClose();
  };
  const invName = invite.invited_name || invite.name || 'this collaborator';
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{...modalBox, maxWidth:"480px"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.2rem", fontWeight:"700", color:T.forest}}>Set sharing preferences</h2>
          <button onClick={onClose} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
        </div>
        <p style={{fontSize:"0.85rem", color:T.muted, lineHeight:"1.6", marginBottom:"1.25rem"}}>
          <strong>{invName}</strong> has been invited. Check any vault categories you'd like to hide from them — everything else stays shared by default.
        </p>
        {categories.length === 0 ? (
          <p style={{fontSize:"0.85rem", color:T.muted, fontStyle:"italic", marginBottom:"1.5rem"}}>No vault items yet — you can set per-item exclusions as you add documents.</p>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:"0.5rem", marginBottom:"1.5rem"}}>
            {categories.map(cat => {
              const count = items.filter(i=>i.category===cat).length;
              const isExcl = excluded.has(cat);
              return (
                <label key={cat} style={{display:"flex", gap:"0.75rem", alignItems:"center", cursor:"pointer", padding:"0.75rem 0.875rem", borderRadius:"8px", border:`1px solid ${isExcl ? T.danger : T.border}`, background:isExcl ? "#fff5f5" : "transparent", transition:"all 0.15s"}}>
                  <input type="checkbox" checked={isExcl} onChange={()=>toggle(cat)} style={{accentColor:T.danger, flexShrink:0}} />
                  <span style={{fontSize:"0.85rem", fontWeight:"500", color:isExcl ? T.danger : T.charcoal, flex:1}}>
                    {VAULT_CAT_LABELS[cat] || cat}
                  </span>
                  <span style={{fontSize:"0.75rem", color:T.muted, flexShrink:0}}>{count} item{count!==1?"s":""}</span>
                </label>
              );
            })}
          </div>
        )}
        <div style={{display:"flex", gap:"0.75rem"}}>
          <button onClick={onClose} style={{...outlineBtn, flex:"0 0 auto"}}>Skip</button>
          <button onClick={handleSave} disabled={saving} style={{...filledBtn, flex:1, padding:"0.75rem", opacity:saving?0.7:1}}>
            {saving ? "Saving…" : excluded.size > 0 ? `Hide ${excluded.size} categor${excluded.size===1?"y":"ies"} from ${invName} →` : "Keep Everything Shared →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AppVault({ vault, onSave, estate, navTo, invitedPermissions, invitedRole, estateSupabaseId, user, profile, invitations, invitedInvitationId, isOwnVault = false }) {
  const isInvited = !!invitedPermissions;
  const isInvitedEstateView = isInvited || (!isOwnVault && profile?.signup_mode === 'invited');
  const vPerms = invitedPermissions || {};
  const isMedicalPoa = invitedRole === 'Medical POA';
  const isExecutorInvited = isInvited && invitedRole === 'Named as executor';
  const isNonExecutorInvited = isInvited && !isExecutorInvited && !isMedicalPoa;
  const canViewMedical = isMedicalPoa && vPerms.view_medical_documents === true;
  const canDownloadMedical = isMedicalPoa && vPerms.download_medical_documents === true;
  const canViewVault = isMedicalPoa ? canViewMedical : (!isInvited || vPerms.full_access || vPerms.view_vault === true);
  const ownerName = estate?.ownerName || estate?.deceasedName || estate?.owner_name || estate?.deceased_name || 'the estate owner';
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [collabNudgeDismissed, setCollabNudgeDismissed] = useState(() => {
    try { return localStorage.getItem('heiros_collab_nudge_dismissed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    if (isInvited) return;
    hasMfaEnabled().then(ok => setMfaEnabled(ok));
  }, [isInvited]);
  const [mode, setMode] = useState("owner");
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [setupDismissed, setSetupDismissed] = useState(false);
  const [showPasswordManager, setShowPasswordManager] = useState(false);
  const showSetup = !vault.owner && !setupDismissed;

  const [inviteSent, setInviteSent] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [vaultView, setVaultView] = useState(() => localStorage.getItem('heiros_vault_view') || 'list');
  const [hoveredTile, setHoveredTile] = useState(null);
  const setView = (v) => { setVaultView(v); localStorage.setItem('heiros_vault_view', v); };
  // Hook A — guided flow state
  const [showGuidedFlow, setShowGuidedFlow] = useState(false);

  const isOwner = !isInvited && mode === "owner";
  const isExecutor = !isInvited && mode === "executor";
  const items = vault.items || [];
  const trusted = vault.trusted || [];
  const medicalBaseItems = isMedicalPoa
    ? items.filter(i => i.shared_with_medical_poa === true)
    : items;
  const filteredItems = (activeCategory ? medicalBaseItems.filter(i=>i.category===activeCategory) : medicalBaseItems)
    .filter(i => {
      if (!activeSubcategory) return true;
      if (activeSubcategory === '__none__') return !i.subcategory;
      return i.subcategory === activeSubcategory;
    })
    .filter(i => {
      if (!isNonExecutorInvited || !invitedInvitationId) return true;
      const excl = i.excluded_invitation_ids || [];
      return !excl.includes(invitedInvitationId) && !excl.includes(String(invitedInvitationId));
    });
  const categoryCount = (id) => items.filter(i=>i.category===id).length;
  const getItemGroups = (itemList) => {
    const hasAnySub = itemList.some(i => i.subcategory);
    if (!hasAnySub || activeSubcategory) return [{ header: null, items: itemList }];
    const map = {};
    const noSub = [];
    itemList.forEach(i => {
      if (i.subcategory) { (map[i.subcategory] = map[i.subcategory] || []).push(i); }
      else noSub.push(i);
    });
    const groups = Object.entries(map).map(([h, its]) => ({ header: h, items: its }));
    if (noSub.length) groups.push({ header: 'Other', items: noSub });
    return groups;
  };

  const addItem = (item) => {
    const newItem = { ...item, id: Date.now(), createdAt: new Date().toISOString() };
    const updated = { ...vault, items: [...items, newItem] };
    onSave(updated); setShowAddItem(false);
  };
  const deleteItem = async (id) => {
    const itemToDelete = items.find(i => i.id === id);
    if (itemToDelete?.file_url) {
      const path = `estates/${estate.id}/vault/${id}/${itemToDelete.file_name}`;
      await supabase.storage.from("vault-files").remove([path]);
    }
    await supabase.from("vault_items").delete().eq("id", id);
    onSave({...vault, items:items.filter(i=>i.id!==id)}); setViewingItem(null);
  };
  const updateItem = (id, changes) => {
    const updated = {...vault, items: items.map(i => i.id===id ? {...i, ...changes, updatedAt: new Date().toISOString()} : i)};
    onSave(updated);
  };
  const addTrusted = (person) => {
    onSave({...vault, trusted:[...trusted,{...person,id:Date.now(),invitedAt:new Date().toLocaleDateString()}]});
    setInviteSent(true);
  };

  if (!canViewVault && estate?.signupMode !== 'owner') {
    return (
      <AppShell estate={estate} tasks={[]} page="app-vault" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
        <PermissionDeniedBanner title="Vault" message="You don't have permission to view the vault. Contact the estate owner to request access." />
      </AppShell>
    );
  }

  return (
    <AppShell estate={estate} tasks={[]} page="app-vault" navTo={navTo} invitedPermissions={invitedPermissions} invitedRole={invitedRole}>
      {mfaEnabled === false && !isInvited && (
        <div style={{background:"#fef3f0", border:"1px solid #f5c6b8", borderLeft:"4px solid #c0392b", padding:"0.875rem 1.25rem", display:"flex", alignItems:"flex-start", gap:"0.75rem", margin:"1rem 1rem 0"}}>
          <span style={{fontSize:"1.1rem", flexShrink:0}}>⚠️</span>
          <div>
            <p style={{fontWeight:"600", color:"#c0392b", fontSize:"0.875rem", marginBottom:"0.25rem"}}>Your Vault is not protected by two-factor authentication.</p>
            <p style={{fontSize:"0.82rem", color:"#7a3020", lineHeight:"1.55"}}>Anyone who gains access to your password could read all vault contents. Enable two-factor authentication in <button onClick={()=>navTo("account")} style={{...btnReset, color:"#c0392b", fontWeight:"600", textDecoration:"underline", fontSize:"0.82rem"}}>Account Settings</button> to protect your Vault.</p>
          </div>
        </div>
      )}
      {isOwner && !collabNudgeDismissed && !(invitations?.length) && (
        <div style={{background:"#f0f7ff", border:"1px solid #b3d4f5", borderLeft:"4px solid #2980b9", padding:"0.875rem 1.25rem", display:"flex", alignItems:"flex-start", gap:"0.75rem", margin:"1rem 1rem 0", borderRadius:"0 8px 8px 0"}}>
          <span style={{fontSize:"1.1rem", flexShrink:0}}>💡</span>
          <div style={{flex:1}}>
            <p style={{fontWeight:"600", color:"#1a5276", fontSize:"0.875rem", marginBottom:"0.25rem"}}>Add your collaborators before uploading documents</p>
            <p style={{fontSize:"0.82rem", color:"#1a5276", lineHeight:"1.55"}}>Adding collaborators first lets you control who can see each item as you upload it — so nothing is shared by accident.</p>
            <button onClick={()=>navTo("app-collab")} style={{...btnReset, marginTop:"0.5rem", fontSize:"0.8rem", color:"#2980b9", fontWeight:"600", textDecoration:"underline"}}>Add Collaborators →</button>
          </div>
          <button onClick={()=>{ setCollabNudgeDismissed(true); try{localStorage.setItem('heiros_collab_nudge_dismissed','1');}catch{} }} style={{...btnReset, color:"#2980b9", fontSize:"1.25rem", lineHeight:1, flexShrink:0}}>×</button>
        </div>
      )}
      {showSetup    && !isInvitedEstateView && <VaultSetupModal onComplete={(o)=>onSave({...vault,owner:o})} onClose={()=>setSetupDismissed(true)} profile={profile} user={user} estate={estate} />}
      {showAddItem  && <VaultAddItemModal onAdd={addItem} onClose={()=>setShowAddItem(false)} initialCategory={activeCategory} initialSubcategory={activeSubcategory} estateSupabaseId={estateSupabaseId} user={user} invitations={invitations} linkedSpouseEstateId={estate?.linked_spouse_estate_id ?? null} />}
      {showInvite   && <VaultInviteModal onInvite={addTrusted} onClose={()=>{setShowInvite(false);setInviteSent(false);}} sent={inviteSent} />}
      {viewingItem  && <VaultItemModal item={viewingItem} isOwner={isOwner} canDownload={isMedicalPoa ? canDownloadMedical : true} onDelete={deleteItem} onClose={()=>setViewingItem(null)} onUpdate={(updatedItem)=>{ updateItem(updatedItem.id, updatedItem); setViewingItem(updatedItem); }} estateSupabaseId={estateSupabaseId} user={user} invitations={invitations} linkedSpouseEstateId={estate?.linked_spouse_estate_id ?? null} />}
      {showPasswordManager && <PasswordManagerModal onClose={()=>setShowPasswordManager(false)} estate={estate} vault={vault} />}
      {/* Hook B — guided flow modal */}
      {showGuidedFlow && isOwner && <VaultGuidedFlow items={VAULT_GUIDED_ITEMS} vault={vault} estate={estate} onAdd={addItem} onClose={()=>setShowGuidedFlow(false)} estateSupabaseId={estateSupabaseId} user={user} invitations={invitations} />}

      {/* Header */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"2rem", flexWrap:"wrap", gap:"1rem"}}>
        <div>
          <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>
            {isMedicalPoa ? "Medical Documents" : isOwner ? "My Vault" : "Estate Vault (Read Only)"}
          </h1>
          <p style={{color:T.muted, fontSize:"0.875rem"}}>
            {isMedicalPoa
              ? `${medicalBaseItems.length} medical document${medicalBaseItems.length!==1?"s":""} · Advance directives, HIPAA auth, and care instructions`
              : isOwner
                ? `${items.length} item${items.length!==1?"s":""} stored · Your executor sees this as read-only after your death`
                : `Read-only view · ${items.length} item${items.length!==1?"s":""} left by the vault owner`}
          </p>
        </div>
        <div style={{display:"flex", gap:"0.625rem", flexWrap:"wrap"}}>
          {!isInvited && (
            <div style={{display:"flex", background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"8px", overflow:"hidden"}}>
              {[{id:"owner",label:"Owner View"},{id:"executor",label:"Executor View"}].map(m=>(
                <button key={m.id} onClick={()=>setMode(m.id)} style={{...btnReset, padding:"0.5rem 0.875rem", fontSize:"0.75rem", fontWeight:"500", background:mode===m.id?T.forest:"transparent", color:mode===m.id?"white":T.muted}}>{m.label}</button>
              ))}
            </div>
          )}
          {isOwner && <>
            {/* Hook C — guided setup button */}
            <button onClick={()=>setShowGuidedFlow(true)} style={{...outlineBtn, fontSize:"0.8rem", padding:"0.5rem 1rem", color:T.forest, borderColor:T.forest}}>✦ Guided Setup</button>
            <button onClick={()=>setShowAddItem(true)} style={{...filledBtn, fontSize:"0.8rem", padding:"0.5rem 1rem"}}>+ Add to Vault</button>
          </>}
        </div>
      </div>

      {isExecutor && (
        <div style={{background:"#fef9f0", border:`1px solid ${T.tan}`, borderRadius:"10px", padding:"0.875rem 1.25rem", marginBottom:"1.5rem", display:"flex", gap:"0.75rem", alignItems:"flex-start"}}>
          <span>👁</span>
          <div>
            <p style={{fontSize:"0.85rem", fontWeight:"500", color:T.goldDark, marginBottom:"0.125rem"}}>Read-only access</p>
            <p style={{fontSize:"0.8rem", color:T.muted}}>As executor, you can view all vault contents but cannot add, edit, or delete items. This preserves the owner's information exactly as they left it.</p>
          </div>
        </div>
      )}
      {isInvited && isMedicalPoa && (
        <div style={{background:"#f0f7fa", border:`1px solid #1a6b8a44`, borderRadius:"10px", padding:"0.875rem 1.25rem", marginBottom:"1.5rem", display:"flex", gap:"0.75rem", alignItems:"flex-start"}}>
          <span>🏥</span>
          <div>
            <p style={{fontSize:"0.85rem", fontWeight:"500", color:"#1a6b8a", marginBottom:"0.125rem"}}>Medical Power of Attorney — Read Only</p>
            <p style={{fontSize:"0.8rem", color:T.muted}}>You can view medical documents relevant to your role. Downloading is {canDownloadMedical ? "permitted" : "not permitted"} for your access level. Contact the estate owner to request changes.</p>
          </div>
        </div>
      )}
      {isInvited && !isMedicalPoa && (
        <div style={{background:T.parchment, border:`1px solid ${T.tan}`, borderRadius:"10px", padding:"0.875rem 1.25rem", marginBottom:"1.5rem", display:"flex", gap:"0.75rem", alignItems:"flex-start"}}>
          <span>👁</span>
          <div>
            <p style={{fontSize:"0.85rem", fontWeight:"500", color:T.goldDark, marginBottom:"0.125rem"}}>Viewing {ownerName}'s estate — Read Only</p>
            <p style={{fontSize:"0.8rem", color:T.muted}}>You can view vault items but cannot add, edit, or delete anything. Contact the estate owner to request changes.</p>
          </div>
        </div>
      )}

      {/* Hook D — progress banner, auto-renders for owners above the category sidebar */}
      {isOwner && <VaultProgressBanner items={VAULT_GUIDED_ITEMS} vault={vault} onOpenGuidedFlow={()=>setShowGuidedFlow(true)} />}

      <div style={{display:"flex", gap:"1.5rem"}}>
        {/* Category sidebar */}
        <div style={{width:"220px", flexShrink:0, display:"flex", flexDirection:"column", gap:"0.375rem"}}>
          {isMedicalPoa ? (
            <>
              <p style={{fontSize:"0.68rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.08em", color:T.muted, padding:"0.25rem 0.875rem 0.5rem", margin:0}}>MEDICAL DOCUMENTS</p>
              <button onClick={()=>setActiveSubcategory(null)} style={{...btnReset, padding:"0.625rem 0.875rem", borderRadius:"8px", textAlign:"left", fontSize:"0.82rem", fontWeight:"500", background:!activeSubcategory?T.forest:"transparent", color:!activeSubcategory?"white":T.muted, display:"flex", justifyContent:"space-between"}}>
                <span>All Shared Documents</span><span style={{opacity:0.7}}>{medicalBaseItems.length}</span>
              </button>
              {[...new Set(medicalBaseItems.map(i => i.subcategory).filter(Boolean))].map(sub => {
                const cnt = medicalBaseItems.filter(i => i.subcategory === sub).length;
                const isActive = activeSubcategory === sub;
                return (
                  <button key={sub} onClick={()=>setActiveSubcategory(sub)}
                    style={{...btnReset, width:"100%", padding:"0.5rem 0.875rem", borderRadius:"8px", textAlign:"left", fontSize:"0.8rem",
                      background: isActive ? T.parchment : "transparent",
                      color: isActive ? T.forest : T.charcoal,
                      fontWeight: isActive ? "500" : "400",
                      border: isActive ? `1px solid ${T.border}` : "1px solid transparent",
                      display:"flex", alignItems:"center", justifyContent:"space-between", gap:"0.375rem"}}>
                    <span style={{display:"flex", alignItems:"center", gap:"0.375rem", minWidth:0, flex:1}}>
                      {isActive && <span style={{width:"3px", height:"11px", background:T.forest, borderRadius:"2px", flexShrink:0, display:"inline-block"}}/>}
                      <span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{sub}</span>
                    </span>
                    <span style={{fontSize:"0.65rem", background:T.forest+"22", color:T.forest, padding:"0.1rem 0.4rem", borderRadius:"10px", flexShrink:0}}>{cnt}</span>
                  </button>
                );
              })}
              {medicalBaseItems.some(i => !i.subcategory) && (
                <button onClick={()=>setActiveSubcategory('__none__')}
                  style={{...btnReset, width:"100%", padding:"0.5rem 0.875rem", borderRadius:"8px", textAlign:"left", fontSize:"0.8rem",
                    background: activeSubcategory==='__none__' ? T.parchment : "transparent",
                    color: activeSubcategory==='__none__' ? T.forest : T.muted,
                    fontWeight: activeSubcategory==='__none__' ? "500" : "400",
                    fontStyle: "italic",
                    border: activeSubcategory==='__none__' ? `1px solid ${T.border}` : "1px solid transparent",
                    display:"flex", alignItems:"center", justifyContent:"space-between", gap:"0.375rem"}}>
                  <span>Uncategorized</span>
                  <span style={{fontSize:"0.65rem", background:T.forest+"22", color:T.forest, padding:"0.1rem 0.4rem", borderRadius:"10px", flexShrink:0}}>{medicalBaseItems.filter(i=>!i.subcategory).length}</span>
                </button>
              )}
            </>
          ) : (
          <>
          <button onClick={()=>{setActiveCategory(null);setActiveSubcategory(null);}} style={{...btnReset, padding:"0.625rem 0.875rem", borderRadius:"8px", textAlign:"left", fontSize:"0.82rem", fontWeight:"500", background:!activeCategory?T.forest:"transparent", color:!activeCategory?"white":T.muted, display:"flex", justifyContent:"space-between"}}>
            <span>All Items</span><span style={{opacity:0.7}}>{items.length}</span>
          </button>
          {VAULT_CATEGORIES.map(cat=>{
            const isSelected = activeCategory === cat.id;
            const subs = VAULT_SUBCATEGORIES[cat.id];
            const hasSubs = subs && subs.length > 0;
            const subCount = (sub) => items.filter(i => i.category === cat.id && i.subcategory === sub).length;
            return (
              <div key={cat.id}>
                <button onClick={()=>{setActiveCategory(cat.id);setActiveSubcategory(null);}} style={{...btnReset, width:"100%", padding:"0.625rem 0.875rem", borderRadius:"8px", textAlign:"left", fontSize:"0.82rem", background:isSelected?T.parchment:"transparent", color:isSelected?T.forest:T.muted, display:"flex", alignItems:"center", gap:"0.5rem", justifyContent:"space-between", border:isSelected?`1px solid ${T.border}`:"1px solid transparent"}}>
                  <span style={{display:"flex", alignItems:"center", gap:"0.5rem"}}><span>{cat.icon}</span><span>{cat.label}</span></span>
                  <div style={{display:"flex", alignItems:"center", gap:"0.375rem", flexShrink:0}}>
                    {categoryCount(cat.id)>0 && <span style={{fontSize:"0.7rem", background:T.forest+"22", color:T.forest, padding:"0.1rem 0.4rem", borderRadius:"10px"}}>{categoryCount(cat.id)}</span>}
                    {hasSubs && <span style={{fontSize:"0.6rem", display:"inline-block", opacity:isSelected?1:0, transform:isSelected?"rotate(90deg)":"rotate(0deg)", transition:"transform 0.2s ease, opacity 0.15s ease", lineHeight:1, color:T.forest}}>▶</span>}
                  </div>
                </button>
                {isSelected && hasSubs && (
                  <div style={{marginTop:"0.1rem", marginLeft:"1.125rem", paddingLeft:"0.625rem", borderLeft:`2px solid ${T.border}`, display:"flex", flexDirection:"column", gap:"0.0625rem"}}>
                    {subs.map(sub => {
                      const cnt = subCount(sub);
                      const isActiveSub = activeSubcategory === sub;
                      return (
                        <button key={sub}
                          onClick={()=>{setActiveCategory(cat.id);setActiveSubcategory(sub);}}
                          style={{...btnReset, width:"100%", padding:"0.275rem 0.5rem", borderRadius:"5px", textAlign:"left", fontSize:"0.8125rem", display:"flex", alignItems:"center", justifyContent:"space-between", gap:"0.375rem",
                            color: isActiveSub ? T.forest : cnt > 0 ? T.charcoal : T.muted,
                            fontWeight: isActiveSub ? "500" : "400",
                            fontStyle: cnt === 0 ? "italic" : "normal",
                            background: isActiveSub ? T.forest+"0d" : "transparent",
                          }}>
                          <span style={{display:"flex", alignItems:"center", gap:"0.3rem", minWidth:0, flex:1}}>
                            {isActiveSub && <span style={{width:"3px", height:"11px", background:T.forest, borderRadius:"2px", flexShrink:0, display:"inline-block"}}/>}
                            <span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{sub}</span>
                          </span>
                          {cnt > 0 && <span style={{fontSize:"0.65rem", background:T.forest+"22", color:T.forest, padding:"0.1rem 0.35rem", borderRadius:"10px", flexShrink:0}}>{cnt}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {isOwner && (
            <div style={{marginTop:"0.5rem"}}>
              <button onClick={()=>{setActiveCategory("will"); setShowAddItem(true);}} style={{...btnReset, width:"100%", padding:"0.625rem 0.875rem", borderRadius:"8px", fontSize:"0.78rem", fontWeight:"500", background:`linear-gradient(135deg, ${T.forest}15, ${T.forest}25)`, color:T.forest, border:`1px solid ${T.forest}30`, display:"flex", alignItems:"center", gap:"0.5rem", justifyContent:"center"}}>
                <span>📄</span> Upload My Will
              </button>
            </div>
          )}


          {/* Password Manager Integration Card */}
          {isOwner && (
            <div style={{marginTop:"1rem", paddingTop:"1rem", borderTop:`1px solid ${T.border}`}}>
              <p style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.08em", color:T.gold, marginBottom:"0.5rem"}}>PASSWORD MANAGER</p>
              <p style={{fontSize:"0.72rem", color:T.muted, lineHeight:"1.5", marginBottom:"0.75rem"}}>
                Pick any password manager below — each lets your executor access accounts when needed, without seeing your passwords while you're alive.
              </p>
              <div style={{display:"flex", gap:"0.25rem", alignItems:"stretch"}}>
                {[
                  {svg:<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" rx="8" fill="#175DDC"/><path d="M18 6L8 10v10c0 5.5 4.3 10.6 10 12 5.7-1.4 10-6.5 10-12V10L18 6z" fill="white"/></svg>, name:"Bitwarden", action:()=>setShowPasswordManager(true), btnLabel:"Set Up", href:null},
                  {svg:<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" rx="8" fill="#1A8CFF"/><circle cx="15" cy="16" r="5" stroke="white" strokeWidth="2.5" fill="none"/><line x1="19" y1="19" x2="27" y2="27" stroke="white" strokeWidth="2.5" strokeLinecap="round"/><line x1="23" y1="24" x2="25" y2="22" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>, name:"1Password", action:null, btnLabel:"Set Up", href:"https://1password.com"},
                  {svg:<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="36" height="36" rx="8" fill="#6D4AFF"/><path d="M18 7L10 11v8c0 4.8 3.4 9.3 8 10.9 4.6-1.6 8-6.1 8-10.9v-8L18 7z" fill="white" opacity="0.9"/><path d="M18 11L13 13.5v5c0 2.8 2 5.4 5 6.3 3-0.9 5-3.5 5-6.3v-5L18 11z" fill="#6D4AFF"/></svg>, name:"Proton Pass", action:null, btnLabel:"Set Up", href:"https://proton.me/pass"},
                ].map(pm=>(
                  <div key={pm.name} style={{flex:"1 1 0", minWidth:0, background:`linear-gradient(135deg, ${T.forest}08, ${T.forest}14)`, border:`1px solid ${T.forest}22`, borderRadius:"8px", padding:"0.625rem", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"space-between", gap:"0.375rem"}}>
                    {pm.svg}
                    <span style={{fontSize:"0.65rem", fontWeight:"600", color:T.forest, textAlign:"center"}}>{pm.name}</span>
                    {pm.href
                      ? <a href={pm.href} target="_blank" rel="noopener noreferrer" style={{marginTop:"auto", display:"block", width:"100%", padding:"0.3rem 0", borderRadius:"5px", background:T.forest, color:"white", fontSize:"0.65rem", fontWeight:"500", textAlign:"center", textDecoration:"none", boxSizing:"border-box"}}>{pm.btnLabel}</a>
                      : <button onClick={pm.action} style={{...btnReset, marginTop:"auto", padding:"0.3rem 0", borderRadius:"5px", background:T.forest, color:"white", fontSize:"0.65rem", fontWeight:"500", textAlign:"center", width:"100%"}}>{pm.btnLabel}</button>
                    }
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </div>

        {/* Items grid */}
        <div style={{flex:1}}>
          {!isMedicalPoa && activeCategory && VAULT_SUBCATEGORIES[activeCategory] && (
            <div style={{display:"flex", gap:"0.5rem", marginBottom:"0.875rem", overflowX:"auto", paddingBottom:"0.25rem", flexWrap:"nowrap"}}>
              {['All', ...VAULT_SUBCATEGORIES[activeCategory]].map(sub => {
                const isAll = sub === 'All';
                const selected = isAll ? !activeSubcategory : activeSubcategory === sub;
                return (
                  <button key={sub} onClick={()=>setActiveSubcategory(isAll ? null : sub)}
                    style={{...btnReset, whiteSpace:"nowrap", padding:"6px 14px", borderRadius:"999px", fontSize:"0.75rem", fontWeight:"500", flexShrink:0, cursor:"pointer",
                      background: selected ? T.forest : T.parchment,
                      color: selected ? "white" : T.muted,
                      border: selected ? "none" : `1px solid ${T.border}`,
                      boxShadow: selected ? "none" : "0 1px 3px rgba(0,0,0,0.04)"}}>
                    {sub}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{display:"flex", justifyContent:"flex-end", marginBottom:"0.75rem"}}>
            <div style={{display:"flex", background:T.parchment, border:`1px solid ${T.border}`, borderRadius:"7px", overflow:"hidden"}}>
              {[{id:"list",icon:"≡",title:"List view"},{id:"tile",icon:"⊞",title:"Tile view"}].map(v=>(
                <button key={v.id} onClick={()=>setView(v.id)} title={v.title}
                  style={{...btnReset, padding:"0.3rem 0.7rem", fontSize:"1.1rem", lineHeight:1,
                    background: vaultView===v.id ? T.forest : "transparent",
                    color: vaultView===v.id ? "white" : T.muted}}>
                  {v.icon}
                </button>
              ))}
            </div>
          </div>
          {filteredItems.length===0 && (activeCategory==="will" || (!activeCategory && items.length===0)) && isOwner ? (
            <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
              {/* Will prompt when viewing will category or vault is completely empty */}
              {estate?.hasWill === "Yes" ? (
                <div style={{background:`linear-gradient(135deg, ${T.forest}06, ${T.forest}12)`, border:`2px solid ${T.forest}25`, borderRadius:"16px", padding:"2rem"}}>
                  <div style={{display:"flex", gap:"1rem", alignItems:"flex-start", marginBottom:"1.25rem"}}>
                    <span style={{fontSize:"2rem"}}>📄</span>
                    <div>
                      <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.15rem", fontWeight:"700", color:T.forest, marginBottom:"0.375rem"}}>Upload Your Existing Will</h3>
                      <p style={{fontSize:"0.82rem", color:T.muted, lineHeight:"1.65", maxWidth:"480px"}}>
                        You told us you already have a will. Upload it here so your executor can access it when the time comes.
                      </p>
                    </div>
                  </div>
                  <div style={{display:"flex", gap:"0.75rem", flexWrap:"wrap", alignItems:"center"}}>
                    <button onClick={()=>setShowAddItem(true)} style={{...filledBtn, padding:"0.75rem 1.5rem", fontSize:"0.875rem"}}>
                      📄 Upload Existing Will
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{background:`linear-gradient(135deg, ${T.forest}06, ${T.forest}12)`, border:`2px solid ${T.forest}25`, borderRadius:"16px", padding:"2rem"}}>
                  <div style={{display:"flex", gap:"1rem", alignItems:"flex-start", marginBottom:"1.25rem"}}>
                    <span style={{fontSize:"2rem"}}>📄</span>
                    <div>
                      <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.15rem", fontWeight:"700", color:T.forest, marginBottom:"0.375rem"}}>
                        Create your will with an estate attorney
                      </h3>
                      <p style={{fontSize:"0.82rem", color:T.muted, lineHeight:"1.65", maxWidth:"480px"}}>
                        A will is the most important document in your estate. HeirOS does not generate wills — to create one, work with a licensed estate attorney in your state. An attorney will ensure your will meets your state's specific requirements for signing, witnessing, and notarization. Once you have your will, upload it here so your executor can access it when needed.
                      </p>
                    </div>
                  </div>
                  <div style={{display:"flex", gap:"0.75rem", flexWrap:"wrap"}}>
                    <button onClick={()=>setShowAddItem(true)} style={{...filledBtn, padding:"0.75rem 1.5rem", fontSize:"0.875rem"}}>
                      📄 Upload My Will
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : vaultView === 'tile' ? (
            <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
              {getItemGroups(filteredItems).map(({header, items:grpItems})=>(
                <div key={header||'_all'}>
                  {header && (
                    <div style={{fontSize:"0.68rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.08em", textTransform:"uppercase", color:T.muted, paddingBottom:"0.375rem", marginBottom:"0.5rem", borderBottom:`1px solid ${T.border}`}}>{header}</div>
                  )}
                  <div style={{display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:"0.875rem"}}>
                    {grpItems.map(item=>{
                      const cat = VAULT_CATEGORIES.find(c=>c.id===item.category);
                      const isWill = item.category === "will" && item.details?.sections;
                      const tileFields = item.details && typeof item.details === 'object' && !Array.isArray(item.details)
                        ? Object.entries(item.details).filter(([,v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object').slice(0,3)
                        : [];
                      const toTileLabel = (k) => k.replace(/_/g,' ').replace(/\b\w/g, c=>c.toUpperCase());
                      return (
                        <div key={item.id}
                          onMouseEnter={()=>setHoveredTile(item.id)}
                          onMouseLeave={()=>setHoveredTile(null)}
                          style={{background:T.white, border:`1px solid ${T.border}`, borderRadius:"12px", padding:"0.875rem", display:"flex", flexDirection:"column", gap:"0.375rem", boxShadow:hoveredTile===item.id?"0 4px 12px rgba(0,0,0,0.09)":"0 1px 3px rgba(0,0,0,0.04)", transition:"box-shadow 0.15s"}}>
                          <div style={{display:"flex", alignItems:"center", gap:"0.375rem"}}>
                            <span style={{fontSize:"0.9rem"}}>{cat?.icon||"📁"}</span>
                            <span style={{fontSize:"0.62rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>{cat?.label||"General"}</span>
                            {item.file_name && <span style={{marginLeft:"auto", fontSize:"0.75rem"}} title="Has attachment">📎</span>}
                          </div>
                          <div style={{fontWeight:"600", fontSize:"0.85rem", color:T.charcoal, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{item.title}</div>
                          {tileFields.length > 0 && (
                            <div style={{display:"flex", flexDirection:"column", gap:"0.25rem", marginTop:"0.125rem"}}>
                              {tileFields.map(([k,v])=>(
                                <div key={k} style={{minWidth:0}}>
                                  <div style={{fontSize:"0.58rem", color:T.muted, fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:"0.1rem"}}>{toTileLabel(k)}</div>
                                  <div style={{fontSize:"0.75rem", color:T.charcoal, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{String(v)}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          <button onClick={()=> setViewingItem(item)} style={{...btnReset, marginTop:"auto", paddingTop:"0.5rem", fontSize:"0.7rem", color:T.forest, fontWeight:"500", borderTop:`1px solid ${T.borderLight}`, textAlign:"left"}}>View Details →</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {isOwner && (
                <div style={{display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:"0.875rem"}}>
                  <div onClick={()=>setShowAddItem(true)} style={{background:"transparent", border:`2px dashed ${T.border}`, borderRadius:"12px", padding:"1.25rem", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"0.5rem", minHeight:"140px", opacity:0.5}}>
                    <span style={{fontSize:"1.5rem"}}>+</span>
                    <span style={{fontSize:"0.8rem", color:T.muted}}>Add item</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            filteredItems.length===0 ? (
              isOwner ? (
                <div onClick={()=>setShowAddItem(true)} style={{display:"flex", alignItems:"center", gap:"0.625rem", padding:"0.875rem 1rem", cursor:"pointer", border:`2px dashed ${T.border}`, borderRadius:"10px", opacity:0.75}}>
                  <span style={{fontSize:"1rem", color:T.gold}}>+</span>
                  <span style={{fontSize:"0.85rem", color:T.gold, fontWeight:"500"}}>Add your first item</span>
                </div>
              ) : (
                <div style={{padding:"2rem", textAlign:"center", color:T.muted, fontSize:"0.85rem"}}>No items in this category.</div>
              )
            ) : (
            <div style={{border:`1px solid ${T.borderLight}`, borderRadius:"10px", overflow:"hidden"}}>
              {getItemGroups(filteredItems).flatMap(({header, items:grpItems}) => [
                ...(header ? [<div key={`hdr-${header}`} style={{padding:"0.3rem 1rem", fontSize:"0.68rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.08em", textTransform:"uppercase", color:T.muted, background:T.parchment, borderBottom:`1px solid ${T.border}`}}>{header}</div>] : []),
                ...grpItems.map((item, idx)=>{
                const cat = VAULT_CATEGORIES.find(c=>c.id===item.category);
                const isWill = item.category === "will" && item.details?.sections;
                const detailFields = item.details && typeof item.details === 'object' && !Array.isArray(item.details)
                  ? Object.entries(item.details).filter(([,v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object').slice(0,2)
                  : [];
                const toLabel = (k) => k.replace(/_/g,' ').replace(/\b\w/g, c=>c.toUpperCase());
                const truncate = (s,n=32) => String(s).length > n ? String(s).slice(0,n)+'…' : String(s);
                return (
                  <div key={item.id}
                    onClick={()=> setViewingItem(item)}
                    style={{display:"flex", alignItems:"center", gap:"0.875rem", padding:"0 1rem", height:"56px", cursor:"pointer", background: idx%2===0 ? T.white : "#fafaf8", borderBottom: idx < grpItems.length-1 ? `1px solid ${T.borderLight}` : "none", transition:"background 0.1s"}}>
                    {/* Left: icon + title + category */}
                    <span style={{fontSize:"1.1rem", flexShrink:0}}>{cat?.icon||"📁"}</span>
                    <div style={{minWidth:0, width:"200px", flexShrink:0}}>
                      <div style={{fontWeight:"600", fontSize:"0.85rem", color:T.charcoal, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{item.title}</div>
                      <div style={{fontSize:"0.68rem", color:T.muted}}>{cat?.label||"General"}</div>
                    </div>
                    {/* Middle: labeled detail fields */}
                    <div style={{flex:1, minWidth:0, display:"flex", flexDirection:"column", gap:"0.1rem", justifyContent:"center"}}>
                      {detailFields.map(([k,v])=>(
                        <p key={k} style={{margin:0, fontSize:"0.75rem", color:T.charcoal, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                          <span style={{color:T.muted, fontSize:"0.68rem"}}>{toLabel(k)}: </span>{truncate(v)}
                        </p>
                      ))}
                    </div>
                    {/* Right: badge + attachment + actions */}
                    <div style={{display:"flex", alignItems:"center", gap:"0.5rem", flexShrink:0}} onClick={e=>e.stopPropagation()}>
                      {item.file_name && <span style={{fontSize:"0.75rem"}} title={item.file_name}>📎</span>}
                      <button onClick={(e)=>{ e.stopPropagation(); setViewingItem(item); }} style={{...btnReset, fontSize:"0.7rem", color:T.forest, fontWeight:"500", padding:"0.2rem 0.5rem", border:`1px solid ${T.forest}22`, borderRadius:"5px"}}>View</button>
                      {isOwner && <button onClick={(e)=>{ e.stopPropagation(); deleteItem(item.id); }} style={{...btnReset, fontSize:"0.7rem", color:T.danger, padding:"0.2rem 0.5rem", border:`1px solid ${T.danger}22`, borderRadius:"5px"}}>Delete</button>}
                    </div>
                  </div>
                );
              })
              ])}
              {isOwner && (
                <div onClick={()=>setShowAddItem(true)} style={{display:"flex", alignItems:"center", gap:"0.625rem", padding:"0 1rem", height:"48px", cursor:"pointer", background:"transparent", borderTop:`1px dashed ${T.border}`, opacity:0.5}}>
                  <span style={{fontSize:"1rem"}}>+</span>
                  <span style={{fontSize:"0.8rem", color:T.muted}}>Add item</span>
                </div>
              )}
            </div>
            )
          )}
        </div>
      </div>
    </AppShell>
  );
}

// ─── VAULT PROGRESS BANNER ────────────────────────────────────────────────────
function VaultProgressBanner({ items, vault, onOpenGuidedFlow }) {
  const hasBusiness = localStorage.getItem('heiros_vault_guided_gate_business') === 'yes';
  const vaultItems = vault?.items || [];

  const isSecured = (item) =>
    item.category && item.subcategory &&
    vaultItems.some(v => v.category === item.category && v.subcategory === item.subcategory);

  const noDocIds = (() => {
    try { return new Set(JSON.parse(localStorage.getItem('heiros_vault_guided_nodoc') || '[]')); } catch { return new Set(); }
  })();

  // Scoreable = non-gate, non-legacy, conditionals respected, noDoc items
  // excluded from the denominator entirely (confirmed not-applicable = resolved)
  const scoreable = items.filter(i =>
    i.kind !== 'gate' &&
    i.tier !== 'legacy' &&
    (i.conditional === null || hasBusiness) &&
    !noDocIds.has(i.id)
  );
  const securedCount = scoreable.filter(i => isSecured(i)).length;
  const totalCount = scoreable.length;
  const remaining = totalCount - securedCount;

  // Next item: first unsecured, non-noDoc in tier 1 then 2 then 3
  const nextItem = (() => {
    for (const tier of [1, 2, 3]) {
      const candidate = items.find(i =>
        i.tier === tier &&
        i.kind !== 'gate' &&
        (i.conditional === null || hasBusiness) &&
        !isSecured(i) &&
        !noDocIds.has(i.id)
      );
      if (candidate) return candidate;
    }
    return null;
  })();

  if (remaining === 0 && totalCount > 0) return null; // all done — hide banner

  const pct = totalCount > 0 ? Math.round((securedCount / totalCount) * 100) : 0;

  return (
    <div style={{background:'#f4f8f5', border:`1px solid ${T.forest}30`, borderLeft:`4px solid ${T.forest}`, borderRadius:'0 8px 8px 0', padding:'0.875rem 1.25rem', marginBottom:'1.25rem', display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap'}}>
      <div style={{flex:1, minWidth:'200px'}}>
        <div style={{display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.35rem'}}>
          <span style={{fontSize:'0.8rem', fontWeight:'600', color:T.forest}}>{securedCount} of {totalCount} items secured</span>
          <div style={{flex:1, height:'5px', background:`${T.forest}20`, borderRadius:'3px', overflow:'hidden', maxWidth:'120px'}}>
            <div style={{width:`${pct}%`, height:'100%', background:T.forest, borderRadius:'3px', transition:'width 0.3s'}} />
          </div>
          <span style={{fontSize:'0.75rem', color:T.muted}}>{pct}%</span>
        </div>
        {nextItem && (
          <p style={{fontSize:'0.78rem', color:T.muted, margin:0}}>
            <span style={{fontWeight:'500', color:T.charcoal}}>Next: </span>{nextItem.prompt}
          </p>
        )}
      </div>
      <button onClick={onOpenGuidedFlow} style={{...outlineBtn, fontSize:'0.78rem', padding:'0.4rem 0.875rem', color:T.forest, borderColor:T.forest, flexShrink:0, whiteSpace:'nowrap'}}>
        ✦ {securedCount === 0 ? 'Start Setup' : 'Continue Setup'}
      </button>
    </div>
  );
}

// ─── VAULT GUIDED FLOW ────────────────────────────────────────────────────────
function VaultGuidedFlow({ items, vault, estate, onAdd, onClose, estateSupabaseId, user, invitations }) {
  const GATE_KEY  = 'heiros_vault_guided_gate_business';
  const NODOC_KEY = 'heiros_vault_guided_nodoc';

  const [hasBusiness, setHasBusiness] = useState(() => {
    const v = localStorage.getItem(GATE_KEY);
    return v === 'yes' ? true : v === 'no' ? false : null; // null = unanswered
  });
  const [noDocIds, setNoDocIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(NODOC_KEY) || '[]')); } catch { return new Set(); }
  });
  const [skippedIds, setSkippedIds] = useState(new Set());
  const [addingItem, setAddingItem] = useState(null);     // {category, subcategory} | null
  const [noDocPending, setNoDocPending] = useState(null); // item showing noDocGuidance before confirming

  const vaultItems = vault?.items || [];

  const isSecured = (item) =>
    item.category && item.subcategory &&
    vaultItems.some(v => v.category === item.category && v.subcategory === item.subcategory);

  // Build the queue for the current session:
  //   1. Exclude legacy
  //   2. Gate item only appears if hasBusiness is unanswered (null)
  //   3. has_business conditional items only appear if hasBusiness === true
  //   4. Exclude secured items
  //   5. Exclude noDoc items (answered "I don't have this")
  //   6. Non-skipped items first (tier order), then skipped (tier order)
  const buildQueue = () => {
    const tierOrder = (t) => t === 1 ? 0 : t === 2 ? 1 : 2;
    const base = items.filter(i => {
      if (i.tier === 'legacy') return false;
      if (i.kind === 'gate') return hasBusiness === null;
      if (i.conditional === 'has_business' && !hasBusiness) return false;
      if (isSecured(i)) return false;
      if (noDocIds.has(i.id)) return false;
      return true;
    });
    const nonSkipped = base.filter(i => !skippedIds.has(i.id)).sort((a, b) => tierOrder(a.tier) - tierOrder(b.tier));
    const skipped    = base.filter(i =>  skippedIds.has(i.id)).sort((a, b) => tierOrder(a.tier) - tierOrder(b.tier));
    return [...nonSkipped, ...skipped];
  };

  const queue       = buildQueue();
  const currentItem = queue[0] || null;
  const legacyItems = items.filter(i => i.tier === 'legacy');

  // Progress counts (non-gate, non-legacy, conditionals respected).
  // noDoc items excluded from the denominator entirely —
  // "confirmed not applicable" is treated as resolved, not still-missing.
  const scoreable    = items.filter(i => i.kind !== 'gate' && i.tier !== 'legacy' && (i.conditional === null || hasBusiness === true) && !noDocIds.has(i.id));
  const securedCount = scoreable.filter(i => isSecured(i)).length;
  const totalCount   = scoreable.length;
  const pct          = totalCount > 0 ? Math.round((securedCount / totalCount) * 100) : 0;

  const markNoDoc = (item) => {
    const newSet = new Set(noDocIds);
    newSet.add(item.id);
    setNoDocIds(newSet);
    localStorage.setItem(NODOC_KEY, JSON.stringify([...newSet]));
    setNoDocPending(null);
  };

  const handleGateYes = () => { setHasBusiness(true);  localStorage.setItem(GATE_KEY, 'yes'); };
  const handleGateNo  = () => { setHasBusiness(false); localStorage.setItem(GATE_KEY, 'no');  };

  const handleHaveIt  = (item) => { setAddingItem({ category: item.category, subcategory: item.subcategory }); };

  const handleItemAdded = (newItem) => {
    onAdd(newItem);
    setAddingItem(null);
    // item drops out of queue automatically on next render because isSecured() returns true
  };

  const handleNoDocClick = (item) => {
    if (item.noDocGuidance) { setNoDocPending(item); }
    else                    { markNoDoc(item); }
  };

  const handleSkip = (item) => {
    // Move to back of queue; don't persist — resets when modal is closed
    if (queue.length > 1) setSkippedIds(prev => new Set([...prev, item.id]));
  };

  const tierLabel = (t) => t === 1 ? 'Priority 1 — Immediate Access' : t === 2 ? 'Priority 2 — Financial & Legal' : 'Priority 3 — Completeness';
  const tierColor = (t) => t === 1 ? '#c0392b' : t === 2 ? T.forest : '#7f8c8d';
  const allDone   = queue.length === 0;

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{...modalBox, maxWidth:'560px'}} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem'}}>
          <div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:'1.4rem', fontWeight:'700', color:T.forest, marginBottom:'0.25rem'}}>Vault Setup</h2>
            <p style={{fontSize:'0.8rem', color:T.muted, margin:0}}>{securedCount} of {totalCount} item{totalCount !== 1 ? 's' : ''} secured</p>
          </div>
          <button onClick={onClose} style={{...btnReset, color:T.muted, fontSize:'1.5rem', lineHeight:1, marginLeft:'1rem'}}>×</button>
        </div>

        {/* Progress bar */}
        <div style={{height:'6px', background:`${T.forest}15`, borderRadius:'4px', overflow:'hidden', marginBottom:'1.5rem'}}>
          <div style={{width:`${pct}%`, height:'100%', background:T.forest, borderRadius:'4px', transition:'width 0.4s'}} />
        </div>

        {/* VaultAddItemModal rendered on top when "I have it" is clicked */}
        {addingItem && (
          <VaultAddItemModal
            onAdd={handleItemAdded}
            onClose={() => setAddingItem(null)}
            initialCategory={addingItem.category}
            initialSubcategory={addingItem.subcategory}
            estateSupabaseId={estateSupabaseId}
            user={user}
            invitations={invitations}
            linkedSpouseEstateId={estate?.linked_spouse_estate_id ?? null}
          />
        )}

        {allDone ? (
          /* ── All done ── */
          <div style={{textAlign:'center', padding:'1.5rem 0'}}>
            <div style={{fontSize:'2.5rem', marginBottom:'0.75rem'}}>🎉</div>
            <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:'1.2rem', fontWeight:'700', color:T.forest, marginBottom:'0.5rem'}}>All priority items addressed!</h3>
            <p style={{fontSize:'0.85rem', color:T.muted, lineHeight:'1.6', marginBottom:'1.5rem'}}>Your vault is looking great. You can always add more items from the main vault view.</p>
            {legacyItems.length > 0 && (
              <div style={{textAlign:'left', borderTop:`1px solid ${T.border}`, paddingTop:'1.25rem'}}>
                <p style={{fontSize:'0.72rem', fontFamily:"'DM Mono', monospace", letterSpacing:'0.08em', color:T.muted, marginBottom:'0.875rem'}}>FOR YOUR FAMILY</p>
                {legacyItems.map(item => (
                  <div key={item.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.625rem 0', borderBottom:`1px solid ${T.border}40`, gap:'1rem'}}>
                    <p style={{fontSize:'0.85rem', color:T.charcoal, margin:0, flex:1}}>{item.prompt}</p>
                    <button
                      onClick={() => { if (item.category && item.subcategory) setAddingItem({ category: item.category, subcategory: item.subcategory }); }}
                      style={{...outlineBtn, fontSize:'0.75rem', padding:'0.3rem 0.75rem', color:T.forest, borderColor:T.forest, flexShrink:0}}>
                      Add
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={onClose} style={{...filledBtn, marginTop:'1.5rem', padding:'0.625rem 1.5rem', fontSize:'0.875rem'}}>Done</button>
          </div>

        ) : currentItem?.kind === 'gate' ? (
          /* ── Gate item — yes/no only ── */
          <div>
            <div style={{background:T.parchment, border:`1px solid ${T.border}`, borderRadius:'12px', padding:'1.25rem 1.5rem', marginBottom:'1.5rem'}}>
              <p style={{fontSize:'0.7rem', fontFamily:"'DM Mono', monospace", letterSpacing:'0.08em', color:'#7f8c8d', marginBottom:'0.625rem'}}>QUICK QUESTION</p>
              <p style={{fontSize:'1rem', fontWeight:'600', color:T.charcoal, lineHeight:'1.5', margin:0}}>{currentItem.prompt}</p>
            </div>
            <div style={{display:'flex', gap:'0.75rem'}}>
              <button onClick={handleGateYes} style={{...filledBtn, flex:1, padding:'0.75rem'}}>Yes</button>
              <button onClick={handleGateNo}  style={{...outlineBtn, flex:1, padding:'0.75rem', color:T.forest, borderColor:T.forest}}>No</button>
            </div>
          </div>

        ) : currentItem ? (
          /* ── Document item ── */
          <div>
            <div style={{background:T.parchment, border:`1px solid ${T.border}`, borderRadius:'12px', padding:'1.25rem 1.5rem', marginBottom:'1.25rem'}}>
              <p style={{fontSize:'0.7rem', fontFamily:"'DM Mono', monospace", letterSpacing:'0.08em', color:tierColor(currentItem.tier), marginBottom:'0.5rem'}}>{tierLabel(currentItem.tier)}</p>
              <p style={{fontSize:'1rem', fontWeight:'600', color:T.charcoal, lineHeight:'1.5', margin:0}}>{currentItem.prompt}</p>
            </div>

            {noDocPending?.id === currentItem.id ? (
              /* noDocGuidance inline — shown before confirming "move on" */
              <div style={{background:'#fef9f0', border:`1px solid ${T.tan}`, borderRadius:'10px', padding:'1rem 1.25rem', marginBottom:'1.25rem'}}>
                <p style={{fontSize:'0.82rem', color:T.charcoal, lineHeight:'1.65', marginBottom:'0.875rem'}}>
                  <span style={{fontWeight:'600'}}>Where to get it: </span>{noDocPending.noDocGuidance}
                </p>
                <div style={{display:'flex', gap:'0.625rem'}}>
                  <button onClick={() => markNoDoc(currentItem)} style={{...outlineBtn, fontSize:'0.78rem', padding:'0.4rem 0.875rem', color:T.muted, borderColor:T.border}}>Got it — move on</button>
                  <button onClick={() => setNoDocPending(null)} style={{...btnReset, fontSize:'0.78rem', color:T.muted, textDecoration:'underline'}}>Back</button>
                </div>
              </div>
            ) : (
              <div style={{display:'flex', flexDirection:'column', gap:'0.625rem'}}>
                <button onClick={() => handleHaveIt(currentItem)} style={{...filledBtn, padding:'0.75rem', fontSize:'0.875rem'}}>
                  ✓ I have it — add to vault
                </button>
                <button onClick={() => handleNoDocClick(currentItem)} style={{...outlineBtn, padding:'0.625rem', fontSize:'0.85rem', color:T.charcoal, borderColor:T.border}}>
                  {currentItem.noDocGuidance ? "I don't have this — show me where to get it" : "I don't have this"}
                </button>
                <button onClick={() => handleSkip(currentItem)} style={{...btnReset, fontSize:'0.78rem', color:T.muted, textDecoration:'underline', padding:'0.25rem', textAlign:'center'}}>
                  Skip for now
                </button>
              </div>
            )}

            <p style={{fontSize:'0.72rem', color:T.muted, marginTop:'1.25rem', textAlign:'center'}}>
              {Math.max(0, queue.length - 1)} more item{queue.length - 1 !== 1 ? 's' : ''} remaining
            </p>
          </div>
        ) : null}

      </div>
    </div>
  );
}

function VaultSetupModal({ onComplete, onClose, profile, user, estate }) {
  const defaultName = estate?.ownerName || estate?.owner_name || profile?.onboarding_data?.fullName || '';
  const defaultEmail = user?.email || '';
  const defaultDob = profile?.onboarding_data?.dob || estate?.vault_owner_dob || '';
  const [form, setForm] = useState({name: defaultName, dob: defaultDob, email: defaultEmail});

  // Pre-fill name from most recent invitation's invited_name if all higher-priority
  // sources are empty. Same priority and double-guard as the Onboarding component.
  useEffect(() => {
    if (form.name) return;
    if (!user?.email) return;
    supabase
      .from('invitations')
      .select('invited_name')
      .ilike('invited_email', user.email.toLowerCase().trim())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data: inv }) => {
        if (inv?.invited_name) {
          setForm(prev => prev.name ? prev : { ...prev, name: inv.invited_name });
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{...modalBox, maxWidth:"460px"}} onClick={e=>e.stopPropagation()}>
        <div style={{textAlign:"center", marginBottom:"1.75rem", position:"relative"}}>
          <button
            type="button"
            aria-label="Close vault setup"
            onClick={onClose}
            style={{...btnReset, position:"absolute", top:0, right:0, color:T.muted, fontSize:"1.5rem", lineHeight:1}}
          >
            ×
          </button>
          <div style={{fontSize:"2.5rem", marginBottom:"0.75rem"}}>🔒</div>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.5rem", fontWeight:"700", color:T.forest, marginBottom:"0.5rem"}}>Set Up Your Vault</h2>
          <p style={{fontSize:"0.85rem", color:T.muted, lineHeight:"1.6"}}>Your vault stores important information for your family. Only you can edit it. Your executor and trusted people get read-only access when the time comes.</p>
        </div>
        <div style={{display:"flex", flexDirection:"column", gap:"1rem", marginBottom:"1.5rem"}}>
          <div><label style={labelStyle}>Your Full Name</label><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="As it should appear to your family" style={inputStyle}/></div>
          <div><label style={labelStyle}>Date of Birth</label><input type="date" value={form.dob} onChange={e=>setForm({...form,dob:e.target.value})} style={inputStyle}/></div>
          <div><label style={labelStyle}>Email Address</label><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="for account recovery only" style={inputStyle}/></div>
        </div>
        <div style={{background:"#f0f9f4", border:"1px solid #c3e6cb", borderRadius:"8px", padding:"0.875rem", marginBottom:"1.5rem", fontSize:"0.78rem", color:T.success, lineHeight:"1.55"}}>
          🔒 Your vault is encrypted and private. Trusted people cannot access it until you grant permission. HeirOS staff cannot read your vault contents.
        </div>
        <button onClick={()=>{if(form.name)onComplete(form);}} disabled={!form.name} style={{...filledBtn, width:"100%", padding:"0.875rem", opacity:!form.name?0.5:1}}>Create My Vault →</button>
      </div>
    </div>
  );
}

function VaultAddItemModal({ onAdd, onClose, initialCategory, initialSubcategory, estateSupabaseId, user, invitations, linkedSpouseEstateId }) {
  const DRAFT_KEY = 'heiros_vault_draft';
  const [step, setStep] = useState(() => {
    if (initialCategory) return 1;
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (d?.form?.title && d.form.category) return 1;
    } catch {}
    return 0;
  });
  const [form, setForm] = useState(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (d?.form?.title) return d.form;
    } catch {}
    return { title:"", category:initialCategory||"", subcategory:initialSubcategory||"", notes:"", shared_with_medical_poa:false, is_joint:false, excludedInvitationIds:[] };
  });
  const [details, setDetails] = useState(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (d?.form?.title) return d.details || {};
    } catch {}
    return {};
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const [jointNudge, setJointNudge] = useState(null);

  // Direct fetch on mount so the joint checkbox is never hidden by a stale parent prop.
  // The parent's ownEstate.linked_spouse_estate_id may not have loaded yet when this modal
  // opens, so we query the DB ourselves and store the result in local state.
  const [resolvedLinkedSpouseEstateId, setResolvedLinkedSpouseEstateId] = useState(linkedSpouseEstateId);
  useEffect(() => {
    if (!estateSupabaseId) return;
    (async () => {
      const { data } = await supabase
        .from('estates')
        .select('linked_spouse_estate_id')
        .eq('id', estateSupabaseId)
        .maybeSingle();
      if (data?.linked_spouse_estate_id) setResolvedLinkedSpouseEstateId(data.linked_spouse_estate_id);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.is_joint || !form.category || !estateSupabaseId || !resolvedLinkedSpouseEstateId) {
      setJointNudge(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('vault_items')
        .select('id, title, estate_id')
        .eq('category', form.category)
        .not('joint_estate_id', 'is', null)
        .or(`estate_id.eq.${estateSupabaseId},estate_id.eq.${resolvedLinkedSpouseEstateId}`)
        .limit(1);
      if (!cancelled) setJointNudge(data?.length ? data[0] : null);
    })();
    return () => { cancelled = true; };
  }, [form.is_joint, form.category, estateSupabaseId, resolvedLinkedSpouseEstateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draft auto-save (debounced 500ms) — only when title is non-empty
  useEffect(() => {
    if (!form.title) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, details })); } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [form, details]);

  const handleClose = () => {
    if (form.title && !saved) { setDiscardConfirm(true); return; }
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setFileError('');
    setUploadError('');
    setSelectedFile(null);
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      setFileError('Only PDF, DOC, and DOCX files are accepted.');
      e.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setFileError('File must be 10 MB or smaller.');
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const analyzeBusinessDoc = async (file) => {
    if (file.type !== 'application/pdf') return null;
    const BUSINESS_DOC_PROMPT = "You are an estate planning assistant. Analyze this business ownership document and extract: 1) What happens to the ownership interest upon the owner's death, 2) Any buyout clauses or buy-sell agreement terms, 3) Valuation method for the ownership interest, 4) Right of first refusal provisions, 5) Any transfer restrictions. Format your response as a concise summary with clear section headers for each topic. If a topic is not addressed in the document, say 'Not specified'.";
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const messages = [{ role: "user", content: [
        { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
        { type: "text", text: "Please analyze this business ownership document as described." }
      ]}];
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        body: { messages, system: BUSINESS_DOC_PROMPT, max_tokens: 1500 }
      });
      if (error) throw error;
      return data?.content?.[0]?.text || null;
    } catch(e) {
      console.log('[HeirOS] AI doc analysis error:', e.message);
      return null;
    }
  };

  const handleSave = async () => {
    if (!form.title) return;
    let file_url = null;
    let file_name = null;
    let ai_analysis = null;
    if (selectedFile && estateSupabaseId && user) {
      setUploading(true);
      setUploadError('');
      try {
        const itemId = Date.now();
        const path = `estates/${estateSupabaseId}/vault/${itemId}/${selectedFile.name}`;
        const { error: upErr } = await supabase.storage.from('vault-files').upload(path, selectedFile, { upsert: true });
        if (upErr) {
          setUploadError(`Upload failed: ${upErr.message}`);
          setUploading(false);
          return;
        }
        const { data: signedData, error: signErr } = await supabase.storage.from('vault-files').createSignedUrl(path, 31536000);
        if (signErr || !signedData?.signedUrl) {
          setUploadError(`Could not get document URL: ${signErr?.message || 'unknown error'}`);
          setUploading(false);
          return;
        }
        file_url = signedData.signedUrl;
        file_name = selectedFile.name;
      } catch(e) {
        setUploadError(`Upload error: ${e.message}`);
        setUploading(false);
        return;
      }
      setUploading(false);
      if (form.category === 'business' && process.env.REACT_APP_ANTHROPIC_API_KEY) {
        setAiAnalyzing(true);
        ai_analysis = await analyzeBusinessDoc(selectedFile);
        setAiAnalyzing(false);
      }
    }
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setSaved(true);
    onAdd({ ...form, details, file_url, file_name, ai_analysis, excluded_invitation_ids: form.excludedInvitationIds || [], joint_estate_id: form.is_joint ? resolvedLinkedSpouseEstateId : null });
  };
  const cat = VAULT_CATEGORIES.find(c=>c.id===form.category);

  const detailFields = {
    financial: [{k:"institution",l:"Institution Name"},{k:"accountType",l:"Account Type"},{k:"accountNumber",l:"Account Number (last 4 ok)"},{k:"contact",l:"Contact / Phone"},{k:"notes2",l:"Additional Notes"}],
    insurance: [{k:"insurer",l:"Insurance Company"},{k:"policyNumber",l:"Policy Number"},{k:"coverage",l:"Coverage Amount"},{k:"agent",l:"Agent Name & Phone"},{k:"notes2",l:"Additional Notes"}],
    property:  [{k:"address",l:"Property Address"},{k:"lender",l:"Lender / Lienholder"},{k:"accountNumber",l:"Loan / Account Number"},{k:"contact",l:"Contact Phone"},{k:"notes2",l:"Additional Notes"}],
    business:  [{k:"companyName",l:"Company Name"},{k:"entityType",l:"Entity Type",options:["LLC","Corporation","Partnership","Sole Proprietorship"]},{k:"ownershipPct",l:"Ownership Percentage"},{k:"coOwners",l:"Co-Owners"},{k:"notes2",l:"Additional Notes"}],
    digital:   [{k:"platform",l:"Platform / Service"},{k:"username",l:"Username or Email"},{k:"password",l:"Password (optional)"},{k:"url",l:"Website URL"},{k:"notes2",l:"Additional Notes"}],
    medical:   [{k:"doctor",l:"Primary Doctor"},{k:"practice",l:"Practice / Hospital"},{k:"phone",l:"Phone Number"},{k:"conditions",l:"Key Conditions / Medications"},{k:"notes2",l:"Additional Notes"}],
    contacts:  [{k:"role",l:"Their Role (e.g. Attorney)"},{k:"fullName",l:"Full Name"},{k:"firm",l:"Firm / Organization"},{k:"phone",l:"Phone Number"},{k:"email2",l:"Email Address"}],
    personal:  [{k:"to",l:"To (recipient)"},{k:"message",l:"Your Message",multiline:true}],
    will:      [{k:"location",l:"Physical Location of Document"},{k:"attorney",l:"Attorney Who Drafted It"},{k:"phone",l:"Attorney Phone"},{k:"notes2",l:"Additional Notes"}],
    other:     [{k:"description",l:"Description"},{k:"location",l:"Location / Access Info"},{k:"notes2",l:"Additional Notes"}],
  };
  const fields = detailFields[form.category]||[];

  return (
    <div style={modalOverlay} onClick={handleClose}>
      <div style={{...modalBox, maxWidth:"540px", position:"relative"}} onClick={e=>e.stopPropagation()}>
        {discardConfirm && (
          <div style={{position:"absolute",inset:0,background:"rgba(255,255,255,0.97)",borderRadius:"inherit",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"2rem",zIndex:10}}>
            <p style={{fontFamily:"'Playfair Display', serif",fontSize:"1.1rem",fontWeight:"700",color:T.forest,marginBottom:"0.5rem",textAlign:"center"}}>Discard unsaved changes?</p>
            <p style={{fontSize:"0.85rem",color:T.muted,marginBottom:"1.5rem",textAlign:"center"}}>Your draft won't be saved.</p>
            <div style={{display:"flex",gap:"0.75rem"}}>
              <button onClick={()=>setDiscardConfirm(false)} style={{...outlineBtn,padding:"0.625rem 1.25rem"}}>Keep Editing</button>
              <button onClick={()=>{ try{localStorage.removeItem(DRAFT_KEY);}catch{} onClose(); }} style={{...btnReset,background:T.danger,color:"white",padding:"0.625rem 1.25rem",borderRadius:"10px",fontSize:"0.875rem",fontWeight:"600"}}>Discard</button>
            </div>
          </div>
        )}
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.35rem", fontWeight:"700", color:T.forest}}>{step===0?"What are you adding?":`Add ${cat?.label}`}</h2>
          <button onClick={handleClose} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
        </div>
        {step===0 ? (
          <>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0.625rem", marginBottom:"1.5rem"}}>
              {VAULT_CATEGORIES.map(c=>(
                <button key={c.id} onClick={()=>setForm({...form,category:c.id})} style={{...btnReset, padding:"0.875rem", borderRadius:"10px", border:`1px solid ${form.category===c.id?T.forest:T.border}`, background:form.category===c.id?T.parchment:"transparent", textAlign:"left", display:"flex", gap:"0.625rem", alignItems:"flex-start"}}>
                  <span style={{fontSize:"1.1rem", flexShrink:0}}>{c.icon}</span>
                  <div>
                    <div style={{fontSize:"0.8rem", fontWeight:"500", color:T.charcoal, lineHeight:"1.2", marginBottom:"0.125rem"}}>{c.label}</div>
                    <div style={{fontSize:"0.68rem", color:T.muted, lineHeight:"1.3"}}>{c.desc}</div>
                  </div>
                </button>
              ))}
            </div>
            <button onClick={()=>setStep(1)} disabled={!form.category} style={{...filledBtn, width:"100%", padding:"0.875rem", opacity:!form.category?0.5:1}}>Continue →</button>
          </>
        ) : (
          <>
            {form.title && !saved && (
              <div style={{background:"#fef9f0",border:"1px solid #f5ddb8",borderLeft:"3px solid #e8a020",borderRadius:"8px",padding:"0.5rem 0.875rem",fontSize:"0.79rem",color:"#7a5010",marginBottom:"0.875rem"}}>
                ⚠️ You have unsaved changes — click Save to keep this item
              </div>
            )}
            <div style={{display:"flex", flexDirection:"column", gap:"1rem", marginBottom:"1.5rem"}}>
              <div><label style={labelStyle}>Title / Label</label><input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder={`e.g. ${cat?.label} details`} style={inputStyle}/></div>
              {VAULT_SUBCATEGORIES[form.category]?.length > 0 && (
                <div>
                  <label style={labelStyle}>Subcategory</label>
                  <select value={form.subcategory||""} onChange={e=>setForm({...form,subcategory:e.target.value})} style={inputStyle}>
                    <option value="">Select a subcategory...</option>
                    {VAULT_SUBCATEGORIES[form.category].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
              {(form.category === 'medical' || form.category === 'will') && (
                <label style={{display:"flex", alignItems:"flex-start", gap:"0.75rem", cursor:"pointer", padding:"0.75rem", borderRadius:"10px", border:`1px solid ${form.shared_with_medical_poa ? "#1a6b8a" : T.border}`, background:form.shared_with_medical_poa ? "#f0f7fa" : "transparent", transition:"all 0.15s"}}>
                  <input type="checkbox" checked={!!form.shared_with_medical_poa} onChange={e=>setForm({...form,shared_with_medical_poa:e.target.checked})} style={{marginTop:"2px", accentColor:"#1a6b8a", flexShrink:0}} />
                  <div>
                    <div style={{fontSize:"0.85rem", fontWeight:"500", color:"#1a6b8a", marginBottom:"0.125rem"}}>Share with Medical Power of Attorney</div>
                    <div style={{fontSize:"0.75rem", color:T.muted, lineHeight:"1.45"}}>Invited Medical POA contacts will be able to see this document in their Medical Documents view.</div>
                  </div>
                </label>
              )}
              {resolvedLinkedSpouseEstateId && (
                <label style={{display:"flex", alignItems:"flex-start", gap:"0.75rem", cursor:"pointer", padding:"0.75rem", borderRadius:"10px", border:`1px solid ${form.is_joint ? T.forest : T.border}`, background:form.is_joint ? T.parchment : "transparent", transition:"all 0.15s"}}>
                  <input type="checkbox" checked={!!form.is_joint} onChange={e=>setForm({...form, is_joint:e.target.checked})} style={{marginTop:"2px", accentColor:T.forest, flexShrink:0}} />
                  <div>
                    <div style={{fontSize:"0.85rem", fontWeight:"500", color:T.forest, marginBottom:"0.125rem"}}>This is a joint asset or document</div>
                    <div style={{fontSize:"0.75rem", color:T.muted, lineHeight:"1.45"}}>It will appear in both of your vaults. Either partner can edit or delete it.</div>
                  </div>
                </label>
              )}
              {jointNudge && (
                <div style={{background:"#fefbf0", border:`1px solid ${T.tan}`, borderRadius:"8px", padding:"0.625rem 0.875rem", fontSize:"0.78rem", color:T.goldDark, lineHeight:"1.55"}}>
                  ⚠️ You already have a joint {cat?.label?.toLowerCase()} item: <strong>{jointNudge.title}</strong>
                  {jointNudge.estate_id !== estateSupabaseId ? ' (added by your partner)' : ''}.{' '}
                  <button onClick={onClose} style={{...btnReset, fontSize:"0.78rem", color:T.goldDark, fontWeight:"600", textDecoration:"underline"}}>
                    Close and find it →
                  </button>
                </div>
              )}
              {fields.map(f=>(
                <div key={f.k}><label style={labelStyle}>{f.l}</label>
                  {f.options
                    ? <select value={details[f.k]||""} onChange={e=>setDetails({...details,[f.k]:e.target.value})} style={inputStyle}>
                        <option value="">Select…</option>
                        {f.options.map(o=><option key={o} value={o}>{o}</option>)}
                      </select>
                    : f.multiline
                      ? <textarea value={details[f.k]||""} onChange={e=>setDetails({...details,[f.k]:e.target.value})} rows={5} style={{...inputStyle,resize:"vertical",lineHeight:"1.6"}}/>
                      : <input value={details[f.k]||""} onChange={e=>setDetails({...details,[f.k]:e.target.value})} style={inputStyle}/>}
                </div>
              ))}
              <div><label style={labelStyle}>General Notes</label><textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Any additional context for your family..." rows={3} style={{...inputStyle,resize:"vertical",lineHeight:"1.6"}}/></div>
              <div>
                <label style={labelStyle}>{form.category === 'business' ? 'Upload Ownership / Operating Agreement (Optional)' : 'Upload Document (Optional)'}</label>
                <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} style={{display:"block", width:"100%", fontSize:"0.82rem", color:T.charcoal, padding:"0.5rem 0"}} />
                {form.category === 'business' && <div style={{fontSize:"0.72rem", color:T.muted, marginTop:"0.25rem"}}>📋 PDF or Word · 10 MB max · AI will analyze the document automatically after upload</div>}
                {fileError && <div style={{marginTop:"0.375rem", fontSize:"0.78rem", color:T.danger}}>{fileError}</div>}
                {selectedFile && !fileError && <div style={{marginTop:"0.375rem", fontSize:"0.78rem", color:T.success}}>📄 {selectedFile.name}</div>}
                {uploadError && <div style={{marginTop:"0.375rem", fontSize:"0.78rem", color:T.danger, background:"#fff5f5", border:`1px solid ${T.danger}`, borderRadius:"6px", padding:"0.375rem 0.625rem"}}>{uploadError}</div>}
              </div>
              {(() => {
                const eligibleCollabs = (invitations || []).filter(inv =>
                  inv.role !== 'Named as executor' &&
                  (inv.status === 'pending' || inv.status === 'accepted') &&
                  inv.invited_name
                );
                // Deduplicate by invited_email so one person with multiple invitation rows
                // (e.g. "Couple Invite" + "Medical POA") appears as a single checkbox entry.
                const seen = {};
                eligibleCollabs.forEach(inv => {
                  const key = (inv.invited_email || inv.invited_name || '').toLowerCase();
                  if (!seen[key]) seen[key] = { name: inv.invited_name, email: inv.invited_email, ids: [], roles: [] };
                  seen[key].ids.push(inv.id);
                  seen[key].roles.push(inv.role);
                });
                const dedupedCollabs = Object.values(seen);
                if (!dedupedCollabs.length) return null;
                return (
                  <div>
                    <label style={labelStyle}>Do Not Share With</label>
                    <p style={{fontSize:"0.75rem", color:T.muted, marginBottom:"0.5rem", lineHeight:"1.5"}}>Check anyone who should <em>not</em> see this item. Executors always have access and cannot be excluded.</p>
                    <div style={{display:"flex", flexDirection:"column", gap:"0.375rem"}}>
                      {dedupedCollabs.map(entry => {
                        const currentIds = form.excludedInvitationIds || [];
                        const excluded = entry.ids.every(id => currentIds.includes(id));
                        return (
                          <label key={entry.email || entry.name} style={{display:"flex", gap:"0.75rem", alignItems:"center", cursor:"pointer", padding:"0.625rem 0.875rem", borderRadius:"8px", border:`1px solid ${excluded ? T.danger : T.border}`, background:excluded ? "#fff5f5" : "transparent", transition:"all 0.15s"}}>
                            <input type="checkbox" checked={excluded} onChange={e => {
                              const ids = form.excludedInvitationIds || [];
                              if (e.target.checked) {
                                const toAdd = entry.ids.filter(id => !ids.includes(id));
                                setForm({...form, excludedInvitationIds: [...ids, ...toAdd]});
                              } else {
                                setForm({...form, excludedInvitationIds: ids.filter(id => !entry.ids.includes(id))});
                              }
                            }} style={{accentColor:T.danger, flexShrink:0}} />
                            <div>
                              <div style={{fontSize:"0.84rem", fontWeight:"500", color: excluded ? T.danger : T.charcoal}}>{entry.name}</div>
                              <div style={{fontSize:"0.72rem", color:T.muted}}>{entry.roles.join(' · ')}{entry.email ? ` · ${entry.email}` : ''}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div style={{display:"flex", gap:"0.625rem"}}>
              {!initialCategory && <button onClick={()=>setStep(0)} disabled={uploading || aiAnalyzing} style={{...outlineBtn, flex:"0 0 auto"}}>← Back</button>}
              <button onClick={handleSave} disabled={!form.title || uploading || aiAnalyzing} style={{...filledBtn, flex:1, padding:"0.875rem", opacity:(!form.title || uploading || aiAnalyzing)?0.6:1}}>
                {aiAnalyzing ? '🤖 Analyzing document…' : uploading ? '⏳ Uploading document…' : 'Save to Vault →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function VaultItemModal({ item, isOwner, canDownload = true, onDelete, onClose, onUpdate, estateSupabaseId, user, invitations, linkedSpouseEstateId }) {
  const cat = VAULT_CATEGORIES.find(c=>c.id===item.category);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [makingJoint, setMakingJoint] = useState(false);

  // Direct fetch on mount so the "Make joint" button is never hidden by a stale parent prop.
  // The parent's ownEstate.linked_spouse_estate_id may not have loaded yet when this modal
  // opens (vault items and ownEstate both load on the same ownEstateId trigger and race),
  // so we query the DB ourselves and store the result in local state.
  const [resolvedLinkedSpouseEstateId, setResolvedLinkedSpouseEstateId] = useState(linkedSpouseEstateId);
  useEffect(() => {
    if (!estateSupabaseId) return;
    (async () => {
      const { data } = await supabase
        .from('estates')
        .select('linked_spouse_estate_id')
        .eq('id', estateSupabaseId)
        .maybeSingle();
      if (data?.linked_spouse_estate_id) setResolvedLinkedSpouseEstateId(data.linked_spouse_estate_id);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Direct fetch on mount for the "Added by you/partner" badge. activeEstateSupabaseId in the
  // parent derives from ownEstateId, which is set by an async buildAccessibleEstates query
  // and may be null/stale when this modal renders. We re-fetch the auth user's own estate
  // id directly so the badge comparison is always correct.
  const [resolvedOwnEstateId, setResolvedOwnEstateId] = useState(estateSupabaseId);
  useEffect(() => {
    (async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;
      const { data } = await supabase
        .from('estates')
        .select('id')
        .eq('user_id', authUser.id)
        .limit(1)
        .maybeSingle();
      if (data?.id) setResolvedOwnEstateId(data.id);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Edit mode state ──────────────────────────────────────────────────────
  const EDIT_DRAFT_KEY = `heiros_vault_edit_draft_${item.id}`;
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    title: item.title || '',
    category: item.category || '',
    subcategory: item.subcategory || '',
    notes: item.notes || '',
    shared_with_medical_poa: item.shared_with_medical_poa === true,
    is_joint: !!item.joint_estate_id,
    excludedInvitationIds: item.excluded_invitation_ids || [],
  });
  const [details, setDetails] = useState(item.details || {});
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(false);

  // Draft auto-save while in edit mode
  useEffect(() => {
    if (!editMode) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(EDIT_DRAFT_KEY, JSON.stringify({ form, details })); } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [editMode, form, details, EDIT_DRAFT_KEY]);

  const enterEditMode = () => {
    // Always re-initialize from current item prop, then check for a saved draft
    const baseForm = {
      title: item.title || '',
      category: item.category || '',
      subcategory: item.subcategory || '',
      notes: item.notes || '',
      shared_with_medical_poa: item.shared_with_medical_poa === true,
      is_joint: !!item.joint_estate_id,
      excludedInvitationIds: item.excluded_invitation_ids || [],
    };
    const baseDetails = item.details || {};
    try {
      const d = JSON.parse(localStorage.getItem(EDIT_DRAFT_KEY) || 'null');
      if (d?.form?.title) { setForm(d.form); setDetails(d.details || {}); }
      else { setForm(baseForm); setDetails(baseDetails); }
    } catch { setForm(baseForm); setDetails(baseDetails); }
    setSelectedFile(null); setFileError(''); setUploadError('');
    setDiscardConfirm(false);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    // Check for unsaved changes
    const dirty = form.title !== item.title || form.category !== item.category ||
      form.subcategory !== (item.subcategory || '') || form.notes !== (item.notes || '') ||
      form.shared_with_medical_poa !== (item.shared_with_medical_poa === true) ||
      JSON.stringify(form.excludedInvitationIds) !== JSON.stringify(item.excluded_invitation_ids || []) ||
      JSON.stringify(details) !== JSON.stringify(item.details || {}) ||
      selectedFile !== null;
    if (dirty) { setDiscardConfirm(true); return; }
    try { localStorage.removeItem(EDIT_DRAFT_KEY); } catch {}
    setEditMode(false);
  };

  const confirmDiscard = () => {
    try { localStorage.removeItem(EDIT_DRAFT_KEY); } catch {}
    setDiscardConfirm(false);
    setEditMode(false);
  };

  const handleMakeJoint = async () => {
    if (!resolvedLinkedSpouseEstateId) return;
    setMakingJoint(true);
    const { error } = await supabase
      .from('vault_items')
      .update({ joint_estate_id: resolvedLinkedSpouseEstateId })
      .eq('id', item.id)
      .eq('user_id', user.id);
    setMakingJoint(false);
    if (!error) onUpdate({ ...item, joint_estate_id: resolvedLinkedSpouseEstateId });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setFileError(''); setUploadError(''); setSelectedFile(null);
    if (!file) return;
    const allowed = ['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) { setFileError('Only PDF, DOC, and DOCX files are accepted.'); e.target.value=''; return; }
    if (file.size > 10*1024*1024) { setFileError('File must be 10 MB or smaller.'); e.target.value=''; return; }
    setSelectedFile(file);
  };

  const analyzeBusinessDoc = async (file) => {
    if (file.type !== 'application/pdf') return null;
    const BUSINESS_DOC_PROMPT = "You are an estate planning assistant. Analyze this business ownership document and extract: 1) What happens to the ownership interest upon the owner's death, 2) Any buyout clauses or buy-sell agreement terms, 3) Valuation method for the ownership interest, 4) Right of first refusal provisions, 5) Any transfer restrictions. Format your response as a concise summary with clear section headers for each topic. If a topic is not addressed in the document, say 'Not specified'.";
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        body: { messages:[{ role:"user", content:[
          { type:"document", source:{ type:"base64", media_type:"application/pdf", data:base64 } },
          { type:"text", text:"Please analyze this business ownership document as described." }
        ]}], system:BUSINESS_DOC_PROMPT, max_tokens:1500 }
      });
      if (error) throw error;
      return data?.content?.[0]?.text || null;
    } catch(e) { console.log('[HeirOS] AI doc analysis error:', e.message); return null; }
  };

  const handleEditSave = async () => {
    if (!form.title) return;
    setSaving(true);
    let file_url = item.file_url;
    let file_name = item.file_name;
    let ai_analysis = item.ai_analysis;

    if (selectedFile && estateSupabaseId && user) {
      setUploading(true); setUploadError('');
      try {
        const path = `estates/${estateSupabaseId}/vault/${item.id}/${selectedFile.name}`;
        const { error: upErr } = await supabase.storage.from('vault-files').upload(path, selectedFile, { upsert: true });
        if (upErr) { setUploadError(`Upload failed: ${upErr.message}`); setUploading(false); setSaving(false); return; }
        const { data: signedData, error: signErr } = await supabase.storage.from('vault-files').createSignedUrl(path, 31536000);
        if (signErr || !signedData?.signedUrl) { setUploadError(`Could not get document URL: ${signErr?.message||'unknown error'}`); setUploading(false); setSaving(false); return; }
        // Delete old file if name changed (same folder, different filename)
        if (item.file_name && item.file_name !== selectedFile.name) {
          const oldPath = `estates/${estateSupabaseId}/vault/${item.id}/${item.file_name}`;
          await supabase.storage.from('vault-files').remove([oldPath]);
        }
        file_url = signedData.signedUrl;
        file_name = selectedFile.name;
      } catch(e) { setUploadError(`Upload error: ${e.message}`); setUploading(false); setSaving(false); return; }
      setUploading(false);
      // Re-run AI analysis for business category when file is replaced
      if (form.category === 'business' && process.env.REACT_APP_ANTHROPIC_API_KEY) {
        setAiAnalyzing(true);
        ai_analysis = await analyzeBusinessDoc(selectedFile);
        setAiAnalyzing(false);
      } else if (form.category === 'business') {
        ai_analysis = null;
      }
    }

    const { error: dbErr } = await supabase.from('vault_items').update({
      title: form.title,
      category: form.category,
      subcategory: form.subcategory || null,
      notes: form.notes || null,
      shared_with_medical_poa: form.shared_with_medical_poa === true,
      details: details || {},
      file_url,
      file_name,
      ai_analysis,
      excluded_invitation_ids: form.excludedInvitationIds || [],
      joint_estate_id: form.is_joint ? resolvedLinkedSpouseEstateId : null,
    }).eq('id', item.id);

    if (dbErr) { setUploadError(`Save failed: ${dbErr.message}`); setSaving(false); return; }

    try { localStorage.removeItem(EDIT_DRAFT_KEY); } catch {}
    const updatedItem = {
      ...item,
      title: form.title,
      category: form.category,
      subcategory: form.subcategory || null,
      notes: form.notes || null,
      shared_with_medical_poa: form.shared_with_medical_poa === true,
      details: details || {},
      file_url, file_name, ai_analysis,
      excluded_invitation_ids: form.excludedInvitationIds || [],
      joint_estate_id: form.is_joint ? resolvedLinkedSpouseEstateId : null,
    };
    setSaving(false);
    onUpdate(updatedItem);
    setEditMode(false);
  };

  const editCat = VAULT_CATEGORIES.find(c=>c.id===form.category);
  const detailFields = {
    financial: [{k:"institution",l:"Institution Name"},{k:"accountType",l:"Account Type"},{k:"accountNumber",l:"Account Number (last 4 ok)"},{k:"contact",l:"Contact / Phone"},{k:"notes2",l:"Additional Notes"}],
    insurance: [{k:"insurer",l:"Insurance Company"},{k:"policyNumber",l:"Policy Number"},{k:"coverage",l:"Coverage Amount"},{k:"agent",l:"Agent Name & Phone"},{k:"notes2",l:"Additional Notes"}],
    property:  [{k:"address",l:"Property Address"},{k:"lender",l:"Lender / Lienholder"},{k:"accountNumber",l:"Loan / Account Number"},{k:"contact",l:"Contact Phone"},{k:"notes2",l:"Additional Notes"}],
    business:  [{k:"companyName",l:"Company Name"},{k:"entityType",l:"Entity Type",options:["LLC","Corporation","Partnership","Sole Proprietorship"]},{k:"ownershipPct",l:"Ownership Percentage"},{k:"coOwners",l:"Co-Owners"},{k:"notes2",l:"Additional Notes"}],
    digital:   [{k:"platform",l:"Platform / Service"},{k:"username",l:"Username or Email"},{k:"password",l:"Password (optional)"},{k:"url",l:"Website URL"},{k:"notes2",l:"Additional Notes"}],
    medical:   [{k:"doctor",l:"Primary Doctor"},{k:"practice",l:"Practice / Hospital"},{k:"phone",l:"Phone Number"},{k:"conditions",l:"Key Conditions / Medications"},{k:"notes2",l:"Additional Notes"}],
    contacts:  [{k:"role",l:"Their Role (e.g. Attorney)"},{k:"fullName",l:"Full Name"},{k:"firm",l:"Firm / Organization"},{k:"phone",l:"Phone Number"},{k:"email2",l:"Email Address"}],
    personal:  [{k:"to",l:"To (recipient)"},{k:"message",l:"Your Message",multiline:true}],
    will:      [{k:"location",l:"Physical Location of Document"},{k:"attorney",l:"Attorney Who Drafted It"},{k:"phone",l:"Attorney Phone"},{k:"notes2",l:"Additional Notes"}],
    other:     [{k:"description",l:"Description"},{k:"location",l:"Location / Access Info"},{k:"notes2",l:"Additional Notes"}],
  };
  const fields = detailFields[form.category] || [];

  // ── Edit mode UI ──────────────────────────────────────────────────────────
  if (editMode) {
    return (
      <div style={modalOverlay} onClick={handleCancelEdit}>
        <div style={{...modalBox, maxWidth:"540px", position:"relative"}} onClick={e=>e.stopPropagation()}>
          {discardConfirm && (
            <div style={{position:"absolute",inset:0,background:"rgba(255,255,255,0.97)",borderRadius:"inherit",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"2rem",zIndex:10}}>
              <p style={{fontFamily:"'Playfair Display', serif",fontSize:"1.1rem",fontWeight:"700",color:T.forest,marginBottom:"0.5rem",textAlign:"center"}}>Discard unsaved changes?</p>
              <p style={{fontSize:"0.85rem",color:T.muted,marginBottom:"1.5rem",textAlign:"center"}}>Your edits won't be saved.</p>
              <div style={{display:"flex",gap:"0.75rem"}}>
                <button onClick={()=>setDiscardConfirm(false)} style={{...outlineBtn,padding:"0.625rem 1.25rem"}}>Keep Editing</button>
                <button onClick={confirmDiscard} style={{...btnReset,background:T.danger,color:"white",padding:"0.625rem 1.25rem",borderRadius:"10px",fontSize:"0.875rem",fontWeight:"600"}}>Discard</button>
              </div>
            </div>
          )}
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem"}}>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.35rem", fontWeight:"700", color:T.forest}}>Edit {editCat?.label || 'Item'}</h2>
            <button onClick={handleCancelEdit} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
          </div>
          <div style={{background:"#fef9f0",border:"1px solid #f5ddb8",borderLeft:"3px solid #e8a020",borderRadius:"8px",padding:"0.5rem 0.875rem",fontSize:"0.79rem",color:"#7a5010",marginBottom:"0.875rem"}}>
            ⚠️ You have unsaved changes — click Save to keep them
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:"1rem", marginBottom:"1.5rem"}}>
            <div><label style={labelStyle}>Title / Label</label><input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} style={inputStyle}/></div>
            {resolvedLinkedSpouseEstateId && (
              <label style={{display:"flex", alignItems:"flex-start", gap:"0.75rem", cursor:"pointer", padding:"0.75rem", borderRadius:"10px", border:`1px solid ${form.is_joint ? T.forest : T.border}`, background:form.is_joint ? T.parchment : "transparent", transition:"all 0.15s"}}>
                <input type="checkbox" checked={!!form.is_joint} onChange={e=>setForm({...form, is_joint:e.target.checked})} style={{marginTop:"2px", accentColor:T.forest, flexShrink:0}} />
                <div>
                  <div style={{fontSize:"0.85rem", fontWeight:"500", color:T.forest, marginBottom:"0.125rem"}}>This is a joint asset or document</div>
                  <div style={{fontSize:"0.75rem", color:T.muted, lineHeight:"1.45"}}>It will appear in both of your vaults. Either partner can edit or delete it.</div>
                </div>
              </label>
            )}
            <div>
              <label style={labelStyle}>Category</label>
              <select value={form.category} onChange={e=>setForm({...form,category:e.target.value,subcategory:''})} style={inputStyle}>
                {VAULT_CATEGORIES.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </select>
            </div>
            {VAULT_SUBCATEGORIES[form.category]?.length > 0 && (
              <div>
                <label style={labelStyle}>Subcategory</label>
                <select value={form.subcategory||""} onChange={e=>setForm({...form,subcategory:e.target.value})} style={inputStyle}>
                  <option value="">Select a subcategory...</option>
                  {VAULT_SUBCATEGORIES[form.category].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {(form.category === 'medical' || form.category === 'will') && (
              <label style={{display:"flex", alignItems:"flex-start", gap:"0.75rem", cursor:"pointer", padding:"0.75rem", borderRadius:"10px", border:`1px solid ${form.shared_with_medical_poa ? "#1a6b8a" : T.border}`, background:form.shared_with_medical_poa ? "#f0f7fa" : "transparent", transition:"all 0.15s"}}>
                <input type="checkbox" checked={!!form.shared_with_medical_poa} onChange={e=>setForm({...form,shared_with_medical_poa:e.target.checked})} style={{marginTop:"2px", accentColor:"#1a6b8a", flexShrink:0}} />
                <div>
                  <div style={{fontSize:"0.85rem", fontWeight:"500", color:"#1a6b8a", marginBottom:"0.125rem"}}>Share with Medical Power of Attorney</div>
                  <div style={{fontSize:"0.75rem", color:T.muted, lineHeight:"1.45"}}>Invited Medical POA contacts will be able to see this document in their Medical Documents view.</div>
                </div>
              </label>
            )}
            {fields.map(f=>(
              <div key={f.k}><label style={labelStyle}>{f.l}</label>
                {f.options
                  ? <select value={details[f.k]||""} onChange={e=>setDetails({...details,[f.k]:e.target.value})} style={inputStyle}>
                      <option value="">Select…</option>
                      {f.options.map(o=><option key={o} value={o}>{o}</option>)}
                    </select>
                  : f.multiline
                    ? <textarea value={details[f.k]||""} onChange={e=>setDetails({...details,[f.k]:e.target.value})} rows={5} style={{...inputStyle,resize:"vertical",lineHeight:"1.6"}}/>
                    : <input value={details[f.k]||""} onChange={e=>setDetails({...details,[f.k]:e.target.value})} style={inputStyle}/>}
              </div>
            ))}
            <div><label style={labelStyle}>General Notes</label><textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Any additional context for your family..." rows={3} style={{...inputStyle,resize:"vertical",lineHeight:"1.6"}}/></div>
            <div>
              <label style={labelStyle}>Document</label>
              {item.file_name && !selectedFile && (
                <div style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.625rem 0.875rem", borderRadius:"8px", border:`1px solid ${T.border}`, background:"#f9f9f7", marginBottom:"0.5rem"}}>
                  <span style={{fontSize:"1rem"}}>📄</span>
                  <span style={{fontSize:"0.82rem", color:T.charcoal, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{item.file_name}</span>
                  <label style={{fontSize:"0.78rem", color:T.forest, fontWeight:"500", cursor:"pointer", flexShrink:0}}>
                    Replace File
                    <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} style={{display:"none"}} />
                  </label>
                </div>
              )}
              {!item.file_name && (
                <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} style={{display:"block", width:"100%", fontSize:"0.82rem", color:T.charcoal, padding:"0.5rem 0"}} />
              )}
              {form.category === 'business' && <div style={{fontSize:"0.72rem", color:T.muted, marginTop:"0.25rem"}}>📋 PDF or Word · 10 MB max · AI will re-analyze the document if replaced</div>}
              {fileError && <div style={{marginTop:"0.375rem", fontSize:"0.78rem", color:T.danger}}>{fileError}</div>}
              {selectedFile && !fileError && (
                <div style={{marginTop:"0.5rem", display:"flex", alignItems:"center", gap:"0.625rem", padding:"0.5rem 0.875rem", borderRadius:"8px", border:`1px solid ${T.success}`, background:"#f0f9f4"}}>
                  <span>📄</span>
                  <span style={{fontSize:"0.82rem", color:T.charcoal, flex:1}}>{selectedFile.name}</span>
                  <button onClick={()=>setSelectedFile(null)} style={{...btnReset, fontSize:"0.78rem", color:T.muted}}>✕ Remove</button>
                </div>
              )}
              {uploadError && <div style={{marginTop:"0.375rem", fontSize:"0.78rem", color:T.danger, background:"#fff5f5", border:`1px solid ${T.danger}`, borderRadius:"6px", padding:"0.375rem 0.625rem"}}>{uploadError}</div>}
            </div>
            {(() => {
              const eligibleCollabs = (invitations || []).filter(inv =>
                inv.role !== 'Named as executor' &&
                (inv.status === 'pending' || inv.status === 'accepted') &&
                inv.invited_name
              );
              // Deduplicate by invited_email so one person with multiple invitation rows
              // (e.g. "Couple Invite" + "Medical POA") appears as a single checkbox entry.
              const seen = {};
              eligibleCollabs.forEach(inv => {
                const key = (inv.invited_email || inv.invited_name || '').toLowerCase();
                if (!seen[key]) seen[key] = { name: inv.invited_name, email: inv.invited_email, ids: [], roles: [] };
                seen[key].ids.push(inv.id);
                seen[key].roles.push(inv.role);
              });
              const dedupedCollabs = Object.values(seen);
              if (!dedupedCollabs.length) return null;
              return (
                <div>
                  <label style={labelStyle}>Do Not Share With</label>
                  <p style={{fontSize:"0.75rem", color:T.muted, marginBottom:"0.5rem", lineHeight:"1.5"}}>Check anyone who should <em>not</em> see this item. Executors always have access and cannot be excluded.</p>
                  <div style={{display:"flex", flexDirection:"column", gap:"0.375rem"}}>
                    {dedupedCollabs.map(entry => {
                      const currentIds = form.excludedInvitationIds || [];
                      const excluded = entry.ids.every(id => currentIds.includes(id));
                      return (
                        <label key={entry.email || entry.name} style={{display:"flex", gap:"0.75rem", alignItems:"center", cursor:"pointer", padding:"0.625rem 0.875rem", borderRadius:"8px", border:`1px solid ${excluded ? T.danger : T.border}`, background:excluded ? "#fff5f5" : "transparent", transition:"all 0.15s"}}>
                          <input type="checkbox" checked={excluded} onChange={e => {
                            const ids = form.excludedInvitationIds || [];
                            if (e.target.checked) {
                              const toAdd = entry.ids.filter(id => !ids.includes(id));
                              setForm({...form, excludedInvitationIds: [...ids, ...toAdd]});
                            } else {
                              setForm({...form, excludedInvitationIds: ids.filter(id => !entry.ids.includes(id))});
                            }
                          }} style={{accentColor:T.danger, flexShrink:0}} />
                          <div>
                            <div style={{fontSize:"0.84rem", fontWeight:"500", color:excluded ? T.danger : T.charcoal}}>{entry.name}</div>
                            <div style={{fontSize:"0.72rem", color:T.muted}}>{entry.roles.join(' · ')}{entry.email ? ` · ${entry.email}` : ''}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
          <div style={{display:"flex", gap:"0.625rem"}}>
            <button onClick={handleCancelEdit} disabled={saving||uploading||aiAnalyzing} style={{...outlineBtn, flex:"0 0 auto", padding:"0.875rem 1.25rem"}}>Cancel</button>
            <button onClick={handleEditSave} disabled={!form.title||saving||uploading||aiAnalyzing} style={{...filledBtn, flex:1, padding:"0.875rem", opacity:(!form.title||saving||uploading||aiAnalyzing)?0.6:1}}>
              {aiAnalyzing ? '🤖 Analyzing document…' : uploading ? '⏳ Uploading document…' : saving ? '⏳ Saving…' : 'Save Changes →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── View mode UI ──────────────────────────────────────────────────────────
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{...modalBox, maxWidth:"520px"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.5rem"}}>
          <div style={{display:"flex", gap:"0.75rem", alignItems:"center"}}>
            <span style={{fontSize:"1.75rem"}}>{cat?.icon}</span>
            <div>
              <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.2rem", fontWeight:"700", color:T.forest}}>{item.title}</h2>
              <p style={{fontSize:"0.78rem", color:T.muted}}>{cat?.label}</p>
            </div>
          </div>
          <button onClick={onClose} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
        </div>
        {!isOwner && <div style={{background:"#f0f9f4", border:"1px solid #c3e6cb", borderRadius:"8px", padding:"0.625rem 0.875rem", marginBottom:"1.25rem", fontSize:"0.75rem", color:T.success}}>👁 Read-only — you cannot edit or delete this item</div>}
        <div style={{display:"flex", flexDirection:"column", gap:"0.875rem", marginBottom:"1.5rem"}}>
          {item.details && Object.entries(item.details).filter(([,v])=>v).map(([k,v])=>(
            <div key={k} style={{borderBottom:`1px solid ${T.borderLight}`, paddingBottom:"0.75rem"}}>
              <div style={{fontSize:"0.68rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", color:T.muted, marginBottom:"0.25rem", textTransform:"uppercase"}}>{k.replace(/([A-Z])/g,' $1').replace(/^./,s=>s.toUpperCase())}</div>
              <div style={{fontSize:"0.9rem", color:T.charcoal, lineHeight:"1.6", whiteSpace:"pre-wrap"}}>{v}</div>
            </div>
          ))}
          {item.notes && <div><div style={{fontSize:"0.68rem", fontFamily:"'DM Mono', monospace", letterSpacing:"0.06em", color:T.muted, marginBottom:"0.25rem"}}>NOTES</div><div style={{fontSize:"0.875rem", color:T.charcoal, lineHeight:"1.65"}}>{item.notes}</div></div>}
          {item.file_url && canDownload && (
            <div>
              <a href={item.file_url} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex", alignItems:"center", gap:"0.375rem", fontSize:"0.82rem", color:T.forest, fontWeight:"500", textDecoration:"none", border:`1px solid ${T.forest}`, borderRadius:"6px", padding:"0.375rem 0.75rem"}}>
                ⬇️ Download Document{item.file_name ? ` — ${item.file_name}` : ''}
              </a>
            </div>
          )}
          {item.file_url && !canDownload && (
            <div style={{fontSize:"0.78rem", color:T.muted, padding:"0.375rem 0"}}>
              🔒 Download not permitted for your access level
            </div>
          )}
          {item.ai_analysis && (
            <div style={{background:"#f0f9f4", border:`1px solid ${T.forest}30`, borderRadius:"10px", padding:"1rem", marginTop:"0.5rem"}}>
              <div style={{fontWeight:"600", color:T.forest, fontSize:"0.85rem", marginBottom:"0.625rem"}}>📋 AI Document Analysis</div>
              <div style={{fontSize:"0.82rem", color:T.charcoal, lineHeight:"1.7", whiteSpace:"pre-wrap"}}>{item.ai_analysis}</div>
            </div>
          )}
        </div>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:"1rem", borderTop:`1px solid ${T.borderLight}`}}>
          <div style={{fontSize:"0.72rem", color:T.muted, fontFamily:"'DM Mono', monospace", display:"flex", alignItems:"center", gap:"0.5rem", flexWrap:"wrap"}}>
            <span>Added {new Date(item.createdAt).toLocaleDateString()}</span>
            {item.joint_estate_id && (
              <span style={{fontSize:"0.7rem", background:T.parchment, border:`1px solid ${T.tan}`, borderRadius:"4px", padding:"0.1rem 0.375rem", color:T.goldDark, fontFamily:"'DM Mono', monospace"}}>
                {item.estate_id === resolvedOwnEstateId ? 'Added by you' : 'Added by your partner'}
              </span>
            )}
          </div>
          {isOwner && (
            <div style={{display:"flex", gap:"0.625rem", alignItems:"center"}}>
              {resolvedLinkedSpouseEstateId && !item.joint_estate_id && (
                <button onClick={handleMakeJoint} disabled={makingJoint} style={{...outlineBtn, fontSize:"0.78rem", padding:"0.3rem 0.875rem", color:T.forest, borderColor:T.forest, opacity:makingJoint?0.6:1}}>
                  {makingJoint ? '⏳ Saving…' : '🔗 Make joint'}
                </button>
              )}
              <button onClick={enterEditMode} style={{...outlineBtn, fontSize:"0.78rem", padding:"0.3rem 0.875rem"}}>Edit</button>
              {confirmDelete
                ? <div style={{display:"flex", gap:"0.5rem", alignItems:"center"}}>
                    <span style={{fontSize:"0.78rem", color:T.danger}}>
                      {item.joint_estate_id
                        ? 'This will remove it from both vaults. Continue?'
                        : 'Delete this item?'}
                    </span>
                    <button onClick={()=>onDelete(item.id)} style={{...btnReset, fontSize:"0.78rem", color:T.danger, fontWeight:"600", border:`1px solid ${T.danger}`, borderRadius:"6px", padding:"0.3rem 0.625rem"}}>Yes, delete</button>
                    <button onClick={()=>setConfirmDelete(false)} style={{...btnReset, fontSize:"0.78rem", color:T.muted}}>Cancel</button>
                  </div>
                : <button onClick={()=>setConfirmDelete(true)} style={{...btnReset, fontSize:"0.78rem", color:T.danger}}>Delete item</button>
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VaultInviteModal({ onInvite, onClose, sent }) {
  const [form, setForm] = useState({name:"", email:"", access:"executor", categories:[]});
  const toggleCat = (id) => setForm(f=>({...f, categories:f.categories.includes(id)?f.categories.filter(c=>c!==id):[...f.categories,id]}));
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{...modalBox, maxWidth:"500px"}} onClick={e=>e.stopPropagation()}>
        {sent ? (
          <div style={{textAlign:"center", padding:"2rem 0"}}>
            <div style={{fontSize:"3rem", marginBottom:"1rem"}}>✉️</div>
            <h3 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, marginBottom:"0.75rem"}}>Invitation sent!</h3>
            <p style={{color:T.muted, fontSize:"0.875rem", lineHeight:"1.65", marginBottom:"0.5rem"}}><strong>{form.name}</strong> has been added with <strong>{VAULT_ACCESS[form.access]?.label}</strong> access.</p>
            <p style={{color:T.muted, fontSize:"0.82rem", marginBottom:"1.5rem"}}>In the full version they'll receive a secure email to accept their role.</p>
            <button onClick={onClose} style={filledBtn}>Done</button>
          </div>
        ) : (
          <>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem"}}>
              <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.35rem", fontWeight:"700", color:T.forest}}>Grant Vault Access</h2>
              <button onClick={onClose} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:"1rem", marginBottom:"1.5rem"}}>
              <div><label style={labelStyle}>Their Full Name</label><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. James Johnson" style={inputStyle}/></div>
              <div><label style={labelStyle}>Their Email Address</label><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="james@email.com" style={inputStyle}/></div>
              <div>
                <label style={labelStyle}>Access Level</label>
                <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
                  {Object.entries(VAULT_ACCESS).map(([key,val])=>(
                    <label key={key} style={{display:"flex", gap:"0.75rem", alignItems:"flex-start", cursor:"pointer", padding:"0.625rem", borderRadius:"8px", border:`1px solid ${form.access===key?val.color:T.border}`, background:form.access===key?val.color+"10":"transparent"}}>
                      <input type="radio" name="inv_access" value={key} checked={form.access===key} onChange={()=>setForm({...form,access:key})} style={{marginTop:"2px"}}/>
                      <div><div style={{fontSize:"0.85rem", fontWeight:"500", color:val.color}}>{val.label}</div><div style={{fontSize:"0.75rem", color:T.muted}}>{val.desc}</div></div>
                    </label>
                  ))}
                </div>
              </div>
              {form.access==="trusted" && (
                <div>
                  <label style={labelStyle}>Which Categories Can They See?</label>
                  <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0.375rem"}}>
                    {VAULT_CATEGORIES.map(c=>(
                      <label key={c.id} style={{display:"flex", gap:"0.5rem", alignItems:"center", cursor:"pointer", padding:"0.5rem 0.625rem", borderRadius:"7px", border:`1px solid ${form.categories.includes(c.id)?T.forest:T.border}`, background:form.categories.includes(c.id)?T.parchment:"transparent", fontSize:"0.78rem"}}>
                        <input type="checkbox" checked={form.categories.includes(c.id)} onChange={()=>toggleCat(c.id)}/>
                        <span>{c.icon}</span><span style={{color:T.charcoal}}>{c.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{background:"#fefbf0", border:`1px solid ${T.tan}`, borderRadius:"8px", padding:"0.75rem", marginBottom:"1.25rem", fontSize:"0.75rem", color:T.goldDark, lineHeight:"1.55"}}>
              ⚠ They will receive a secure invitation. They cannot access your vault until they accept. You can revoke access at any time.
            </div>
            <button onClick={()=>{if(form.name&&form.email)onInvite(form);}} disabled={!form.name||!form.email} style={{...filledBtn, width:"100%", padding:"0.875rem", opacity:(!form.name||!form.email)?0.5:1}}>Send Invitation →</button>
          </>
        )}
      </div>
    </div>
  );
}

function PasswordManagerModal({ onClose, estate, vault }) {
  const [step, setStep] = useState(0);
  const [manager, setManager] = useState("bitwarden");
  const [checklist, setChecklist] = useState({});
  const executorName = estate?.executorName || "your executor";
  const executorEmail = estate?.executorEmail || "[executor email]";

  const managers = [
    {
      id: "bitwarden",
      name: "Bitwarden",
      icon: "🔐",
      tag: "Best for HeirOS",
      tagColor: T.forest,
      enc: "AES-256 + zero-knowledge",
      openSource: true,
      emergencyAccess: true,
      price: "Free / $10/yr Premium",
      desc: "Open-source, independently audited, and has a built-in Emergency Access feature purpose-built for exactly this scenario. The best fit for HeirOS users.",
      url: "https://bitwarden.com",
    },
    {
      id: "1password",
      name: "1Password",
      icon: "🔑",
      tag: "Most polished",
      tagColor: T.goldDark,
      enc: "AES-256-GCM + PBKDF2",
      openSource: false,
      emergencyAccess: false,
      price: "$35.88/yr",
      desc: "Excellent UX and strong security. No native emergency access feature — executor access must be handled by storing your Emergency Kit securely in your HeirOS vault.",
      url: "https://1password.com",
    },
    {
      id: "dashlane",
      name: "Dashlane",
      icon: "🛡",
      tag: "Includes VPN",
      tagColor: "#2980b9",
      enc: "AES-256 + PBKDF2",
      openSource: false,
      emergencyAccess: false,
      price: "$59.99/yr",
      desc: "Feature-rich with dark web monitoring and VPN. No emergency access feature — access must be planned through HeirOS vault instructions.",
      url: "https://dashlane.com",
    },
  ];

  const selected = managers.find(m => m.id === manager);

  // Step content per manager
  const bitwardenSteps = [
    {
      title: "Create a Bitwarden Premium account",
      desc: `If you don't have Bitwarden yet, sign up at bitwarden.com. Emergency Access requires a Premium account ($10/year — worth every penny for this feature alone).`,
      action: { label: "Open Bitwarden →", url: "https://bitwarden.com/go/start-free/" },
      checkKey: "account",
    },
    {
      title: `Add ${executorName} as an Emergency Contact`,
      desc: `In Bitwarden: go to Settings → Emergency Access → Add Emergency Contact. Enter ${executorEmail}. Set their access type to "View" (read-only). They will NOT see your passwords until they request access after your death.`,
      action: { label: "Open Bitwarden Emergency Access →", url: "https://vault.bitwarden.com/#/settings/emergency-access" },
      checkKey: "contact",
    },
    {
      title: "Set your waiting period",
      desc: `Choose how many days Bitwarden waits before granting access after a request. We recommend 7 days — long enough for you to deny if you're alive, short enough for your family when you're not. You can set up to 90 days.`,
      checkKey: "waitperiod",
    },
    {
      title: `${executorName} must accept the invitation`,
      desc: `Bitwarden will send ${executorEmail} an email invitation. They need to accept it to be confirmed as your emergency contact. They must have a free Bitwarden account (no Premium required on their end).`,
      checkKey: "accepted",
    },
    {
      title: "Record this setup in your HeirOS vault",
      desc: `Add a note in your HeirOS Digital Assets vault confirming that your executor has Bitwarden Emergency Access. This ensures they know to request it when the time comes.`,
      checkKey: "recorded",
    },
  ];

  const genericSteps = [
    {
      title: `Set up a ${selected?.name} account`,
      desc: `Sign up or log into your existing ${selected?.name} account at ${selected?.url}.`,
      action: { label: `Open ${selected?.name} →`, url: selected?.url },
      checkKey: "account",
    },
    {
      title: "Locate your Emergency Kit or Master Password",
      desc: `${selected?.name} doesn't have a built-in emergency access feature like Bitwarden. Instead, ${selected?.name} provides an "Emergency Kit" PDF containing your account email, Secret Key, and Master Password fields. Print this, fill it in, and store it securely.`,
      checkKey: "kit",
    },
    {
      title: "Store the Emergency Kit in your HeirOS vault",
      desc: `Add the Emergency Kit details as a "Digital Assets" item in your HeirOS vault (not the password itself — just the location). For example: "1Password Emergency Kit is in the fireproof safe at 123 Main St, combination 1234."`,
      checkKey: "stored",
    },
    {
      title: "Tell your executor what to expect",
      desc: `Add a Personal Message in your HeirOS vault explaining that they should retrieve the Emergency Kit from its secure location and use it to access your ${selected?.name} vault. Include the account email they'll need.`,
      checkKey: "message",
    },
  ];

  const steps = manager === "bitwarden" ? bitwardenSteps : genericSteps;
  const allChecked = steps.every(s => checklist[s.checkKey]);

  const toggleCheck = (key) => setChecklist(c => ({...c, [key]: !c[key]}));

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{...modalBox, maxWidth:"620px"}} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.5rem"}}>
          <div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Password Manager Integration</h2>
            <p style={{fontSize:"0.8rem", color:T.muted}}>Give your executor access to your passwords — only after you're gone.</p>
          </div>
          <button onClick={onClose} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
        </div>

        {step === 0 && (
          <>
            {/* Security explainer */}
            <div style={{background:`linear-gradient(135deg, ${T.forest}08, ${T.forest}12)`, border:`1px solid ${T.forest}20`, borderRadius:"12px", padding:"1rem 1.25rem", marginBottom:"1.5rem"}}>
              <p style={{fontSize:"0.8rem", fontWeight:"600", color:T.forest, marginBottom:"0.375rem"}}>🔒 How this works — and why it's safe</p>
              <p style={{fontSize:"0.78rem", color:T.muted, lineHeight:"1.65"}}>
                Password managers use <strong>zero-knowledge encryption</strong> — not even the company can read your passwords. Your executor gets no access while you're alive. When they request access after your death, there's a waiting period where you could still deny it. Only after that period does access open.
              </p>
            </div>

            {/* Manager picker */}
            <p style={{fontSize:"0.8rem", fontWeight:"600", color:T.charcoal, marginBottom:"0.875rem"}}>Choose your password manager:</p>
            <div style={{display:"flex", flexDirection:"column", gap:"0.625rem", marginBottom:"1.5rem"}}>
              {managers.map(m => (
                <label key={m.id} style={{display:"flex", gap:"1rem", alignItems:"flex-start", cursor:"pointer", padding:"1rem", borderRadius:"12px", border:`2px solid ${manager===m.id ? T.forest : T.border}`, background:manager===m.id ? T.parchment : T.white, transition:"all 0.15s"}}>
                  <input type="radio" name="manager" value={m.id} checked={manager===m.id} onChange={()=>setManager(m.id)} style={{marginTop:"3px"}} />
                  <div style={{flex:1}}>
                    <div style={{display:"flex", alignItems:"center", gap:"0.5rem", marginBottom:"0.25rem", flexWrap:"wrap"}}>
                      <span style={{fontSize:"1.1rem"}}>{m.icon}</span>
                      <span style={{fontWeight:"600", color:T.charcoal, fontSize:"0.9rem"}}>{m.name}</span>
                      <span style={{fontSize:"0.6rem", background:`${m.tagColor}18`, color:m.tagColor, padding:"0.15rem 0.5rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace"}}>{m.tag}</span>
                      {m.emergencyAccess && <span style={{fontSize:"0.6rem", background:"#f0faf5", color:T.success, padding:"0.15rem 0.5rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace"}}>✓ Emergency Access</span>}
                    </div>
                    <p style={{fontSize:"0.78rem", color:T.muted, lineHeight:"1.5", marginBottom:"0.5rem"}}>{m.desc}</p>
                    <div style={{display:"flex", gap:"1rem", flexWrap:"wrap"}}>
                      <span style={{fontSize:"0.7rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>🔒 {m.enc}</span>
                      <span style={{fontSize:"0.7rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>{m.openSource ? "✓ Open source" : "○ Closed source"}</span>
                      <span style={{fontSize:"0.7rem", color:T.muted, fontFamily:"'DM Mono', monospace"}}>💵 {m.price}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{display:"flex", gap:"0.75rem"}}>
              <button onClick={onClose} style={{...outlineBtn, flex:"0 0 auto"}}>Cancel</button>
              <button onClick={()=>setStep(1)} style={{...filledBtn, flex:1, padding:"0.875rem"}}>
                Set Up {selected?.name} Integration →
              </button>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            {/* Manager badge */}
            <div style={{display:"flex", alignItems:"center", gap:"0.625rem", padding:"0.625rem 0.875rem", background:T.parchment, borderRadius:"8px", marginBottom:"1.5rem", border:`1px solid ${T.border}`}}>
              <span style={{fontSize:"1.1rem"}}>{selected?.icon}</span>
              <span style={{fontSize:"0.85rem", fontWeight:"600", color:T.forest}}>{selected?.name} Setup Guide</span>
              <button onClick={()=>setStep(0)} style={{...btnReset, marginLeft:"auto", fontSize:"0.75rem", color:T.muted}}>← Change</button>
            </div>

            {manager === "bitwarden" && (
              <div style={{background:"#f0faf5", border:"1px solid #c3e6cb", borderRadius:"8px", padding:"0.75rem 1rem", marginBottom:"1.25rem", fontSize:"0.78rem", color:T.success, lineHeight:"1.55"}}>
                ✓ <strong>Best choice.</strong> Bitwarden's Emergency Access is purpose-built for this. Your executor ({executorName}) will never see your passwords until they request access — and only after the waiting period you set.
              </div>
            )}

            {/* Checklist steps */}
            <div style={{display:"flex", flexDirection:"column", gap:"0.875rem", marginBottom:"1.5rem"}}>
              {steps.map((s, i) => (
                <div key={s.checkKey} style={{display:"flex", gap:"0.875rem", alignItems:"flex-start", padding:"1rem", borderRadius:"10px", background:checklist[s.checkKey] ? "#f0faf5" : T.white, border:`1px solid ${checklist[s.checkKey] ? "#c3e6cb" : T.borderLight}`, transition:"all 0.2s"}}>
                  <button onClick={()=>toggleCheck(s.checkKey)} style={{...btnReset, width:"22px", height:"22px", borderRadius:"50%", border:`2px solid ${checklist[s.checkKey] ? T.success : T.border}`, background:checklist[s.checkKey] ? T.success : "transparent", color:"white", fontSize:"0.7rem", fontWeight:"700", flexShrink:0, marginTop:"1px", display:"flex", alignItems:"center", justifyContent:"center"}}>
                    {checklist[s.checkKey] ? "✓" : ""}
                  </button>
                  <div style={{flex:1}}>
                    <div style={{display:"flex", alignItems:"center", gap:"0.5rem", marginBottom:"0.375rem"}}>
                      <span style={{fontSize:"0.7rem", fontFamily:"'DM Mono', monospace", color:T.muted}}>Step {i+1}</span>
                    </div>
                    <p style={{fontSize:"0.875rem", fontWeight:"600", color:checklist[s.checkKey]?T.success:T.charcoal, marginBottom:"0.375rem", textDecoration:checklist[s.checkKey]?"line-through":"none"}}>{s.title}</p>
                    <p style={{fontSize:"0.8rem", color:T.muted, lineHeight:"1.6"}}>{s.desc}</p>
                    {s.action && (
                      <a href={s.action.url} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex", alignItems:"center", gap:"0.375rem", marginTop:"0.5rem", fontSize:"0.78rem", color:T.forest, fontWeight:"500", textDecoration:"none", border:`1px solid ${T.forest}`, borderRadius:"6px", padding:"0.3rem 0.75rem"}}>
                        {s.action.label} ↗
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Completion state */}
            {allChecked ? (
              <div style={{background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"12px", padding:"1.25rem", textAlign:"center", color:"white", marginBottom:"1rem"}}>
                <p style={{fontFamily:"'Playfair Display', serif", fontSize:"1.1rem", fontWeight:"600", marginBottom:"0.375rem"}}>✓ Setup complete</p>
                <p style={{fontSize:"0.82rem", opacity:0.85, marginBottom:"0.75rem"}}>Your executor has been set up with password access. Your family will have everything they need.</p>
                <button onClick={onClose} style={{...btnReset, background:"rgba(255,255,255,0.2)", color:"white", border:"1px solid rgba(255,255,255,0.3)", borderRadius:"8px", padding:"0.5rem 1.25rem", fontSize:"0.85rem", fontWeight:"500"}}>Close</button>
              </div>
            ) : (
              <div style={{display:"flex", gap:"0.75rem", alignItems:"center"}}>
                <div style={{flex:1, height:"4px", background:T.parchment, borderRadius:"2px", overflow:"hidden"}}>
                  <div style={{height:"100%", width:`${(Object.values(checklist).filter(Boolean).length / steps.length)*100}%`, background:`linear-gradient(90deg, ${T.forest}, ${T.forestLight})`, borderRadius:"2px", transition:"width 0.4s ease"}} />
                </div>
                <span style={{fontSize:"0.75rem", color:T.muted, fontFamily:"'DM Mono', monospace", flexShrink:0}}>
                  {Object.values(checklist).filter(Boolean).length}/{steps.length} done
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Footer({ navTo }) {
  return (
    <footer style={{background:T.forestDark, color:"rgba(255,255,255,0.7)", padding:"3rem 2rem 2rem"}}>
      <div style={{maxWidth:"1100px", margin:"0 auto"}}>
        <div style={{display:"flex", flexWrap:"wrap", gap:"3rem", marginBottom:"2.5rem", justifyContent:"space-between"}}>
          <div style={{maxWidth:"260px"}}>
            <div style={{display:"flex", alignItems:"center", gap:"0.5rem", marginBottom:"0.875rem"}}>
              <div style={{width:"30px", height:"30px", background:`linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, borderRadius:"7px", display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:"0.9rem", fontFamily:"'Playfair Display', serif", fontWeight:"700"}}>H</div>
              <span style={{fontFamily:"'Playfair Display', serif", fontSize:"1.2rem", fontWeight:"700", color:"white"}}>HeirOS</span>
            </div>
            <p style={{fontSize:"0.82rem", lineHeight:"1.65"}}>Guided estate settlement for families. Flat fee of $299. Not a law firm.</p>
          </div>
          {[
            { title:"Product", links:[{l:"Home",p:"home"},{l:"Pricing",p:"pricing"},{l:"Sign Up",p:"signup"},{l:"Sign In",p:"login"}] },
            { title:"Resources", links:[{l:"Blog",p:"blog"},{l:"FAQ",p:"faq"},{l:"Documents",p:"documents"},{l:"Helpful Links",p:"links"},{l:"Find an Attorney",p:"attorneys"},{l:"Terms of Use",p:"terms"},{l:"Privacy Policy",p:"privacy"},{l:"Contact Us",p:"contact"}] },
          ].map((col,i)=>(
            <div key={i}>
              <p style={{fontFamily:"'DM Mono', monospace", fontSize:"0.7rem", letterSpacing:"0.1em", color:T.gold, marginBottom:"1rem"}}>{col.title}</p>
              <div style={{display:"flex", flexDirection:"column", gap:"0.5rem"}}>
                {col.links.map(l=><button key={l.p} onClick={()=>navTo(l.p)} style={{...btnReset, fontSize:"0.85rem", color:"rgba(255,255,255,0.65)", textAlign:"left"}}>{l.l}</button>)}
              </div>
            </div>
          ))}
        </div>
        <div style={{paddingTop:"1.5rem", borderTop:"1px solid rgba(255,255,255,0.1)", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"0.75rem"}}>
          <p style={{fontSize:"0.75rem"}}>© 2026 HeirOS · Not a law firm · Not legal advice</p>
          <p style={{fontSize:"0.75rem"}}>Built to help families through the hardest paperwork of their lives.</p>
        </div>
      </div>
    </footer>
  );
}

// ─── REPORT DEATH MODAL ───────────────────────────────────────────────────────
function ReportDeathModal({ estate, user, estateSupabaseId, onClose, onReported }) {
  const deceasedName = estate?.deceasedName || estate?.ownerName || "the estate owner";
  const [form, setForm] = useState({ dateOfDeath:"", relationship: estate?.executorRelation || "", confirmed:false });
  const [step, setStep] = useState("form"); // "form" | "submitting" | "confirmation"
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(72);

  // Countdown timer
  useEffect(() => {
    if (step !== "confirmation" || !report) return;
    const interval = setInterval(() => {
      const deadline = new Date(report.confirmation_deadline);
      const hoursLeft = Math.max(0, (deadline - new Date()) / (1000 * 60 * 60));
      setTimeLeft(hoursLeft);
    }, 60000);
    // Initial set
    if (report.confirmation_deadline) {
      const deadline = new Date(report.confirmation_deadline);
      setTimeLeft(Math.max(0, (deadline - new Date()) / (1000 * 60 * 60)));
    }
    return () => clearInterval(interval);
  }, [step, report]);

  const handleSubmit = async () => {
    if (!form.dateOfDeath || !form.confirmed) return;
    setStep("submitting");
    setError(null);

    let result = null;
    if (user && estateSupabaseId) {
      const { data, error: err } = await reportDeath(user.id, estateSupabaseId, {
        deceasedName: deceasedName,
        dateOfDeath: form.dateOfDeath,
        relationship: form.relationship,
      }, {
        ownerEmail: estate?.ownerEmail || null,
        ownerName: estate?.ownerName || '',
        executorName: estate?.executorName || user?.email || '',
        ownerId: estate?.userId || null,
      });
      if (err) { setError("Unable to file report. Please try again."); setStep("form"); return; }
      result = data;
      // Log notification
      await logNotification(user.id, "death_reported",
        `Death reported for ${deceasedName}`,
        `You reported the passing of ${deceasedName} on ${new Date(form.dateOfDeath).toLocaleDateString()}. The 72-hour verification period has begun.`,
        user.email);
    } else {
      // No Supabase — create a local report for display
      result = {
        id: "local-" + Date.now(),
        deceased_name: deceasedName,
        date_of_death: form.dateOfDeath,
        relationship: form.relationship,
        status: "pending",
        confirmation_deadline: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
        created_at: new Date().toISOString(),
      };
    }

    setReport(result);
    setStep("confirmation");
    if (onReported) onReported(result);
  };

  const pct = Math.max(0, Math.min(100, ((72 - timeLeft) / 72) * 100));

  return (
    <div style={modalOverlay} onClick={step==="form"?onClose:undefined}>
      <div style={{...modalBox, maxWidth:"500px"}} onClick={e=>e.stopPropagation()}>

        {step === "form" && (
          <>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.5rem"}}>
              <div>
                <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.35rem", fontWeight:"700", color:"#7c2d12", marginBottom:"0.375rem"}}>
                  Report the Passing of {deceasedName}
                </h2>
                <p style={{fontSize:"0.8rem", color:T.muted, lineHeight:"1.5"}}>This will begin a 72-hour verification period before full estate access is granted.</p>
              </div>
              <button onClick={onClose} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
            </div>

            <div style={{display:"flex", flexDirection:"column", gap:"1.125rem", marginBottom:"1.75rem"}}>
              <div>
                <label style={labelStyle}>Date of Passing <span style={{color:T.danger}}>*</span></label>
                <input type="date" value={form.dateOfDeath} onChange={e=>setForm({...form,dateOfDeath:e.target.value})} style={inputStyle} max={new Date().toISOString().split("T")[0]} />
              </div>
              <div>
                <label style={labelStyle}>Your Relationship to {deceasedName}</label>
                <input value={form.relationship} onChange={e=>setForm({...form,relationship:e.target.value})} placeholder="e.g. Named executor, Child, Spouse" style={inputStyle} />
              </div>
              <label style={{display:"flex", gap:"0.875rem", alignItems:"flex-start", cursor:"pointer", padding:"1rem", borderRadius:"10px", border:`2px solid ${form.confirmed?"#7c2d12":T.border}`, background:form.confirmed?"#fff5f0":T.white, transition:"all 0.15s"}}>
                <input type="checkbox" checked={form.confirmed} onChange={e=>setForm({...form,confirmed:e.target.checked})} style={{marginTop:"2px", flexShrink:0}} />
                <div>
                  <p style={{fontSize:"0.85rem", fontWeight:"600", color:form.confirmed?"#7c2d12":T.charcoal, marginBottom:"0.25rem"}}>I confirm that {deceasedName} has passed away</p>
                  <p style={{fontSize:"0.75rem", color:T.muted, lineHeight:"1.5"}}>I am authorized to manage their estate and attest this information is accurate to the best of my knowledge.</p>
                </div>
              </label>
            </div>

            {error && <p style={{color:T.danger, fontSize:"0.82rem", marginBottom:"1rem"}}>{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={!form.dateOfDeath || !form.confirmed}
              style={{...filledBtn, width:"100%", padding:"0.875rem", background:"#7c2d12", opacity:(!form.dateOfDeath||!form.confirmed)?0.5:1}}
            >
              Report Passing & Begin Verification →
            </button>

            <p style={{fontSize:"0.72rem", color:T.muted, marginTop:"1rem", textAlign:"center", lineHeight:"1.5"}}>
              Falsely reporting a death is a serious matter. HeirOS reserves the right to take action against fraudulent reports.
            </p>
          </>
        )}

        {step === "submitting" && (
          <div style={{textAlign:"center", padding:"2rem 0"}}>
            <div style={{width:"48px", height:"48px", border:`3px solid ${T.borderLight}`, borderTopColor:T.forest, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 1rem"}} />
            <p style={{color:T.muted}}>Filing report…</p>
          </div>
        )}

        {step === "confirmation" && (
          <div>
            <div style={{textAlign:"center", marginBottom:"2rem"}}>
              <div style={{fontSize:"2.5rem", marginBottom:"0.75rem"}}>🕊️</div>
              <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.35rem", fontWeight:"700", color:T.charcoal, marginBottom:"0.5rem"}}>Death report filed</h2>
              <p style={{color:T.muted, fontSize:"0.875rem", lineHeight:"1.65"}}>
                The passing of <strong>{deceasedName}</strong> has been recorded. A 72-hour verification period has begun.
              </p>
            </div>

            <div style={{background:T.parchment, borderRadius:"14px", padding:"1.5rem", marginBottom:"1.5rem"}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.875rem"}}>
                <p style={{fontSize:"0.82rem", fontWeight:"600", color:T.charcoal}}>Verification period</p>
                <span style={{fontSize:"0.82rem", fontFamily:"'DM Mono', monospace", color:T.forest, fontWeight:"600"}}>{Math.round(timeLeft)}h remaining</span>
              </div>
              <div style={{background:"white", borderRadius:"100px", height:"10px", overflow:"hidden", marginBottom:"0.75rem"}}>
                <div style={{height:"100%", width:`${pct}%`, background:`linear-gradient(90deg, ${T.forest}, ${T.forestLight})`, borderRadius:"100px", transition:"width 1s"}} />
              </div>
              <p style={{fontSize:"0.75rem", color:T.muted, lineHeight:"1.5"}}>
                Full estate access will unlock automatically after 72 hours. {deceasedName} has been notified and can cancel this report if this is an error.
              </p>
            </div>

            <div style={{background:"#fff8f0", border:`1px solid ${T.tan}`, borderRadius:"10px", padding:"1rem", marginBottom:"1.5rem", fontSize:"0.8rem", color:T.goldDark, lineHeight:"1.6"}}>
              🔗 The estate owner can cancel this report by clicking the secure link sent to their email, or by visiting their HeirOS account.
            </div>

            <button onClick={onClose} style={{...filledBtn, width:"100%"}}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CANCEL DEATH REPORT PAGE ─────────────────────────────────────────────────
function CancelDeathReportPage({ reportId, navTo, onCancelled }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("confirm"); // "confirm" | "cancelling" | "done"
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reportId) { setLoading(false); return; }
    getDeathReportById(reportId).then(({ data, error: err }) => {
      setReport(data || null);
      setLoading(false);
      if (err) setError("Unable to load report details.");
    });
  }, [reportId]);

  const handleCancel = async () => {
    if (!reportId) return;
    setStep("cancelling");
    const { error: err } = await cancelDeathReport(reportId, {
      executorEmail: report?.estates?.executor_email || null,
      ownerName: report?.estates?.owner_name || '',
    });
    if (err) { setError("Unable to cancel report. Please try again."); setStep("confirm"); return; }
    setStep("done");
    if (onCancelled) onCancelled();
  };

  if (loading) {
    return (
      <div style={{minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:"48px", height:"48px", border:`3px solid ${T.forestLight}`, borderTopColor:T.forest, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 1rem"}} />
          <p style={{color:T.muted}}>Loading report details…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh", background:T.cream, display:"flex", alignItems:"center", justifyContent:"center", padding:"2rem 1rem"}}>
      <div style={{maxWidth:"480px", width:"100%"}}>
        <div style={{textAlign:"center", marginBottom:"2rem"}}>
          <div style={{width:"52px", height:"52px", background:`linear-gradient(135deg, ${T.forest}, ${T.forestLight})`, borderRadius:"13px", display:"flex", alignItems:"center", justifyContent:"center", color:"white", fontSize:"1.5rem", fontWeight:"700", fontFamily:"'Playfair Display', serif", margin:"0 auto 0.875rem"}}>H</div>
          <span style={{fontFamily:"'Playfair Display', serif", fontSize:"1.6rem", fontWeight:"700", color:T.forest, letterSpacing:"-0.02em"}}>HeirOS</span>
        </div>

        <div style={{background:T.white, borderRadius:"20px", padding:"2.5rem", boxShadow:"0 4px 24px rgba(0,0,0,0.08)"}}>
          {step === "done" ? (
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:"3rem", marginBottom:"1rem"}}>✅</div>
              <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, marginBottom:"0.75rem"}}>Death report cancelled</h2>
              <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.65", marginBottom:"2rem"}}>
                The report has been cancelled and the estate status has been restored. The executor has been notified.
              </p>
              <button onClick={()=>navTo("app-dashboard")} style={{...filledBtn, width:"100%"}}>Go to My Estate →</button>
            </div>
          ) : (
            <>
              <div style={{textAlign:"center", marginBottom:"2rem"}}>
                <div style={{fontSize:"2.5rem", marginBottom:"0.75rem"}}>⚠️</div>
                <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:"#7c2d12", marginBottom:"0.5rem"}}>
                  {!reportId ? "Cancel Death Report" : "Are you alive?"}
                </h2>
                <p style={{color:T.muted, fontSize:"0.9rem", lineHeight:"1.65"}}>
                  {report
                    ? <>Your executor reported your passing on <strong>{new Date(report.created_at).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</strong>. If this was a mistake or you are alive, click below to cancel this report immediately.</>
                    : !reportId ? "No active death report was found." : "Loading report details…"
                  }
                </p>
              </div>

              {report && (
                <div style={{background:T.parchment, borderRadius:"12px", padding:"1.25rem", marginBottom:"1.5rem"}}>
                  <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0.75rem"}}>
                    {[
                      ["Reported on", new Date(report.created_at).toLocaleDateString()],
                      ["Reported by", report.reported_by || "Unknown"],
                      ["Status", report.status],
                      ["Access unlocks", report.confirmation_deadline ? new Date(report.confirmation_deadline).toLocaleDateString() : "—"],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <p style={{fontSize:"0.68rem", fontFamily:"'DM Mono', monospace", color:T.muted, marginBottom:"0.2rem"}}>{label.toUpperCase()}</p>
                        <p style={{fontSize:"0.82rem", color:T.charcoal, fontWeight:"500", textTransform:"capitalize"}}>{val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && <p style={{color:T.danger, fontSize:"0.82rem", marginBottom:"1rem"}}>{error}</p>}

              <div style={{display:"flex", flexDirection:"column", gap:"0.75rem"}}>
                {reportId && report?.status === "pending" && (
                  <button
                    onClick={handleCancel}
                    disabled={step === "cancelling"}
                    style={{...filledBtn, width:"100%", padding:"0.875rem", background:"#7c2d12", opacity:step==="cancelling"?0.6:1}}
                  >
                    {step === "cancelling" ? "Cancelling…" : "Cancel Death Report — I'm Alive →"}
                  </button>
                )}
                {report?.status !== "pending" && report && (
                  <div style={{background:"#fefbf0", border:`1px solid ${T.tan}`, borderRadius:"8px", padding:"0.875rem 1rem", fontSize:"0.82rem", color:T.goldDark}}>
                    This report has already been {report.status}. No further action is needed.
                  </div>
                )}
                <button onClick={()=>navTo("home")} style={{...outlineBtn, width:"100%"}}>Return to HeirOS</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ADD ROLE MODAL ───────────────────────────────────────────────────────────
// Lets the owner assign additional roles to an already-accepted collaborator.
// Each selected role creates a pending_acknowledgment invitation row.
const ADD_ROLE_OPTIONS = [
  { value: 'Named as executor',  label: 'Named as executor',  isSuccession: true,  orderKey: 'executor_order'     },
  { value: 'Medical POA',        label: 'Medical POA',         isSuccession: true,  orderKey: 'medical_poa_order'  },
  { value: 'Power of Attorney',  label: 'Power of Attorney',  isSuccession: true,  orderKey: 'poa_order'          },
  { value: 'Collaborator',       label: 'Collaborator',        isSuccession: false, orderKey: null                 },
  { value: 'Beneficiary',        label: 'Beneficiary',         isSuccession: false, orderKey: null                 },
];
const RANK_OPTIONS = [
  { value: 1, label: 'Primary (Serves first)' },
  { value: 2, label: 'First Backup' },
  { value: 3, label: 'Second Backup' },
];
// ── Role permission defaults ───────────────────────────────────────────────────
// These alias ROLE_PERMISSIONS from supabase.js, which is the client-side
// source of truth. supabase/functions/_shared/rolePermissions.ts is the
// Edge Function counterpart. All three must stay textually identical.
// Never define a separate local copy of these permission sets.
const DEFAULT_POA_PERMISSIONS      = ROLE_PERMISSIONS['Power of Attorney'];
const DEFAULT_EXECUTOR_PERMISSIONS = ROLE_PERMISSIONS['Named as executor'];
const DEFAULT_MPOA_PERMISSIONS     = ROLE_PERMISSIONS['Medical POA'];
const DEFAULT_COLLABORATOR_PERMISSIONS = {
  view_documents: true, download_documents: false,
  view_checklist: true, edit_checklist: false,
  view_vault: false, view_family: true,
  full_access: false, view_medical_documents: false, download_medical_documents: false,
};
const DEFAULT_BENEFICIARY_PERMISSIONS = {
  view_checklist: true, view_documents: false, download_documents: false,
  view_vault: false, view_family: false, full_access: false,
  edit_checklist: false, view_medical_documents: false, download_medical_documents: false,
};

function AddRoleModal({ target, estateId, invitedById, inviterName, currentInvitations, onClose, onSubmitted }) {
  const [selectedRoles, setSelectedRoles] = useState({});
  const [selectedRanks, setSelectedRanks] = useState({ 'Named as executor': 1, 'Medical POA': 1, 'Power of Attorney': 1 });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Roles the person already holds (non-declined)
  const existingRoles = new Set(
    (currentInvitations || [])
      .filter(inv => inv.invited_email?.toLowerCase() === target.email?.toLowerCase() && inv.status !== 'declined')
      .map(inv => inv.role)
  );

  const toggleRole = (role) => {
    setSelectedRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const handleSubmit = async () => {
    const rows = ADD_ROLE_OPTIONS
      .filter(r => selectedRoles[r.value])
      .map(r => ({
        email: target.email,
        name: target.name,
        role: r.value,
        inviterName,
        permissions:
          r.value === 'Named as executor'  ? DEFAULT_EXECUTOR_PERMISSIONS  :
          r.value === 'Medical POA'        ? DEFAULT_MPOA_PERMISSIONS       :
          r.value === 'Power of Attorney'  ? DEFAULT_POA_PERMISSIONS        :
          r.value === 'Collaborator'       ? DEFAULT_COLLABORATOR_PERMISSIONS :
          r.value === 'Beneficiary'        ? DEFAULT_BENEFICIARY_PERMISSIONS  :
          {},
        ...(r.isSuccession && r.orderKey ? { [r.orderKey]: selectedRanks[r.value] || 1 } : {}),
      }));

    if (!rows.length) { setError("Please select at least one role."); return; }
    setSubmitting(true); setError(null);

    // Step 1: DB insert — this is the source of truth.
    let insertedRows = [];
    try {
      const { data, error: err } = await createAddRoleInvitations(estateId, invitedById, rows);
      if (err) throw err;
      insertedRows = data || [];
    } catch(e) {
      setError("Failed to assign roles. Please try again.");
      setSubmitting(false);
      return;
    }

    // Step 2: Commit UI state immediately — DB insert confirmed.
    setSuccess(true);

    // Step 3: Send notification email (non-blocking — UI success is already shown).
    const roleNames = rows.map(r => r.role);
    sendEmail(target.email, 'role_acknowledgment_needed', {
      inviteeName: target.name || target.email,
      inviterName,
      roles: roleNames,
    }).then(({ error: emailErr }) => {
      if (emailErr) console.error('[HeirOS] role_acknowledgment_needed email failed:', emailErr);
      else console.log('[HeirOS] role_acknowledgment_needed email sent OK to', target.email);
    }).catch(e => console.error('[HeirOS] role_acknowledgment_needed email exception:', e));

    setTimeout(() => { onSubmitted(insertedRows); }, 1200);
  };

  return (
    <div style={modalOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{...modalBox, maxWidth:"480px"}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.25rem"}}>
          <div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.35rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>Add Role</h2>
            <p style={{fontSize:"0.8rem", color:T.muted}}>Assign additional roles to {target.name || target.email}</p>
          </div>
          <button onClick={onClose} style={{...btnReset, fontSize:"1.4rem", color:T.muted, lineHeight:1}}>×</button>
        </div>

        <div style={{background:T.parchment, borderRadius:"8px", padding:"0.625rem 1rem", marginBottom:"1.25rem", fontSize:"0.8rem", color:T.muted}}>
          <strong style={{color:T.charcoal}}>{target.name}</strong> {target.email ? `· ${target.email}` : ''}
        </div>

        <p style={{fontSize:"0.8rem", color:T.muted, marginBottom:"0.875rem"}}>Select roles to assign. Each role will require their acknowledgment before taking effect.</p>

        <div style={{display:"flex", flexDirection:"column", gap:"0.625rem", marginBottom:"1.25rem"}}>
          {ADD_ROLE_OPTIONS.map(opt => {
            const alreadyHas = existingRoles.has(opt.value);
            const isSelected = !!selectedRoles[opt.value];
            return (
              <div key={opt.value}>
                <label style={{display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.75rem 1rem",
                  background: alreadyHas ? T.parchment : isSelected ? "#f0faf5" : "transparent",
                  border:`1px solid ${alreadyHas ? T.borderLight : isSelected ? T.forest+"44" : T.borderLight}`,
                  borderRadius:"10px",
                  cursor: alreadyHas ? "not-allowed" : "pointer",
                  opacity: alreadyHas ? 0.6 : 1}}>
                  <input type="checkbox"
                    checked={isSelected && !alreadyHas}
                    disabled={alreadyHas}
                    onChange={() => !alreadyHas && toggleRole(opt.value)}
                    style={{width:"1rem", height:"1rem", accentColor:T.forest, flexShrink:0}} />
                  <div style={{flex:1}}>
                    <div style={{fontSize:"0.875rem", fontWeight:"500", color:T.charcoal}}>{opt.label}</div>
                    {alreadyHas && <div style={{fontSize:"0.7rem", color:T.muted}}>Already holds this role</div>}
                  </div>
                </label>
                {isSelected && !alreadyHas && opt.isSuccession && (
                  <div style={{padding:"0.625rem 1rem 0.75rem 2.75rem", borderLeft:`2px solid ${T.forest}22`}}>
                    <label style={{...labelStyle, marginBottom:"0.25rem"}}>Position</label>
                    <select
                      value={selectedRanks[opt.value] || 1}
                      onChange={e => setSelectedRanks(prev => ({...prev, [opt.value]: Number(e.target.value)}))}
                      style={{...inputStyle, padding:"0.5rem 0.75rem", fontSize:"0.82rem"}}>
                      {RANK_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && <p style={{color:T.danger, fontSize:"0.8rem", marginBottom:"0.75rem"}}>{error}</p>}
        {success && <p style={{color:T.success, fontSize:"0.8rem", marginBottom:"0.75rem"}}>✓ Roles assigned — awaiting acknowledgment</p>}

        <div style={{display:"flex", justifyContent:"flex-end", gap:"0.75rem"}}>
          <button onClick={onClose} style={{...outlineBtn, fontSize:"0.875rem"}}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || success} style={{...filledBtn, fontSize:"0.875rem", opacity:(submitting||success)?0.6:1}}>
            {submitting ? "Assigning…" : success ? "Done ✓" : "Assign Roles"}
          </button>
        </div>
        <p style={{fontSize:"0.72rem", color:T.muted, marginTop:"0.75rem", textAlign:"center"}}>
          {target.name || "They"} will receive an email and must acknowledge each role before it takes effect.
        </p>
      </div>
    </div>
  );
}

// ─── APP PENDING ROLES (acknowledgment screen) ────────────────────────────────
// ─── PENDING ROLES MODAL ─────────────────────────────────────────────────────
// Auto-shown once per session when the user has pending_acknowledgment invitations.
// Reuses the same logic as AppPendingRoles but rendered as a blocking modal overlay.
function PendingRolesModal({ user, setPendingAckCount, onClose }) {
  const [items, setItems] = useState(null);
  const [processing, setProcessing] = useState({});
  const [outcomes, setOutcomes] = useState({});

  useEffect(() => {
    if (!user?.email) return;
    getPendingAcknowledgments(user.email).then(({ data }) => {
      setItems(data || []);
    });
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAcknowledge = async (inv) => {
    setProcessing(prev => ({...prev, [inv.id]: 'accepting'}));
    try {
      const { error: dbErr } = await acknowledgeRole(inv.id, user.id);
      if (dbErr) { setProcessing(prev => ({...prev, [inv.id]: 'done'})); return; }
      setOutcomes(prev => ({...prev, [inv.id]: 'accepted'}));
      if (setPendingAckCount) setPendingAckCount(prev => Math.max(0, prev - 1));
      if (user?.email && setPendingAckCount) {
        getPendingAcknowledgments(user.email).then(({ data }) => setPendingAckCount((data || []).length));
      }
      const ownerEmail = inv.estates?.owner_email;
      const ownerName = inv.estates?.owner_name || 'the estate owner';
      if (ownerEmail) {
        const posLabel = inv.executor_order ? ` (Position #${inv.executor_order})`
          : inv.medical_poa_order ? ` (Position #${inv.medical_poa_order})`
          : inv.poa_order ? ` (Position #${inv.poa_order})` : '';
        sendEmail(ownerEmail, 'role_acknowledged', {
          ownerName,
          collaboratorName: user?.user_metadata?.full_name || user?.email,
          roleName: `${inv.role}${posLabel}`,
        }).catch(() => {});
      }
    } catch(e) { /* non-fatal */ } finally {
      setProcessing(prev => ({...prev, [inv.id]: 'done'}));
    }
  };

  const handleDecline = async (inv) => {
    setProcessing(prev => ({...prev, [inv.id]: 'declining'}));
    try {
      const { error: dbErr } = await declineRole(inv.id);
      if (dbErr) { setProcessing(prev => ({...prev, [inv.id]: 'done'})); return; }
      setOutcomes(prev => ({...prev, [inv.id]: 'declined'}));
      if (setPendingAckCount) setPendingAckCount(prev => Math.max(0, prev - 1));
      if (user?.email && setPendingAckCount) {
        getPendingAcknowledgments(user.email).then(({ data }) => setPendingAckCount((data || []).length));
      }
      const ownerEmail = inv.estates?.owner_email;
      const ownerName = inv.estates?.owner_name || 'the estate owner';
      if (ownerEmail) {
        const posLabel = inv.executor_order ? ` (Position #${inv.executor_order})`
          : inv.medical_poa_order ? ` (Position #${inv.medical_poa_order})`
          : inv.poa_order ? ` (Position #${inv.poa_order})` : '';
        sendEmail(ownerEmail, 'role_declined', {
          ownerName,
          collaboratorName: user?.user_metadata?.full_name || user?.email,
          roleName: `${inv.role}${posLabel}`,
        }).catch(() => {});
      }
    } catch(e) { /* non-fatal */ } finally {
      setProcessing(prev => ({...prev, [inv.id]: 'done'}));
    }
  };

  const positionLabel = (inv) => {
    if (inv.executor_order) return ['Primary', 'First Backup', 'Second Backup'][inv.executor_order - 1] || `#${inv.executor_order}`;
    if (inv.medical_poa_order) return ['Primary', 'First Backup', 'Second Backup'][inv.medical_poa_order - 1] || `#${inv.medical_poa_order}`;
    if (inv.poa_order) return ['Primary', 'First Backup', 'Second Backup'][inv.poa_order - 1] || `#${inv.poa_order}`;
    return null;
  };

  const pendingItems = (items || []).filter(inv => !outcomes[inv.id]);
  const resolvedItems = (items || []).filter(inv => outcomes[inv.id]);
  const allResolved = items !== null && pendingItems.length === 0;

  return (
    <div style={{...modalOverlay, alignItems:"center", zIndex:10000}}>
      <div style={{...modalBox, maxWidth:"560px", maxHeight:"85vh", overflowY:"auto"}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.25rem"}}>
          <div>
            <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.4rem", fontWeight:"700", color:T.forest, marginBottom:"0.25rem"}}>
              New Role Assignments
            </h2>
            <p style={{fontSize:"0.825rem", color:T.muted}}>
              {allResolved
                ? "All done — your responses have been recorded."
                : "You've been assigned new roles. Please accept or decline each one to continue."}
            </p>
          </div>
          {/* Only show × when everything is resolved */}
          {allResolved && (
            <button onClick={onClose} style={{...btnReset, fontSize:"1.4rem", color:T.muted, lineHeight:1}}>×</button>
          )}
        </div>

        {items === null ? (
          <div style={{textAlign:"center", padding:"3rem"}}>
            <div style={{width:"36px", height:"36px", border:`2px solid ${T.forestLight}`, borderTopColor:T.forest, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 1rem"}} />
            <p style={{color:T.muted, fontSize:"0.875rem"}}>Loading…</p>
          </div>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
            {pendingItems.map(inv => {
              const pos = positionLabel(inv);
              const estateName = inv.estates?.owner_name ? `${inv.estates.owner_name}'s Estate` : 'Estate';
              const proc = processing[inv.id];
              return (
                <div key={inv.id} style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"14px", padding:"1.5rem", borderLeft:`3px solid #7c4c8a`}}>
                  <div style={{marginBottom:"1rem"}}>
                    <div style={{display:"flex", alignItems:"center", gap:"0.5rem", flexWrap:"wrap", marginBottom:"0.375rem"}}>
                      <span style={{fontWeight:"600", color:T.charcoal, fontSize:"0.95rem"}}>{inv.role}</span>
                      {pos && <span style={{fontSize:"0.7rem", background:T.parchment, color:T.muted, padding:"0.15rem 0.5rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace"}}>{pos}</span>}
                    </div>
                    <p style={{fontSize:"0.82rem", color:T.muted, marginBottom:"0.25rem"}}>
                      Estate: <strong style={{color:T.charcoal}}>{estateName}</strong>
                    </p>
                    {inv.inviter_name && <p style={{fontSize:"0.78rem", color:T.muted}}>Assigned by {inv.inviter_name}</p>}
                  </div>
                  <div style={{background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:"8px", padding:"0.625rem 0.875rem", marginBottom:"1rem", fontSize:"0.78rem", color:"#9a3412"}}>
                    ⚠ This role has no effect on your current access until you acknowledge it.
                  </div>
                  <div style={{display:"flex", gap:"0.75rem", flexWrap:"wrap"}}>
                    <button onClick={() => handleAcknowledge(inv)} disabled={!!proc}
                      style={{...filledBtn, fontSize:"0.82rem", padding:"0.5rem 1.25rem", opacity:proc?0.6:1}}>
                      {proc === 'accepting' ? "Accepting…" : "Accept responsibility"}
                    </button>
                    <button onClick={() => handleDecline(inv)} disabled={!!proc}
                      style={{...outlineBtn, fontSize:"0.82rem", padding:"0.5rem 1rem", color:T.danger, borderColor:T.danger, opacity:proc?0.6:1}}>
                      {proc === 'declining' ? "Declining…" : "Decline"}
                    </button>
                  </div>
                </div>
              );
            })}

            {resolvedItems.length > 0 && (
              <div>
                {resolvedItems.map(inv => {
                  const outcome = outcomes[inv.id];
                  const pos = positionLabel(inv);
                  const estateName = inv.estates?.owner_name ? `${inv.estates.owner_name}'s Estate` : 'Estate';
                  return (
                    <div key={inv.id} style={{background:outcome==='accepted'?"#f0faf5":"#fff5f5", border:`1px solid ${outcome==='accepted'?"#c3e6cb":"#fca5a5"}`, borderRadius:"12px", padding:"1rem 1.25rem", marginBottom:"0.5rem", display:"flex", alignItems:"center", gap:"1rem"}}>
                      <span style={{fontSize:"1.25rem"}}>{outcome === 'accepted' ? '✅' : '❌'}</span>
                      <div>
                        <div style={{fontSize:"0.875rem", fontWeight:"500", color:T.charcoal}}>{inv.role}{pos ? ` · ${pos}` : ''}</div>
                        <div style={{fontSize:"0.75rem", color:T.muted}}>{estateName} · {outcome === 'accepted' ? 'Accepted' : 'Declined'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {allResolved ? (
              <button onClick={onClose} style={{...filledBtn, width:"100%", marginTop:"0.5rem"}}>
                Continue to HeirOS ✓
              </button>
            ) : (
              <button onClick={onClose} style={{...btnReset, fontSize:"0.78rem", color:T.muted, textAlign:"center", textDecoration:"underline", padding:"0.5rem", display:"block", width:"100%"}}>
                Decide later — I'll review these on my next sign-in
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PENDING ROLES PAGE ───────────────────────────────────────────────────────
function AppPendingRoles({ user, navTo, setPendingAckCount }) {
  const [items, setItems] = useState(null); // null = loading
  const [processing, setProcessing] = useState({}); // invId -> 'accepting' | 'declining' | 'done'
  const [outcomes, setOutcomes] = useState({}); // invId -> 'accepted' | 'declined'

  useEffect(() => {
    if (!user?.email) return;
    getPendingAcknowledgments(user.email).then(({ data }) => {
      setItems(data || []);
    });
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAcknowledge = async (inv) => {
    setProcessing(prev => ({...prev, [inv.id]: 'accepting'}));
    try {
      const { error: dbErr } = await acknowledgeRole(inv.id, user.id);
      if (dbErr) {
        console.error('[HeirOS] acknowledge DB error', dbErr);
        setProcessing(prev => ({...prev, [inv.id]: 'done'}));
        return; // do not update UI or send email on failure
      }
      // DB confirmed — mark success first, then send email (non-blocking)
      setOutcomes(prev => ({...prev, [inv.id]: 'accepted'}));
      // Optimistically decrement the badge immediately so it clears without
      // waiting for the async re-fetch (which corrects any drift afterward).
      if (setPendingAckCount) {
        setPendingAckCount(prev => Math.max(0, prev - 1));
      }
      // Re-fetch to confirm true pending count after DB write settles
      if (user?.email && setPendingAckCount) {
        getPendingAcknowledgments(user.email).then(({ data }) => {
          setPendingAckCount((data || []).length);
        });
      }
      const ownerEmail = inv.estates?.owner_email;
      const ownerName = inv.estates?.owner_name || 'the estate owner';
      if (ownerEmail) {
        const posLabel = inv.executor_order ? ` (Position #${inv.executor_order})`
          : inv.medical_poa_order ? ` (Position #${inv.medical_poa_order})`
          : inv.poa_order ? ` (Position #${inv.poa_order})` : '';
        sendEmail(ownerEmail, 'role_acknowledged', {
          ownerName,
          collaboratorName: user?.user_metadata?.full_name || user?.email,
          roleName: `${inv.role}${posLabel}`,
        }).catch(e => console.warn('[HeirOS] role_acknowledged email failed (non-fatal)', e));
      }
    } catch(e) {
      console.error('[HeirOS] acknowledge unexpected error', e);
    } finally {
      setProcessing(prev => ({...prev, [inv.id]: 'done'}));
    }
  };

  const handleDecline = async (inv) => {
    setProcessing(prev => ({...prev, [inv.id]: 'declining'}));
    try {
      const { error: dbErr } = await declineRole(inv.id);
      if (dbErr) {
        console.error('[HeirOS] decline DB error', dbErr);
        setProcessing(prev => ({...prev, [inv.id]: 'done'}));
        return; // do not update UI or send email on failure
      }
      // DB confirmed — mark success first, then send email (non-blocking)
      setOutcomes(prev => ({...prev, [inv.id]: 'declined'}));
      // Optimistically decrement the badge immediately so it clears without
      // waiting for the async re-fetch (which corrects any drift afterward).
      if (setPendingAckCount) {
        setPendingAckCount(prev => Math.max(0, prev - 1));
      }
      // Re-fetch to confirm true pending count after DB write settles
      if (user?.email && setPendingAckCount) {
        getPendingAcknowledgments(user.email).then(({ data }) => {
          setPendingAckCount((data || []).length);
        });
      }
      const ownerEmail = inv.estates?.owner_email;
      const ownerName = inv.estates?.owner_name || 'the estate owner';
      if (ownerEmail) {
        const posLabel = inv.executor_order ? ` (Position #${inv.executor_order})`
          : inv.medical_poa_order ? ` (Position #${inv.medical_poa_order})`
          : inv.poa_order ? ` (Position #${inv.poa_order})` : '';
        sendEmail(ownerEmail, 'role_declined', {
          ownerName,
          collaboratorName: user?.user_metadata?.full_name || user?.email,
          roleName: `${inv.role}${posLabel}`,
        }).catch(e => console.warn('[HeirOS] role_declined email failed (non-fatal)', e));
      }
    } catch(e) {
      console.error('[HeirOS] decline unexpected error', e);
    } finally {
      setProcessing(prev => ({...prev, [inv.id]: 'done'}));
    }
  };

  const positionLabel = (inv) => {
    if (inv.executor_order) return ['Primary', 'First Backup', 'Second Backup'][inv.executor_order - 1] || `#${inv.executor_order}`;
    if (inv.medical_poa_order) return ['Primary', 'First Backup', 'Second Backup'][inv.medical_poa_order - 1] || `#${inv.medical_poa_order}`;
    if (inv.poa_order) return ['Primary', 'First Backup', 'Second Backup'][inv.poa_order - 1] || `#${inv.poa_order}`;
    return null;
  };

  const pendingItems = (items || []).filter(inv => !outcomes[inv.id]);
  const resolvedItems = (items || []).filter(inv => outcomes[inv.id]);

  return (
    <div style={{maxWidth:"640px", margin:"0 auto", padding:"2rem 1.25rem"}}>
      <div style={{marginBottom:"2rem"}}>
        <button onClick={() => navTo('app-dashboard')} style={{...btnReset, fontSize:"0.82rem", color:T.muted, marginBottom:"1rem", display:"flex", alignItems:"center", gap:"0.375rem"}}>
          ← Back to Dashboard
        </button>
        <h1 style={{fontFamily:"'Playfair Display', serif", fontSize:"2rem", fontWeight:"700", color:T.forest, marginBottom:"0.375rem"}}>
          Pending Role Acknowledgments
        </h1>
        <p style={{color:T.muted, fontSize:"0.875rem"}}>
          Review roles assigned to you. Each role takes effect only after your acknowledgment.
        </p>
      </div>

      {items === null ? (
        <div style={{textAlign:"center", padding:"3rem"}}>
          <div style={{width:"36px", height:"36px", border:`2px solid ${T.forestLight}`, borderTopColor:T.forest, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 1rem"}} />
          <p style={{color:T.muted, fontSize:"0.875rem"}}>Loading…</p>
        </div>
      ) : pendingItems.length === 0 && resolvedItems.length === 0 ? (
        <div style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"14px", padding:"3rem", textAlign:"center"}}>
          <div style={{fontSize:"2.5rem", marginBottom:"0.75rem", opacity:0.4}}>✅</div>
          <p style={{fontWeight:"500", color:T.charcoal, marginBottom:"0.25rem"}}>No pending acknowledgments</p>
          <p style={{fontSize:"0.82rem", color:T.muted}}>You're all caught up. Any new role assignments will appear here.</p>
        </div>
      ) : (
        <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
          {pendingItems.map(inv => {
            const pos = positionLabel(inv);
            const estateName = inv.estates?.owner_name ? `${inv.estates.owner_name}'s Estate` : 'Estate';
            const proc = processing[inv.id];
            return (
              <div key={inv.id} style={{background:T.white, border:`1px solid ${T.borderLight}`, borderRadius:"14px", padding:"1.5rem", borderLeft:`3px solid #7c4c8a`}}>
                <div style={{marginBottom:"1rem"}}>
                  <div style={{display:"flex", alignItems:"center", gap:"0.5rem", flexWrap:"wrap", marginBottom:"0.375rem"}}>
                    <span style={{fontWeight:"600", color:T.charcoal, fontSize:"0.95rem"}}>{inv.role}</span>
                    {pos && <span style={{fontSize:"0.7rem", background:T.parchment, color:T.muted, padding:"0.15rem 0.5rem", borderRadius:"4px", fontFamily:"'DM Mono', monospace"}}>{pos}</span>}
                  </div>
                  <p style={{fontSize:"0.82rem", color:T.muted, marginBottom:"0.25rem"}}>
                    Estate: <strong style={{color:T.charcoal}}>{estateName}</strong>
                  </p>
                  {inv.inviter_name && <p style={{fontSize:"0.78rem", color:T.muted}}>Assigned by {inv.inviter_name}</p>}
                </div>
                <div style={{background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:"8px", padding:"0.625rem 0.875rem", marginBottom:"1rem", fontSize:"0.78rem", color:"#9a3412"}}>
                  ⚠ This role is pending — it has no effect on your current access until you acknowledge it.
                </div>
                <div style={{display:"flex", gap:"0.75rem", flexWrap:"wrap"}}>
                  <button
                    onClick={() => handleAcknowledge(inv)}
                    disabled={!!proc}
                    style={{...filledBtn, fontSize:"0.82rem", padding:"0.5rem 1.25rem", opacity:proc?0.6:1}}>
                    {proc === 'accepting' ? "Accepting…" : "Accept responsibility"}
                  </button>
                  <button
                    onClick={() => handleDecline(inv)}
                    disabled={!!proc}
                    style={{...outlineBtn, fontSize:"0.82rem", padding:"0.5rem 1rem", color:T.danger, borderColor:T.danger, opacity:proc?0.6:1}}>
                    {proc === 'declining' ? "Declining…" : "Decline"}
                  </button>
                </div>
              </div>
            );
          })}

          {resolvedItems.length > 0 && (
            <div style={{marginTop:"1rem"}}>
              <h3 style={{fontSize:"0.875rem", fontWeight:"600", color:T.muted, marginBottom:"0.625rem"}}>Resolved</h3>
              {resolvedItems.map(inv => {
                const outcome = outcomes[inv.id];
                const pos = positionLabel(inv);
                const estateName = inv.estates?.owner_name ? `${inv.estates.owner_name}'s Estate` : 'Estate';
                return (
                  <div key={inv.id} style={{background:outcome==='accepted'?"#f0faf5":"#fff5f5", border:`1px solid ${outcome==='accepted'?"#c3e6cb":"#fca5a5"}`, borderRadius:"12px", padding:"1rem 1.25rem", marginBottom:"0.5rem", display:"flex", alignItems:"center", gap:"1rem"}}>
                    <span style={{fontSize:"1.25rem"}}>{outcome === 'accepted' ? '✅' : '❌'}</span>
                    <div>
                      <div style={{fontSize:"0.875rem", fontWeight:"500", color:T.charcoal}}>{inv.role}{pos ? ` · ${pos}` : ''}</div>
                      <div style={{fontSize:"0.75rem", color:T.muted}}>{estateName} · {outcome === 'accepted' ? 'Accepted' : 'Declined'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── REPLACE EXECUTOR MODAL ───────────────────────────────────────────────────
function ReplaceExecutorModal({ target, estate, user, estateSupabaseId, onClose, onReplaced }) {
  const [form, setForm] = useState({ name:"", email:"" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim()) { setErr("Name and email are required."); return; }
    setSaving(true); setErr("");
    const { error } = await updateEstateExecutor(user.id, target.rank, form.name.trim(), form.email.trim());
    if (error) { setErr("Failed to save. Please try again."); setSaving(false); return; }
    if (target.email) {
      sendEmail(target.email, 'access_revoked', {
        name: target.name || '',
        ownerName: estate?.ownerName || estate?.deceasedName || '',
        role: target.relation || `Executor #${target.rank}`,
      });
    }
    if (estateSupabaseId) {
      createInvitation(estateSupabaseId, form.email.trim(), 'executor', user.id).catch(()=>{});
    }
    onReplaced(target.rank, form.name.trim(), form.email.trim());
    setSaving(false);
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{...modalBox, maxWidth:"460px"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem"}}>
          <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.2rem", fontWeight:"700", color:T.forest}}>Replace Executor #{target.rank}</h2>
          <button onClick={onClose} style={{...btnReset, color:T.muted, fontSize:"1.5rem", lineHeight:1}}>×</button>
        </div>
        {target.name && (
          <div style={{background:T.parchment, borderRadius:"8px", padding:"0.75rem 1rem", marginBottom:"1.25rem", fontSize:"0.82rem", color:T.muted}}>
            Currently: <strong style={{color:T.charcoal}}>{target.name}</strong>{target.email ? ` (${target.email})` : ""}
          </div>
        )}
        <div style={{display:"flex", flexDirection:"column", gap:"1rem", marginBottom:"1.25rem"}}>
          <div>
            <label style={labelStyle}>New Executor Name</label>
            <input value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))} placeholder="Full legal name" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>New Executor Email</label>
            <input type="email" value={form.email} onChange={e=>setForm(f=>({...f, email:e.target.value}))} placeholder="email@example.com" style={inputStyle} />
          </div>
        </div>
        {err && <p style={{color:T.danger, fontSize:"0.8rem", marginBottom:"0.75rem"}}>{err}</p>}
        <div style={{display:"flex", gap:"0.75rem"}}>
          <button onClick={onClose} style={{...outlineBtn, flex:1}}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{...filledBtn, flex:1, opacity:saving?0.6:1}}>
            {saving ? "Saving…" : "Replace Executor"}
          </button>
        </div>
        <p style={{fontSize:"0.72rem", color:T.muted, marginTop:"0.875rem", textAlign:"center"}}>
          An invitation will be sent to the new executor. The previous executor will be notified by email.
        </p>
      </div>
    </div>
  );
}

// ─── ESTATE ACCESS SECTION (AccountPage sub-component) ────────────────────────
function EstateAccessSection({ user, profile, setProfile, navTo }) {
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveErr, setLeaveErr] = useState("");

  const role = profile?.invited_role?.replace(/_/g, " ") || "collaborator";

  const handleLeave = async () => {
    setLeaving(true); setLeaveErr("");
    const { error } = await leaveEstate(user.id);
    if (error) { setLeaveErr("Failed to leave. Please try again."); setLeaving(false); return; }
    setProfile(prev => ({...(prev||{}), invited_estate_id: null, invited_role: null}));
    setLeaving(false);
    setShowLeaveConfirm(false);
    navTo("app-dashboard");
  };

  const cardStyle = { background:"white", borderRadius:"16px", padding:"1.5rem 1.75rem", boxShadow:"0 1px 4px rgba(0,0,0,0.06)", marginBottom:"1.5rem" };
  const sectionTitle = { fontFamily:"'Playfair Display', serif", fontSize:"1.1rem", fontWeight:"700", color:T.charcoal, marginBottom:"0.25rem" };
  const sectionSub = { fontSize:"0.82rem", color:T.muted, marginBottom:"1.25rem", lineHeight:"1.6" };

  return (
    <div style={cardStyle}>
      <h2 style={sectionTitle}>Estate Access</h2>
      <p style={sectionSub}>You have been granted access to another person's estate on HeirOS.</p>
      <div style={{background:T.parchment, borderRadius:"10px", padding:"1rem 1.25rem", marginBottom:"1.25rem", display:"flex", alignItems:"center", gap:"0.75rem"}}>
        <div style={{width:"36px", height:"36px", borderRadius:"50%", background:`${T.forest}20`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem", flexShrink:0}}>⚖️</div>
        <div>
          <div style={{fontSize:"0.88rem", fontWeight:"500", color:T.charcoal, textTransform:"capitalize"}}>{role}</div>
          <div style={{fontSize:"0.75rem", color:T.muted}}>Your role on this estate</div>
        </div>
      </div>
      <button onClick={()=>setShowLeaveConfirm(true)} style={{...outlineBtn, borderColor:T.danger, color:T.danger, fontSize:"0.82rem"}}>
        Leave This Estate
      </button>
      {leaveErr && <p style={{color:T.danger, fontSize:"0.8rem", marginTop:"0.5rem"}}>{leaveErr}</p>}

      {showLeaveConfirm && (
        <div style={modalOverlay} onClick={()=>setShowLeaveConfirm(false)}>
          <div style={{...modalBox, maxWidth:"400px"}} onClick={e=>e.stopPropagation()}>
            <div style={{textAlign:"center", marginBottom:"1.5rem"}}>
              <div style={{fontSize:"2rem", marginBottom:"0.75rem"}}>⚠️</div>
              <h2 style={{fontFamily:"'Playfair Display', serif", fontSize:"1.2rem", fontWeight:"700", color:T.charcoal, marginBottom:"0.5rem"}}>Leave this estate?</h2>
              <p style={{color:T.muted, fontSize:"0.875rem", lineHeight:"1.65"}}>You will lose access to this estate and its documents. The estate owner will not be automatically notified.</p>
            </div>
            <div style={{display:"flex", gap:"0.75rem"}}>
              <button onClick={()=>setShowLeaveConfirm(false)} style={{...outlineBtn, flex:1}}>Cancel</button>
              <button onClick={handleLeave} disabled={leaving} style={{...filledBtn, flex:1, background:T.danger, opacity:leaving?0.6:1}}>
                {leaving ? "Leaving…" : "Yes, Leave"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

